{
  "openapi": "3.1.0",
  "info": {
    "description": "The Whop REST API. Please see https://docs.whop.com/developer/api/getting-started for more details.",
    "termsOfService": "https://whop.com/tos-developer-api/",
    "title": "Whop API",
    "version": "1.0.0",
    "x-api-version-date": "2026-07-08-1"
  },
  "servers": [
    {
      "description": "Production Whop API",
      "url": "https://api.whop.com/api/v1"
    },
    {
      "description": "Sandbox Whop API",
      "url": "https://sandbox-api.whop.com/api/v1"
    }
  ],
  "tags": [
    {
      "description": "An Account represents a person or business on Whop that can have its own profile, wallet, and account-scoped settings. Use accounts for customers, creators, merchants, sellers, or connected businesses your integration supports.\n\nUse the Accounts API to create accounts, list accounts visible to your credentials, retrieve or update an account, and retrieve the account associated with the current API key.\n",
      "name": "Accounts",
      "x-whop-summary": "A business on Whop: profile, wallet, capabilities, settings."
    },
    {
      "description": "A User represents a person on Whop. Users have a public profile and can buy products, join accounts, and access experiences.\n\nUse the Users API to search for users, retrieve or update profiles, and check whether a user has access to an account, product, or experience.\n",
      "name": "Users",
      "x-whop-summary": "A person on Whop: profile and connected identities."
    },
    {
      "description": "Stats represent aggregated activity for an account over time. They help you understand revenue, transactions, disputes, members, referrals, and advertising performance across reporting periods like days, weeks, or months.\n\nUse the Stats API to list available metrics and their filterable properties, then retrieve time-series values for a date range.\n",
      "name": "Stats",
      "x-whop-summary": "Aggregated financial, audience, and traffic reporting."
    },
    {
      "description": "A Ledger Activity row is a single financial event on an account's ledger — a payment, withdrawal, refund, transfer, on-chain deposit, swap, or card transaction. Each row is derived from the underlying ledger lines and carries a typed `resource` and `source` so you can present and link the event without extra lookups.\n\nUse Ledger Activity to build a statement or transaction feed for an account or user. Reconcile against your own records with `amount` (signed, in the currency's smallest precision units) and `posted_at`, and use `available_at` to know when inflows became withdrawable.\n",
      "name": "Ledgers",
      "x-whop-summary": "The activity feed behind an account or user's balance."
    },
    {
      "description": "Payouts represent money sent from an account or user balance to an external destination, such as a bank account, wallet, or other saved payout method.\n\nUse the Payouts API to create payouts from stablecoin accounts, list payout history for accounts or users, monitor payout statuses, and show expected arrival details for funds leaving Whop.\n",
      "name": "Payouts",
      "x-whop-summary": "Send money from a balance to a bank or wallet."
    },
    {
      "description": "Cards represent Whop-issued virtual payment cards that spend from an account or user balance. Cards can be assigned to cardholders and configured with spending limits for controlled spending.\n\nUse the Cards API to issue cards, list cards for an account or user, and retrieve active card details such as the card number and CVC.\n",
      "name": "Cards",
      "x-whop-summary": "Issue cards that spend from a balance."
    },
    {
      "description": "Transfers move value between identities on Whop. They are used for account-to-account money movement, user payouts inside Whop, crypto transfers, and claim links depending on the destination type.\n\nUse the Transfers API to create a transfer, list previous transfers, and retrieve a transfer by ID when reconciling money movement between accounts or users.\n",
      "name": "Transfers",
      "x-whop-summary": "Move funds between Whop accounts and users."
    },
    {
      "description": "Deposits describe ways to add funds to an account balance, including hosted deposit pages, bank deposit instructions, and supported crypto wallet addresses.\n\nUse the Deposits API to create deposit instructions for an account.\n",
      "name": "Deposits",
      "x-whop-summary": "Add funds to a balance."
    },
    {
      "description": "Swaps convert value between supported tokens, chains, or wallet destinations for an account. A swap quote describes the expected output, fees, and approval requirements before you create the swap.\n\nUse the Swaps API to quote a conversion, create the swap, list recent swaps, and retrieve status until the transaction completes.\n",
      "name": "Swaps",
      "x-whop-summary": "Convert a balance between currencies."
    },
    {
      "description": "A Verification represents an identity review for a person or business. Accounts and users complete verification when Whop needs to confirm who they are before enabling payouts or compliance-sensitive workflows.\n\nUse the Verifications API to start or resume a hosted verification session, check review status, and submit requested details or documents. If `requested_information` contains items, submit answers with [Update Verification](/api-reference/beta/verifications/update-verification).\n",
      "name": "Verifications",
      "x-whop-summary": "Identity review required before payouts and card issuing."
    },
    {
      "description": "A Product is a digital good or service sold on Whop. Products may contain plans for pricing and/or experiences for content delivery.\n\nUse the Products API to create products, list products visible to your credentials, retrieve product details, update product metadata or merchandising fields, and delete products that should no longer be sold.\n",
      "name": "Products",
      "x-whop-summary": "The things you sell. Each owns plans and a store page."
    },
    {
      "description": "A Plan defines how customers buy a product. It controls pricing, billing cadence, availability, tax behavior, checkout fields, and purchase visibility.\n\nUse the Plans API to create plans for products, list existing plans, retrieve or update plan configuration, calculate tax for checkout, and delete plans that should no longer be offered.\n",
      "name": "Plans",
      "x-whop-summary": "Pricing for a product: one-time, recurring, trials, stock."
    },
    {
      "description": "A Checkout Configuration is a reusable checkout link owned by an account. In `payment` mode it sells a specific plan; in `setup` mode it collects and saves payment details without charging. Each configuration can also override which payment methods are accepted and how 3D Secure is enforced for that checkout.\n\nUse the Checkout Configurations API to create checkout links for an existing or inline plan, list configurations for an account, retrieve the configuration behind a checkout URL, and delete links that should no longer be used.\n",
      "name": "Checkout Configurations",
      "x-whop-summary": "Turn a plan into a shareable, prefilled checkout link."
    },
    {
      "description": "The Partners API covers your Whop partner activity: the users you referred onto Whop, the businesses you referred and the earnings generated from their processing volume, and the partner leaderboard.\n\nUse it to enroll as a Whop partner, list the users you referred, list your referred businesses and review their earnings, and see the partner leaderboard.\n",
      "name": "Partners",
      "x-whop-summary": "The users and businesses you referred to Whop, and what you earn from them."
    },
    {
      "description": "A Bounty is a paid task posted by an account or user. The reward is held in escrow when the bounty publishes, workers submit proof of completed work, and each accepted submission is paid out until every winner slot fills.\n\nUse the Bounties API to create and publish a bounty, list an account's bounties for reporting or dashboards, list the bounties a user can work or has participated in, and retrieve a single bounty by ID.\n",
      "name": "Bounties",
      "x-whop-summary": "Paid tasks with reviewed submissions and escrowed rewards."
    },
    {
      "description": "A Bounty Submission is one worker's attempt on a bounty. It starts as an in-progress attempt, enters the review queue when proof is submitted, and ends approved (paid from the bounty's escrowed pool) or denied.\n\nUse the Bounty Submissions API to submit proof of completed work to a bounty, list the submissions you authored, and review the submissions on your bounties — across every bounty or narrowed to one.\n",
      "name": "Bounty Submissions",
      "x-whop-summary": "Work submitted to a bounty, from attempt to payout."
    },
    {
      "description": "A Person represents a visitor or customer of an account, assembled from [pixel events](/api-reference/beta/events/event) and purchase activity — ad clicks, storefront visits, and checkouts.\n\nUse the People API to list the people of an account and retrieve a single person.\n",
      "name": "People",
      "x-whop-summary": "Visitors and customers of an account, aggregated from pixel events."
    },
    {
      "description": "An Event records conversion or engagement activity for an account, such as page views, purchases, or leads. Each event ties the action to the [person](/api-reference/beta/people/person) who took it, so activity can be attributed to the ads and links that drove it.\n\nUse the Events API to send new tracking events, list recent identity-linked events for an account, and inspect the events recorded for a person.\n",
      "name": "Events",
      "x-whop-summary": "Conversion and engagement events tracked for attribution."
    },
    {
      "description": "An Ad is the individual creative unit delivered by an [ad group](/api-reference/beta/ad-groups/ad-group). It holds the copy, creative assets, and destination URL for one ad.\n\nUse the Ads API to list ads for an account, create ads inside ad groups, retrieve or update creative details, delete ads that should stop running, and pause or resume delivery.\n",
      "name": "Ads",
      "x-whop-summary": "The creative: copy, assets, and destination URL."
    },
    {
      "description": "An Ad Campaign is the top-level container for paid ads on an ad network. It sets the platform, objective, and budget strategy shared by its [ad groups](/api-reference/beta/ad-groups/ad-group) and ads.\n\nUse the Ad Campaigns API to create campaigns, list campaigns for an account, retrieve or update campaign settings, and pause or resume campaign delivery.\n",
      "name": "Ad Campaigns",
      "x-whop-summary": "Platform, objective, and budget for a set of ads."
    },
    {
      "description": "An Ad Group sits inside an [ad campaign](/api-reference/beta/ad-campaigns/ad-campaign) and controls delivery for [ads](/api-reference/beta/ads/ad). It sets the audience, placements, schedule, budget, and optimization goal for its ads.\n\nUse the Ad Groups API to create ad groups in campaigns, list or retrieve targeting and delivery settings, update budgets or targeting, delete groups that should stop running, and pause or resume delivery.\n",
      "name": "Ad Groups",
      "x-whop-summary": "Audience, placements, and schedule within a campaign."
    },
    {
      "description": "An Audience represents a customer list uploaded to Whop for ad targeting. Audiences belong to an account and sync to supported ad platforms as custom audiences.\n\nUse the Audiences API to create audiences from CSV uploads, monitor processing status, and list or delete audiences for an account. Created audiences are usable for targeting after processing reaches `ready` or `partial`.\n",
      "name": "Audiences",
      "x-whop-summary": "Reusable targeting lists for ad groups."
    },
    {
      "description": "A Media Asset is an AI-generated image or video created from a prompt and billed from an account balance. When generation finishes, the asset includes a file that can be attached anywhere Whop accepts files.\n\nUse the Media API to start a generation job and retrieve the asset while it processes or after it is ready.\n",
      "name": "Media",
      "x-whop-summary": "AI-generated assets, billed from a balance, attachable wherever files are accepted."
    },
    {
      "description": "A Social Account represents an external profile connected to a Whop account or user, such as a Facebook page or Instagram account. Connecting a social account lets Whop run [ads](/api-reference/beta/ads/ad) under that profile's identity and promote its existing posts.\n\nUse the Social Accounts API to list connected accounts, create a Whop-managed Facebook page, start an OAuth connection, disconnect a social account, and list a connected profile's posts.\n",
      "name": "Social Accounts",
      "x-whop-summary": "Connected Facebook and Instagram accounts that run ads."
    },
    {
      "description": "An App is software you build on Whop. It can be a hosted web app served at `<route>.whop.app` or an API integration installed as an experience, and it belongs to the account that owns its credentials, settings, builds, and runtime logs.\n\nUse the Apps API to manage app configuration and, for hosted apps, read server runtime logs for console output, uncaught exceptions, and failed requests. Logs are retained for 7 days and can be filtered by build, level, time window, and message text.\n",
      "name": "Apps",
      "x-whop-summary": "Apps you build on Whop: metadata, hosted builds, runtime logs."
    }
  ],
  "x-tagGroups": [
    {
      "description": "The accounts and people everything else hangs off.",
      "name": "Core Resources",
      "tags": [
        "Accounts",
        "Users",
        "Webhooks",
        "Stats"
      ]
    },
    {
      "description": "Balances and every way funds move in or out.",
      "name": "Money",
      "tags": [
        "Ledgers",
        "Payouts",
        "Cards",
        "Transfers",
        "Deposits",
        "Swaps",
        "Payout Methods",
        "Verifications"
      ]
    },
    {
      "description": "Sell products and get paid.",
      "name": "Commerce",
      "tags": [
        "Products",
        "Plans",
        "Checkout Configurations"
      ]
    },
    {
      "description": "Refer users and businesses to Whop and track what you earn.",
      "name": "Partners",
      "tags": [
        "Partners"
      ]
    },
    {
      "description": "Post paid tasks and pay people for completed work.",
      "name": "Workforce",
      "tags": [
        "Bounties",
        "Bounty Submissions"
      ]
    },
    {
      "description": "Who visits and what converts, captured by the pixel.",
      "name": "Tracking",
      "tags": [
        "People",
        "Events",
        "Reports"
      ]
    },
    {
      "description": "Run and measure ad campaigns from your integration.",
      "name": "Ads",
      "tags": [
        "Ads",
        "Ad Campaigns",
        "Ad Groups",
        "Audiences"
      ]
    },
    {
      "description": "Generate media assets from your integration.",
      "name": "Media",
      "tags": [
        "Media"
      ]
    },
    {
      "description": "Accounts connected from other platforms.",
      "name": "Identity",
      "tags": [
        "Social Accounts"
      ]
    },
    {
      "description": "Build, host, and observe apps on the Whop platform.",
      "name": "Developer",
      "tags": [
        "Apps"
      ]
    }
  ],
  "paths": {
    "/accounts": {
      "get": {
        "description": "Lists accounts visible to the credential. User tokens return the user's business accounts; business account API keys return the requesting business account and its connected accounts.",
        "operationId": "list",
        "parameters": [
          {
            "description": "The number of accounts to return (default 10, max 50).",
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "type": "integer"
            }
          },
          {
            "description": "A cursor; returns accounts after this position.",
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The number of accounts to return from the end of the range.",
            "in": "query",
            "name": "last",
            "required": false,
            "schema": {
              "type": "integer"
            }
          },
          {
            "description": "A cursor; returns accounts before this position.",
            "in": "query",
            "name": "before",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The field to sort accounts by.",
            "in": "query",
            "name": "order",
            "required": false,
            "schema": {
              "default": "created_at",
              "enum": [
                "created_at"
              ],
              "type": "string"
            }
          },
          {
            "description": "Sort direction.",
            "in": "query",
            "name": "direction",
            "required": false,
            "schema": {
              "default": "desc",
              "enum": [
                "asc",
                "desc"
              ],
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "$ref": "#/components/schemas/Account"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "end_cursor",
                        "start_cursor",
                        "has_next_page",
                        "has_previous_page"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "accounts listed"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the required balance-read scope"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "company:balance:read"
            ]
          }
        ],
        "summary": "List Accounts",
        "tags": [
          "Accounts"
        ]
      },
      "post": {
        "description": "Creates an account. User tokens create business accounts; business account API keys create connected accounts. Tax fields (`tax_remitted_by`, `product_tax_code_id`, `business_address`, `tax_identifiers`) are configured with Update Account, not at creation.",
        "operationId": "create",
        "parameters": [],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "country": {
                    "description": "The ISO 3166-1 alpha-2 country code where the account's business is located (e.g. `US`). Defaults to the parent account's country for connected accounts.",
                    "type": "string"
                  },
                  "email": {
                    "description": "The email address of the account owner. Required for business account API key requests.",
                    "type": "string"
                  },
                  "metadata": {
                    "additionalProperties": true,
                    "description": "Arbitrary key/value metadata to store on the account.",
                    "type": "object"
                  },
                  "title": {
                    "description": "The display name of the account. Defaults to `metadata.external_id` or the owner's email when omitted.",
                    "type": "string"
                  }
                },
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "201": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Account"
                }
              }
            },
            "description": "account created"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "the request body is invalid"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the required connected-account creation scope"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "company:create_child"
            ]
          }
        ],
        "summary": "Create Account",
        "tags": [
          "Accounts"
        ]
      }
    },
    "/accounts/me": {
      "get": {
        "description": "Retrieves the business account associated with the current business account API key.",
        "operationId": "me",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Account"
                }
              }
            },
            "description": "requesting account retrieved"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the required balance-read scope"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "company:balance:read"
            ]
          }
        ],
        "summary": "Retrieve Requesting Account",
        "tags": [
          "Accounts"
        ],
        "parameters": []
      }
    },
    "/accounts/{account_id}": {
      "get": {
        "description": "Retrieves a single account visible to the credential, including its crypto wallet.",
        "operationId": "get",
        "parameters": [
          {
            "description": "Account ID, prefixed `biz_`.",
            "in": "path",
            "name": "account_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Account"
                }
              }
            },
            "description": "account retrieved"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the required balance-read scope"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "no account with that ID is visible to the credential"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "company:balance:read"
            ]
          }
        ],
        "summary": "Retrieve Account",
        "tags": [
          "Accounts"
        ]
      },
      "patch": {
        "description": "Updates an account. User tokens can update business accounts; business account API keys can update connected accounts.",
        "operationId": "update",
        "parameters": [
          {
            "description": "Account ID, prefixed `biz_`.",
            "in": "path",
            "name": "account_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "affiliate_application_required": {
                    "description": "Whether prospective affiliates must submit an application before promoting this account.",
                    "type": "boolean"
                  },
                  "affiliate_instructions": {
                    "description": "Guidelines shown to affiliates promoting this account.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "banner_image": {
                    "additionalProperties": true,
                    "description": "Attachment input for the account banner image.",
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "business_address": {
                    "description": "Account business address used to calculate tax. A complete address in a supported country is required when `tax_remitted_by` is `self`.",
                    "properties": {
                      "city": {
                        "description": "City name.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "country": {
                        "description": "Two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`.",
                        "type": "string"
                      },
                      "line1": {
                        "description": "First line of the street address.",
                        "type": "string"
                      },
                      "line2": {
                        "description": "Second line of the street address.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "postal_code": {
                        "description": "Postal or ZIP code.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "state": {
                        "description": "State, province, or region code, for example `CA`.",
                        "type": [
                          "string",
                          "null"
                        ]
                      }
                    },
                    "type": "object"
                  },
                  "business_type": {
                    "description": "High-level business category for the account.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "country": {
                    "description": "Country where the account is located.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "description": {
                    "description": "Account promotional description.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "featured_affiliate_product_id": {
                    "description": "The ID of the product to feature for affiliates. Pass `null` to clear.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "home_preferences": {
                    "description": "Public account home page preferences.",
                    "items": {
                      "type": "string"
                    },
                    "type": "array"
                  },
                  "industry_group": {
                    "description": "Account industry group.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "industry_type": {
                    "description": "Specific industry vertical for the account.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "invoice_prefix": {
                    "description": "Prefix used for account invoices.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "logo": {
                    "additionalProperties": true,
                    "description": "Attachment input for the account logo.",
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "metadata": {
                    "additionalProperties": true,
                    "description": "Arbitrary key/value metadata to store on the account.",
                    "type": "object"
                  },
                  "onboarding_type": {
                    "description": "The type of onboarding the account has completed.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "opengraph_image": {
                    "additionalProperties": true,
                    "description": "Attachment input for the account Open Graph image.",
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "opengraph_image_variant": {
                    "description": "The account Open Graph image variant.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "other_business_description": {
                    "description": "The description of the business type when business_type is other.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "other_industry_description": {
                    "description": "The description of the industry type when industry_type is other.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "product_tax_code_id": {
                    "description": "ID of the tax classification code applied by default to the account's products. See the available [product categories](https://docs.numeral.com/essentials/product-categories).",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "require_2fa": {
                    "description": "Whether the account requires authorized users to have two-factor authentication enabled.",
                    "type": "boolean"
                  },
                  "route": {
                    "description": "The unique URL slug for the account.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "send_customer_emails": {
                    "description": "Whether Whop sends transactional emails to customers on behalf of this account.",
                    "type": "boolean"
                  },
                  "show_joined_whops": {
                    "description": "Whether the account appears in joined whops on other accounts.",
                    "type": "boolean"
                  },
                  "show_reviews_dtc": {
                    "description": "Whether reviews are displayed on direct-to-consumer product pages.",
                    "type": "boolean"
                  },
                  "show_user_directory": {
                    "description": "Whether the account shows users in the user directory.",
                    "type": "boolean"
                  },
                  "social_links": {
                    "description": "The full list of social links to display for the account.",
                    "items": {
                      "additionalProperties": true,
                      "type": "object"
                    },
                    "type": "array"
                  },
                  "store_page_config": {
                    "additionalProperties": true,
                    "description": "Account store page display configuration.",
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "target_audience": {
                    "description": "The target audience for this account.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "tax_collection_enabled_states": {
                    "description": "US state codes (50 states plus `DC`) where the account collects tax. Replaces the full set on update. Only settable when `tax_remitted_by` is `self`.",
                    "items": {
                      "enum": [
                        "AL",
                        "AK",
                        "AZ",
                        "AR",
                        "CA",
                        "CO",
                        "CT",
                        "DE",
                        "DC",
                        "FL",
                        "GA",
                        "HI",
                        "ID",
                        "IL",
                        "IN",
                        "IA",
                        "KS",
                        "KY",
                        "LA",
                        "ME",
                        "MD",
                        "MA",
                        "MI",
                        "MN",
                        "MS",
                        "MO",
                        "MT",
                        "NE",
                        "NV",
                        "NH",
                        "NJ",
                        "NM",
                        "NY",
                        "NC",
                        "ND",
                        "OH",
                        "OK",
                        "OR",
                        "PA",
                        "RI",
                        "SC",
                        "SD",
                        "TN",
                        "TX",
                        "UT",
                        "VT",
                        "VA",
                        "WA",
                        "WV",
                        "WI",
                        "WY"
                      ],
                      "type": "string"
                    },
                    "type": "array"
                  },
                  "tax_identifiers": {
                    "description": "Account tax/VAT registrations to add or update. When `tax_remitted_by` is `self`, tax is calculated and collected only in the countries where the account holds a registration.",
                    "items": {
                      "properties": {
                        "tax_id_type": {
                          "description": "Tax ID type, for example `eu_vat`, `gb_vat`, or `us_ein`.",
                          "enum": [
                            "ad_nrt",
                            "ao_tin",
                            "ar_cuit",
                            "al_tin",
                            "am_tin",
                            "aw_tin",
                            "au_abn",
                            "au_arn",
                            "eu_vat",
                            "az_tin",
                            "bs_tin",
                            "bh_vat",
                            "bd_bin",
                            "bb_tin",
                            "by_tin",
                            "bj_ifu",
                            "bo_tin",
                            "ba_tin",
                            "br_cnpj",
                            "br_cpf",
                            "bg_uic",
                            "bf_ifu",
                            "kh_tin",
                            "cm_niu",
                            "ca_bn",
                            "ca_gst_hst",
                            "ca_pst_bc",
                            "ca_pst_mb",
                            "ca_pst_sk",
                            "ca_qst",
                            "cv_nif",
                            "cl_tin",
                            "cn_tin",
                            "co_nit",
                            "cd_nif",
                            "cr_tin",
                            "hr_oib",
                            "do_rcn",
                            "ec_ruc",
                            "eg_tin",
                            "sv_nit",
                            "et_tin",
                            "eu_oss_vat",
                            "ge_vat",
                            "gh_tin",
                            "de_stn",
                            "gb_vat",
                            "gn_nif",
                            "hk_br",
                            "hu_tin",
                            "is_vat",
                            "in_gst",
                            "id_npwp",
                            "il_vat",
                            "jp_cn",
                            "jp_rn",
                            "jp_trn",
                            "kz_bin",
                            "ke_pin",
                            "kg_tin",
                            "la_tin",
                            "li_uid",
                            "li_vat",
                            "my_frp",
                            "my_itn",
                            "my_sst",
                            "mr_nif",
                            "mx_rfc",
                            "md_vat",
                            "me_pib",
                            "ma_vat",
                            "np_pan",
                            "nz_gst",
                            "ng_tin",
                            "mk_vat",
                            "no_vat",
                            "no_voec",
                            "om_vat",
                            "pe_ruc",
                            "ph_tin",
                            "ro_tin",
                            "ru_inn",
                            "ru_kpp",
                            "sa_vat",
                            "sn_ninea",
                            "rs_pib",
                            "sg_gst",
                            "sg_uen",
                            "si_tin",
                            "za_vat",
                            "kr_brn",
                            "es_cif",
                            "ch_uid",
                            "ch_vat",
                            "tw_vat",
                            "tj_tin",
                            "tz_vat",
                            "th_vat",
                            "tr_tin",
                            "ug_tin",
                            "ua_vat",
                            "ae_trn",
                            "us_ein",
                            "uy_ruc",
                            "uz_tin",
                            "uz_vat",
                            "ve_rif",
                            "vn_tin",
                            "zm_tin",
                            "zw_tin",
                            "sr_fin"
                          ],
                          "type": "string"
                        },
                        "tax_id_value": {
                          "description": "Tax ID value, for example `DE123456789`.",
                          "type": "string"
                        }
                      },
                      "required": [
                        "tax_id_type",
                        "tax_id_value"
                      ],
                      "type": "object"
                    },
                    "type": "array"
                  },
                  "tax_remitted_by": {
                    "description": "Who calculates and remits tax for the account: `whop` (Whop calculates and remits), `self` (Whop calculates; the account collects and remits), or `none` (neither; the account is responsible). `self` requires a `business_address` in a supported country.",
                    "enum": [
                      "whop",
                      "self",
                      "none"
                    ],
                    "type": "string"
                  },
                  "title": {
                    "description": "The display name of the account.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "use_logo_as_opengraph_image_fallback": {
                    "description": "Whether the account uses its logo as the fallback Open Graph image.",
                    "type": "boolean"
                  }
                },
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Account"
                }
              }
            },
            "description": "account updated"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "store page config is not an object"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the required update scope"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "no account with that ID is visible to the credential"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "company:update"
            ]
          }
        ],
        "summary": "Update Account",
        "tags": [
          "Accounts"
        ]
      }
    },
    "/accounts/{account_id}/llc": {
      "post": {
        "description": "Starts an LLC formation for a business account. On submission, the application is validated and the response returns a hosted checkout URL. Once paid, the filing is submitted. Track progress through the account's [`llc_formation`](/api-reference/beta/accounts/retrieve-account) field on Retrieve Account.",
        "operationId": "llc",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "Account ID, prefixed `biz_`.",
            "in": "path",
            "name": "account_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "business_info": {
                    "description": "The company to form.",
                    "properties": {
                      "address": {
                        "description": "Company mailing address. Required unless `use_registered_agent` is `true`.",
                        "properties": {
                          "city": {
                            "type": "string"
                          },
                          "country": {
                            "description": "Two-letter ISO 3166-1 country code, for example `US`.",
                            "type": "string"
                          },
                          "line1": {
                            "description": "First line of the street address.",
                            "type": "string"
                          },
                          "line2": {
                            "description": "Second line of the street address.",
                            "type": "string"
                          },
                          "postal_code": {
                            "description": "Postal or ZIP code.",
                            "type": "string"
                          },
                          "state": {
                            "description": "State or region code, for example `CA`.",
                            "type": "string"
                          }
                        },
                        "required": [
                          "line1",
                          "city",
                          "state",
                          "postal_code",
                          "country"
                        ],
                        "type": "object"
                      },
                      "business_type": {
                        "description": "High-level business category, from the Whop business taxonomy. Valid values are listed on [Business Types & Industries](/api-reference/beta/business-types).",
                        "type": "string"
                      },
                      "entity_suffix": {
                        "description": "Legal entity ending appended to `legal_name`. Defaults to `LLC`; unrecognized values fall back to `LLC`.",
                        "enum": [
                          "LLC",
                          "L.L.C",
                          "L.L.C.",
                          "Limited Liability Company"
                        ],
                        "type": "string",
                        "x-enumNames": [
                          "LLC",
                          "L_L_C",
                          "L_L_C_",
                          "Limited_Liability_Company"
                        ]
                      },
                      "expedite_ein": {
                        "description": "Request expedited EIN processing for an additional fee. Available only when no founder supplies an SSN.",
                        "type": "boolean"
                      },
                      "formation_state": {
                        "description": "Two-letter code of the US state (or `DC`) to form the LLC in.",
                        "enum": [
                          "AL",
                          "AK",
                          "AZ",
                          "AR",
                          "CA",
                          "CO",
                          "CT",
                          "DE",
                          "DC",
                          "FL",
                          "GA",
                          "HI",
                          "ID",
                          "IL",
                          "IN",
                          "IA",
                          "KS",
                          "KY",
                          "LA",
                          "ME",
                          "MD",
                          "MA",
                          "MI",
                          "MN",
                          "MS",
                          "MO",
                          "MT",
                          "NE",
                          "NV",
                          "NH",
                          "NJ",
                          "NM",
                          "NY",
                          "NC",
                          "ND",
                          "OH",
                          "OK",
                          "OR",
                          "PA",
                          "RI",
                          "SC",
                          "SD",
                          "TN",
                          "TX",
                          "UT",
                          "VT",
                          "VA",
                          "WA",
                          "WV",
                          "WI",
                          "WY"
                        ],
                        "type": "string"
                      },
                      "industry_group": {
                        "description": "Industry group, from the Whop business taxonomy. Valid values are listed on [Business Types & Industries](/api-reference/beta/business-types).",
                        "type": "string"
                      },
                      "industry_type": {
                        "description": "Specific industry vertical, from the Whop business taxonomy. Valid values are listed on [Business Types & Industries](/api-reference/beta/business-types).",
                        "type": "string"
                      },
                      "legal_name": {
                        "description": "Legal name for the new company.",
                        "type": "string"
                      },
                      "phone": {
                        "description": "Business phone number in E.164 format, for example `+12125550100`.",
                        "type": "string"
                      },
                      "use_registered_agent": {
                        "description": "Use the registered agent's address as the company address instead of `address`.",
                        "type": "boolean"
                      },
                      "website": {
                        "description": "Company website URL.",
                        "type": "string"
                      }
                    },
                    "required": [
                      "legal_name",
                      "formation_state",
                      "business_type",
                      "industry_group",
                      "industry_type"
                    ],
                    "type": "object"
                  },
                  "founders": {
                    "description": "The company's founders. Exactly one must be marked `is_primary` — the responsible party for the filing.",
                    "items": {
                      "properties": {
                        "address": {
                          "description": "Founder's personal address.",
                          "properties": {
                            "city": {
                              "type": "string"
                            },
                            "country": {
                              "description": "Two-letter ISO 3166-1 country code, for example `US`.",
                              "type": "string"
                            },
                            "line1": {
                              "description": "First line of the street address.",
                              "type": "string"
                            },
                            "line2": {
                              "description": "Second line of the street address.",
                              "type": "string"
                            },
                            "postal_code": {
                              "description": "Postal or ZIP code.",
                              "type": "string"
                            },
                            "state": {
                              "description": "State or region code, for example `CA`.",
                              "type": "string"
                            }
                          },
                          "required": [
                            "line1",
                            "city",
                            "state",
                            "postal_code",
                            "country"
                          ],
                          "type": "object"
                        },
                        "date_of_birth": {
                          "description": "Formatted as `YYYY-MM-DD`.",
                          "type": "string"
                        },
                        "email": {
                          "type": "string"
                        },
                        "first_name": {
                          "type": "string"
                        },
                        "is_primary": {
                          "description": "Marks the responsible party for the filing. Exactly one founder must be primary.",
                          "type": "boolean"
                        },
                        "last_name": {
                          "type": "string"
                        },
                        "ownership_percentage": {
                          "description": "The founder's ownership share: greater than `0`, at most `100`. Shares across founders must total `100`.",
                          "type": "number"
                        },
                        "phone": {
                          "description": "Phone number in E.164 format, for example `+12125550100`.",
                          "type": "string"
                        },
                        "ssn": {
                          "description": "The founder's US Social Security Number. Leave empty if the founder is not a US resident. Non-US founders can request expedited EIN processing via the `expedite_ein` option in `business_info`.",
                          "type": "string"
                        }
                      },
                      "required": [
                        "is_primary",
                        "first_name",
                        "last_name",
                        "email",
                        "phone",
                        "address",
                        "ownership_percentage"
                      ],
                      "type": "object"
                    },
                    "type": "array"
                  }
                },
                "required": [
                  "business_info",
                  "founders"
                ],
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "checkout_session_id": {
                      "description": "Checkout session ID, prefixed `ch_`.",
                      "type": "string"
                    },
                    "checkout_url": {
                      "description": "Hosted checkout URL. Send the buyer here to pay for the formation; the filing is submitted once payment completes.",
                      "type": "string"
                    },
                    "currency": {
                      "description": "Always `usd`.",
                      "type": "string"
                    },
                    "total": {
                      "description": "Total due at checkout in USD cents.",
                      "type": "integer"
                    }
                  },
                  "required": [
                    "checkout_url",
                    "checkout_session_id",
                    "total",
                    "currency"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "application accepted and checkout created"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "invalid submission"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "the credential cannot manage the account"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "account not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "incorporation:write"
            ]
          }
        ],
        "summary": "Register LLC",
        "tags": [
          "Accounts"
        ]
      }
    },
    "/accounts/{account_id}/preferences": {
      "get": {
        "description": "Retrieves the account's preferences: a singleton settings document keyed by preference name.",
        "operationId": "preferences",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "ads_payment_methods": {
                      "description": "How the account pays for Whop Ads spend. `primary` is charged first; `backup` covers the charge when the primary fails. `null` until ads billing has been configured.",
                      "properties": {
                        "backup": {
                          "properties": {
                            "card_brand": {
                              "description": "Card brand, present for `card` entries.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "exp_month": {
                              "description": "Expiration month, present for `card` entries.",
                              "type": [
                                "integer",
                                "null"
                              ]
                            },
                            "exp_year": {
                              "description": "Expiration year, present for `card` entries.",
                              "type": [
                                "integer",
                                "null"
                              ]
                            },
                            "icon_url": {
                              "description": "Balance owner icon URL, present for `platform_balance` entries.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "id": {
                              "description": "The funding source ID: a Whop balance (`ldgr_`) for `platform_balance`, or a payment method (`payt_`) for `card`.",
                              "type": "string"
                            },
                            "last4": {
                              "description": "Last four digits, present for `card` entries.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "title": {
                              "description": "Balance name, present for company `platform_balance` entries (null for a personal balance).",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "type": {
                              "description": "The funding source kind: a Whop balance or a saved card.",
                              "enum": [
                                "platform_balance",
                                "card"
                              ],
                              "type": "string"
                            }
                          },
                          "required": [
                            "type",
                            "id"
                          ],
                          "type": [
                            "object",
                            "null"
                          ]
                        },
                        "primary": {
                          "properties": {
                            "card_brand": {
                              "description": "Card brand, present for `card` entries.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "exp_month": {
                              "description": "Expiration month, present for `card` entries.",
                              "type": [
                                "integer",
                                "null"
                              ]
                            },
                            "exp_year": {
                              "description": "Expiration year, present for `card` entries.",
                              "type": [
                                "integer",
                                "null"
                              ]
                            },
                            "icon_url": {
                              "description": "Balance owner icon URL, present for `platform_balance` entries.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "id": {
                              "description": "The funding source ID: a Whop balance (`ldgr_`) for `platform_balance`, or a payment method (`payt_`) for `card`.",
                              "type": "string"
                            },
                            "last4": {
                              "description": "Last four digits, present for `card` entries.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "title": {
                              "description": "Balance name, present for company `platform_balance` entries (null for a personal balance).",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "type": {
                              "description": "The funding source kind: a Whop balance or a saved card.",
                              "enum": [
                                "platform_balance",
                                "card"
                              ],
                              "type": "string"
                            }
                          },
                          "required": [
                            "type",
                            "id"
                          ],
                          "type": [
                            "object",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "primary",
                        "backup"
                      ],
                      "type": [
                        "object",
                        "null"
                      ]
                    }
                  },
                  "required": [
                    "ads_payment_methods"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "preferences retrieved"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "unauthorized"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "account not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:create"
            ]
          }
        ],
        "summary": "Retrieve Account Preferences",
        "tags": [
          "Accounts"
        ],
        "parameters": [
          {
            "description": "Account ID, prefixed `biz_`.",
            "in": "path",
            "name": "account_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ]
      },
      "patch": {
        "description": "Updates the account's preferences. Each top-level key present in the body is replaced as a whole; omitted keys are left untouched. `ads_payment_methods` always requires a `primary` entry. `backup` is optional and any pairing is allowed — two cards, `card`+`platform_balance`, or a single method — so a card-only advertiser can fund ads without a platform balance. The `primary` and `backup` must be different sources. A `platform_balance` entry may omit `id` to use the account's default Whop balance. Configuring a `card` requires a user token; account API keys can set up platform-balance billing only.",
        "operationId": "update-preferences",
        "parameters": [
          {
            "description": "Account ID, prefixed `biz_`.",
            "in": "path",
            "name": "account_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "ads_payment_methods": {
                    "description": "How the account pays for Whop Ads spend. `primary` is charged first; `backup` covers the charge when the primary fails.",
                    "properties": {
                      "backup": {
                        "description": "Optional second method charged if the primary fails. Any pairing is allowed (two cards, card+balance, balance+card); omit it to run on a single method. Must differ from the primary.",
                        "properties": {
                          "id": {
                            "description": "The funding source ID: a Whop balance (`ldgr_`) for `platform_balance`, or a payment method (`payt_`) for `card`. Optional for `platform_balance` — defaults to the account's default Whop balance. Required for `card`.",
                            "type": "string"
                          },
                          "type": {
                            "description": "The funding source kind.",
                            "enum": [
                              "platform_balance",
                              "card"
                            ],
                            "type": "string"
                          }
                        },
                        "required": [
                          "type"
                        ],
                        "type": "object"
                      },
                      "primary": {
                        "properties": {
                          "id": {
                            "description": "The funding source ID: a Whop balance (`ldgr_`) for `platform_balance`, or a payment method (`payt_`) for `card`. Optional for `platform_balance` — defaults to the account's default Whop balance. Required for `card`.",
                            "type": "string"
                          },
                          "type": {
                            "description": "The funding source kind.",
                            "enum": [
                              "platform_balance",
                              "card"
                            ],
                            "type": "string"
                          }
                        },
                        "required": [
                          "type"
                        ],
                        "type": "object"
                      }
                    },
                    "required": [
                      "primary"
                    ],
                    "type": "object"
                  }
                },
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "ads_payment_methods": {
                      "description": "How the account pays for Whop Ads spend. `primary` is charged first; `backup` covers the charge when the primary fails. `null` until ads billing has been configured.",
                      "properties": {
                        "backup": {
                          "properties": {
                            "card_brand": {
                              "description": "Card brand, present for `card` entries.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "exp_month": {
                              "description": "Expiration month, present for `card` entries.",
                              "type": [
                                "integer",
                                "null"
                              ]
                            },
                            "exp_year": {
                              "description": "Expiration year, present for `card` entries.",
                              "type": [
                                "integer",
                                "null"
                              ]
                            },
                            "icon_url": {
                              "description": "Balance owner icon URL, present for `platform_balance` entries.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "id": {
                              "description": "The funding source ID: a Whop balance (`ldgr_`) for `platform_balance`, or a payment method (`payt_`) for `card`.",
                              "type": "string"
                            },
                            "last4": {
                              "description": "Last four digits, present for `card` entries.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "title": {
                              "description": "Balance name, present for company `platform_balance` entries (null for a personal balance).",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "type": {
                              "description": "The funding source kind: a Whop balance or a saved card.",
                              "enum": [
                                "platform_balance",
                                "card"
                              ],
                              "type": "string"
                            }
                          },
                          "required": [
                            "type",
                            "id"
                          ],
                          "type": [
                            "object",
                            "null"
                          ]
                        },
                        "primary": {
                          "properties": {
                            "card_brand": {
                              "description": "Card brand, present for `card` entries.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "exp_month": {
                              "description": "Expiration month, present for `card` entries.",
                              "type": [
                                "integer",
                                "null"
                              ]
                            },
                            "exp_year": {
                              "description": "Expiration year, present for `card` entries.",
                              "type": [
                                "integer",
                                "null"
                              ]
                            },
                            "icon_url": {
                              "description": "Balance owner icon URL, present for `platform_balance` entries.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "id": {
                              "description": "The funding source ID: a Whop balance (`ldgr_`) for `platform_balance`, or a payment method (`payt_`) for `card`.",
                              "type": "string"
                            },
                            "last4": {
                              "description": "Last four digits, present for `card` entries.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "title": {
                              "description": "Balance name, present for company `platform_balance` entries (null for a personal balance).",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "type": {
                              "description": "The funding source kind: a Whop balance or a saved card.",
                              "enum": [
                                "platform_balance",
                                "card"
                              ],
                              "type": "string"
                            }
                          },
                          "required": [
                            "type",
                            "id"
                          ],
                          "type": [
                            "object",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "primary",
                        "backup"
                      ],
                      "type": [
                        "object",
                        "null"
                      ]
                    }
                  },
                  "required": [
                    "ads_payment_methods"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "preferences updated"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "invalid preferences"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "unauthorized"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "forbidden"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "account not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:create"
            ]
          }
        ],
        "summary": "Update Account Preferences",
        "tags": [
          "Accounts"
        ]
      }
    },
    "/accounts/{account_id}/recommend_actions": {
      "get": {
        "description": "Lists the recommended actions computed for the account — the same set embedded on the account resource, served on their own so a caller can fetch just the recommendations.",
        "operationId": "recommend_actions",
        "parameters": [
          {
            "description": "Account ID, prefixed `biz_`.",
            "in": "path",
            "name": "account_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "$ref": "#/components/schemas/AccountRecommendedAction"
                      },
                      "type": "array"
                    }
                  },
                  "required": [
                    "data"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "empty for a connected (child) account"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "account not visible to the credential"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "company:balance:read"
            ]
          }
        ],
        "summary": "List Recommended Actions",
        "tags": [
          "Accounts"
        ]
      }
    },
    "/ad_campaigns": {
      "get": {
        "description": "Lists the ad campaigns for an account, with stats over the requested window.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The account the campaigns belong to. Defaults to the account-scoped key's own account.",
            "in": "query",
            "name": "account_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Only return campaigns with this status.",
            "in": "query",
            "name": "status",
            "required": false,
            "schema": {
              "enum": [
                "draft",
                "active",
                "paused",
                "payment_failed"
              ],
              "type": "string"
            }
          },
          {
            "description": "Filter campaigns by a title or ID substring.",
            "in": "query",
            "name": "query",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The field to sort by. Defaults to created_at. Stat columns (spend, impressions, …) rank over the stats_from/stats_to window across the whole list, not just the current page. results, cost_per_result and return_on_ad_spend rank by the same Whop pixel-attributed values the response reports.",
            "in": "query",
            "name": "order",
            "required": false,
            "schema": {
              "enum": [
                "created_at",
                "updated_at",
                "spend",
                "impressions",
                "reach",
                "clicks",
                "unique_clicks",
                "frequency",
                "click_through_rate",
                "results",
                "cost_per_mille",
                "cost_per_click",
                "cost_per_result",
                "return_on_ad_spend"
              ],
              "type": "string"
            }
          },
          {
            "description": "The sort direction. Defaults to desc.",
            "in": "query",
            "name": "direction",
            "required": false,
            "schema": {
              "enum": [
                "asc",
                "desc"
              ],
              "type": "string"
            }
          },
          {
            "description": "Only return campaigns created before this timestamp.",
            "in": "query",
            "name": "created_before",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Only return campaigns created after this timestamp.",
            "in": "query",
            "name": "created_after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Start of the stats window. Defaults to all-time.",
            "in": "query",
            "name": "stats_from",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "End of the stats window. Defaults to now.",
            "in": "query",
            "name": "stats_to",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "IANA timezone (e.g. America/New_York) the stats window is interpreted in. Bare stats_from/stats_to dates resolve to day boundaries on this clock. Defaults to UTC.",
            "in": "query",
            "name": "time_zone",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The number of campaigns to return.",
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "default": 20,
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to fetch the page after (from page_info.end_cursor).",
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The number of campaigns to return from the end of the range.",
            "in": "query",
            "name": "last",
            "required": false,
            "schema": {
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to fetch the page before (from page_info.start_cursor).",
            "in": "query",
            "name": "before",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "$ref": "#/components/schemas/AdCampaign"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "nullable": true,
                          "type": "string"
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "nullable": true,
                          "type": "string"
                        }
                      },
                      "required": [
                        "has_next_page",
                        "end_cursor",
                        "has_previous_page",
                        "start_cursor"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "ad campaigns listed"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "account_id missing and unresolvable"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:basic:read"
            ]
          }
        ],
        "summary": "List Ad Campaigns",
        "tags": [
          "Ad Campaigns"
        ]
      },
      "post": {
        "description": "Creates an ad campaign for an account.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "account_id": {
                    "description": "The account to create the campaign under. Defaults to the account-scoped key's own account.",
                    "type": "string"
                  },
                  "bid_type": {
                    "description": "How delivery bids in the ad auction: `minimum_cost` gets the most results for the budget, `average_target` holds an average cost per result, `maximum_target` never bids above a cap. Only for campaigns that own the budget.",
                    "enum": [
                      "minimum_cost",
                      "average_target",
                      "maximum_target"
                    ],
                    "type": "string"
                  },
                  "budget_amount": {
                    "description": "The campaign's budget, in the ad account's currency. Required when budget_optimization is `ad_campaign`; omit when each ad group sets its own budget.",
                    "type": "number"
                  },
                  "budget_optimization": {
                    "description": "Which level owns the budget: the whole campaign (`ad_campaign`) or each ad group individually (`ad_group`). Defaults to `ad_group`.",
                    "enum": [
                      "ad_campaign",
                      "ad_group"
                    ],
                    "type": "string"
                  },
                  "budget_type": {
                    "description": "Whether the budget is spent per day (`daily`) or over the campaign's full run (`lifetime`). Defaults to `daily`.",
                    "enum": [
                      "daily",
                      "lifetime"
                    ],
                    "type": "string"
                  },
                  "desired_cost_per_result": {
                    "description": "Cost per result to aim for (`average_target`) or never exceed (`maximum_target`). Only for campaigns that own the budget.",
                    "type": "number"
                  },
                  "ends_at": {
                    "description": "When the campaign stops delivering, as an ISO 8601 timestamp. Only for campaigns that own the budget.",
                    "type": "string"
                  },
                  "objective": {
                    "description": "The goal the campaign optimizes toward.",
                    "enum": [
                      "awareness",
                      "traffic",
                      "engagement",
                      "leads",
                      "sales"
                    ],
                    "type": "string"
                  },
                  "platform": {
                    "description": "The ad network the campaign runs on.",
                    "enum": [
                      "meta"
                    ],
                    "type": "string"
                  },
                  "special_ad_categories": {
                    "description": "Regulated categories the campaign falls under. Ads in these categories are subject to extra targeting restrictions.",
                    "items": {
                      "enum": [
                        "housing",
                        "employment",
                        "financial_products",
                        "politics"
                      ],
                      "type": "string"
                    },
                    "type": "array"
                  },
                  "starts_at": {
                    "description": "When the campaign starts delivering, as an ISO 8601 timestamp. Only for campaigns that own the budget.",
                    "type": "string"
                  },
                  "title": {
                    "description": "The title of the campaign.",
                    "type": "string"
                  }
                },
                "required": [
                  "title",
                  "platform",
                  "objective"
                ],
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/AdCampaign"
                }
              }
            },
            "description": "ad campaign created"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "unsupported platform"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:create"
            ]
          }
        ],
        "summary": "Create an Ad Campaign",
        "tags": [
          "Ad Campaigns"
        ]
      }
    },
    "/ad_campaigns/{id}": {
      "delete": {
        "description": "Deletes an ad campaign and archives it on the ad platform (cascades to ad groups and ads). Returns true on success.",
        "operationId": "delete",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "type": "boolean"
                }
              }
            },
            "description": "ad campaign deleted"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "ad campaign not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "summary": "Delete an Ad Campaign",
        "tags": [
          "Ad Campaigns"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The ad campaign ID.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ]
      },
      "get": {
        "description": "Retrieves a single ad campaign with stats over the requested window.",
        "operationId": "get",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The ad campaign ID.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Start of the stats window.",
            "in": "query",
            "name": "stats_from",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "End of the stats window.",
            "in": "query",
            "name": "stats_to",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "IANA timezone the stats window is interpreted in. Defaults to UTC.",
            "in": "query",
            "name": "time_zone",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/AdCampaign"
                }
              }
            },
            "description": "ad campaign retrieved"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "ad campaign not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:basic:read"
            ]
          }
        ],
        "summary": "Retrieve an Ad Campaign",
        "tags": [
          "Ad Campaigns"
        ]
      },
      "patch": {
        "description": "Updates an ad campaign's editable fields (title, budget, schedule, bid strategy, and — before launch — budget optimization), and launches a draft campaign by setting status to active. Objective, budget type, special ad categories and desired cost per result are fixed at creation and cannot be changed.",
        "operationId": "update",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The ad campaign ID.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "bid_type": {
                    "description": "How delivery bids in the ad auction: `minimum_cost` gets the most results for the budget, `average_target` holds an average cost per result, `maximum_target` never bids above a cap. Switching to `minimum_cost` clears the cap amounts stored on the campaign's ad groups. Only for campaigns that own the budget.",
                    "enum": [
                      "minimum_cost",
                      "average_target",
                      "maximum_target"
                    ],
                    "type": "string"
                  },
                  "budget_amount": {
                    "description": "The campaign budget, in the account's currency. Interpreted as daily or lifetime per the campaign's existing budget type.",
                    "type": "number"
                  },
                  "budget_optimization": {
                    "description": "Which level owns the budget: the whole campaign (`ad_campaign`) or each ad group individually (`ad_group`). Only changeable before the campaign is live on the ad network; switching to `ad_campaign` requires budget_amount in the same request, and switching to `ad_group` clears the campaign budget.",
                    "enum": [
                      "ad_campaign",
                      "ad_group"
                    ],
                    "type": "string"
                  },
                  "ends_at": {
                    "description": "When the campaign stops delivering, as an ISO 8601 timestamp. Only for campaigns that own the budget.",
                    "type": "string"
                  },
                  "starts_at": {
                    "description": "When the campaign starts delivering, as an ISO 8601 timestamp. Only for campaigns that own the budget.",
                    "type": "string"
                  },
                  "status": {
                    "description": "Set to active to launch a draft campaign (moderates and pushes it live). Live-campaign pause and resume use the pause and unpause actions.",
                    "enum": [
                      "active"
                    ],
                    "type": "string"
                  },
                  "title": {
                    "description": "The name of the campaign.",
                    "type": "string"
                  }
                },
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/AdCampaign"
                }
              }
            },
            "description": "ad campaign updated"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "summary": "Update an Ad Campaign",
        "tags": [
          "Ad Campaigns"
        ]
      }
    },
    "/ad_campaigns/{id}/pause": {
      "post": {
        "description": "Pauses an active ad campaign.",
        "operationId": "pause",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/AdCampaign"
                }
              }
            },
            "description": "ad campaign paused"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "summary": "Pause an Ad Campaign",
        "tags": [
          "Ad Campaigns"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The ad campaign ID.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ]
      }
    },
    "/ad_campaigns/{id}/unpause": {
      "post": {
        "description": "Resumes a paused ad campaign.",
        "operationId": "unpause",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/AdCampaign"
                }
              }
            },
            "description": "ad campaign unpaused"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "summary": "Unpause an Ad Campaign",
        "tags": [
          "Ad Campaigns"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The ad campaign ID.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ]
      }
    },
    "/ad_groups": {
      "get": {
        "description": "Lists ad groups for the account, newest first.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "Account whose ad groups to list. Defaults to the authenticated account.",
            "in": "query",
            "name": "account_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter to ad groups in this campaign.",
            "in": "query",
            "name": "ad_campaign_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter to ad groups with this status.",
            "in": "query",
            "name": "status",
            "required": false,
            "schema": {
              "enum": [
                "active",
                "paused",
                "rejected"
              ],
              "type": "string"
            }
          },
          {
            "description": "Filter ad groups by a title or ID substring.",
            "in": "query",
            "name": "query",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The field to sort by. Defaults to created_at. Stat columns (spend, impressions, …) rank over the stats_from/stats_to window across the whole list, not just the current page. results, cost_per_result and return_on_ad_spend rank by the same Whop pixel-attributed values the response reports.",
            "in": "query",
            "name": "order",
            "required": false,
            "schema": {
              "enum": [
                "created_at",
                "updated_at",
                "spend",
                "impressions",
                "reach",
                "clicks",
                "unique_clicks",
                "frequency",
                "click_through_rate",
                "results",
                "cost_per_mille",
                "cost_per_click",
                "cost_per_result",
                "return_on_ad_spend"
              ],
              "type": "string"
            }
          },
          {
            "description": "The sort direction. Defaults to desc.",
            "in": "query",
            "name": "direction",
            "required": false,
            "schema": {
              "enum": [
                "asc",
                "desc"
              ],
              "type": "string"
            }
          },
          {
            "description": "Only return ad groups created before this timestamp.",
            "in": "query",
            "name": "created_before",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Only return ad groups created after this timestamp.",
            "in": "query",
            "name": "created_after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Start of the stats window. Defaults to all-time.",
            "in": "query",
            "name": "stats_from",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "End of the stats window. Defaults to now.",
            "in": "query",
            "name": "stats_to",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "IANA timezone (e.g. America/New_York) the stats window is interpreted in. Bare stats_from/stats_to dates resolve to day boundaries on this clock. Defaults to UTC.",
            "in": "query",
            "name": "time_zone",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The number of ad groups to return.",
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "default": 20,
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to fetch the page after (from page_info.end_cursor).",
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The number of ad groups to return from the end of the range.",
            "in": "query",
            "name": "last",
            "required": false,
            "schema": {
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to fetch the page before (from page_info.start_cursor).",
            "in": "query",
            "name": "before",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "$ref": "#/components/schemas/AdGroup"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "nullable": true,
                          "type": "string"
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "nullable": true,
                          "type": "string"
                        }
                      },
                      "required": [
                        "has_next_page",
                        "end_cursor",
                        "has_previous_page",
                        "start_cursor"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "ad groups listed"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:basic:read"
            ]
          }
        ],
        "summary": "List Ad Groups",
        "tags": [
          "Ad Groups"
        ]
      },
      "post": {
        "description": "Creates an ad group (ad set) in a campaign.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "ad_campaign_id": {
                    "description": "The ad campaign to create the ad group in, prefixed `adcamp_`.",
                    "type": "string"
                  },
                  "audiences": {
                    "description": "Saved audiences to deliver to or exclude. Can't be combined with demographics.automatic.",
                    "properties": {
                      "exclude": {
                        "description": "IDs of saved audiences to exclude from delivery, prefixed `adaud_`.",
                        "items": {
                          "type": "string"
                        },
                        "type": "array"
                      },
                      "include": {
                        "description": "IDs of saved audiences to deliver to, prefixed `adaud_`.",
                        "items": {
                          "type": "string"
                        },
                        "type": "array"
                      }
                    },
                    "type": "object"
                  },
                  "bid_type": {
                    "description": "How delivery bids in the ad auction: `minimum_cost` gets the most results for the budget, `average_target` keeps the average cost per result near desired_cost_per_result, and `maximum_target` never bids above it.",
                    "enum": [
                      "minimum_cost",
                      "average_target",
                      "maximum_target"
                    ],
                    "type": "string"
                  },
                  "budget_amount": {
                    "description": "This ad group's budget, in the ad account's currency. Omit when the budget is set on the campaign instead.",
                    "type": "number"
                  },
                  "budget_type": {
                    "description": "Whether budget_amount is spent per day (`daily`) or over the ad group's full run (`lifetime`).",
                    "enum": [
                      "daily",
                      "lifetime"
                    ],
                    "type": "string"
                  },
                  "conversion_event": {
                    "$ref": "#/components/schemas/ConversionEvent"
                  },
                  "conversion_location": {
                    "description": "Where the result you're optimizing for happens: `website` (your site), `profile` (your social media profile), `messaging` (a direct-message conversation), `on_ad` (engagement with the ad itself), or a lead form (`instant_forms`, `instant_forms_and_messenger`, `website_and_instant_forms`). The lead form itself is set on the ad.",
                    "enum": [
                      "website",
                      "profile",
                      "messaging",
                      "on_ad",
                      "instant_forms",
                      "instant_forms_and_messenger",
                      "website_and_instant_forms"
                    ],
                    "type": "string"
                  },
                  "demographics": {
                    "description": "Age, gender, and automatic-audience targeting.",
                    "properties": {
                      "automatic": {
                        "description": "Turn on automatic audience targeting (Advantage+ on Meta): the platform can deliver beyond the ages, genders, and detailed targeting you set, treating them as suggestions.",
                        "type": "boolean"
                      },
                      "gender": {
                        "description": "Gender to target.",
                        "enum": [
                          "all",
                          "male",
                          "female"
                        ],
                        "type": "string"
                      },
                      "maximum_age": {
                        "description": "Oldest age to target.",
                        "type": "integer"
                      },
                      "minimum_age": {
                        "description": "Youngest age to target.",
                        "type": "integer"
                      }
                    },
                    "type": "object"
                  },
                  "desired_cost_per_result": {
                    "description": "Cost per result to aim for (`average_target`) or never exceed (`maximum_target`).",
                    "type": "number"
                  },
                  "detailed_targeting": {
                    "description": "Interest, behavior, and demographic targeting, using categories from the ad platform's targeting taxonomy. At most 100 entries per section. Can't be combined with demographics.automatic, and unavailable to campaigns with special_ad_categories. Send the complete intended state — a section you omit is cleared.",
                    "properties": {
                      "behaviors": {
                        "description": "Behavior categories to target, such as frequent travelers.",
                        "items": {
                          "properties": {
                            "id": {
                              "description": "The ad platform's ID for the category in its targeting taxonomy.",
                              "type": "string"
                            },
                            "name": {
                              "description": "Category name, such as `Movies`.",
                              "type": "string"
                            }
                          },
                          "required": [
                            "id"
                          ],
                          "type": "object"
                        },
                        "type": "array"
                      },
                      "demographics": {
                        "description": "Demographic categories to target, such as life events or industries.",
                        "items": {
                          "properties": {
                            "id": {
                              "description": "The ad platform's ID for the category in its targeting taxonomy.",
                              "type": "string"
                            },
                            "name": {
                              "description": "Category name, such as `Recently moved`.",
                              "type": "string"
                            },
                            "type": {
                              "description": "Kind of demographic the category belongs to.",
                              "enum": [
                                "life_events",
                                "industries",
                                "income",
                                "family_statuses"
                              ],
                              "type": "string"
                            }
                          },
                          "required": [
                            "id",
                            "type"
                          ],
                          "type": "object"
                        },
                        "type": "array"
                      },
                      "interests": {
                        "description": "Interest categories to target, such as an interest in movies.",
                        "items": {
                          "properties": {
                            "id": {
                              "description": "The ad platform's ID for the category in its targeting taxonomy.",
                              "type": "string"
                            },
                            "name": {
                              "description": "Category name, such as `Movies`.",
                              "type": "string"
                            }
                          },
                          "required": [
                            "id"
                          ],
                          "type": "object"
                        },
                        "type": "array"
                      }
                    },
                    "type": "object"
                  },
                  "devices": {
                    "description": "Device platforms and operating systems to target.",
                    "properties": {
                      "operating_systems": {
                        "description": "Operating systems to target. Empty targets all operating systems.",
                        "items": {
                          "properties": {
                            "minimum_version": {
                              "description": "Lowest OS version to target, such as `18.0`. Omit to target any version.",
                              "type": "string"
                            },
                            "os": {
                              "description": "Operating system to target.",
                              "enum": [
                                "ios",
                                "android"
                              ],
                              "type": "string"
                            }
                          },
                          "required": [
                            "os"
                          ],
                          "type": "object"
                        },
                        "type": "array"
                      },
                      "platforms": {
                        "description": "Device types to target. Empty targets all devices.",
                        "items": {
                          "enum": [
                            "mobile",
                            "desktop"
                          ],
                          "type": "string"
                        },
                        "type": "array"
                      }
                    },
                    "type": "object"
                  },
                  "dynamic_creative": {
                    "description": "Let the ad platform automatically mix and match this ad group's creatives and copy to find the best-performing combinations. Set at creation; can't be changed afterward.",
                    "type": "boolean"
                  },
                  "ends_at": {
                    "description": "When the ad group stops delivering, as an ISO 8601 timestamp. Omit to run until paused.",
                    "type": "string"
                  },
                  "frequency_cap": {
                    "description": "Cap on how often one person sees ads from this ad group. Only available with `reach` optimization.",
                    "properties": {
                      "maximum_impressions": {
                        "description": "Most times one person can be shown ads from this ad group within the window.",
                        "type": "integer"
                      },
                      "per_days": {
                        "description": "Length of the rolling window, in days.",
                        "type": "integer"
                      }
                    },
                    "type": "object"
                  },
                  "languages": {
                    "description": "Languages to target, as ISO 639 codes such as `en` or `es`. Empty or omitted targets all languages.",
                    "items": {
                      "type": "string"
                    },
                    "type": "array"
                  },
                  "message_apps": {
                    "description": "Apps the conversation opens in. Required when conversion_location is `messaging`.",
                    "items": {
                      "enum": [
                        "messenger",
                        "instagram",
                        "whatsapp"
                      ],
                      "type": "string"
                    },
                    "type": "array"
                  },
                  "minimum_daily_spend": {
                    "description": "Minimum the ad group tries to spend each day.",
                    "type": "number"
                  },
                  "optimization_goal": {
                    "description": "The result the ad group's delivery is optimized to get the most of.",
                    "enum": [
                      "conversions",
                      "link_clicks",
                      "landing_page_views",
                      "reach",
                      "impressions",
                      "engagement",
                      "conversations",
                      "video_views",
                      "thruplay",
                      "two_second_views",
                      "page_likes",
                      "social_profile",
                      "ad_recall_lift",
                      "event_responses",
                      "reminders_set",
                      "lead_generation",
                      "quality_lead",
                      "value",
                      "profile_and_page_engagement"
                    ],
                    "type": "string"
                  },
                  "placements": {
                    "anyOf": [
                      {
                        "enum": [
                          "automatic"
                        ],
                        "type": "string"
                      },
                      {
                        "items": {
                          "properties": {
                            "platform": {
                              "description": "Platform the ads run on.",
                              "enum": [
                                "facebook",
                                "instagram",
                                "messenger",
                                "audience_network",
                                "threads",
                                "whatsapp"
                              ],
                              "type": "string"
                            },
                            "positions": {
                              "description": "Positions to target within the platform, such as `feed` or `story`. Omit to target all of the platform's positions.",
                              "items": {
                                "type": "string"
                              },
                              "type": "array"
                            }
                          },
                          "required": [
                            "platform"
                          ],
                          "type": "object"
                        },
                        "type": "array"
                      }
                    ],
                    "description": "`automatic` to let the ad platform choose placements, or the list of platforms and positions to target. Omit a platform's positions to target all of them.\n\nValid positions per platform:\n\n- `facebook`: `feed`, `right_hand_column`, `marketplace`, `search`, `profile_feed`, `notification`, `story`, `instream_video`, `facebook_reels`, `facebook_reels_overlay`, `biz_disco_feed`\n- `instagram`: `stream`, `story`, `explore`, `explore_home`, `reels`, `profile_feed`, `profile_reels`, `ig_search`\n- `messenger`: `story`\n- `audience_network`: `classic`, `rewarded_video`\n- `threads`: `threads_stream`\n- `whatsapp`: `status`"
                  },
                  "regions": {
                    "description": "Locations to target and exclude.",
                    "properties": {
                      "exclude": {
                        "description": "Locations excluded from targeting. Country groups can't be excluded.",
                        "properties": {
                          "cities": {
                            "description": "Cities, keyed by the ad platform's location taxonomy.",
                            "items": {
                              "properties": {
                                "key": {
                                  "description": "The ad platform's key for the city in its location taxonomy.",
                                  "type": "string"
                                },
                                "name": {
                                  "description": "City name, such as `Austin`.",
                                  "type": "string"
                                }
                              },
                              "required": [
                                "key"
                              ],
                              "type": "object"
                            },
                            "type": "array"
                          },
                          "countries": {
                            "description": "Countries, as ISO 3166-1 alpha-2 codes such as `US`.",
                            "items": {
                              "type": "string"
                            },
                            "type": "array"
                          },
                          "country_groups": {
                            "description": "Multi-country groups such as `worldwide` or `europe`. Include-only — groups can't be excluded.",
                            "items": {
                              "type": "string"
                            },
                            "type": "array"
                          },
                          "custom_locations": {
                            "description": "Circular areas, each a coordinate plus a radius. At most 200 across include and exclude.",
                            "items": {
                              "properties": {
                                "distance_unit": {
                                  "description": "Unit for `radius`. Defaults to `mile`.",
                                  "enum": [
                                    "mile",
                                    "kilometer"
                                  ],
                                  "type": "string"
                                },
                                "latitude": {
                                  "description": "Latitude of the center point.",
                                  "type": "number"
                                },
                                "longitude": {
                                  "description": "Longitude of the center point.",
                                  "type": "number"
                                },
                                "name": {
                                  "description": "Label for the location, such as a city or address.",
                                  "type": "string"
                                },
                                "radius": {
                                  "description": "Radius around the center point: 1-50 miles or 1-80 kilometers.",
                                  "type": "number"
                                }
                              },
                              "required": [
                                "latitude",
                                "longitude",
                                "radius"
                              ],
                              "type": "object"
                            },
                            "type": "array"
                          },
                          "regions": {
                            "description": "States and provinces, as ISO 3166-2 codes such as `US-CA`.",
                            "items": {
                              "type": "string"
                            },
                            "type": "array"
                          },
                          "zips": {
                            "description": "ZIP and postal codes, as bare strings or objects with a key.",
                            "items": {
                              "anyOf": [
                                {
                                  "type": "string"
                                },
                                {
                                  "properties": {
                                    "key": {
                                      "description": "The ZIP or postal code.",
                                      "type": "string"
                                    }
                                  },
                                  "required": [
                                    "key"
                                  ],
                                  "type": "object"
                                }
                              ]
                            },
                            "type": "array"
                          }
                        },
                        "type": "object"
                      },
                      "include": {
                        "description": "Locations the ad group targets.",
                        "properties": {
                          "cities": {
                            "description": "Cities, keyed by the ad platform's location taxonomy.",
                            "items": {
                              "properties": {
                                "key": {
                                  "description": "The ad platform's key for the city in its location taxonomy.",
                                  "type": "string"
                                },
                                "name": {
                                  "description": "City name, such as `Austin`.",
                                  "type": "string"
                                }
                              },
                              "required": [
                                "key"
                              ],
                              "type": "object"
                            },
                            "type": "array"
                          },
                          "countries": {
                            "description": "Countries, as ISO 3166-1 alpha-2 codes such as `US`.",
                            "items": {
                              "type": "string"
                            },
                            "type": "array"
                          },
                          "country_groups": {
                            "description": "Multi-country groups such as `worldwide` or `europe`. Include-only — groups can't be excluded.",
                            "items": {
                              "type": "string"
                            },
                            "type": "array"
                          },
                          "custom_locations": {
                            "description": "Circular areas, each a coordinate plus a radius. At most 200 across include and exclude.",
                            "items": {
                              "properties": {
                                "distance_unit": {
                                  "description": "Unit for `radius`. Defaults to `mile`.",
                                  "enum": [
                                    "mile",
                                    "kilometer"
                                  ],
                                  "type": "string"
                                },
                                "latitude": {
                                  "description": "Latitude of the center point.",
                                  "type": "number"
                                },
                                "longitude": {
                                  "description": "Longitude of the center point.",
                                  "type": "number"
                                },
                                "name": {
                                  "description": "Label for the location, such as a city or address.",
                                  "type": "string"
                                },
                                "radius": {
                                  "description": "Radius around the center point: 1-50 miles or 1-80 kilometers.",
                                  "type": "number"
                                }
                              },
                              "required": [
                                "latitude",
                                "longitude",
                                "radius"
                              ],
                              "type": "object"
                            },
                            "type": "array"
                          },
                          "regions": {
                            "description": "States and provinces, as ISO 3166-2 codes such as `US-CA`.",
                            "items": {
                              "type": "string"
                            },
                            "type": "array"
                          },
                          "zips": {
                            "description": "ZIP and postal codes, as bare strings or objects with a key.",
                            "items": {
                              "anyOf": [
                                {
                                  "type": "string"
                                },
                                {
                                  "properties": {
                                    "key": {
                                      "description": "The ZIP or postal code.",
                                      "type": "string"
                                    }
                                  },
                                  "required": [
                                    "key"
                                  ],
                                  "type": "object"
                                }
                              ]
                            },
                            "type": "array"
                          }
                        },
                        "type": "object"
                      }
                    },
                    "type": "object"
                  },
                  "starts_at": {
                    "description": "When the ad group starts delivering, as an ISO 8601 timestamp. Omit to start as soon as it's active.",
                    "type": "string"
                  },
                  "status": {
                    "description": "Initial status (default: `active`).",
                    "enum": [
                      "active",
                      "paused"
                    ],
                    "type": "string"
                  },
                  "title": {
                    "description": "The display name of the ad group.",
                    "type": "string"
                  }
                },
                "required": [
                  "ad_campaign_id"
                ],
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/AdGroup"
                }
              }
            },
            "description": "ad group created"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "ad campaign not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:create"
            ]
          }
        ],
        "summary": "Create an Ad Group",
        "tags": [
          "Ad Groups"
        ]
      }
    },
    "/ad_groups/{id}": {
      "delete": {
        "description": "Deletes an ad group. Returns true on success.",
        "operationId": "delete",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "type": "boolean"
                }
              }
            },
            "description": "ad group deleted"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "ad group not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "summary": "Delete an Ad Group",
        "tags": [
          "Ad Groups"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The ad group ID.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ]
      },
      "get": {
        "description": "Retrieves a single ad group.",
        "operationId": "get",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The ad group ID.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Start of the stats window.",
            "in": "query",
            "name": "stats_from",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "End of the stats window.",
            "in": "query",
            "name": "stats_to",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "IANA timezone the stats window is interpreted in. Defaults to UTC.",
            "in": "query",
            "name": "time_zone",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/AdGroup"
                }
              }
            },
            "description": "ad group retrieved"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "ad group not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:basic:read"
            ]
          }
        ],
        "summary": "Retrieve an Ad Group",
        "tags": [
          "Ad Groups"
        ]
      },
      "patch": {
        "description": "Updates an ad group's editable fields. Only the keys you send are changed.",
        "operationId": "update",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The ad group ID.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "audiences": {
                    "description": "Saved audiences to deliver to or exclude. Can't be combined with demographics.automatic.",
                    "properties": {
                      "exclude": {
                        "description": "IDs of saved audiences to exclude from delivery, prefixed `adaud_`.",
                        "items": {
                          "type": "string"
                        },
                        "type": "array"
                      },
                      "include": {
                        "description": "IDs of saved audiences to deliver to, prefixed `adaud_`.",
                        "items": {
                          "type": "string"
                        },
                        "type": "array"
                      }
                    },
                    "type": "object"
                  },
                  "bid_type": {
                    "description": "How delivery bids in the ad auction: `minimum_cost` gets the most results for the budget, `average_target` keeps the average cost per result near desired_cost_per_result, and `maximum_target` never bids above it.",
                    "enum": [
                      "minimum_cost",
                      "average_target",
                      "maximum_target"
                    ],
                    "type": "string"
                  },
                  "budget_amount": {
                    "description": "This ad group's budget, in the ad account's currency. Omit when the budget is set on the campaign instead.",
                    "type": "number"
                  },
                  "budget_type": {
                    "description": "Whether budget_amount is spent per day (`daily`) or over the ad group's full run (`lifetime`).",
                    "enum": [
                      "daily",
                      "lifetime"
                    ],
                    "type": "string"
                  },
                  "conversion_event": {
                    "$ref": "#/components/schemas/ConversionEvent"
                  },
                  "conversion_location": {
                    "description": "Where the result you're optimizing for happens: `website` (your site), `profile` (your social media profile), `messaging` (a direct-message conversation), `on_ad` (engagement with the ad itself), or a lead form (`instant_forms`, `instant_forms_and_messenger`, `website_and_instant_forms`). The lead form itself is set on the ad.",
                    "enum": [
                      "website",
                      "profile",
                      "messaging",
                      "on_ad",
                      "instant_forms",
                      "instant_forms_and_messenger",
                      "website_and_instant_forms"
                    ],
                    "type": "string"
                  },
                  "demographics": {
                    "description": "Age, gender, and automatic-audience targeting.",
                    "properties": {
                      "automatic": {
                        "description": "Turn on automatic audience targeting (Advantage+ on Meta): the platform can deliver beyond the ages, genders, and detailed targeting you set, treating them as suggestions.",
                        "type": "boolean"
                      },
                      "gender": {
                        "description": "Gender to target.",
                        "enum": [
                          "all",
                          "male",
                          "female"
                        ],
                        "type": "string"
                      },
                      "maximum_age": {
                        "description": "Oldest age to target.",
                        "type": "integer"
                      },
                      "minimum_age": {
                        "description": "Youngest age to target.",
                        "type": "integer"
                      }
                    },
                    "type": "object"
                  },
                  "desired_cost_per_result": {
                    "description": "Cost per result to aim for (`average_target`) or never exceed (`maximum_target`).",
                    "type": "number"
                  },
                  "detailed_targeting": {
                    "description": "Interest, behavior, and demographic targeting, using categories from the ad platform's targeting taxonomy. At most 100 entries per section. Can't be combined with demographics.automatic, and unavailable to campaigns with special_ad_categories. Send the complete intended state — a section you omit is cleared.",
                    "properties": {
                      "behaviors": {
                        "description": "Behavior categories to target, such as frequent travelers.",
                        "items": {
                          "properties": {
                            "id": {
                              "description": "The ad platform's ID for the category in its targeting taxonomy.",
                              "type": "string"
                            },
                            "name": {
                              "description": "Category name, such as `Movies`.",
                              "type": "string"
                            }
                          },
                          "required": [
                            "id"
                          ],
                          "type": "object"
                        },
                        "type": "array"
                      },
                      "demographics": {
                        "description": "Demographic categories to target, such as life events or industries.",
                        "items": {
                          "properties": {
                            "id": {
                              "description": "The ad platform's ID for the category in its targeting taxonomy.",
                              "type": "string"
                            },
                            "name": {
                              "description": "Category name, such as `Recently moved`.",
                              "type": "string"
                            },
                            "type": {
                              "description": "Kind of demographic the category belongs to.",
                              "enum": [
                                "life_events",
                                "industries",
                                "income",
                                "family_statuses"
                              ],
                              "type": "string"
                            }
                          },
                          "required": [
                            "id",
                            "type"
                          ],
                          "type": "object"
                        },
                        "type": "array"
                      },
                      "interests": {
                        "description": "Interest categories to target, such as an interest in movies.",
                        "items": {
                          "properties": {
                            "id": {
                              "description": "The ad platform's ID for the category in its targeting taxonomy.",
                              "type": "string"
                            },
                            "name": {
                              "description": "Category name, such as `Movies`.",
                              "type": "string"
                            }
                          },
                          "required": [
                            "id"
                          ],
                          "type": "object"
                        },
                        "type": "array"
                      }
                    },
                    "type": "object"
                  },
                  "devices": {
                    "description": "Device platforms and operating systems to target.",
                    "properties": {
                      "operating_systems": {
                        "description": "Operating systems to target. Empty targets all operating systems.",
                        "items": {
                          "properties": {
                            "minimum_version": {
                              "description": "Lowest OS version to target, such as `18.0`. Omit to target any version.",
                              "type": "string"
                            },
                            "os": {
                              "description": "Operating system to target.",
                              "enum": [
                                "ios",
                                "android"
                              ],
                              "type": "string"
                            }
                          },
                          "required": [
                            "os"
                          ],
                          "type": "object"
                        },
                        "type": "array"
                      },
                      "platforms": {
                        "description": "Device types to target. Empty targets all devices.",
                        "items": {
                          "enum": [
                            "mobile",
                            "desktop"
                          ],
                          "type": "string"
                        },
                        "type": "array"
                      }
                    },
                    "type": "object"
                  },
                  "ends_at": {
                    "description": "When the ad group stops delivering, as an ISO 8601 timestamp. Omit to run until paused.",
                    "type": "string"
                  },
                  "frequency_cap": {
                    "description": "Cap on how often one person sees ads from this ad group. Only available with `reach` optimization.",
                    "properties": {
                      "maximum_impressions": {
                        "description": "Most times one person can be shown ads from this ad group within the window.",
                        "type": "integer"
                      },
                      "per_days": {
                        "description": "Length of the rolling window, in days.",
                        "type": "integer"
                      }
                    },
                    "type": "object"
                  },
                  "languages": {
                    "description": "Languages to target, as ISO 639 codes such as `en` or `es`. Empty or omitted targets all languages.",
                    "items": {
                      "type": "string"
                    },
                    "type": "array"
                  },
                  "message_apps": {
                    "description": "Apps the conversation opens in. Required when conversion_location is `messaging`.",
                    "items": {
                      "enum": [
                        "messenger",
                        "instagram",
                        "whatsapp"
                      ],
                      "type": "string"
                    },
                    "type": "array"
                  },
                  "minimum_daily_spend": {
                    "description": "Minimum the ad group tries to spend each day.",
                    "type": "number"
                  },
                  "optimization_goal": {
                    "description": "The result the ad group's delivery is optimized to get the most of.",
                    "enum": [
                      "conversions",
                      "link_clicks",
                      "landing_page_views",
                      "reach",
                      "impressions",
                      "engagement",
                      "conversations",
                      "video_views",
                      "thruplay",
                      "two_second_views",
                      "page_likes",
                      "social_profile",
                      "ad_recall_lift",
                      "event_responses",
                      "reminders_set",
                      "lead_generation",
                      "quality_lead",
                      "value",
                      "profile_and_page_engagement"
                    ],
                    "type": "string"
                  },
                  "placements": {
                    "anyOf": [
                      {
                        "enum": [
                          "automatic"
                        ],
                        "type": "string"
                      },
                      {
                        "items": {
                          "properties": {
                            "platform": {
                              "description": "Platform the ads run on.",
                              "enum": [
                                "facebook",
                                "instagram",
                                "messenger",
                                "audience_network",
                                "threads",
                                "whatsapp"
                              ],
                              "type": "string"
                            },
                            "positions": {
                              "description": "Positions to target within the platform, such as `feed` or `story`. Omit to target all of the platform's positions.",
                              "items": {
                                "type": "string"
                              },
                              "type": "array"
                            }
                          },
                          "required": [
                            "platform"
                          ],
                          "type": "object"
                        },
                        "type": "array"
                      }
                    ],
                    "description": "`automatic` to let the ad platform choose placements, or the list of platforms and positions to target. Omit a platform's positions to target all of them.\n\nValid positions per platform:\n\n- `facebook`: `feed`, `right_hand_column`, `marketplace`, `search`, `profile_feed`, `notification`, `story`, `instream_video`, `facebook_reels`, `facebook_reels_overlay`, `biz_disco_feed`\n- `instagram`: `stream`, `story`, `explore`, `explore_home`, `reels`, `profile_feed`, `profile_reels`, `ig_search`\n- `messenger`: `story`\n- `audience_network`: `classic`, `rewarded_video`\n- `threads`: `threads_stream`\n- `whatsapp`: `status`"
                  },
                  "regions": {
                    "description": "Locations to target and exclude.",
                    "properties": {
                      "exclude": {
                        "description": "Locations excluded from targeting. Country groups can't be excluded.",
                        "properties": {
                          "cities": {
                            "description": "Cities, keyed by the ad platform's location taxonomy.",
                            "items": {
                              "properties": {
                                "key": {
                                  "description": "The ad platform's key for the city in its location taxonomy.",
                                  "type": "string"
                                },
                                "name": {
                                  "description": "City name, such as `Austin`.",
                                  "type": "string"
                                }
                              },
                              "required": [
                                "key"
                              ],
                              "type": "object"
                            },
                            "type": "array"
                          },
                          "countries": {
                            "description": "Countries, as ISO 3166-1 alpha-2 codes such as `US`.",
                            "items": {
                              "type": "string"
                            },
                            "type": "array"
                          },
                          "country_groups": {
                            "description": "Multi-country groups such as `worldwide` or `europe`. Include-only — groups can't be excluded.",
                            "items": {
                              "type": "string"
                            },
                            "type": "array"
                          },
                          "custom_locations": {
                            "description": "Circular areas, each a coordinate plus a radius. At most 200 across include and exclude.",
                            "items": {
                              "properties": {
                                "distance_unit": {
                                  "description": "Unit for `radius`. Defaults to `mile`.",
                                  "enum": [
                                    "mile",
                                    "kilometer"
                                  ],
                                  "type": "string"
                                },
                                "latitude": {
                                  "description": "Latitude of the center point.",
                                  "type": "number"
                                },
                                "longitude": {
                                  "description": "Longitude of the center point.",
                                  "type": "number"
                                },
                                "name": {
                                  "description": "Label for the location, such as a city or address.",
                                  "type": "string"
                                },
                                "radius": {
                                  "description": "Radius around the center point: 1-50 miles or 1-80 kilometers.",
                                  "type": "number"
                                }
                              },
                              "required": [
                                "latitude",
                                "longitude",
                                "radius"
                              ],
                              "type": "object"
                            },
                            "type": "array"
                          },
                          "regions": {
                            "description": "States and provinces, as ISO 3166-2 codes such as `US-CA`.",
                            "items": {
                              "type": "string"
                            },
                            "type": "array"
                          },
                          "zips": {
                            "description": "ZIP and postal codes, as bare strings or objects with a key.",
                            "items": {
                              "anyOf": [
                                {
                                  "type": "string"
                                },
                                {
                                  "properties": {
                                    "key": {
                                      "description": "The ZIP or postal code.",
                                      "type": "string"
                                    }
                                  },
                                  "required": [
                                    "key"
                                  ],
                                  "type": "object"
                                }
                              ]
                            },
                            "type": "array"
                          }
                        },
                        "type": "object"
                      },
                      "include": {
                        "description": "Locations the ad group targets.",
                        "properties": {
                          "cities": {
                            "description": "Cities, keyed by the ad platform's location taxonomy.",
                            "items": {
                              "properties": {
                                "key": {
                                  "description": "The ad platform's key for the city in its location taxonomy.",
                                  "type": "string"
                                },
                                "name": {
                                  "description": "City name, such as `Austin`.",
                                  "type": "string"
                                }
                              },
                              "required": [
                                "key"
                              ],
                              "type": "object"
                            },
                            "type": "array"
                          },
                          "countries": {
                            "description": "Countries, as ISO 3166-1 alpha-2 codes such as `US`.",
                            "items": {
                              "type": "string"
                            },
                            "type": "array"
                          },
                          "country_groups": {
                            "description": "Multi-country groups such as `worldwide` or `europe`. Include-only — groups can't be excluded.",
                            "items": {
                              "type": "string"
                            },
                            "type": "array"
                          },
                          "custom_locations": {
                            "description": "Circular areas, each a coordinate plus a radius. At most 200 across include and exclude.",
                            "items": {
                              "properties": {
                                "distance_unit": {
                                  "description": "Unit for `radius`. Defaults to `mile`.",
                                  "enum": [
                                    "mile",
                                    "kilometer"
                                  ],
                                  "type": "string"
                                },
                                "latitude": {
                                  "description": "Latitude of the center point.",
                                  "type": "number"
                                },
                                "longitude": {
                                  "description": "Longitude of the center point.",
                                  "type": "number"
                                },
                                "name": {
                                  "description": "Label for the location, such as a city or address.",
                                  "type": "string"
                                },
                                "radius": {
                                  "description": "Radius around the center point: 1-50 miles or 1-80 kilometers.",
                                  "type": "number"
                                }
                              },
                              "required": [
                                "latitude",
                                "longitude",
                                "radius"
                              ],
                              "type": "object"
                            },
                            "type": "array"
                          },
                          "regions": {
                            "description": "States and provinces, as ISO 3166-2 codes such as `US-CA`.",
                            "items": {
                              "type": "string"
                            },
                            "type": "array"
                          },
                          "zips": {
                            "description": "ZIP and postal codes, as bare strings or objects with a key.",
                            "items": {
                              "anyOf": [
                                {
                                  "type": "string"
                                },
                                {
                                  "properties": {
                                    "key": {
                                      "description": "The ZIP or postal code.",
                                      "type": "string"
                                    }
                                  },
                                  "required": [
                                    "key"
                                  ],
                                  "type": "object"
                                }
                              ]
                            },
                            "type": "array"
                          }
                        },
                        "type": "object"
                      }
                    },
                    "type": "object"
                  },
                  "starts_at": {
                    "description": "When the ad group starts delivering, as an ISO 8601 timestamp. Omit to start as soon as it's active.",
                    "type": "string"
                  },
                  "status": {
                    "description": "Initial status (default: `active`).",
                    "enum": [
                      "active",
                      "paused"
                    ],
                    "type": "string"
                  },
                  "title": {
                    "description": "The display name of the ad group.",
                    "type": "string"
                  }
                },
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/AdGroup"
                }
              }
            },
            "description": "ad group updated"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "summary": "Update an Ad Group",
        "tags": [
          "Ad Groups"
        ]
      }
    },
    "/ad_groups/{id}/pause": {
      "post": {
        "description": "Pauses delivery of an ad group.",
        "operationId": "pause",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/AdGroup"
                }
              }
            },
            "description": "ad group paused"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "summary": "Pause an Ad Group",
        "tags": [
          "Ad Groups"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The ad group ID.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ]
      }
    },
    "/ad_groups/{id}/unpause": {
      "post": {
        "description": "Resumes delivery of a paused ad group.",
        "operationId": "unpause",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/AdGroup"
                }
              }
            },
            "description": "ad group unpaused"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "summary": "Unpause an Ad Group",
        "tags": [
          "Ad Groups"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The ad group ID.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ]
      }
    },
    "/ads": {
      "get": {
        "description": "Lists the ads for an account, with stats over the requested window.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The account the ads belong to. Defaults to the account-scoped key's own account.",
            "in": "query",
            "name": "account_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Only return ads in this ad campaign.",
            "in": "query",
            "name": "ad_campaign_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Only return ads in this ad group.",
            "in": "query",
            "name": "ad_group_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Only return ads with this status.",
            "in": "query",
            "name": "status",
            "required": false,
            "schema": {
              "enum": [
                "active",
                "paused",
                "in_review",
                "rejected"
              ],
              "type": "string"
            }
          },
          {
            "description": "Filter ads by a title or ID substring.",
            "in": "query",
            "name": "query",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The field to sort by. Defaults to created_at. Stat columns (spend, impressions, …) rank over the stats_from/stats_to window across the whole list, not just the current page. results, cost_per_result and return_on_ad_spend rank by the same Whop pixel-attributed values the response reports.",
            "in": "query",
            "name": "order",
            "required": false,
            "schema": {
              "enum": [
                "created_at",
                "updated_at",
                "spend",
                "impressions",
                "reach",
                "clicks",
                "unique_clicks",
                "frequency",
                "click_through_rate",
                "results",
                "cost_per_mille",
                "cost_per_click",
                "cost_per_result",
                "return_on_ad_spend"
              ],
              "type": "string"
            }
          },
          {
            "description": "The sort direction. Defaults to desc.",
            "in": "query",
            "name": "direction",
            "required": false,
            "schema": {
              "enum": [
                "asc",
                "desc"
              ],
              "type": "string"
            }
          },
          {
            "description": "Only return ads created before this timestamp.",
            "in": "query",
            "name": "created_before",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Only return ads created after this timestamp.",
            "in": "query",
            "name": "created_after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Start of the stats window. Defaults to all-time.",
            "in": "query",
            "name": "stats_from",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "End of the stats window. Defaults to now.",
            "in": "query",
            "name": "stats_to",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "IANA timezone (e.g. America/New_York) the stats window is interpreted in. Bare stats_from/stats_to dates resolve to day boundaries on this clock. Defaults to UTC.",
            "in": "query",
            "name": "time_zone",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The number of ads to return.",
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "default": 20,
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to fetch the page after (from page_info.end_cursor).",
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The number of ads to return from the end of the range.",
            "in": "query",
            "name": "last",
            "required": false,
            "schema": {
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to fetch the page before (from page_info.start_cursor).",
            "in": "query",
            "name": "before",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "$ref": "#/components/schemas/Ad"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "nullable": true,
                          "type": "string"
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "nullable": true,
                          "type": "string"
                        }
                      },
                      "required": [
                        "has_next_page",
                        "end_cursor",
                        "has_previous_page",
                        "start_cursor"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "ads listed"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "account_id missing and unresolvable"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:basic:read"
            ]
          }
        ],
        "summary": "List Ads",
        "tags": [
          "Ads"
        ]
      },
      "post": {
        "description": "Creates an ad in an ad group.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "ad_group": {
                    "description": "An inline ad group to create (same shape as POST /ad_groups, including ad_campaign_id). Creates the ad group and the ad together. Provide this OR ad_group_id.",
                    "type": "object"
                  },
                  "ad_group_id": {
                    "description": "The existing ad group to create the ad in. Provide this OR ad_group, not both.",
                    "type": "string"
                  },
                  "call_to_action": {
                    "description": "The call-to-action button shown on the ad.",
                    "enum": [
                      "apply_now",
                      "book_now",
                      "call_now",
                      "contact_us",
                      "download",
                      "get_directions",
                      "get_offer",
                      "get_quote",
                      "learn_more",
                      "listen_now",
                      "message_page",
                      "no_button",
                      "open_link",
                      "order_now",
                      "request_time",
                      "see_details",
                      "see_menu",
                      "send_updates",
                      "shop_now",
                      "sign_up",
                      "subscribe",
                      "watch_more"
                    ],
                    "type": "string"
                  },
                  "creatives": {
                    "description": "The ad's creative assets. Each entry is an uploaded file id with an optional format; omit format for the original asset.",
                    "items": {
                      "properties": {
                        "crop": {
                          "description": "The saved crop window for this creative, in source image pixels. Omit it for the original asset or for a format that has not been cropped.",
                          "properties": {
                            "height": {
                              "type": "number"
                            },
                            "width": {
                              "type": "number"
                            },
                            "x": {
                              "type": "number"
                            },
                            "y": {
                              "type": "number"
                            }
                          },
                          "type": "object"
                        },
                        "format": {
                          "enum": [
                            "square",
                            "vertical",
                            "horizontal"
                          ],
                          "type": "string"
                        },
                        "id": {
                          "description": "Uploaded file ID, prefixed `file_`.",
                          "type": "string"
                        }
                      },
                      "type": "object"
                    },
                    "type": "array"
                  },
                  "descriptions": {
                    "description": "The description variants shown on the ad.",
                    "items": {
                      "type": "string"
                    },
                    "type": "array"
                  },
                  "headlines": {
                    "description": "The headline variants shown on the ad.",
                    "items": {
                      "type": "string"
                    },
                    "type": "array"
                  },
                  "lead_form": {
                    "description": "Instant lead form for the ad. Only allowed when the ad group's conversion_location is an instant-form destination (instant_forms, instant_forms_and_messenger, website_and_instant_forms). Mutually exclusive with lead_form_id.",
                    "properties": {
                      "completion": {
                        "description": "Optional completion screen shown after submission; url sets the follow-up website button.",
                        "properties": {
                          "button_text": {
                            "type": "string"
                          },
                          "description": {
                            "type": "string"
                          },
                          "headline": {
                            "type": "string"
                          },
                          "url": {
                            "type": "string"
                          }
                        },
                        "type": "object"
                      },
                      "disclaimer": {
                        "description": "Optional custom consent disclaimer with checkboxes.",
                        "properties": {
                          "body": {
                            "type": "string"
                          },
                          "checkboxes": {
                            "items": {
                              "properties": {
                                "checked_by_default": {
                                  "type": "boolean"
                                },
                                "key": {
                                  "type": "string"
                                },
                                "required": {
                                  "type": "boolean"
                                },
                                "text": {
                                  "type": "string"
                                }
                              },
                              "type": "object"
                            },
                            "type": "array"
                          },
                          "title": {
                            "type": "string"
                          }
                        },
                        "type": "object"
                      },
                      "form_type": {
                        "description": "more_volume (default) is quickest to submit; higher_intent adds a confirmation step.",
                        "enum": [
                          "more_volume",
                          "higher_intent"
                        ],
                        "type": "string"
                      },
                      "intro": {
                        "description": "Optional intro screen shown before the questions.",
                        "properties": {
                          "description": {
                            "type": "string"
                          },
                          "headline": {
                            "type": "string"
                          }
                        },
                        "type": "object"
                      },
                      "name": {
                        "description": "Internal name for the form. Auto-generated if omitted.",
                        "type": "string"
                      },
                      "phone_verification": {
                        "description": "Require SMS verification of the phone number (higher_intent forms).",
                        "type": "boolean"
                      },
                      "privacy_policy": {
                        "description": "Your privacy policy. url is required by the ad platform.",
                        "properties": {
                          "link_text": {
                            "type": "string"
                          },
                          "url": {
                            "type": "string"
                          }
                        },
                        "type": "object"
                      },
                      "questions": {
                        "description": "The questions on the form. Standard prefill types need only a type; a custom question needs a label and a format (plus options for multiple_choice). Options carry an optional key and answer-routing logic.",
                        "items": {
                          "properties": {
                            "format": {
                              "enum": [
                                "short_answer",
                                "multiple_choice",
                                "appointment"
                              ],
                              "type": "string"
                            },
                            "label": {
                              "type": "string"
                            },
                            "options": {
                              "items": {
                                "properties": {
                                  "key": {
                                    "type": "string"
                                  },
                                  "logic": {
                                    "properties": {
                                      "action": {
                                        "enum": [
                                          "go_to_question",
                                          "submit_form",
                                          "close_form"
                                        ],
                                        "type": "string"
                                      },
                                      "target_end_page_index": {
                                        "type": "integer"
                                      },
                                      "target_question_index": {
                                        "type": "integer"
                                      }
                                    },
                                    "type": "object"
                                  },
                                  "value": {
                                    "type": "string"
                                  }
                                },
                                "type": "object"
                              },
                              "type": "array"
                            },
                            "type": {
                              "enum": [
                                "email",
                                "phone",
                                "full_name",
                                "first_name",
                                "last_name",
                                "city",
                                "state",
                                "zip",
                                "country",
                                "street_address",
                                "job_title",
                                "company_name",
                                "work_email",
                                "work_phone_number",
                                "dob",
                                "gender",
                                "marital_status",
                                "relationship_status",
                                "military_status",
                                "date_time",
                                "custom"
                              ],
                              "type": "string"
                            }
                          },
                          "type": "object"
                        },
                        "type": "array"
                      }
                    },
                    "type": "object"
                  },
                  "lead_form_id": {
                    "description": "Use an existing instant form instead of creating one — the form's platform ID, from a form already on the ad's Facebook page. Only allowed when the ad group's conversion_location is an instant-form destination. Mutually exclusive with lead_form.",
                    "type": "string"
                  },
                  "messaging_config": {
                    "description": "Click-to-message welcome copy: the greeting (message) and the ice-breaker prompt (keyword).",
                    "properties": {
                      "keyword": {
                        "type": "string"
                      },
                      "message": {
                        "type": "string"
                      }
                    },
                    "type": "object"
                  },
                  "multi_advertiser_ads": {
                    "description": "Whether the ad can appear alongside other advertisers' ads in the same unit. Defaults to true.",
                    "type": "boolean"
                  },
                  "post_id": {
                    "description": "Promote an existing post instead of uploading creatives — a Facebook post or Instagram media id. Mutually exclusive with creatives. Pair with post_source.",
                    "type": "string"
                  },
                  "post_source": {
                    "description": "Which network post_id refers to — facebook (a page post) or instagram (a media id). Authoritative; when omitted the source is inferred from the id shape.",
                    "enum": [
                      "facebook",
                      "instagram"
                    ],
                    "type": "string"
                  },
                  "primary_texts": {
                    "description": "The primary text variants shown in the ad body.",
                    "items": {
                      "type": "string"
                    },
                    "type": "array"
                  },
                  "social_accounts": {
                    "description": "The social accounts the ad runs under — a connected Facebook page and, optionally, an Instagram profile.",
                    "items": {
                      "properties": {
                        "id": {
                          "description": "Social account ID, prefixed `sacc_`.",
                          "type": "string"
                        }
                      },
                      "type": "object"
                    },
                    "type": "array"
                  },
                  "title": {
                    "description": "The display name of the ad.",
                    "type": "string"
                  },
                  "url": {
                    "description": "The URL the ad links to.",
                    "type": "string"
                  },
                  "url_parameters": {
                    "description": "Query parameters appended to the destination URL, keyed by parameter name.",
                    "type": "object"
                  }
                },
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Ad"
                }
              }
            },
            "description": "ad created"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "Whop pixel not installed on the destination"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "ad group not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:create"
            ]
          }
        ],
        "summary": "Create an Ad",
        "tags": [
          "Ads"
        ]
      }
    },
    "/ads/{id}": {
      "delete": {
        "description": "Deletes an ad. Returns true on success.",
        "operationId": "delete",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "type": "boolean"
                }
              }
            },
            "description": "ad deleted"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "ad not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "summary": "Delete an Ad",
        "tags": [
          "Ads"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The ad ID.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ]
      },
      "get": {
        "description": "Retrieves a single ad with stats over the requested window.",
        "operationId": "get",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The ad ID.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Start of the stats window.",
            "in": "query",
            "name": "stats_from",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "End of the stats window.",
            "in": "query",
            "name": "stats_to",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "IANA timezone the stats window is interpreted in. Defaults to UTC.",
            "in": "query",
            "name": "time_zone",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Ad"
                }
              }
            },
            "description": "ad retrieved"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "ad not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:basic:read"
            ]
          }
        ],
        "summary": "Retrieve an Ad",
        "tags": [
          "Ads"
        ]
      },
      "patch": {
        "description": "Updates an ad's editable fields.",
        "operationId": "update",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The ad ID.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "call_to_action": {
                    "description": "The call-to-action button shown on the ad.",
                    "enum": [
                      "apply_now",
                      "book_now",
                      "call_now",
                      "contact_us",
                      "download",
                      "get_directions",
                      "get_offer",
                      "get_quote",
                      "learn_more",
                      "listen_now",
                      "message_page",
                      "no_button",
                      "open_link",
                      "order_now",
                      "request_time",
                      "see_details",
                      "see_menu",
                      "send_updates",
                      "shop_now",
                      "sign_up",
                      "subscribe",
                      "watch_more"
                    ],
                    "type": "string"
                  },
                  "creatives": {
                    "description": "The ad's creative assets. Each entry is an uploaded file id with an optional format; omit format for the original asset. Replaces a live ad's creative on the platform.",
                    "items": {
                      "properties": {
                        "crop": {
                          "description": "The saved crop window for this creative, in source image pixels. Omit it for the original asset or for a format that has not been cropped.",
                          "properties": {
                            "height": {
                              "type": "number"
                            },
                            "width": {
                              "type": "number"
                            },
                            "x": {
                              "type": "number"
                            },
                            "y": {
                              "type": "number"
                            }
                          },
                          "type": "object"
                        },
                        "format": {
                          "enum": [
                            "square",
                            "vertical",
                            "horizontal"
                          ],
                          "type": "string"
                        },
                        "id": {
                          "description": "Uploaded file ID, prefixed `file_`.",
                          "type": "string"
                        }
                      },
                      "type": "object"
                    },
                    "type": "array"
                  },
                  "descriptions": {
                    "description": "The description variants shown on the ad.",
                    "items": {
                      "type": "string"
                    },
                    "type": "array"
                  },
                  "headlines": {
                    "description": "The headline variants shown on the ad.",
                    "items": {
                      "type": "string"
                    },
                    "type": "array"
                  },
                  "lead_form": {
                    "description": "Instant lead form for the ad. Only allowed when the ad group's conversion_location is an instant-form destination (instant_forms, instant_forms_and_messenger, website_and_instant_forms). Mutually exclusive with lead_form_id.",
                    "properties": {
                      "completion": {
                        "description": "Optional completion screen shown after submission; url sets the follow-up website button.",
                        "properties": {
                          "button_text": {
                            "type": "string"
                          },
                          "description": {
                            "type": "string"
                          },
                          "headline": {
                            "type": "string"
                          },
                          "url": {
                            "type": "string"
                          }
                        },
                        "type": "object"
                      },
                      "disclaimer": {
                        "description": "Optional custom consent disclaimer with checkboxes.",
                        "properties": {
                          "body": {
                            "type": "string"
                          },
                          "checkboxes": {
                            "items": {
                              "properties": {
                                "checked_by_default": {
                                  "type": "boolean"
                                },
                                "key": {
                                  "type": "string"
                                },
                                "required": {
                                  "type": "boolean"
                                },
                                "text": {
                                  "type": "string"
                                }
                              },
                              "type": "object"
                            },
                            "type": "array"
                          },
                          "title": {
                            "type": "string"
                          }
                        },
                        "type": "object"
                      },
                      "form_type": {
                        "description": "more_volume (default) is quickest to submit; higher_intent adds a confirmation step.",
                        "enum": [
                          "more_volume",
                          "higher_intent"
                        ],
                        "type": "string"
                      },
                      "intro": {
                        "description": "Optional intro screen shown before the questions.",
                        "properties": {
                          "description": {
                            "type": "string"
                          },
                          "headline": {
                            "type": "string"
                          }
                        },
                        "type": "object"
                      },
                      "name": {
                        "description": "Internal name for the form. Auto-generated if omitted.",
                        "type": "string"
                      },
                      "phone_verification": {
                        "description": "Require SMS verification of the phone number (higher_intent forms).",
                        "type": "boolean"
                      },
                      "privacy_policy": {
                        "description": "Your privacy policy. url is required by the ad platform.",
                        "properties": {
                          "link_text": {
                            "type": "string"
                          },
                          "url": {
                            "type": "string"
                          }
                        },
                        "type": "object"
                      },
                      "questions": {
                        "description": "The questions on the form. Standard prefill types need only a type; a custom question needs a label and a format (plus options for multiple_choice). Options carry an optional key and answer-routing logic.",
                        "items": {
                          "properties": {
                            "format": {
                              "enum": [
                                "short_answer",
                                "multiple_choice",
                                "appointment"
                              ],
                              "type": "string"
                            },
                            "label": {
                              "type": "string"
                            },
                            "options": {
                              "items": {
                                "properties": {
                                  "key": {
                                    "type": "string"
                                  },
                                  "logic": {
                                    "properties": {
                                      "action": {
                                        "enum": [
                                          "go_to_question",
                                          "submit_form",
                                          "close_form"
                                        ],
                                        "type": "string"
                                      },
                                      "target_end_page_index": {
                                        "type": "integer"
                                      },
                                      "target_question_index": {
                                        "type": "integer"
                                      }
                                    },
                                    "type": "object"
                                  },
                                  "value": {
                                    "type": "string"
                                  }
                                },
                                "type": "object"
                              },
                              "type": "array"
                            },
                            "type": {
                              "enum": [
                                "email",
                                "phone",
                                "full_name",
                                "first_name",
                                "last_name",
                                "city",
                                "state",
                                "zip",
                                "country",
                                "street_address",
                                "job_title",
                                "company_name",
                                "work_email",
                                "work_phone_number",
                                "dob",
                                "gender",
                                "marital_status",
                                "relationship_status",
                                "military_status",
                                "date_time",
                                "custom"
                              ],
                              "type": "string"
                            }
                          },
                          "type": "object"
                        },
                        "type": "array"
                      }
                    },
                    "type": "object"
                  },
                  "lead_form_id": {
                    "description": "Use an existing instant form instead of creating one — the form's platform ID, from a form already on the ad's Facebook page. Only allowed when the ad group's conversion_location is an instant-form destination. Mutually exclusive with lead_form. Replaces a stored lead_form.",
                    "type": "string"
                  },
                  "messaging_config": {
                    "description": "Click-to-message welcome copy: the greeting (message) and the ice-breaker prompt (keyword).",
                    "properties": {
                      "keyword": {
                        "type": "string"
                      },
                      "message": {
                        "type": "string"
                      }
                    },
                    "type": "object"
                  },
                  "multi_advertiser_ads": {
                    "description": "Whether the ad can appear alongside other advertisers' ads in the same unit. Defaults to true.",
                    "type": "boolean"
                  },
                  "post_id": {
                    "description": "Promote an existing post instead of uploading creatives — a Facebook post or Instagram media id. Mutually exclusive with creatives. Pair with post_source.",
                    "type": "string"
                  },
                  "post_source": {
                    "description": "Which network post_id refers to — facebook (a page post) or instagram (a media id). Authoritative; when omitted the source is inferred from the id shape.",
                    "enum": [
                      "facebook",
                      "instagram"
                    ],
                    "type": "string"
                  },
                  "primary_texts": {
                    "description": "The primary text variants shown in the ad body.",
                    "items": {
                      "type": "string"
                    },
                    "type": "array"
                  },
                  "social_accounts": {
                    "description": "The social accounts the ad runs under — a connected Facebook page and, optionally, an Instagram profile.",
                    "items": {
                      "properties": {
                        "id": {
                          "description": "Social account ID, prefixed `sacc_`.",
                          "type": "string"
                        }
                      },
                      "type": "object"
                    },
                    "type": "array"
                  },
                  "title": {
                    "description": "The display name of the ad.",
                    "type": "string"
                  },
                  "url": {
                    "description": "The URL the ad links to.",
                    "type": "string"
                  },
                  "url_parameters": {
                    "description": "Query parameters appended to the destination URL, keyed by parameter name.",
                    "type": "object"
                  }
                },
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Ad"
                }
              }
            },
            "description": "ad updated"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "summary": "Update an Ad",
        "tags": [
          "Ads"
        ]
      }
    },
    "/ads/{id}/pause": {
      "post": {
        "description": "Pauses an active ad.",
        "operationId": "pause",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Ad"
                }
              }
            },
            "description": "ad paused"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "summary": "Pause an Ad",
        "tags": [
          "Ads"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The ad ID.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ]
      }
    },
    "/ads/{id}/unpause": {
      "post": {
        "description": "Resumes a paused ad.",
        "operationId": "unpause",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Ad"
                }
              }
            },
            "description": "ad unpaused"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "ad_campaign:update"
            ]
          }
        ],
        "summary": "Unpause an Ad",
        "tags": [
          "Ads"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The ad ID.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ]
      }
    },
    "/apps/{id}/logs": {
      "get": {
        "description": "Lists a hosted app's server runtime logs, most recent first: console output, uncaught exceptions, and failed-request summaries captured on whop.app hosting. Logs are retained for 7 days.",
        "operationId": "logs",
        "parameters": [
          {
            "description": "The ID of the app, which will look like app_*************.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Only return logs from this build.",
            "in": "query",
            "name": "app_build_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Only return console lines of this level.",
            "in": "query",
            "name": "level",
            "required": false,
            "schema": {
              "enum": [
                "log",
                "debug",
                "info",
                "warn",
                "error"
              ],
              "type": "string"
            }
          },
          {
            "description": "Only return logs whose message contains this text (case-insensitive).",
            "in": "query",
            "name": "query",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Start of the time window as an ISO 8601 timestamp. Defaults to 7 days before created_before.",
            "in": "query",
            "name": "created_after",
            "required": false,
            "schema": {
              "format": "date-time",
              "type": "string"
            }
          },
          {
            "description": "End of the time window as an ISO 8601 timestamp. Defaults to now.",
            "in": "query",
            "name": "created_before",
            "required": false,
            "schema": {
              "format": "date-time",
              "type": "string"
            }
          },
          {
            "description": "The number of log lines to return (max 500).",
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "type": "integer"
            }
          },
          {
            "description": "A cursor for fetching logs after a previous page.",
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "A cursor for fetching logs before a later page.",
            "in": "query",
            "name": "before",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "properties": {
                          "app_build_id": {
                            "type": "string"
                          },
                          "app_id": {
                            "type": "string"
                          },
                          "cpu_time_ms": {
                            "type": "integer"
                          },
                          "created_at": {
                            "format": "date-time",
                            "type": "string"
                          },
                          "level": {
                            "type": "string"
                          },
                          "message": {
                            "type": "string"
                          },
                          "outcome": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "request_id": {
                            "type": "string"
                          },
                          "request_method": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "request_path": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "response_status": {
                            "type": [
                              "integer",
                              "null"
                            ]
                          },
                          "source": {
                            "enum": [
                              "console",
                              "exception",
                              "request"
                            ],
                            "type": "string"
                          },
                          "stack": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "truncated": {
                            "type": "boolean"
                          },
                          "wall_time_ms": {
                            "type": "integer"
                          }
                        },
                        "required": [
                          "app_id",
                          "app_build_id",
                          "request_id",
                          "created_at",
                          "source",
                          "level",
                          "message"
                        ],
                        "type": "object"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "has_next_page",
                        "has_previous_page"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "logs listed"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "missing the developer:logs:read scope"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "app not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "developer:logs:read"
            ]
          }
        ],
        "summary": "List App Logs",
        "tags": [
          "Apps"
        ]
      }
    },
    "/audiences": {
      "get": {
        "description": "Lists uploaded customer-list audiences for an account. Pass `audience_id` to return a specific audience.",
        "operationId": "list",
        "parameters": [
          {
            "description": "Account ID, prefixed `biz_`.",
            "in": "query",
            "name": "account_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Audience ID, prefixed `adaud_`, used to filter the response to one audience.",
            "in": "query",
            "name": "audience_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter by audience type: `custom` (uploaded lists) or `lookalike`.",
            "in": "query",
            "name": "audience_type",
            "required": false,
            "schema": {
              "enum": [
                "custom",
                "lookalike"
              ],
              "type": "string"
            }
          },
          {
            "description": "Number of audiences to return. Defaults to 20; maximum 100.",
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "type": "integer"
            }
          },
          {
            "description": "Cursor for the next page of audiences.",
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "$ref": "#/components/schemas/Audience"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "end_cursor",
                        "start_cursor",
                        "has_next_page",
                        "has_previous_page"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "Audiences listed."
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "Missing or invalid authentication."
          }
        },
        "security": [
          {
            "bearerAuth": [
              "audience:basic:read"
            ]
          }
        ],
        "summary": "List Audiences",
        "tags": [
          "Audiences"
        ]
      },
      "post": {
        "description": "Creates an audience. Default (`audience_type` omitted or `custom`): creates one audience from an uploaded customer identity CSV file (`name`, `column_mapping`, and `file_id` required) and starts processing it; responds with the audience object. With `audience_type: lookalike`: creates a ladder of Meta lookalike audiences from an existing ready custom audience (`source_audience_id`, `count`, and `percentage` required) — `count` equal similarity bands slicing the top `percentage`% (3 audiences at 6% = 0–2%, 2–4%, 4–6%), each returned as its own audience in a `{ data: [...] }` envelope.",
        "operationId": "create",
        "parameters": [],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "account_id": {
                    "description": "Account ID, prefixed `biz_`.",
                    "type": "string"
                  },
                  "audience_type": {
                    "description": "What to create. Defaults to `custom` (CSV upload).",
                    "enum": [
                      "custom",
                      "lookalike"
                    ],
                    "type": "string"
                  },
                  "column_mapping": {
                    "description": "Custom audiences only. Maps supported identity fields to CSV column headers. Map at least one of `email` or `phone`.",
                    "properties": {
                      "country": {
                        "description": "CSV header for ISO 3166-1 alpha-2 country codes, such as `US`.",
                        "type": "string"
                      },
                      "email": {
                        "description": "CSV header for email addresses.",
                        "type": "string"
                      },
                      "first_name": {
                        "description": "CSV header for first names.",
                        "type": "string"
                      },
                      "last_name": {
                        "description": "CSV header for last names.",
                        "type": "string"
                      },
                      "phone": {
                        "description": "CSV header for phone numbers.",
                        "type": "string"
                      }
                    },
                    "type": "object"
                  },
                  "count": {
                    "description": "Lookalikes only. Number of lookalike audiences to create (1–6).",
                    "type": "integer"
                  },
                  "file_id": {
                    "description": "Custom audiences only. Direct upload ID from the standard media upload endpoint.",
                    "type": "string"
                  },
                  "name": {
                    "description": "Audience display name. Required for custom audiences; lookalike names are generated from the source audience.",
                    "type": "string"
                  },
                  "percentage": {
                    "description": "Lookalikes only. Total similarity reach as a whole percent (1–20), sliced evenly across `count` — must be divisible by `count`.",
                    "type": "integer"
                  },
                  "source_audience_id": {
                    "description": "Lookalikes only. The ready custom audience (`adaud_`) to build from; it needs at least 100 matched people.",
                    "type": "string"
                  }
                },
                "required": [
                  "account_id"
                ],
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "201": {
            "content": {
              "application/json": {
                "schema": {
                  "oneOf": [
                    {
                      "$ref": "#/components/schemas/Audience"
                    },
                    {
                      "properties": {
                        "data": {
                          "items": {
                            "$ref": "#/components/schemas/Audience"
                          },
                          "type": "array"
                        }
                      },
                      "required": [
                        "data"
                      ],
                      "type": "object"
                    }
                  ]
                }
              }
            },
            "description": "Audience created — the audience object for custom audiences, or `{ data: [...] }` for lookalike ladders."
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "Missing or invalid authentication."
          }
        },
        "security": [
          {
            "bearerAuth": [
              "audience:update"
            ]
          }
        ],
        "summary": "Create Audience",
        "tags": [
          "Audiences"
        ]
      }
    },
    "/audiences/{audience_id}": {
      "delete": {
        "description": "Deletes an audience so it is no longer available for targeting.",
        "operationId": "delete",
        "parameters": [
          {
            "description": "Audience ID, prefixed `adaud_`.",
            "in": "path",
            "name": "audience_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "success": {
                      "type": "boolean"
                    }
                  },
                  "required": [
                    "success"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "Audience deleted."
          }
        },
        "security": [
          {
            "bearerAuth": [
              "audience:update"
            ]
          }
        ],
        "summary": "Delete Audience",
        "tags": [
          "Audiences"
        ]
      }
    },
    "/bounties": {
      "get": {
        "description": "Lists bounties visible to the credential — for an account API key, the account's bounties including scheduled drafts; for a user token, the bounties the user can see and work.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "Scope the list to this account (`biz_` tag). Requires read access to the account; account API keys may pass their own account or a connected account.",
            "in": "query",
            "name": "account_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "List the bounties this user participated in (`user_` tag). Must be the authenticated user.",
            "in": "query",
            "name": "user_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter by lifecycle state.",
            "in": "query",
            "name": "status",
            "required": false,
            "schema": {
              "enum": [
                "scheduled",
                "open",
                "closed",
                "completed",
                "canceled"
              ],
              "type": "string"
            }
          },
          {
            "description": "Substring match on the bounty title or ID.",
            "in": "query",
            "name": "query",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Only bounties created after this ISO 8601 timestamp.",
            "in": "query",
            "name": "created_after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Only bounties created before this ISO 8601 timestamp.",
            "in": "query",
            "name": "created_before",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Sort field.",
            "in": "query",
            "name": "order",
            "required": false,
            "schema": {
              "default": "created_at",
              "enum": [
                "created_at",
                "gross_paid_out_amount"
              ],
              "type": "string"
            }
          },
          {
            "description": "Sort direction.",
            "in": "query",
            "name": "direction",
            "required": false,
            "schema": {
              "default": "desc",
              "enum": [
                "asc",
                "desc"
              ],
              "type": "string"
            }
          },
          {
            "description": "Number of bounties to return from the start of the window.",
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "default": 20,
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to paginate forwards from.",
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Number of bounties to return from the end of the window.",
            "in": "query",
            "name": "last",
            "required": false,
            "schema": {
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to paginate backwards from.",
            "in": "query",
            "name": "before",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "$ref": "#/components/schemas/BountyListItem"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "end_cursor",
                        "start_cursor",
                        "has_next_page",
                        "has_previous_page"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "bounties listed"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "invalid status filter"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing credentials"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the bounty-read scope"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "bounty:basic:read"
            ]
          }
        ],
        "summary": "List Bounties",
        "tags": [
          "Bounties"
        ]
      },
      "post": {
        "description": "Creates a bounty and escrows its reward pool. Publishes immediately, or as a scheduled draft when you set `publish_at`.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "accepted_submissions_limit": {
                    "description": "Number of submissions that can be accepted (winner slots). Defaults to 1. The escrowed total is `gross_reward_amount` times this limit and must be at least $5.",
                    "type": [
                      "integer",
                      "null"
                    ]
                  },
                  "account_id": {
                    "description": "Account whose balance funds the bounty pool (`biz_` tag). Defaults to the caller's personal balance. Requires permission to move the account's funds.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "allowed_country_codes": {
                    "description": "Countries whose residents can work the bounty, as ISO 3166 alpha-2 codes. Empty means worldwide.",
                    "items": {
                      "type": "string"
                    },
                    "type": [
                      "array",
                      "null"
                    ]
                  },
                  "description": {
                    "description": "Full task instructions shown to workers.",
                    "type": "string"
                  },
                  "experience_id": {
                    "description": "Experience to host the bounty in (`exp_` tag). Any visibility — public for an open bounty, private for an invited one. Required unless account_id is set, in which case the bounty anchors in that account's public forum.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "frequency": {
                    "description": "How often the schedule creates a new bounty. Each occurrence is a separate bounty. Defaults to `once`; only applies with `publish_at`.",
                    "enum": [
                      "once",
                      "hourly",
                      "daily",
                      "weekly",
                      "monthly"
                    ],
                    "example": "weekly",
                    "type": "string"
                  },
                  "gross_reward_amount": {
                    "description": "Gross bounty-pool amount (USD) escrowed per accepted submission, in whole dollars. Platform fees and affiliate shares are paid from this amount.",
                    "type": "number"
                  },
                  "publish_at": {
                    "description": "ISO 8601 time to publish the bounty. When set, the bounty is created as a hidden draft and funded + published at this time instead of immediately.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "publish_at_timezone": {
                    "description": "IANA timezone for recurring occurrences. Required when publish_at is set.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "title": {
                    "description": "Short name of the task shown to workers.",
                    "type": "string"
                  }
                },
                "required": [
                  "title",
                  "description",
                  "gross_reward_amount"
                ],
                "type": "object"
              }
            }
          }
        },
        "responses": {
          "201": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Bounty"
                }
              }
            },
            "description": "bounty created"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "missing reward amount"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential cannot fund the requested account"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "bounty:create"
            ]
          }
        ],
        "summary": "Create Bounty",
        "tags": [
          "Bounties"
        ]
      }
    },
    "/bounties/{id}": {
      "get": {
        "description": "Retrieves a bounty by ID. Bounties outside the caller's scope return `404`.",
        "operationId": "get",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Bounty"
                }
              }
            },
            "description": "bounty retrieved"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "bounty outside the caller's scope"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "bounty:basic:read"
            ]
          }
        ],
        "summary": "Retrieve Bounty",
        "tags": [
          "Bounties"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "Bounty ID (`bnty_` tag).",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ]
      }
    },
    "/bounty_submissions": {
      "get": {
        "description": "Lists bounty submissions visible to the credential — for a user token, the submissions they authored plus those on bounties they posted; for an account API key, the submissions on the account's bounties.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "Scope the list to submissions on this account's bounties (`biz_` tag). Requires read access to the account.",
            "in": "query",
            "name": "account_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Only submissions on this bounty (`bnty_` tag).",
            "in": "query",
            "name": "bounty_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter by lifecycle state.",
            "in": "query",
            "name": "status",
            "required": false,
            "schema": {
              "enum": [
                "in_progress",
                "submitted",
                "approved",
                "denied"
              ],
              "type": "string"
            }
          },
          {
            "description": "Only submissions created after this ISO 8601 timestamp.",
            "in": "query",
            "name": "created_after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Only submissions created before this ISO 8601 timestamp.",
            "in": "query",
            "name": "created_before",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Sort field.",
            "in": "query",
            "name": "order",
            "required": false,
            "schema": {
              "default": "created_at",
              "enum": [
                "created_at"
              ],
              "type": "string"
            }
          },
          {
            "description": "Sort direction.",
            "in": "query",
            "name": "direction",
            "required": false,
            "schema": {
              "default": "desc",
              "enum": [
                "asc",
                "desc"
              ],
              "type": "string"
            }
          },
          {
            "description": "Number of submissions to return from the start of the window.",
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "default": 20,
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to paginate forwards from.",
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Number of submissions to return from the end of the window.",
            "in": "query",
            "name": "last",
            "required": false,
            "schema": {
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to paginate backwards from.",
            "in": "query",
            "name": "before",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "$ref": "#/components/schemas/BountySubmission"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "end_cursor",
                        "start_cursor",
                        "has_next_page",
                        "has_previous_page"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "bounty submissions listed"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "invalid status filter"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing credentials"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the bounty-read scope"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "bounty:basic:read"
            ]
          }
        ],
        "summary": "List Bounty Submissions",
        "tags": [
          "Bounty Submissions"
        ]
      },
      "post": {
        "description": "Submits work to a workforce bounty. Include a `deliverable` payload matching the bounty's accepted deliverable type: `content_url` for link-based bounties, `media` for upload-based bounties. The submission lands directly in review. Requires a user credential — account API keys cannot author submissions.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "affiliate_code": {
                    "description": "Affiliate code crediting the referrer, when the worker arrived through one.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "bounty_id": {
                    "description": "The bounty to submit to (`bnty_` tag).",
                    "type": "string"
                  },
                  "deliverable": {
                    "description": "The submitted work, matching one of the bounty's accepted deliverable types.",
                    "properties": {
                      "caption": {
                        "description": "Optional written context shown to reviewers.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "file_ids": {
                        "description": "Uploaded file IDs. Required when `type` is `media`.",
                        "items": {
                          "type": "string"
                        },
                        "type": "array"
                      },
                      "type": {
                        "description": "Deliverable shape. Must be accepted by the bounty's goal type.",
                        "enum": [
                          "content_url",
                          "media"
                        ],
                        "type": "string"
                      },
                      "urls": {
                        "description": "The posted content links, up to 10. Required when `type` is `content_url`.",
                        "items": {
                          "type": "string"
                        },
                        "type": "array"
                      }
                    },
                    "required": [
                      "type"
                    ],
                    "type": [
                      "object",
                      "null"
                    ]
                  }
                },
                "required": [
                  "bounty_id"
                ],
                "type": "object"
              }
            }
          }
        },
        "responses": {
          "201": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/BountySubmission"
                }
              }
            },
            "description": "submission created with an inline deliverable"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "deliverable type not accepted by the bounty"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing credentials"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "user credential lacks the submission-create scope"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "bounty not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "bounty:submission:create"
            ]
          }
        ],
        "summary": "Create Bounty Submission",
        "tags": [
          "Bounty Submissions"
        ]
      }
    },
    "/cards": {
      "get": {
        "description": "Lists issued Whop virtual cards for an account or user, including pending invitation cards that have not been issued by the card provider yet. Pass exactly one of account_id (a biz_ identifier) or user_id (a user_ identifier). Non-owner team members only see cards assigned to them. Users without the payout:account:read scope can still list cards assigned to them (for example moderators or external cardholders). Use GET /cards/:card_id to retrieve a single card with its secrets.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The owning account ID (a biz_ identifier). Provide this or user_id.",
            "in": "query",
            "name": "account_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The owning user ID (a user_ identifier). Provide this or account_id.",
            "in": "query",
            "name": "user_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "properties": {
                          "billing": {
                            "description": "The billing address.",
                            "properties": {
                              "city": {
                                "description": "Billing city.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "country_code": {
                                "description": "Billing country code.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "line1": {
                                "description": "Street address line 1.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "line2": {
                                "description": "Street address line 2.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "postal_code": {
                                "description": "Billing postal code.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "region": {
                                "description": "Billing region or state.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              }
                            },
                            "required": [
                              "line1",
                              "line2",
                              "city",
                              "region",
                              "postal_code",
                              "country_code"
                            ],
                            "type": [
                              "object",
                              "null"
                            ]
                          },
                          "canceled_at": {
                            "description": "When the card was canceled.",
                            "format": "date-time",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "created_at": {
                            "description": "When the card was created.",
                            "format": "date-time",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "expiration_month": {
                            "description": "Card expiration month.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "expiration_year": {
                            "description": "Card expiration year.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "id": {
                            "description": "Card ID, prefixed `icrd_`.",
                            "type": "string"
                          },
                          "last4": {
                            "description": "Last four digits of the card number. `null` for pending invitation cards.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "limit": {
                            "description": "The spending limit configuration.",
                            "properties": {
                              "amount": {
                                "description": "The limit amount in dollars.",
                                "type": "number"
                              },
                              "frequency": {
                                "description": "Limit window, for example `per24HourPeriod` or `perAuthorization`.",
                                "type": "string"
                              }
                            },
                            "required": [
                              "amount",
                              "frequency"
                            ],
                            "type": [
                              "object",
                              "null"
                            ]
                          },
                          "name": {
                            "description": "Card display name.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "object": {
                            "enum": [
                              "card"
                            ],
                            "type": "string"
                          },
                          "secrets": {
                            "description": "Sensitive card details. Present only on `GET /cards/:card_id` for active cards; `null` when the card is inactive or details cannot be retrieved.",
                            "properties": {
                              "card_number": {
                                "description": "Full card number.",
                                "type": "string"
                              },
                              "cvc": {
                                "description": "Card verification code.",
                                "type": "string"
                              },
                              "name_on_card": {
                                "description": "Cardholder name printed on the card.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              }
                            },
                            "required": [
                              "card_number",
                              "cvc",
                              "name_on_card"
                            ],
                            "type": [
                              "object",
                              "null"
                            ]
                          },
                          "spent_last_month": {
                            "description": "Total spend in the last 30 days, in cents.",
                            "type": [
                              "integer",
                              "null"
                            ]
                          },
                          "status": {
                            "description": "The card status.",
                            "enum": [
                              null,
                              "active",
                              "frozen",
                              "canceled",
                              "invited"
                            ],
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "type": {
                            "description": "The card type.",
                            "enum": [
                              null,
                              "virtual",
                              "physical"
                            ],
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "user_id": {
                            "description": "Cardholder user ID, prefixed `user_`, when assigned.",
                            "type": [
                              "string",
                              "null"
                            ]
                          }
                        },
                        "required": [
                          "object",
                          "id",
                          "name",
                          "type",
                          "status",
                          "last4",
                          "expiration_month",
                          "expiration_year",
                          "user_id",
                          "spent_last_month",
                          "limit",
                          "billing",
                          "created_at",
                          "canceled_at"
                        ],
                        "type": "object"
                      },
                      "type": "array"
                    }
                  },
                  "required": [
                    "data"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "cards listed for an assigned cardholder without the ledger scope"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "neither account_id nor user_id provided"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the payout-account-read scope"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "no ledger for that owner is visible to the credential"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "payout:account:read"
            ]
          }
        ],
        "summary": "List Cards",
        "tags": [
          "Cards"
        ]
      },
      "post": {
        "description": "Issues a virtual card. For an individual (consumer) card issuing account, the card is issued to the account's own cardholder. For a company (business) card issuing account, pass assigned_user_id to issue the card to a company member; if that member is not yet an approved card-issuing user, the card is provisioned asynchronously or an onboarding invitation is sent (HTTP 202). Pass exactly one of account_id (a biz_ identifier) or user_id (a user_ identifier). Returns the newly created card resource.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "account_id": {
                    "description": "The owning account ID (a biz_ identifier). Provide this or user_id.",
                    "type": "string"
                  },
                  "assigned_user_id": {
                    "description": "The company member (a user_ identifier) to assign the card to. Required for company (business) card issuing accounts.",
                    "type": "string"
                  },
                  "name": {
                    "description": "A display name for the card.",
                    "type": "string"
                  },
                  "spend_limit": {
                    "description": "Spending limit amount, in dollars.",
                    "type": "number"
                  },
                  "spend_limit_frequency": {
                    "description": "The spending limit window.",
                    "enum": [
                      "daily",
                      "weekly",
                      "monthly",
                      "one_time"
                    ],
                    "type": "string"
                  },
                  "transaction_limit": {
                    "description": "Per-transaction limit amount, in dollars.",
                    "type": "number"
                  },
                  "user_id": {
                    "description": "The owning user ID (a user_ identifier). Provide this or account_id.",
                    "type": "string"
                  }
                },
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "201": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "billing": {
                      "description": "The billing address.",
                      "properties": {
                        "city": {
                          "description": "Billing city.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "country_code": {
                          "description": "Billing country code.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "line1": {
                          "description": "Street address line 1.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "line2": {
                          "description": "Street address line 2.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "postal_code": {
                          "description": "Billing postal code.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "region": {
                          "description": "Billing region or state.",
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "line1",
                        "line2",
                        "city",
                        "region",
                        "postal_code",
                        "country_code"
                      ],
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "canceled_at": {
                      "description": "When the card was canceled.",
                      "format": "date-time",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "created_at": {
                      "description": "When the card was created.",
                      "format": "date-time",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "expiration_month": {
                      "description": "Card expiration month.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "expiration_year": {
                      "description": "Card expiration year.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "id": {
                      "description": "Card ID, prefixed `icrd_`.",
                      "type": "string"
                    },
                    "last4": {
                      "description": "Last four digits of the card number. `null` for pending invitation cards.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "limit": {
                      "description": "The spending limit configuration.",
                      "properties": {
                        "amount": {
                          "description": "The limit amount in dollars.",
                          "type": "number"
                        },
                        "frequency": {
                          "description": "Limit window, for example `per24HourPeriod` or `perAuthorization`.",
                          "type": "string"
                        }
                      },
                      "required": [
                        "amount",
                        "frequency"
                      ],
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "name": {
                      "description": "Card display name.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "object": {
                      "enum": [
                        "card"
                      ],
                      "type": "string"
                    },
                    "secrets": {
                      "description": "Sensitive card details. Present only on `GET /cards/:card_id` for active cards; `null` when the card is inactive or details cannot be retrieved.",
                      "properties": {
                        "card_number": {
                          "description": "Full card number.",
                          "type": "string"
                        },
                        "cvc": {
                          "description": "Card verification code.",
                          "type": "string"
                        },
                        "name_on_card": {
                          "description": "Cardholder name printed on the card.",
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "card_number",
                        "cvc",
                        "name_on_card"
                      ],
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "spent_last_month": {
                      "description": "Total spend in the last 30 days, in cents.",
                      "type": [
                        "integer",
                        "null"
                      ]
                    },
                    "status": {
                      "description": "The card status.",
                      "enum": [
                        null,
                        "active",
                        "frozen",
                        "canceled",
                        "invited"
                      ],
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "type": {
                      "description": "The card type.",
                      "enum": [
                        null,
                        "virtual",
                        "physical"
                      ],
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "user_id": {
                      "description": "Cardholder user ID, prefixed `user_`, when assigned.",
                      "type": [
                        "string",
                        "null"
                      ]
                    }
                  },
                  "required": [
                    "object",
                    "id",
                    "name",
                    "type",
                    "status",
                    "last4",
                    "expiration_month",
                    "expiration_year",
                    "user_id",
                    "spent_last_month",
                    "limit",
                    "billing",
                    "created_at",
                    "canceled_at"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "company card created for an assigned member"
          },
          "202": {
            "content": {
              "application/json": {
                "schema": {
                  "oneOf": [
                    {
                      "properties": {
                        "object": {
                          "enum": [
                            "card_provisioning"
                          ],
                          "type": "string"
                        },
                        "provisioning_job_id": {
                          "description": "Background job ID to poll until the card is ready.",
                          "type": "string"
                        }
                      },
                      "required": [
                        "object",
                        "provisioning_job_id"
                      ],
                      "type": "object"
                    },
                    {
                      "properties": {
                        "invitation_sent": {
                          "description": "Always true; the member must finish onboarding before the card is issued.",
                          "type": "boolean"
                        },
                        "object": {
                          "enum": [
                            "card_invitation"
                          ],
                          "type": "string"
                        }
                      },
                      "required": [
                        "object",
                        "invitation_sent"
                      ],
                      "type": "object"
                    }
                  ]
                }
              }
            },
            "description": "card not issued synchronously: provisioning started or onboarding invitation sent"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "business card issuing account without an assigned_user_id"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the payout-account-update scope"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "no ledger for that owner is visible to the credential"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "payout:account:update"
            ]
          }
        ],
        "summary": "Create Card",
        "tags": [
          "Cards"
        ]
      }
    },
    "/cards/{card_id}": {
      "get": {
        "description": "Retrieves a single card by its icrd_ identifier, including its secrets (full card number, CVC, and cardholder name) for active cards.",
        "operationId": "get",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "Card ID to retrieve, prefixed `icrd_`.",
            "in": "path",
            "name": "card_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The owning account ID (a biz_ identifier). Provide this or user_id.",
            "in": "query",
            "name": "account_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The owning user ID (a user_ identifier). Provide this or account_id.",
            "in": "query",
            "name": "user_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "billing": {
                      "description": "The billing address.",
                      "properties": {
                        "city": {
                          "description": "Billing city.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "country_code": {
                          "description": "Billing country code.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "line1": {
                          "description": "Street address line 1.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "line2": {
                          "description": "Street address line 2.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "postal_code": {
                          "description": "Billing postal code.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "region": {
                          "description": "Billing region or state.",
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "line1",
                        "line2",
                        "city",
                        "region",
                        "postal_code",
                        "country_code"
                      ],
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "canceled_at": {
                      "description": "When the card was canceled.",
                      "format": "date-time",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "created_at": {
                      "description": "When the card was created.",
                      "format": "date-time",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "expiration_month": {
                      "description": "Card expiration month.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "expiration_year": {
                      "description": "Card expiration year.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "id": {
                      "description": "Card ID, prefixed `icrd_`.",
                      "type": "string"
                    },
                    "last4": {
                      "description": "Last four digits of the card number. `null` for pending invitation cards.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "limit": {
                      "description": "The spending limit configuration.",
                      "properties": {
                        "amount": {
                          "description": "The limit amount in dollars.",
                          "type": "number"
                        },
                        "frequency": {
                          "description": "Limit window, for example `per24HourPeriod` or `perAuthorization`.",
                          "type": "string"
                        }
                      },
                      "required": [
                        "amount",
                        "frequency"
                      ],
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "name": {
                      "description": "Card display name.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "object": {
                      "enum": [
                        "card"
                      ],
                      "type": "string"
                    },
                    "secrets": {
                      "description": "Sensitive card details. Present only on `GET /cards/:card_id` for active cards; `null` when the card is inactive or details cannot be retrieved.",
                      "properties": {
                        "card_number": {
                          "description": "Full card number.",
                          "type": "string"
                        },
                        "cvc": {
                          "description": "Card verification code.",
                          "type": "string"
                        },
                        "name_on_card": {
                          "description": "Cardholder name printed on the card.",
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "card_number",
                        "cvc",
                        "name_on_card"
                      ],
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "spent_last_month": {
                      "description": "Total spend in the last 30 days, in cents.",
                      "type": [
                        "integer",
                        "null"
                      ]
                    },
                    "status": {
                      "description": "The card status.",
                      "enum": [
                        null,
                        "active",
                        "frozen",
                        "canceled",
                        "invited"
                      ],
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "type": {
                      "description": "The card type.",
                      "enum": [
                        null,
                        "virtual",
                        "physical"
                      ],
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "user_id": {
                      "description": "Cardholder user ID, prefixed `user_`, when assigned.",
                      "type": [
                        "string",
                        "null"
                      ]
                    }
                  },
                  "required": [
                    "object",
                    "id",
                    "name",
                    "type",
                    "status",
                    "last4",
                    "expiration_month",
                    "expiration_year",
                    "user_id",
                    "spent_last_month",
                    "limit",
                    "billing",
                    "created_at",
                    "canceled_at"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "card retrieved with secrets"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "card not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "payout:account:read"
            ]
          }
        ],
        "summary": "Retrieve Card",
        "tags": [
          "Cards"
        ]
      },
      "patch": {
        "description": "Updates an issued card. All fields are optional; only the fields you pass are changed. Updates the card name, billing address, and spending limits in one call, sets a new PIN, freezes or unfreezes the card, or cancels it. Pass canceled: true alone to cancel the card — cancellation is permanent and a canceled card cannot be uncanceled. Pass exactly one of account_id (a biz_ identifier) or user_id (a user_ identifier). Assigned cardholders without the payout:account:update scope can update the PIN and freeze state of their own card. The PIN can only be changed on a card assigned to the acting user. Returns the updated card resource.",
        "operationId": "update",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "Card ID to retrieve, prefixed `icrd_`.",
            "in": "path",
            "name": "card_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "account_id": {
                    "description": "The owning account ID (a biz_ identifier). Provide this or user_id.",
                    "type": "string"
                  },
                  "billing": {
                    "description": "New billing address. Requires line1, city, region, postal_code, and country_code.",
                    "properties": {
                      "city": {
                        "description": "Billing city.",
                        "type": "string"
                      },
                      "country_code": {
                        "description": "Billing country code, ISO 3166-1 alpha-2.",
                        "type": "string"
                      },
                      "line1": {
                        "description": "Street address line 1.",
                        "type": "string"
                      },
                      "line2": {
                        "description": "Street address line 2.",
                        "type": "string"
                      },
                      "postal_code": {
                        "description": "Billing postal code.",
                        "type": "string"
                      },
                      "region": {
                        "description": "Billing region or state.",
                        "type": "string"
                      }
                    },
                    "required": [
                      "line1",
                      "city",
                      "region",
                      "postal_code",
                      "country_code"
                    ],
                    "type": "object"
                  },
                  "canceled": {
                    "description": "Pass `true` to permanently cancel the card. A canceled card cannot be uncanceled. Cannot be combined with other fields.",
                    "type": "boolean"
                  },
                  "frozen": {
                    "description": "Pass `true` to freeze the card, `false` to unfreeze it.",
                    "type": "boolean"
                  },
                  "name": {
                    "description": "A display name for the card.",
                    "type": "string"
                  },
                  "pin": {
                    "description": "New 4-digit PIN. Can only be set on a card assigned to the acting user.",
                    "type": "string"
                  },
                  "remove_limit": {
                    "description": "Pass `true` to remove the spending limit (make the card unlimited).",
                    "type": "boolean"
                  },
                  "spend_limit": {
                    "description": "Spending limit amount, in dollars.",
                    "type": "number"
                  },
                  "spend_limit_frequency": {
                    "description": "The spending limit window.",
                    "enum": [
                      "daily",
                      "weekly",
                      "monthly",
                      "one_time"
                    ],
                    "type": "string"
                  },
                  "transaction_limit": {
                    "description": "Per-transaction limit amount, in dollars.",
                    "type": "number"
                  },
                  "user_id": {
                    "description": "The owning user ID (a user_ identifier). Provide this or account_id.",
                    "type": "string"
                  }
                },
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "billing": {
                      "description": "The billing address.",
                      "properties": {
                        "city": {
                          "description": "Billing city.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "country_code": {
                          "description": "Billing country code.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "line1": {
                          "description": "Street address line 1.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "line2": {
                          "description": "Street address line 2.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "postal_code": {
                          "description": "Billing postal code.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "region": {
                          "description": "Billing region or state.",
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "line1",
                        "line2",
                        "city",
                        "region",
                        "postal_code",
                        "country_code"
                      ],
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "canceled_at": {
                      "description": "When the card was canceled.",
                      "format": "date-time",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "created_at": {
                      "description": "When the card was created.",
                      "format": "date-time",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "expiration_month": {
                      "description": "Card expiration month.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "expiration_year": {
                      "description": "Card expiration year.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "id": {
                      "description": "Card ID, prefixed `icrd_`.",
                      "type": "string"
                    },
                    "last4": {
                      "description": "Last four digits of the card number. `null` for pending invitation cards.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "limit": {
                      "description": "The spending limit configuration.",
                      "properties": {
                        "amount": {
                          "description": "The limit amount in dollars.",
                          "type": "number"
                        },
                        "frequency": {
                          "description": "Limit window, for example `per24HourPeriod` or `perAuthorization`.",
                          "type": "string"
                        }
                      },
                      "required": [
                        "amount",
                        "frequency"
                      ],
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "name": {
                      "description": "Card display name.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "object": {
                      "enum": [
                        "card"
                      ],
                      "type": "string"
                    },
                    "secrets": {
                      "description": "Sensitive card details. Present only on `GET /cards/:card_id` for active cards; `null` when the card is inactive or details cannot be retrieved.",
                      "properties": {
                        "card_number": {
                          "description": "Full card number.",
                          "type": "string"
                        },
                        "cvc": {
                          "description": "Card verification code.",
                          "type": "string"
                        },
                        "name_on_card": {
                          "description": "Cardholder name printed on the card.",
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "card_number",
                        "cvc",
                        "name_on_card"
                      ],
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "spent_last_month": {
                      "description": "Total spend in the last 30 days, in cents.",
                      "type": [
                        "integer",
                        "null"
                      ]
                    },
                    "status": {
                      "description": "The card status.",
                      "enum": [
                        null,
                        "active",
                        "frozen",
                        "canceled",
                        "invited"
                      ],
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "type": {
                      "description": "The card type.",
                      "enum": [
                        null,
                        "virtual",
                        "physical"
                      ],
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "user_id": {
                      "description": "Cardholder user ID, prefixed `user_`, when assigned.",
                      "type": [
                        "string",
                        "null"
                      ]
                    }
                  },
                  "required": [
                    "object",
                    "id",
                    "name",
                    "type",
                    "status",
                    "last4",
                    "expiration_month",
                    "expiration_year",
                    "user_id",
                    "spent_last_month",
                    "limit",
                    "billing",
                    "created_at",
                    "canceled_at"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "card canceled"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "invalid update request"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the payout-account-update scope"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "card not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "payout:account:update"
            ]
          }
        ],
        "summary": "Update Card",
        "tags": [
          "Cards"
        ]
      }
    },
    "/checkout_configurations": {
      "get": {
        "description": "Lists checkout configurations for an account.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "Account ID, prefixed `biz_`.",
            "in": "query",
            "name": "company_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Only return checkout configurations for this plan ID, prefixed `plan_`.",
            "in": "query",
            "name": "plan_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Only return checkout configurations created before this Unix timestamp.",
            "in": "query",
            "name": "created_before",
            "required": false,
            "schema": {
              "type": "integer"
            }
          },
          {
            "description": "Only return checkout configurations created after this Unix timestamp.",
            "in": "query",
            "name": "created_after",
            "required": false,
            "schema": {
              "type": "integer"
            }
          },
          {
            "description": "Field used to sort checkout configurations.",
            "in": "query",
            "name": "order",
            "required": false,
            "schema": {
              "default": "created_at",
              "enum": [
                "created_at"
              ],
              "type": "string"
            }
          },
          {
            "description": "Sort direction. Defaults to `desc`.",
            "in": "query",
            "name": "direction",
            "required": false,
            "schema": {
              "default": "desc",
              "enum": [
                "asc",
                "desc"
              ],
              "type": "string"
            }
          },
          {
            "description": "Number of checkout configurations to return.",
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "type": "integer"
            }
          },
          {
            "description": "Cursor for the next page of results.",
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "properties": {
                          "affiliate_code": {
                            "description": "Affiliate code applied at checkout, or `null` when none is set.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "company_id": {
                            "description": "Account ID, prefixed `biz_`.",
                            "type": "string"
                          },
                          "created_at": {
                            "description": "When the checkout configuration was created, as an ISO 8601 timestamp.",
                            "type": "string"
                          },
                          "currency": {
                            "description": "Currency used for setup-mode payment method availability; defaults to `usd` when omitted.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "id": {
                            "description": "Checkout configuration ID, prefixed `ch_`.",
                            "type": "string"
                          },
                          "metadata": {
                            "description": "Custom key-value metadata copied to payments and memberships. `null` without the `checkout_configuration:basic:read` scope.",
                            "type": [
                              "object",
                              "null"
                            ]
                          },
                          "mode": {
                            "description": "Checkout mode: `payment` collects payment now; `setup` saves payment details for later.",
                            "enum": [
                              "payment",
                              "setup"
                            ],
                            "type": "string"
                          },
                          "payment_method_configuration": {
                            "description": "Payment method overrides for this checkout. `null` when it uses the plan or platform defaults.",
                            "properties": {
                              "disabled": {
                                "description": "Payment methods explicitly disabled for checkout.",
                                "items": {
                                  "type": "string"
                                },
                                "type": "array"
                              },
                              "enabled": {
                                "description": "Payment methods explicitly enabled for checkout.",
                                "items": {
                                  "type": "string"
                                },
                                "type": "array"
                              },
                              "include_platform_defaults": {
                                "description": "Whether platform default payment methods are included.",
                                "type": "boolean"
                              }
                            },
                            "type": [
                              "object",
                              "null"
                            ]
                          },
                          "plan": {
                            "description": "Plan used for payment checkout. `null` in setup mode.",
                            "properties": {
                              "adaptive_pricing_enabled": {
                                "description": "Whether this plan accepts local currency payments via adaptive pricing.",
                                "type": "boolean"
                              },
                              "billing_period": {
                                "description": "Recurring billing interval in days, such as 30 for monthly or 365 for annual. `null` for one-time plans.",
                                "type": [
                                  "integer",
                                  "null"
                                ]
                              },
                              "currency": {
                                "description": "Three-letter ISO currency code for the plan's prices.",
                                "type": "string"
                              },
                              "expiration_days": {
                                "description": "Access duration in days for expiration-based plans.",
                                "type": [
                                  "integer",
                                  "null"
                                ]
                              },
                              "id": {
                                "description": "Plan ID, prefixed `plan_`.",
                                "type": "string"
                              },
                              "initial_price": {
                                "description": "Initial purchase price in the plan currency.",
                                "type": "number"
                              },
                              "plan_type": {
                                "description": "Billing model for the plan: `renewal` (recurring) or `one_time` (single payment).",
                                "type": "string"
                              },
                              "release_method": {
                                "description": "Sales method for the plan, such as `buy_now` or `waitlist`.",
                                "type": "string"
                              },
                              "renewal_price": {
                                "description": "Recurring price charged each billing period.",
                                "type": "number"
                              },
                              "three_ds_level": {
                                "description": "3D Secure behavior for this plan, or `null` to use the account default.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "trial_period_days": {
                                "description": "Free trial days before the first renewal charge.",
                                "type": [
                                  "integer",
                                  "null"
                                ]
                              },
                              "visibility": {
                                "description": "Whether the plan is visible to customers or hidden from public view.",
                                "type": "string"
                              }
                            },
                            "required": [
                              "id",
                              "visibility",
                              "plan_type",
                              "release_method",
                              "currency",
                              "billing_period",
                              "expiration_days",
                              "initial_price",
                              "renewal_price",
                              "trial_period_days",
                              "three_ds_level",
                              "adaptive_pricing_enabled"
                            ],
                            "type": [
                              "object",
                              "null"
                            ]
                          },
                          "purchase_url": {
                            "description": "Checkout URL you can send to customers.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "redirect_url": {
                            "description": "URL customers are sent to after checkout, or `null` when no redirect is configured.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "three_ds_level": {
                            "description": "3D Secure behavior for this checkout, or `null` to use the account default.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "updated_at": {
                            "description": "When the checkout configuration was last updated, as an ISO 8601 timestamp.",
                            "type": "string"
                          }
                        },
                        "required": [
                          "id",
                          "created_at",
                          "updated_at",
                          "company_id",
                          "mode"
                        ],
                        "type": "object"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "end_cursor",
                        "start_cursor",
                        "has_next_page",
                        "has_previous_page"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "checkout configurations listed"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "company_id missing"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          }
        },
        "security": [
          {
            "bearerAuth": []
          }
        ],
        "summary": "List checkout configurations",
        "tags": [
          "Checkout Configurations"
        ]
      },
      "post": {
        "description": "Creates a reusable checkout configuration for an existing or inline plan.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "example": {
                  "company_id": "biz_xxxxxxxxxxxxxx",
                  "plan_id": "plan_xxxxxxxxxxxxx"
                },
                "properties": {
                  "affiliate_code": {
                    "description": "Affiliate code to apply to the checkout.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "company_id": {
                    "description": "Account ID, prefixed `biz_`.",
                    "type": "string"
                  },
                  "currency": {
                    "description": "Currency used for setup-mode payment method availability.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "metadata": {
                    "description": "Custom key-value metadata copied to payments and memberships.",
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "mode": {
                    "description": "Checkout mode: `payment` collects payment for a plan now; `setup` saves payment details without charging. Defaults to `payment`.",
                    "enum": [
                      "payment",
                      "setup"
                    ],
                    "type": "string"
                  },
                  "payment_method_configuration": {
                    "description": "Payment method overrides for this checkout. `null` uses the plan or platform defaults.",
                    "properties": {
                      "disabled": {
                        "description": "Payment methods explicitly disabled for checkout.",
                        "items": {
                          "type": "string"
                        },
                        "type": "array"
                      },
                      "enabled": {
                        "description": "Payment methods explicitly enabled for checkout.",
                        "items": {
                          "type": "string"
                        },
                        "type": "array"
                      },
                      "include_platform_defaults": {
                        "description": "Whether platform default payment methods are included.",
                        "type": "boolean"
                      }
                    },
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "plan": {
                    "description": "Plan attributes used to create or find a plan for this checkout configuration. Mutually exclusive with `plan_id`.",
                    "properties": {
                      "billing_period": {
                        "description": "Recurring billing interval in days, such as 30 for monthly or 365 for annual.",
                        "type": [
                          "integer",
                          "null"
                        ]
                      },
                      "company_id": {
                        "description": "Account ID for the inline plan, prefixed `biz_`. Defaults to the account resolved from the request.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "currency": {
                        "description": "Three-letter ISO currency code for the plan's prices.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "description": {
                        "description": "Customer-visible plan description.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "expiration_days": {
                        "description": "Access duration in days for expiration-based plans.",
                        "type": [
                          "integer",
                          "null"
                        ]
                      },
                      "force_create_new_plan": {
                        "description": "Whether to create a new plan instead of reusing a matching one.",
                        "type": [
                          "boolean",
                          "null"
                        ]
                      },
                      "initial_price": {
                        "description": "Initial purchase price in the plan currency.",
                        "type": [
                          "number",
                          "null"
                        ]
                      },
                      "metadata": {
                        "description": "Custom key-value metadata stored on the plan.",
                        "type": [
                          "object",
                          "null"
                        ]
                      },
                      "override_tax_type": {
                        "description": "Tax classification override for this plan.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "payment_method_configuration": {
                        "description": "Payment method overrides for the inline plan. `null` uses platform defaults.",
                        "properties": {
                          "disabled": {
                            "description": "Payment methods explicitly disabled for this plan.",
                            "items": {
                              "type": "string"
                            },
                            "type": "array"
                          },
                          "enabled": {
                            "description": "Payment methods explicitly enabled for this plan.",
                            "items": {
                              "type": "string"
                            },
                            "type": "array"
                          },
                          "include_platform_defaults": {
                            "description": "Whether platform default payment methods are included.",
                            "type": "boolean"
                          }
                        },
                        "type": [
                          "object",
                          "null"
                        ]
                      },
                      "plan_type": {
                        "description": "Billing model for the plan: `renewal` (recurring) or `one_time` (single payment).",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "product_id": {
                        "description": "Product ID the inline plan should belong to, prefixed `prod_`.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "release_method": {
                        "description": "Sales method for the plan, such as `buy_now` or `waitlist`.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "renewal_price": {
                        "description": "Recurring price charged each billing period.",
                        "type": [
                          "number",
                          "null"
                        ]
                      },
                      "stock": {
                        "description": "Units available for purchase.",
                        "type": [
                          "integer",
                          "null"
                        ]
                      },
                      "title": {
                        "description": "Plan display name shown to customers.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "trial_period_days": {
                        "description": "Free trial days before the first renewal charge.",
                        "type": [
                          "integer",
                          "null"
                        ]
                      },
                      "unlimited_stock": {
                        "description": "Whether the plan has unlimited stock.",
                        "type": [
                          "boolean",
                          "null"
                        ]
                      },
                      "visibility": {
                        "description": "Whether the plan is visible to customers or hidden from public view.",
                        "type": [
                          "string",
                          "null"
                        ]
                      }
                    },
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "plan_id": {
                    "description": "Existing plan ID, prefixed `plan_`. Mutually exclusive with `plan`.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "redirect_url": {
                    "description": "URL customers are sent to after checkout.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "three_ds_level": {
                    "description": "3D Secure behavior for this checkout.",
                    "type": [
                      "string",
                      "null"
                    ]
                  }
                },
                "type": "object"
              }
            }
          }
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "affiliate_code": {
                      "description": "Affiliate code applied at checkout, or `null` when none is set.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "company_id": {
                      "description": "Account ID, prefixed `biz_`.",
                      "type": "string"
                    },
                    "created_at": {
                      "description": "When the checkout configuration was created, as an ISO 8601 timestamp.",
                      "type": "string"
                    },
                    "currency": {
                      "description": "Currency used for setup-mode payment method availability; defaults to `usd` when omitted.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "id": {
                      "description": "Checkout configuration ID, prefixed `ch_`.",
                      "type": "string"
                    },
                    "metadata": {
                      "description": "Custom key-value metadata copied to payments and memberships. `null` without the `checkout_configuration:basic:read` scope.",
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "mode": {
                      "description": "Checkout mode: `payment` collects payment now; `setup` saves payment details for later.",
                      "enum": [
                        "payment",
                        "setup"
                      ],
                      "type": "string"
                    },
                    "payment_method_configuration": {
                      "description": "Payment method overrides for this checkout. `null` when it uses the plan or platform defaults.",
                      "properties": {
                        "disabled": {
                          "description": "Payment methods explicitly disabled for checkout.",
                          "items": {
                            "type": "string"
                          },
                          "type": "array"
                        },
                        "enabled": {
                          "description": "Payment methods explicitly enabled for checkout.",
                          "items": {
                            "type": "string"
                          },
                          "type": "array"
                        },
                        "include_platform_defaults": {
                          "description": "Whether platform default payment methods are included.",
                          "type": "boolean"
                        }
                      },
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "plan": {
                      "description": "Plan used for payment checkout. `null` in setup mode.",
                      "properties": {
                        "adaptive_pricing_enabled": {
                          "description": "Whether this plan accepts local currency payments via adaptive pricing.",
                          "type": "boolean"
                        },
                        "billing_period": {
                          "description": "Recurring billing interval in days, such as 30 for monthly or 365 for annual. `null` for one-time plans.",
                          "type": [
                            "integer",
                            "null"
                          ]
                        },
                        "currency": {
                          "description": "Three-letter ISO currency code for the plan's prices.",
                          "type": "string"
                        },
                        "expiration_days": {
                          "description": "Access duration in days for expiration-based plans.",
                          "type": [
                            "integer",
                            "null"
                          ]
                        },
                        "id": {
                          "description": "Plan ID, prefixed `plan_`.",
                          "type": "string"
                        },
                        "initial_price": {
                          "description": "Initial purchase price in the plan currency.",
                          "type": "number"
                        },
                        "plan_type": {
                          "description": "Billing model for the plan: `renewal` (recurring) or `one_time` (single payment).",
                          "type": "string"
                        },
                        "release_method": {
                          "description": "Sales method for the plan, such as `buy_now` or `waitlist`.",
                          "type": "string"
                        },
                        "renewal_price": {
                          "description": "Recurring price charged each billing period.",
                          "type": "number"
                        },
                        "three_ds_level": {
                          "description": "3D Secure behavior for this plan, or `null` to use the account default.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "trial_period_days": {
                          "description": "Free trial days before the first renewal charge.",
                          "type": [
                            "integer",
                            "null"
                          ]
                        },
                        "visibility": {
                          "description": "Whether the plan is visible to customers or hidden from public view.",
                          "type": "string"
                        }
                      },
                      "required": [
                        "id",
                        "visibility",
                        "plan_type",
                        "release_method",
                        "currency",
                        "billing_period",
                        "expiration_days",
                        "initial_price",
                        "renewal_price",
                        "trial_period_days",
                        "three_ds_level",
                        "adaptive_pricing_enabled"
                      ],
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "purchase_url": {
                      "description": "Checkout URL you can send to customers.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "redirect_url": {
                      "description": "URL customers are sent to after checkout, or `null` when no redirect is configured.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "three_ds_level": {
                      "description": "3D Secure behavior for this checkout, or `null` to use the account default.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "updated_at": {
                      "description": "When the checkout configuration was last updated, as an ISO 8601 timestamp.",
                      "type": "string"
                    }
                  },
                  "required": [
                    "id",
                    "created_at",
                    "updated_at",
                    "company_id",
                    "mode"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "checkout configuration created"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          }
        },
        "security": [
          {
            "bearerAuth": []
          }
        ],
        "summary": "Create a checkout configuration",
        "tags": [
          "Checkout Configurations"
        ]
      }
    },
    "/checkout_configurations/{id}": {
      "delete": {
        "description": "Deletes a checkout configuration so its checkout URL can no longer be used.",
        "operationId": "delete",
        "responses": {
          "200": {
            "description": "checkout configuration deleted"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "not found"
          }
        },
        "security": [
          {
            "bearerAuth": []
          }
        ],
        "summary": "Delete a checkout configuration",
        "tags": [
          "Checkout Configurations"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The ID of the checkout configuration.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ]
      },
      "get": {
        "description": "Retrieves a checkout configuration by ID. This endpoint is public so a checkout page can load from the configuration URL.",
        "operationId": "get",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "affiliate_code": {
                      "description": "Affiliate code applied at checkout, or `null` when none is set.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "company_id": {
                      "description": "Account ID, prefixed `biz_`.",
                      "type": "string"
                    },
                    "created_at": {
                      "description": "When the checkout configuration was created, as an ISO 8601 timestamp.",
                      "type": "string"
                    },
                    "currency": {
                      "description": "Currency used for setup-mode payment method availability; defaults to `usd` when omitted.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "id": {
                      "description": "Checkout configuration ID, prefixed `ch_`.",
                      "type": "string"
                    },
                    "metadata": {
                      "description": "Custom key-value metadata copied to payments and memberships. `null` without the `checkout_configuration:basic:read` scope.",
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "mode": {
                      "description": "Checkout mode: `payment` collects payment now; `setup` saves payment details for later.",
                      "enum": [
                        "payment",
                        "setup"
                      ],
                      "type": "string"
                    },
                    "payment_method_configuration": {
                      "description": "Payment method overrides for this checkout. `null` when it uses the plan or platform defaults.",
                      "properties": {
                        "disabled": {
                          "description": "Payment methods explicitly disabled for checkout.",
                          "items": {
                            "type": "string"
                          },
                          "type": "array"
                        },
                        "enabled": {
                          "description": "Payment methods explicitly enabled for checkout.",
                          "items": {
                            "type": "string"
                          },
                          "type": "array"
                        },
                        "include_platform_defaults": {
                          "description": "Whether platform default payment methods are included.",
                          "type": "boolean"
                        }
                      },
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "plan": {
                      "description": "Plan used for payment checkout. `null` in setup mode.",
                      "properties": {
                        "adaptive_pricing_enabled": {
                          "description": "Whether this plan accepts local currency payments via adaptive pricing.",
                          "type": "boolean"
                        },
                        "billing_period": {
                          "description": "Recurring billing interval in days, such as 30 for monthly or 365 for annual. `null` for one-time plans.",
                          "type": [
                            "integer",
                            "null"
                          ]
                        },
                        "currency": {
                          "description": "Three-letter ISO currency code for the plan's prices.",
                          "type": "string"
                        },
                        "expiration_days": {
                          "description": "Access duration in days for expiration-based plans.",
                          "type": [
                            "integer",
                            "null"
                          ]
                        },
                        "id": {
                          "description": "Plan ID, prefixed `plan_`.",
                          "type": "string"
                        },
                        "initial_price": {
                          "description": "Initial purchase price in the plan currency.",
                          "type": "number"
                        },
                        "plan_type": {
                          "description": "Billing model for the plan: `renewal` (recurring) or `one_time` (single payment).",
                          "type": "string"
                        },
                        "release_method": {
                          "description": "Sales method for the plan, such as `buy_now` or `waitlist`.",
                          "type": "string"
                        },
                        "renewal_price": {
                          "description": "Recurring price charged each billing period.",
                          "type": "number"
                        },
                        "three_ds_level": {
                          "description": "3D Secure behavior for this plan, or `null` to use the account default.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "trial_period_days": {
                          "description": "Free trial days before the first renewal charge.",
                          "type": [
                            "integer",
                            "null"
                          ]
                        },
                        "visibility": {
                          "description": "Whether the plan is visible to customers or hidden from public view.",
                          "type": "string"
                        }
                      },
                      "required": [
                        "id",
                        "visibility",
                        "plan_type",
                        "release_method",
                        "currency",
                        "billing_period",
                        "expiration_days",
                        "initial_price",
                        "renewal_price",
                        "trial_period_days",
                        "three_ds_level",
                        "adaptive_pricing_enabled"
                      ],
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "purchase_url": {
                      "description": "Checkout URL you can send to customers.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "redirect_url": {
                      "description": "URL customers are sent to after checkout, or `null` when no redirect is configured.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "three_ds_level": {
                      "description": "3D Secure behavior for this checkout, or `null` to use the account default.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "updated_at": {
                      "description": "When the checkout configuration was last updated, as an ISO 8601 timestamp.",
                      "type": "string"
                    }
                  },
                  "required": [
                    "id",
                    "created_at",
                    "updated_at",
                    "company_id",
                    "mode"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "checkout configuration found (unauthenticated)"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "not found"
          }
        },
        "summary": "Retrieve a checkout configuration",
        "tags": [
          "Checkout Configurations"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The ID of the checkout configuration.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ]
      }
    },
    "/deposits": {
      "post": {
        "description": "Resolves a deposit destination and returns the on-chain addresses that can fund it. No authentication is required; any business can be resolved by its account ID. A caller authenticated as a user can additionally resolve their own user account.",
        "operationId": "create",
        "parameters": [],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "amount": {
                    "description": "Amount to prefill on hosted deposit page.",
                    "type": "number"
                  },
                  "destination": {
                    "description": "Destination account ID or wallet address. Object form is supported for compatibility.",
                    "oneOf": [
                      {
                        "type": "string"
                      },
                      {
                        "properties": {
                          "account_id": {
                            "description": "Destination account ID.",
                            "type": "string"
                          },
                          "address": {
                            "description": "Destination wallet address.",
                            "type": "string"
                          },
                          "network": {
                            "description": "Destination wallet network.",
                            "type": "string"
                          }
                        },
                        "type": "object"
                      }
                    ]
                  },
                  "metadata": {
                    "additionalProperties": true,
                    "description": "Metadata to include with the deposit response.",
                    "type": "object"
                  },
                  "network": {
                    "description": "Destination network override.",
                    "type": [
                      "string",
                      "null"
                    ]
                  }
                },
                "required": [
                  "destination"
                ],
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "201": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "account_id": {
                      "description": "Account ID of the destination owner. Null for raw wallet address destinations.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "amount": {
                      "description": "Requested deposit amount.",
                      "type": "string"
                    },
                    "hosted_url": {
                      "description": "URL of the hosted deposit page. Only present for business destinations.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "metadata": {
                      "additionalProperties": true,
                      "description": "Metadata from the request.",
                      "type": "object"
                    },
                    "methods": {
                      "description": "Available deposit methods for destination.",
                      "properties": {
                        "bank": {
                          "description": "Bank deposit details. Only present when bank deposits are active for the destination account.",
                          "properties": {
                            "currencies": {
                              "description": "Bank transfer currencies available for this deposit.",
                              "items": {
                                "properties": {
                                  "account_number": {
                                    "description": "Bank account number for deposits in this currency.",
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  },
                                  "currency": {
                                    "description": "Currency supported by these bank instructions.",
                                    "type": "string"
                                  },
                                  "deposit_bank_address": {
                                    "description": "Receiving bank address.",
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  },
                                  "deposit_bank_name": {
                                    "description": "Receiving bank name.",
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  },
                                  "deposit_beneficiary_name": {
                                    "description": "Beneficiary name to use for transfer.",
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  },
                                  "deposit_reference": {
                                    "description": "Reference to include with bank transfer.",
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  },
                                  "rails": {
                                    "description": "Active deposit rails for this currency, such as `ach`, `wire`, or `sepa`.",
                                    "items": {
                                      "type": "string"
                                    },
                                    "type": "array"
                                  },
                                  "routing_number": {
                                    "description": "Bank routing number for deposits in this currency.",
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  },
                                  "swift_bic": {
                                    "description": "SWIFT/BIC code for international wires, when available.",
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  }
                                },
                                "required": [
                                  "currency",
                                  "account_number",
                                  "routing_number",
                                  "deposit_bank_name",
                                  "deposit_bank_address",
                                  "deposit_beneficiary_name",
                                  "deposit_reference",
                                  "swift_bic",
                                  "rails"
                                ],
                                "type": "object"
                              },
                              "type": "array"
                            }
                          },
                          "required": [
                            "currencies"
                          ],
                          "type": [
                            "object",
                            "null"
                          ]
                        },
                        "crypto": {
                          "description": "Crypto networks available for this deposit, each with its on-chain deposit address and the tokens accepted on that network.",
                          "items": {
                            "properties": {
                              "deposit_address": {
                                "description": "Address to send funds to on this network. Null when the provider has not issued one yet.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "icon_url": {
                                "description": "Network icon URL.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "name": {
                                "description": "Network display name, such as `Ethereum` or `Solana`.",
                                "type": "string"
                              },
                              "supported_currencies": {
                                "description": "Tokens accepted for deposit on this network.",
                                "items": {
                                  "properties": {
                                    "icon_url": {
                                      "description": "Token icon URL. Null when no icon is available.",
                                      "type": [
                                        "string",
                                        "null"
                                      ]
                                    },
                                    "name": {
                                      "description": "Token symbol, such as `USDC`.",
                                      "type": "string"
                                    }
                                  },
                                  "required": [
                                    "name",
                                    "icon_url"
                                  ],
                                  "type": "object"
                                },
                                "type": "array"
                              }
                            },
                            "required": [
                              "name",
                              "deposit_address",
                              "icon_url",
                              "supported_currencies"
                            ],
                            "type": "object"
                          },
                          "type": "array"
                        }
                      },
                      "required": [
                        "bank",
                        "crypto"
                      ],
                      "type": "object"
                    },
                    "object": {
                      "enum": [
                        "deposit"
                      ],
                      "type": "string"
                    }
                  },
                  "required": [
                    "object",
                    "account_id",
                    "hosted_url",
                    "methods",
                    "metadata"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "deposit created"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "request is invalid"
          }
        },
        "summary": "Create Deposit",
        "tags": [
          "Deposits"
        ]
      }
    },
    "/events": {
      "get": {
        "description": "Lists pixel events, most recent first. Pass person_id for one person's journey, or omit it to list identity-linked events for an account within an explicit time range. Events are shaped like the POST /events intake: attribution in context, identity in user.",
        "operationId": "list",
        "parameters": [
          {
            "description": "The ID of the person. Omit to list recent identity-linked events for the account.",
            "in": "query",
            "name": "person_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The ID of the account, which will look like biz_*************. Optional for account API keys; required for credentials that can access multiple accounts.",
            "in": "query",
            "name": "account_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Start of the time range as an ISO 8601 timestamp. Required when person_id is omitted.",
            "in": "query",
            "name": "from",
            "required": false,
            "schema": {
              "format": "date-time",
              "type": "string"
            }
          },
          {
            "description": "End of the time range as an ISO 8601 timestamp. Required when person_id is omitted; otherwise defaults to now.",
            "in": "query",
            "name": "to",
            "required": false,
            "schema": {
              "format": "date-time",
              "type": "string"
            }
          },
          {
            "description": "The number of events to return.",
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "type": "integer"
            }
          },
          {
            "description": "A cursor for fetching events after a previous page.",
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "A cursor for fetching events before a later page.",
            "in": "query",
            "name": "before",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "properties": {
                          "context": {
                            "properties": {
                              "ad_campaign_id": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "ad_id": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "ad_set_id": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "utm_campaign": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "utm_content": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "utm_medium": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "utm_source": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "utm_term": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              }
                            },
                            "type": [
                              "object",
                              "null"
                            ]
                          },
                          "currency": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "custom_name": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "event_id": {
                            "type": "string"
                          },
                          "event_name": {
                            "type": "string"
                          },
                          "event_time": {
                            "format": "date-time",
                            "type": "string"
                          },
                          "id": {
                            "type": "string"
                          },
                          "path": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "person_id": {
                            "type": "string"
                          },
                          "questions": {
                            "items": {
                              "properties": {
                                "answer": {
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "id": {
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "key": {
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "options": {
                                  "items": {
                                    "type": "string"
                                  },
                                  "type": [
                                    "array",
                                    "null"
                                  ]
                                },
                                "question": {
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "type": {
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                }
                              },
                              "type": "object"
                            },
                            "type": [
                              "array",
                              "null"
                            ]
                          },
                          "referrer_url": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "total_usd_amount": {
                            "type": [
                              "number",
                              "null"
                            ]
                          },
                          "url": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "user": {
                            "properties": {
                              "city": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "country": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "email": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "first_name": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "last_name": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "name": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "phone": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "state": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              }
                            },
                            "type": [
                              "object",
                              "null"
                            ]
                          },
                          "value": {
                            "type": [
                              "number",
                              "null"
                            ]
                          }
                        },
                        "required": [
                          "id",
                          "event_id",
                          "event_name",
                          "person_id",
                          "event_time"
                        ],
                        "type": "object"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "has_next_page",
                        "has_previous_page"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "account events listed"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "company:basic:read",
              "member:basic:read",
              "member:journey:read"
            ]
          }
        ],
        "summary": "List Events",
        "tags": [
          "Events"
        ]
      },
      "post": {
        "description": "Tracks a conversion or engagement event for an account.",
        "operationId": "create",
        "parameters": [],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "description": "Parameters for CreateConversion",
                "properties": {
                  "account_id": {
                    "description": "The account to associate with this event.",
                    "type": "string"
                  },
                  "action_source": {
                    "description": "Where the event originated.",
                    "oneOf": [
                      {
                        "description": "The channel where an event originated",
                        "enum": [
                          "email",
                          "website",
                          "app",
                          "phone_call",
                          "chat",
                          "physical_store",
                          "system_generated",
                          "business_messaging",
                          "other"
                        ],
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ]
                  },
                  "context": {
                    "description": "Tracking and attribution context.",
                    "properties": {
                      "ad_campaign_id": {
                        "description": "Ad campaign ID.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "ad_id": {
                        "description": "Ad ID.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "ad_set_id": {
                        "description": "Ad set ID.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "fbc": {
                        "description": "Facebook click cookie (_fbc, format fb.1.{timestamp}.{fbclid}).",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "fbclid": {
                        "description": "Facebook click ID.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "fbp": {
                        "description": "Facebook browser pixel ID.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "fingerprint": {
                        "description": "Client-side device fingerprint.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "fingerprint_confidence": {
                        "description": "Confidence score (0-1) for the device fingerprint.",
                        "example": 6.9,
                        "type": [
                          "number",
                          "null"
                        ]
                      },
                      "ga": {
                        "description": "Google Analytics client ID.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "gbraid": {
                        "description": "Google Ads gbraid click ID (iOS privacy).",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "gclid": {
                        "description": "Google click ID.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "ig_sid": {
                        "description": "Instagram session ID.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "ip_address": {
                        "description": "IP address.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "language": {
                        "description": "Browser language (e.g. en-US).",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "li_fat_id": {
                        "description": "LinkedIn click ID.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "msclkid": {
                        "description": "Microsoft Advertising (Bing) click ID.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "rdt_cid": {
                        "description": "Reddit click ID.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "sccid": {
                        "description": "Snapchat click ID.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "screen_resolution": {
                        "description": "Screen resolution (e.g. 1920x1080).",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "timezone": {
                        "description": "IANA timezone (e.g. America/New_York).",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "ttclid": {
                        "description": "TikTok click ID.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "ttp": {
                        "description": "TikTok pixel ID.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "twclid": {
                        "description": "X (Twitter) click ID.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "user_agent": {
                        "description": "Browser user agent string.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "utm_campaign": {
                        "description": "UTM campaign parameter.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "utm_content": {
                        "description": "UTM content parameter.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "utm_id": {
                        "description": "UTM ID parameter.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "utm_medium": {
                        "description": "UTM medium parameter.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "utm_source": {
                        "description": "UTM source parameter.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "utm_term": {
                        "description": "UTM term parameter.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "wbraid": {
                        "description": "Google Ads wbraid click ID (iOS privacy).",
                        "type": [
                          "string",
                          "null"
                        ]
                      }
                    },
                    "required": [],
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "currency": {
                    "description": "ISO 4217 currency code.",
                    "oneOf": [
                      {
                        "description": "The available currencies on the platform",
                        "enum": [
                          "usd",
                          "sgd",
                          "inr",
                          "aud",
                          "brl",
                          "cad",
                          "dkk",
                          "eur",
                          "nok",
                          "gbp",
                          "sek",
                          "chf",
                          "hkd",
                          "huf",
                          "jpy",
                          "mxn",
                          "myr",
                          "pln",
                          "czk",
                          "nzd",
                          "aed",
                          "eth",
                          "ape",
                          "cop",
                          "ron",
                          "thb",
                          "bgn",
                          "idr",
                          "dop",
                          "php",
                          "try",
                          "krw",
                          "twd",
                          "vnd",
                          "pkr",
                          "clp",
                          "uyu",
                          "ars",
                          "zar",
                          "dzd",
                          "tnd",
                          "mad",
                          "kes",
                          "kwd",
                          "jod",
                          "all",
                          "xcd",
                          "amd",
                          "bsd",
                          "bhd",
                          "bob",
                          "bam",
                          "khr",
                          "crc",
                          "xof",
                          "egp",
                          "etb",
                          "gmd",
                          "ghs",
                          "gtq",
                          "gyd",
                          "ils",
                          "jmd",
                          "mop",
                          "mga",
                          "mur",
                          "mdl",
                          "mnt",
                          "nad",
                          "ngn",
                          "mkd",
                          "omr",
                          "pyg",
                          "pen",
                          "qar",
                          "rwf",
                          "sar",
                          "rsd",
                          "lkr",
                          "tzs",
                          "ttd",
                          "uzs",
                          "rub",
                          "btc",
                          "cny",
                          "usdt",
                          "kzt",
                          "awg",
                          "whop_usd",
                          "xau"
                        ],
                        "type": "string"
                      },
                      {
                        "type": "null"
                      }
                    ]
                  },
                  "custom_name": {
                    "description": "Custom event name when event_name is 'custom'. Maximum 35 chars for this value.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "duration": {
                    "description": "For 'leave' events: milliseconds the visitor spent on the page.",
                    "example": 42,
                    "type": [
                      "integer",
                      "null"
                    ]
                  },
                  "event_id": {
                    "description": "Client-provided identifier for deduplication. Generated if omitted.",
                    "example": "evnt_xxxxxxxxxxxxx",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "event_name": {
                    "description": "The type of event.\n\nUse a standard event (lead, submit_application, contact, complete_registration, schedule, view_content, add_to_cart) or pass your own name directly for a custom event.",
                    "example": "course_completed",
                    "type": "string"
                  },
                  "event_time": {
                    "description": "When the event occurred. Defaults to now.",
                    "example": "2023-12-01T05:00:00.401Z",
                    "format": "date-time",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "plan_id": {
                    "description": "The plan associated with the event.",
                    "example": "plan_xxxxxxxxxxxxx",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "product_id": {
                    "description": "The product associated with the event.",
                    "example": "prod_xxxxxxxxxxxxx",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "referrer_url": {
                    "description": "The referring URL.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "resumed": {
                    "description": "For 'page' events: true when the page was restored from the back/forward cache.",
                    "type": [
                      "boolean",
                      "null"
                    ]
                  },
                  "source": {
                    "description": "For 'identify' events: where the identity was captured (url, form, manual, iframe).",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "title": {
                    "description": "For 'page' events: the document title.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "url": {
                    "description": "The URL where the event occurred.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "user": {
                    "description": "User identity and profile data.",
                    "properties": {
                      "anonymous_id": {
                        "description": "An anonymous identifier for the user.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "birthdate": {
                        "description": "Date of birth (YYYY-MM-DD).",
                        "example": "1990-01-15",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "city": {
                        "description": "City.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "country": {
                        "description": "Country.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "email": {
                        "description": "Email address.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "external_id": {
                        "description": "An external identifier for the user.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "first_name": {
                        "description": "First name.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "gender": {
                        "description": "Gender.",
                        "oneOf": [
                          {
                            "description": "Gender",
                            "enum": [
                              "male",
                              "female"
                            ],
                            "type": "string"
                          },
                          {
                            "type": "null"
                          }
                        ]
                      },
                      "last_name": {
                        "description": "Last name.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "linked_anonymous_id": {
                        "description": "A second anonymous identifier to link to this user (e.g. captured across an iframe boundary).",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "linked_wuid": {
                        "description": "A wuid from a linked frame, captured across an iframe boundary.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "member_id": {
                        "description": "The Whop member ID.",
                        "example": "mber_xxxxxxxxxxxxx",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "membership_id": {
                        "description": "The Whop membership ID.",
                        "example": "mem_xxxxxxxxxxxxxx",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "name": {
                        "description": "Full display name.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "phone": {
                        "description": "Phone number.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "postal_code": {
                        "description": "Postal code.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "state": {
                        "description": "State or region.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "user_id": {
                        "description": "The Whop user ID.",
                        "example": "user_xxxxxxxxxxxxx",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "username": {
                        "description": "Username.",
                        "type": [
                          "string",
                          "null"
                        ]
                      }
                    },
                    "required": [],
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "value": {
                    "description": "Monetary value associated with the event.",
                    "example": 6.9,
                    "type": [
                      "number",
                      "null"
                    ]
                  }
                },
                "required": [
                  "account_id",
                  "event_name"
                ],
                "type": "object"
              }
            }
          }
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "id": {
                      "type": "string"
                    }
                  },
                  "required": [
                    "id"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "event created"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "event:create"
            ]
          },
          {}
        ],
        "summary": "Create Event",
        "tags": [
          "Events"
        ]
      }
    },
    "/financial-activity": {
      "get": {
        "description": "Returns a paginated activity feed for one account or user, derived from ledger lines with typed resource and source objects for presentation. Pass exactly one of `account_id` (a `biz_` identifier) or `user_id` (a `user_` identifier). Filter by line type, currency, posted timestamp, or settlement date to reconcile a specific window. Pass `include_owned_accounts=true` with your own `user_id` to aggregate your personal ledger and the businesses you own into one feed; each row then carries the owning `account`.",
        "operationId": "list",
        "parameters": [
          {
            "description": "The owning account ID (a biz_ identifier). Provide this or user_id.",
            "in": "query",
            "name": "account_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The owning user ID (a user_ identifier). Provide this or account_id.",
            "in": "query",
            "name": "user_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "When true, aggregates the authenticated user's personal ledger with the businesses they own (owner role with balance read) into one feed. Requires user_id to be the authenticated user; cannot be combined with account_id or the settlement-date filters. Each returned row includes the owning `account`.",
            "in": "query",
            "name": "include_owned_accounts",
            "required": false,
            "schema": {
              "type": "boolean"
            }
          },
          {
            "description": "Optional ledger line categories to include. Some categories (for example `onchain_deposit`, which covers inbound crypto deposits such as MoonPay onramps) are only returned when explicitly requested here.",
            "explode": true,
            "in": "query",
            "name": "line_types",
            "required": false,
            "schema": {
              "items": {
                "type": "string"
              },
              "type": "array"
            },
            "style": "form"
          },
          {
            "description": "Optional currency code filter, for example `usd`.",
            "in": "query",
            "name": "currency",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Only include rows posted after this ISO 8601 timestamp.",
            "in": "query",
            "name": "posted_after",
            "required": false,
            "schema": {
              "format": "date-time",
              "type": "string"
            }
          },
          {
            "description": "Only include rows posted before this ISO 8601 timestamp.",
            "in": "query",
            "name": "posted_before",
            "required": false,
            "schema": {
              "format": "date-time",
              "type": "string"
            }
          },
          {
            "description": "Only include rows whose funds became withdrawable on or after this `YYYY-MM-DD` settlement date (UTC), distinct from posted_at. Requires currency.",
            "in": "query",
            "name": "available_after",
            "required": false,
            "schema": {
              "format": "date",
              "type": "string"
            }
          },
          {
            "description": "Only include rows whose funds became withdrawable on or before this `YYYY-MM-DD` settlement date (UTC). Set equal to available_after for a single day. Requires currency.",
            "in": "query",
            "name": "available_before",
            "required": false,
            "schema": {
              "format": "date",
              "type": "string"
            }
          },
          {
            "description": "Maximum number of rows to return.",
            "in": "query",
            "name": "limit",
            "required": false,
            "schema": {
              "default": 20,
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor returned by the previous page.",
            "in": "query",
            "name": "cursor",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "$ref": "#/components/schemas/LedgerActivity"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "has_next_page",
                        "end_cursor",
                        "has_previous_page",
                        "start_cursor"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "activities listed for a user"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "neither account_id nor user_id provided"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the required balance-read scope"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "no ledger for that owner is visible to the credential"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "company:balance:read"
            ]
          }
        ],
        "summary": "List Financial Activity",
        "tags": [
          "Ledgers"
        ]
      }
    },
    "/financial_reports": {
      "get": {
        "description": "Returns a financial report (balance activity, income statement, or balance summary) for the given ledger account over the requested date range, or a platform-wide report when account_id is `global` (internal admin access only). Dates are Unix timestamps in seconds (UTC). Use `in_currency` with `currency` to aggregate multi-currency activity into a single display currency via FX conversion.",
        "operationId": "report",
        "parameters": [
          {
            "description": "The owning account ID (a biz_ identifier), or `global` for a platform-wide report across all ledger accounts (requires internal admin access).",
            "in": "query",
            "name": "account_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The type of financial report to generate.",
            "in": "query",
            "name": "report_type",
            "required": true,
            "schema": {
              "enum": [
                "balance_activity",
                "income_statement",
                "balance_summary"
              ],
              "type": "string"
            }
          },
          {
            "description": "Filter rows to this currency, for example `usd`. Defaults to `usd` unless `in_currency` is provided.",
            "in": "query",
            "name": "currency",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Aggregate all activity into this display currency via FX conversion.",
            "in": "query",
            "name": "in_currency",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Start of the report window as a Unix timestamp (seconds, UTC). Required for platform-wide (global) reports.",
            "in": "query",
            "name": "from_date",
            "required": false,
            "schema": {
              "type": "integer"
            }
          },
          {
            "description": "End of the report window as a Unix timestamp (seconds, UTC). Required for platform-wide (global) reports.",
            "in": "query",
            "name": "to_date",
            "required": false,
            "schema": {
              "type": "integer"
            }
          },
          {
            "description": "Grouping granularity for report rows.",
            "in": "query",
            "name": "group_by",
            "required": false,
            "schema": {
              "default": "month",
              "enum": [
                "day",
                "week",
                "month"
              ],
              "type": "string"
            }
          },
          {
            "description": "IANA timezone (for example `America/New_York`) used to bucket report periods and to interpret calendar-day boundaries for balance snapshots. Defaults to UTC. from_date/to_date remain exact Unix instants regardless of this setting. Platform-wide (global) reports are UTC-only and reject other timezones.",
            "in": "query",
            "name": "timezone",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Platform-wide (global) reports only: when true, return cumulative balances as of to_date (all history, no lower bound) instead of activity within the period.",
            "in": "query",
            "name": "cumulative",
            "required": false,
            "schema": {
              "default": false,
              "type": "boolean"
            }
          },
          {
            "description": "Platform-wide (global) reports only: narrow the report to ledger lines on the ledger account owned by this account ID (a biz_ identifier). Ignored unless account_id is `global`.",
            "in": "query",
            "name": "scope_account_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "beginning_balance": {
                      "type": [
                        "number",
                        "null"
                      ]
                    },
                    "ending_balance": {
                      "type": [
                        "number",
                        "null"
                      ]
                    },
                    "fx_excluded_currencies": {
                      "items": {
                        "type": "string"
                      },
                      "type": "array"
                    },
                    "report_type": {
                      "type": "string"
                    },
                    "rows": {
                      "items": {
                        "properties": {
                          "account_ik_path": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "account_name": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "account_type": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "amount": {
                            "type": "number"
                          },
                          "grouping": {
                            "type": "string"
                          },
                          "line_category": {
                            "type": "string"
                          },
                          "line_count": {
                            "type": [
                              "integer",
                              "null"
                            ]
                          },
                          "period": {
                            "type": "string"
                          },
                          "profit_and_loss_section": {
                            "enum": [
                              "revenue",
                              "cost_of_revenue",
                              null
                            ],
                            "type": [
                              "string",
                              "null"
                            ]
                          }
                        },
                        "required": [
                          "period",
                          "grouping",
                          "line_category",
                          "amount"
                        ],
                        "type": "object"
                      },
                      "type": "array"
                    },
                    "total": {
                      "type": "number"
                    }
                  },
                  "required": [
                    "report_type",
                    "rows",
                    "total"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "financial report returned"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "invalid report type"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the required balance-read scope"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "no ledger for that account"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "company:balance:read"
            ]
          }
        ],
        "summary": "Get Financial Report",
        "tags": [
          "Ledgers"
        ]
      }
    },
    "/media/generate": {
      "post": {
        "description": "Starts an AI media generation job billed from the account's balance. Generation is asynchronous — poll `GET /media/{id}` until the asset is `ready`, then use `file.id` anywhere attachments are accepted.",
        "operationId": "generate",
        "parameters": [],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "account_id": {
                    "description": "Account ID, prefixed `biz_`. Defaults to the account the API key belongs to.",
                    "type": "string"
                  },
                  "duration_seconds": {
                    "description": "Video length in seconds. Video only; defaults to 5.",
                    "enum": [
                      5,
                      10,
                      15
                    ],
                    "type": "integer"
                  },
                  "prompt": {
                    "description": "What to generate. Up to 2,000 characters.",
                    "type": "string"
                  },
                  "reference_media": {
                    "description": "Optional reference image file IDs (`file_` prefixed), up to 4. For video, a single reference seeds the opening frame; multiple references guide subject and style instead.",
                    "items": {
                      "type": "string"
                    },
                    "type": "array"
                  },
                  "resolution": {
                    "description": "Video resolution. Video only; defaults to `1080p`. `1080p` is not supported by Seedance 2.0 Fast or Mini; `4k` is only supported by Seedance 2.0.",
                    "enum": [
                      "480p",
                      "720p",
                      "1080p",
                      "4k"
                    ],
                    "type": "string"
                  },
                  "type": {
                    "description": "The kind of media to generate.",
                    "enum": [
                      "video",
                      "image"
                    ],
                    "type": "string"
                  }
                },
                "required": [
                  "type",
                  "prompt"
                ],
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "201": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/MediaAsset"
                }
              }
            },
            "description": "Generation started."
          },
          "400": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "error": {
                      "properties": {
                        "message": {
                          "type": "string"
                        },
                        "type": {
                          "type": "string"
                        }
                      },
                      "required": [
                        "type",
                        "message"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "error"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "Invalid generation parameters."
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "Missing or invalid authentication."
          },
          "402": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "error": {
                      "properties": {
                        "deposit_url": {
                          "description": "Hosted page where the account can deposit funds before retrying.",
                          "type": "string"
                        },
                        "message": {
                          "type": "string"
                        },
                        "type": {
                          "enum": [
                            "payment_required"
                          ],
                          "type": "string"
                        }
                      },
                      "required": [
                        "type",
                        "message",
                        "deposit_url"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "error"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "Insufficient balance."
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "Missing the media:generate scope."
          }
        },
        "security": [
          {
            "bearerAuth": [
              "media:generate"
            ]
          }
        ],
        "summary": "Generate Media Asset",
        "tags": [
          "Media"
        ]
      }
    },
    "/media/{id}": {
      "get": {
        "description": "Retrieves a media asset by ID. Poll this while the asset is `processing`.",
        "operationId": "get",
        "parameters": [
          {
            "description": "Media asset ID, prefixed `media_`.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/MediaAsset"
                }
              }
            },
            "description": "Media asset retrieved."
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "Unknown media asset, including generation jobs not created through this API."
          }
        },
        "security": [
          {
            "bearerAuth": [
              "media:read"
            ]
          }
        ],
        "summary": "Retrieve Media Asset",
        "tags": [
          "Media"
        ]
      }
    },
    "/partners": {
      "post": {
        "description": "Enrolls the calling user in the Whop partner program, making their partner businesses eligible for earnings. Idempotent — enrolling again keeps the original enrollment time.",
        "operationId": "create",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "additionalProperties": false,
                  "properties": {
                    "referral_link": {
                      "description": "The caller's referral link — businesses that sign up through it are attributed to the caller.",
                      "type": "string"
                    },
                    "whop_partner_enabled_at": {
                      "description": "When the caller became a Whop partner.",
                      "format": "date-time",
                      "type": "string"
                    }
                  },
                  "required": [
                    "whop_partner_enabled_at",
                    "referral_link"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "enrolled"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the partner-create scope"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "partner:create"
            ]
          }
        ],
        "summary": "Enroll as a Whop partner",
        "tags": [
          "Partners"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ]
      }
    },
    "/partners/businesses": {
      "get": {
        "description": "Lists the businesses the authenticated user referred onto Whop, most recent first.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "Filter by referral status.",
            "in": "query",
            "name": "status",
            "required": false,
            "schema": {
              "enum": [
                "active",
                "removed"
              ],
              "type": "string"
            }
          },
          {
            "description": "When true, only businesses with pending or completed earnings paid to the caller.",
            "in": "query",
            "name": "has_earnings",
            "required": false,
            "schema": {
              "type": "boolean"
            }
          },
          {
            "description": "Number of partner businesses to return from the start of the window.",
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "default": 20,
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to fetch the page after (from page_info.end_cursor).",
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Number of partner businesses to return from the end of the window.",
            "in": "query",
            "name": "last",
            "required": false,
            "schema": {
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to fetch the page before (from page_info.start_cursor).",
            "in": "query",
            "name": "before",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The field to sort partner businesses by.",
            "in": "query",
            "name": "order",
            "required": false,
            "schema": {
              "default": "created_at",
              "enum": [
                "created_at",
                "referral_started_at",
                "referral_expires_at",
                "payout_percentage",
                "volume_usd",
                "earnings_usd"
              ],
              "type": "string"
            }
          },
          {
            "description": "Sort direction.",
            "in": "query",
            "name": "direction",
            "required": false,
            "schema": {
              "default": "desc",
              "enum": [
                "asc",
                "desc"
              ],
              "type": "string"
            }
          },
          {
            "description": "Only return partner businesses created before this timestamp.",
            "in": "query",
            "name": "created_before",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Only return partner businesses created after this timestamp.",
            "in": "query",
            "name": "created_after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter to referrals attributed to this user. For first-tier referrals, this is the referred account owner; for second-tier referrals, this is the partner you recruited.",
            "in": "query",
            "name": "referred_user_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter by the referred user's exact username. Ignored when `referred_user_id` is present.",
            "in": "query",
            "name": "referred_username",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter to only first-tier referrals or only second-tier referrals.",
            "in": "query",
            "name": "tier",
            "required": false,
            "schema": {
              "enum": [
                "first",
                "second"
              ],
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "properties": {
                          "account": {
                            "description": "Referred account.",
                            "properties": {
                              "id": {
                                "description": "Referred account ID.",
                                "type": "string"
                              },
                              "logo_url": {
                                "description": "Referred account logo URL.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "route": {
                                "description": "Referred account route.",
                                "type": "string"
                              },
                              "title": {
                                "description": "Referred account display name.",
                                "type": "string"
                              }
                            },
                            "required": [
                              "id",
                              "title",
                              "logo_url",
                              "route"
                            ],
                            "type": [
                              "object",
                              "null"
                            ]
                          },
                          "created_at": {
                            "description": "When the partner business was created.",
                            "format": "date-time",
                            "type": "string"
                          },
                          "earnings_usd": {
                            "properties": {
                              "completed": {
                                "description": "Commission already paid out, in USD.",
                                "type": "string"
                              },
                              "pending": {
                                "description": "Commission scheduled but not yet paid, in USD.",
                                "type": "string"
                              },
                              "total": {
                                "description": "Pending + completed commission, in USD.",
                                "type": "string"
                              }
                            },
                            "required": [
                              "total",
                              "pending",
                              "completed"
                            ],
                            "type": "object"
                          },
                          "first_tier_partner": {
                            "description": "The partner who referred the business owner onto Whop (first tier). Null if there is no active first-tier partner.",
                            "properties": {
                              "id": {
                                "description": "User ID, prefixed `user_`.",
                                "type": "string"
                              },
                              "name": {
                                "description": "The user's display name.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "profile_picture": {
                                "description": "The user's profile picture.",
                                "properties": {
                                  "url": {
                                    "description": "The user's profile picture URL.",
                                    "type": "string"
                                  }
                                },
                                "required": [
                                  "url"
                                ],
                                "type": "object"
                              },
                              "username": {
                                "description": "The user's unique username.",
                                "type": "string"
                              }
                            },
                            "required": [
                              "id",
                              "username",
                              "name",
                              "profile_picture"
                            ],
                            "type": [
                              "object",
                              "null"
                            ]
                          },
                          "id": {
                            "description": "Partner business ID.",
                            "type": "string"
                          },
                          "my_partner_tier": {
                            "description": "Which tier the caller earns on for this business: `first` (they referred the owner) or `second` (they referred the first-tier partner).",
                            "enum": [
                              "first",
                              "second"
                            ],
                            "type": "string"
                          },
                          "object": {
                            "enum": [
                              "partner_business"
                            ],
                            "type": "string"
                          },
                          "owner": {
                            "description": "The owner of the referred business.",
                            "properties": {
                              "id": {
                                "description": "User ID, prefixed `user_`.",
                                "type": "string"
                              },
                              "name": {
                                "description": "The user's display name.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "profile_picture": {
                                "description": "The user's profile picture.",
                                "properties": {
                                  "url": {
                                    "description": "The user's profile picture URL.",
                                    "type": "string"
                                  }
                                },
                                "required": [
                                  "url"
                                ],
                                "type": "object"
                              },
                              "username": {
                                "description": "The user's unique username.",
                                "type": "string"
                              }
                            },
                            "required": [
                              "id",
                              "username",
                              "name",
                              "profile_picture"
                            ],
                            "type": [
                              "object",
                              "null"
                            ]
                          },
                          "payout_percentage": {
                            "description": "Referrer's share of Whop gross profit, as a fraction (0.3 = 30%). Second-tier referrals earn a flat 0.1.",
                            "format": "float",
                            "type": "number"
                          },
                          "referral_expires_at": {
                            "description": "When the referral expires.",
                            "format": "date-time",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "referral_started_at": {
                            "description": "When the referral became active.",
                            "format": "date-time",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "status": {
                            "description": "Current referral status.",
                            "enum": [
                              "active",
                              "removed"
                            ],
                            "example": "active",
                            "type": "string"
                          },
                          "volume_usd": {
                            "properties": {
                              "attributed": {
                                "description": "Credited GMV (awaiting_settlement + settled); excludes canceled and reversed, in USD.",
                                "type": "string"
                              },
                              "awaiting_settlement": {
                                "description": "GMV awaiting settlement (commission not yet computed), in USD.",
                                "type": "string"
                              },
                              "settled": {
                                "description": "GMV of pending + completed payments, in USD.",
                                "type": "string"
                              }
                            },
                            "required": [
                              "attributed",
                              "awaiting_settlement",
                              "settled"
                            ],
                            "type": "object"
                          }
                        },
                        "required": [
                          "object",
                          "id",
                          "account",
                          "owner",
                          "first_tier_partner",
                          "my_partner_tier",
                          "status",
                          "payout_percentage",
                          "referral_started_at",
                          "referral_expires_at",
                          "created_at",
                          "volume_usd",
                          "earnings_usd"
                        ],
                        "type": "object"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "has_next_page",
                        "end_cursor",
                        "has_previous_page",
                        "start_cursor"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "partner businesses listed"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "invalid sort field"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the referral-read scope"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "partner:basic:read"
            ]
          }
        ],
        "summary": "List a partner's referred businesses",
        "tags": [
          "Partners"
        ]
      }
    },
    "/partners/businesses/{id}": {
      "get": {
        "description": "Retrieves a single referred business and its referral terms.",
        "operationId": "get",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "account": {
                      "description": "Referred account.",
                      "properties": {
                        "capabilities": {
                          "$ref": "#/components/schemas/AccountCapabilities",
                          "nullable": true
                        },
                        "id": {
                          "description": "Referred account ID.",
                          "type": "string"
                        },
                        "logo_url": {
                          "description": "Referred account logo URL.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "recommended_actions": {
                          "description": "Optional actions that unlock capabilities or grow the referred account.",
                          "items": {
                            "$ref": "#/components/schemas/AccountRecommendedAction"
                          },
                          "type": [
                            "array",
                            "null"
                          ]
                        },
                        "required_actions": {
                          "description": "Actions the referred account owner must take to unblock capabilities.",
                          "items": {
                            "$ref": "#/components/schemas/AccountRequiredAction"
                          },
                          "type": [
                            "array",
                            "null"
                          ]
                        },
                        "route": {
                          "description": "Referred account route.",
                          "type": "string"
                        },
                        "title": {
                          "description": "Referred account display name.",
                          "type": "string"
                        }
                      },
                      "required": [
                        "id",
                        "title",
                        "logo_url",
                        "route",
                        "capabilities",
                        "required_actions",
                        "recommended_actions"
                      ],
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "created_at": {
                      "description": "When the partner business was created.",
                      "format": "date-time",
                      "type": "string"
                    },
                    "earnings_usd": {
                      "properties": {
                        "completed": {
                          "description": "Commission already paid out, in USD.",
                          "type": "string"
                        },
                        "pending": {
                          "description": "Commission scheduled but not yet paid, in USD.",
                          "type": "string"
                        },
                        "total": {
                          "description": "Pending + completed commission, in USD.",
                          "type": "string"
                        }
                      },
                      "required": [
                        "total",
                        "pending",
                        "completed"
                      ],
                      "type": "object"
                    },
                    "first_tier_partner": {
                      "description": "The partner who referred the business owner onto Whop (first tier). Null if there is no active first-tier partner.",
                      "properties": {
                        "id": {
                          "description": "User ID, prefixed `user_`.",
                          "type": "string"
                        },
                        "name": {
                          "description": "The user's display name.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "profile_picture": {
                          "description": "The user's profile picture.",
                          "properties": {
                            "url": {
                              "description": "The user's profile picture URL.",
                              "type": "string"
                            }
                          },
                          "required": [
                            "url"
                          ],
                          "type": "object"
                        },
                        "username": {
                          "description": "The user's unique username.",
                          "type": "string"
                        }
                      },
                      "required": [
                        "id",
                        "username",
                        "name",
                        "profile_picture"
                      ],
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "id": {
                      "description": "Partner business ID.",
                      "type": "string"
                    },
                    "my_partner_tier": {
                      "description": "Which tier the caller earns on for this business: `first` (they referred the owner) or `second` (they referred the first-tier partner).",
                      "enum": [
                        "first",
                        "second"
                      ],
                      "type": "string"
                    },
                    "object": {
                      "enum": [
                        "partner_business"
                      ],
                      "type": "string"
                    },
                    "owner": {
                      "description": "The owner of the referred business.",
                      "properties": {
                        "id": {
                          "description": "User ID, prefixed `user_`.",
                          "type": "string"
                        },
                        "name": {
                          "description": "The user's display name.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "profile_picture": {
                          "description": "The user's profile picture.",
                          "properties": {
                            "url": {
                              "description": "The user's profile picture URL.",
                              "type": "string"
                            }
                          },
                          "required": [
                            "url"
                          ],
                          "type": "object"
                        },
                        "username": {
                          "description": "The user's unique username.",
                          "type": "string"
                        }
                      },
                      "required": [
                        "id",
                        "username",
                        "name",
                        "profile_picture"
                      ],
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "payout_percentage": {
                      "description": "Referrer's share of Whop gross profit, as a fraction (0.3 = 30%). Second-tier referrals earn a flat 0.1.",
                      "format": "float",
                      "type": "number"
                    },
                    "referral_expires_at": {
                      "description": "When the referral expires.",
                      "format": "date-time",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "referral_started_at": {
                      "description": "When the referral became active.",
                      "format": "date-time",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "status": {
                      "description": "Current referral status.",
                      "enum": [
                        "active",
                        "removed"
                      ],
                      "type": "string"
                    },
                    "volume_usd": {
                      "properties": {
                        "attributed": {
                          "description": "Credited GMV (awaiting_settlement + settled); excludes canceled and reversed, in USD.",
                          "type": "string"
                        },
                        "awaiting_settlement": {
                          "description": "GMV awaiting settlement (commission not yet computed), in USD.",
                          "type": "string"
                        },
                        "settled": {
                          "description": "GMV of pending + completed payments, in USD.",
                          "type": "string"
                        }
                      },
                      "required": [
                        "attributed",
                        "awaiting_settlement",
                        "settled"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "object",
                    "id",
                    "account",
                    "owner",
                    "first_tier_partner",
                    "my_partner_tier",
                    "status",
                    "payout_percentage",
                    "referral_started_at",
                    "referral_expires_at",
                    "created_at",
                    "volume_usd",
                    "earnings_usd"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "partner business retrieved"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "no such partner business is visible to the credential"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "partner:basic:read"
            ]
          }
        ],
        "summary": "Retrieve a referred business",
        "tags": [
          "Partners"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The partner business ID (a coma_ identifier).",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ]
      }
    },
    "/partners/businesses/{id}/earnings": {
      "get": {
        "description": "Lists the earnings Whop pays out for one referred business's activity, most recent first.",
        "operationId": "earnings",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The partner business ID (a coma_ identifier).",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter by earning status.",
            "in": "query",
            "name": "status",
            "required": false,
            "schema": {
              "enum": [
                "awaiting_settlement",
                "pending",
                "completed",
                "canceled",
                "reversed"
              ],
              "type": "string"
            }
          },
          {
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "default": 20,
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "in": "query",
            "name": "last",
            "required": false,
            "schema": {
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "in": "query",
            "name": "before",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The field to sort earnings by.",
            "in": "query",
            "name": "order",
            "required": false,
            "schema": {
              "default": "created_at",
              "enum": [
                "created_at",
                "commission_amount",
                "transaction_amount",
                "payout_at"
              ],
              "type": "string"
            }
          },
          {
            "description": "Sort direction.",
            "in": "query",
            "name": "direction",
            "required": false,
            "schema": {
              "default": "desc",
              "enum": [
                "asc",
                "desc"
              ],
              "type": "string"
            }
          },
          {
            "description": "Only return earnings created before this timestamp.",
            "in": "query",
            "name": "created_before",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Only return earnings created after this timestamp.",
            "in": "query",
            "name": "created_after",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "properties": {
                          "account": {
                            "description": "Referred account.",
                            "properties": {
                              "id": {
                                "description": "Referred account ID.",
                                "type": "string"
                              },
                              "logo_url": {
                                "description": "Referred account logo URL.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "route": {
                                "description": "Referred account route.",
                                "type": "string"
                              },
                              "title": {
                                "description": "Referred account display name.",
                                "type": "string"
                              }
                            },
                            "required": [
                              "id",
                              "title",
                              "logo_url",
                              "route"
                            ],
                            "type": [
                              "object",
                              "null"
                            ]
                          },
                          "cancelation_reason": {
                            "description": "Why the earning was canceled or reversed, if applicable.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "commission_amount_usd": {
                            "description": "What the referrer earns, in USD. Null until the earning settles.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "created_at": {
                            "format": "date-time",
                            "type": "string"
                          },
                          "financial_activity": {
                            "description": "Income and cost lines behind this earning's commission. Null for earnings settled before this data was recorded.",
                            "items": {
                              "properties": {
                                "amount": {
                                  "description": "Line amount in its native currency.",
                                  "type": "string"
                                },
                                "amount_usd": {
                                  "description": "Line amount in USD.",
                                  "type": "string"
                                },
                                "category": {
                                  "description": "Fee or cost category of the line.",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "created_at": {
                                  "format": "date-time",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "currency": {
                                  "description": "Currency of the native amount.",
                                  "type": "string"
                                },
                                "type": {
                                  "description": "Whether the line is income Whop collected or a cost Whop paid.",
                                  "enum": [
                                    "income",
                                    "expense"
                                  ],
                                  "example": "income",
                                  "type": "string"
                                }
                              },
                              "required": [
                                "type",
                                "category",
                                "currency",
                                "amount",
                                "amount_usd",
                                "created_at"
                              ],
                              "type": "object"
                            },
                            "type": [
                              "array",
                              "null"
                            ]
                          },
                          "id": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "income_source": {
                            "description": "Which income source the commission is on: product-sales gross profit or Whop Ads spend billed to the business.",
                            "enum": [
                              "sales",
                              "ad_spend"
                            ],
                            "example": "sales",
                            "type": "string"
                          },
                          "object": {
                            "enum": [
                              "partner_business_earning"
                            ],
                            "type": "string"
                          },
                          "payout_at": {
                            "format": "date-time",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "payout_percentage": {
                            "description": "The referrer's share of Whop's gross profit, as a fraction (0.3 = 30%). Null until the earning settles.",
                            "format": "float",
                            "type": [
                              "number",
                              "null"
                            ]
                          },
                          "product": {
                            "properties": {
                              "id": {
                                "type": "string"
                              },
                              "route": {
                                "type": "string"
                              },
                              "title": {
                                "type": "string"
                              }
                            },
                            "required": [
                              "id",
                              "title",
                              "route"
                            ],
                            "type": [
                              "object",
                              "null"
                            ]
                          },
                          "resource": {
                            "anyOf": [
                              {
                                "properties": {
                                  "alternative_payment_method": {
                                    "properties": {
                                      "image_url": {
                                        "type": [
                                          "string",
                                          "null"
                                        ]
                                      },
                                      "name": {
                                        "type": "string"
                                      }
                                    },
                                    "required": [
                                      "name",
                                      "image_url"
                                    ],
                                    "type": [
                                      "object",
                                      "null"
                                    ]
                                  },
                                  "brand": {
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  },
                                  "created_at": {
                                    "format": "date-time",
                                    "type": "string"
                                  },
                                  "currency": {
                                    "type": "string"
                                  },
                                  "id": {
                                    "type": "string"
                                  },
                                  "last4": {
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  },
                                  "object": {
                                    "enum": [
                                      "receipt"
                                    ],
                                    "type": "string"
                                  },
                                  "payment_method_type": {
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  },
                                  "processor": {
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  }
                                },
                                "required": [
                                  "object",
                                  "id",
                                  "currency",
                                  "payment_method_type",
                                  "processor",
                                  "brand",
                                  "last4",
                                  "created_at",
                                  "alternative_payment_method"
                                ],
                                "type": [
                                  "object",
                                  "null"
                                ]
                              },
                              {
                                "type": "null"
                              }
                            ],
                            "description": "The resource that generated the affiliate earning."
                          },
                          "second_tier": {
                            "description": "Whether this earning is a second-tier (grandparent) commission.",
                            "type": "boolean"
                          },
                          "status": {
                            "description": "Current status of the earning.",
                            "enum": [
                              "awaiting_settlement",
                              "pending",
                              "completed",
                              "canceled",
                              "reversed"
                            ],
                            "example": "pending",
                            "type": "string"
                          },
                          "transaction_amount_usd": {
                            "description": "The sale amount the commission is calculated from, in USD.",
                            "type": "string"
                          }
                        },
                        "required": [
                          "object",
                          "id",
                          "account",
                          "product",
                          "status",
                          "second_tier",
                          "income_source",
                          "transaction_amount_usd",
                          "payout_percentage",
                          "commission_amount_usd",
                          "financial_activity",
                          "cancelation_reason",
                          "created_at",
                          "payout_at",
                          "resource"
                        ],
                        "type": "object"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "has_next_page",
                        "end_cursor",
                        "has_previous_page",
                        "start_cursor"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "earnings listed"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "invalid sort direction"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "partner:basic:read"
            ]
          }
        ],
        "summary": "List a partner's referred business earnings",
        "tags": [
          "Partners"
        ]
      }
    },
    "/partners/leaderboard": {
      "get": {
        "description": "Ranks referrers by partner business earnings — all-time by default, or over the current day, month, year, or trailing 30 days — and includes the caller's own standing.",
        "operationId": "leaderboard",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "Time window for the rankings. `day`, `month`, and `year` count earnings since the start of the current calendar day, month, or year; `last_30_days` counts earnings over the trailing 30 days; `all_time` ranks lifetime earnings.",
            "in": "query",
            "name": "period",
            "required": false,
            "schema": {
              "default": "all_time",
              "enum": [
                "day",
                "month",
                "year",
                "last_30_days",
                "all_time"
              ],
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "leaders": {
                      "description": "The top referrers by total earnings, best first.",
                      "items": {
                        "properties": {
                          "first_referral_started_at": {
                            "description": "When the referrer's earliest partner business became active.",
                            "format": "date-time",
                            "type": "string"
                          },
                          "rank": {
                            "description": "1-based leaderboard position.",
                            "type": "integer"
                          },
                          "total_earnings_usd": {
                            "description": "The referrer's pending + completed earnings across all referred businesses, in USD.",
                            "type": "string"
                          },
                          "total_volume_usd": {
                            "description": "Credited GMV across all the referrer's referred businesses, in USD.",
                            "type": "string"
                          },
                          "user": {
                            "description": "The ranked referrer. Identity fields (id, name, username, profile_picture) are returned only on the caller's own entry; other referrers expose coarse location only.",
                            "properties": {
                              "city": {
                                "description": "The city where the referrer is located, derived from their IP address. Null if location sharing is disabled.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "country": {
                                "description": "The country where the referrer is located, derived from their IP address. Null if location sharing is disabled.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "id": {
                                "description": "User ID, prefixed `user_`. Present only on the caller's own entry.",
                                "type": "string"
                              },
                              "name": {
                                "description": "The user's display name. Present only on the caller's own entry.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "profile_picture": {
                                "description": "The user's profile picture. Present only on the caller's own entry.",
                                "properties": {
                                  "url": {
                                    "description": "The user's profile picture URL.",
                                    "type": "string"
                                  }
                                },
                                "required": [
                                  "url"
                                ],
                                "type": "object"
                              },
                              "username": {
                                "description": "The user's unique username. Present only on the caller's own entry.",
                                "type": "string"
                              }
                            },
                            "required": [
                              "country",
                              "city"
                            ],
                            "type": [
                              "object",
                              "null"
                            ]
                          }
                        },
                        "required": [
                          "rank",
                          "total_earnings_usd",
                          "total_volume_usd",
                          "first_referral_started_at",
                          "user"
                        ],
                        "type": "object"
                      },
                      "maxItems": 10,
                      "type": "array"
                    },
                    "me": {
                      "description": "The caller's own standing; null when the caller has no referral earnings.",
                      "properties": {
                        "first_referral_started_at": {
                          "description": "When the referrer's earliest partner business became active.",
                          "format": "date-time",
                          "type": "string"
                        },
                        "rank": {
                          "description": "1-based leaderboard position.",
                          "type": "integer"
                        },
                        "total_earnings_usd": {
                          "description": "The referrer's pending + completed earnings across all referred businesses, in USD.",
                          "type": "string"
                        },
                        "total_volume_usd": {
                          "description": "Credited GMV across all the referrer's referred businesses, in USD.",
                          "type": "string"
                        },
                        "user": {
                          "description": "The ranked referrer. Identity fields (id, name, username, profile_picture) are returned only on the caller's own entry; other referrers expose coarse location only.",
                          "properties": {
                            "city": {
                              "description": "The city where the referrer is located, derived from their IP address. Null if location sharing is disabled.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "country": {
                              "description": "The country where the referrer is located, derived from their IP address. Null if location sharing is disabled.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "id": {
                              "description": "User ID, prefixed `user_`. Present only on the caller's own entry.",
                              "type": "string"
                            },
                            "name": {
                              "description": "The user's display name. Present only on the caller's own entry.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "profile_picture": {
                              "description": "The user's profile picture. Present only on the caller's own entry.",
                              "properties": {
                                "url": {
                                  "description": "The user's profile picture URL.",
                                  "type": "string"
                                }
                              },
                              "required": [
                                "url"
                              ],
                              "type": "object"
                            },
                            "username": {
                              "description": "The user's unique username. Present only on the caller's own entry.",
                              "type": "string"
                            }
                          },
                          "required": [
                            "country",
                            "city"
                          ],
                          "type": [
                            "object",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "rank",
                        "total_earnings_usd",
                        "total_volume_usd",
                        "first_referral_started_at",
                        "user"
                      ],
                      "type": [
                        "object",
                        "null"
                      ]
                    }
                  },
                  "required": [
                    "leaders",
                    "me"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "caller has no referral earnings"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "invalid period"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the referral-read scope"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "partner:basic:read"
            ]
          }
        ],
        "summary": "Retrieve the partner leaderboard",
        "tags": [
          "Partners"
        ]
      }
    },
    "/partners/referred_users": {
      "get": {
        "description": "Lists the users the caller referred onto Whop (newest first), each with the second-tier earnings the caller has made from that user's businesses.",
        "operationId": "referred_users",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "When true, only referred users who brought at least one business onto Whop.",
            "in": "query",
            "name": "has_businesses",
            "required": false,
            "schema": {
              "type": "boolean"
            }
          },
          {
            "description": "When true, only referred users with at least one business that has generated earnings.",
            "in": "query",
            "name": "has_earning_businesses",
            "required": false,
            "schema": {
              "type": "boolean"
            }
          },
          {
            "description": "Number of referred users to return from the start of the window.",
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "default": 20,
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to fetch the page after (from page_info.end_cursor).",
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Number of referred users to return from the end of the window.",
            "in": "query",
            "name": "last",
            "required": false,
            "schema": {
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to fetch the page before (from page_info.start_cursor).",
            "in": "query",
            "name": "before",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "properties": {
                          "total_earnings_usd": {
                            "type": "string"
                          },
                          "total_volume_usd": {
                            "type": "string"
                          },
                          "user": {
                            "properties": {
                              "city": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "country": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "id": {
                                "type": "string"
                              },
                              "name": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "profile_picture": {
                                "properties": {
                                  "url": {
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  }
                                },
                                "type": "object"
                              },
                              "username": {
                                "type": "string"
                              }
                            },
                            "required": [
                              "id",
                              "username"
                            ],
                            "type": "object"
                          }
                        },
                        "required": [
                          "total_earnings_usd",
                          "total_volume_usd",
                          "user"
                        ],
                        "type": "object"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "has_next_page",
                        "end_cursor",
                        "has_previous_page",
                        "start_cursor"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "referred users listed"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "partner:basic:read"
            ]
          }
        ],
        "summary": "List the users the caller referred",
        "tags": [
          "Partners"
        ]
      }
    },
    "/payouts": {
      "get": {
        "description": "Lists payouts (withdrawal requests) for an account or user, most recent first. Pass exactly one of account_id (a biz_ identifier) or user_id (a user_ identifier). The saved payout method on each payout additionally requires the payout:destination:read scope and is null without it.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The owning account ID (a biz_ identifier). Provide this or user_id.",
            "in": "query",
            "name": "account_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The owning user ID (a user_ identifier). Provide this or account_id.",
            "in": "query",
            "name": "user_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Optional currency code filter, for example `usd`.",
            "in": "query",
            "name": "currency",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Number of payouts to return from the start of the window.",
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "default": 20,
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to fetch the page after (from page_info.end_cursor).",
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Number of payouts to return from the end of the window.",
            "in": "query",
            "name": "last",
            "required": false,
            "schema": {
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to fetch the page before (from page_info.start_cursor).",
            "in": "query",
            "name": "before",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "properties": {
                          "amount": {
                            "description": "The payout amount in whole currency units.",
                            "format": "float",
                            "type": "number"
                          },
                          "created_at": {
                            "description": "When the payout was created.",
                            "format": "date-time",
                            "type": "string"
                          },
                          "currency": {
                            "description": "Payout currency.",
                            "type": "string"
                          },
                          "estimated_arrival": {
                            "description": "Estimated time the funds become available in the destination account.",
                            "format": "date-time",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "fee_amount": {
                            "description": "The fee charged for the payout, in the payout currency.",
                            "format": "float",
                            "type": "number"
                          },
                          "id": {
                            "description": "Payout ID.",
                            "type": "string"
                          },
                          "object": {
                            "enum": [
                              "payout"
                            ],
                            "type": "string"
                          },
                          "payer_name": {
                            "description": "Name of the entity processing the payout.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "payout_request_id": {
                            "description": "The ID returned by POST /payouts when this payout was requested. Match it to the settled payout in GET /payouts. Null for payouts not created by POST /payouts.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "payout_token": {
                            "description": "The saved payout method used. Requires payout:destination:read; null without it.",
                            "properties": {
                              "nickname": {
                                "description": "Saved payout method nickname.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "payout_destination": {
                                "description": "Payout destination display details.",
                                "properties": {
                                  "icon_url": {
                                    "description": "Payout destination icon URL.",
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  },
                                  "payer_name": {
                                    "description": "Payout destination display name.",
                                    "type": [
                                      "string",
                                      "null"
                                    ]
                                  }
                                },
                                "required": [
                                  "payer_name",
                                  "icon_url"
                                ],
                                "type": [
                                  "object",
                                  "null"
                                ]
                              }
                            },
                            "required": [
                              "nickname",
                              "payout_destination"
                            ],
                            "type": [
                              "object",
                              "null"
                            ]
                          },
                          "speed": {
                            "description": "Payout delivery speed.",
                            "enum": [
                              "standard",
                              "instant"
                            ],
                            "type": "string"
                          },
                          "status": {
                            "description": "Current payout status.",
                            "enum": [
                              "requested",
                              "awaiting_payment",
                              "in_transit",
                              "completed",
                              "failed",
                              "canceled",
                              "denied"
                            ],
                            "type": "string"
                          }
                        },
                        "required": [
                          "object",
                          "id",
                          "payout_request_id",
                          "status",
                          "amount",
                          "currency",
                          "fee_amount",
                          "speed",
                          "created_at",
                          "estimated_arrival",
                          "payer_name",
                          "payout_token"
                        ],
                        "type": "object"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "has_next_page",
                        "end_cursor",
                        "has_previous_page",
                        "start_cursor"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "payout method nulled without the destination-read scope"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "neither account_id nor user_id provided"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the withdrawal-read scope"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "no ledger for that owner is visible to the credential"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "payout:withdrawal:read"
            ]
          }
        ],
        "summary": "List Payouts",
        "tags": [
          "Payouts"
        ]
      },
      "post": {
        "description": "Creates a payout from a stablecoin account to a saved payout method. The account's funds move from its stablecoin balance to an external bank account, wallet, or crypto address. Accounts that pay out from a fiat balance use POST /withdrawals. Requires the payouts API to be enabled for the account; contact support to enable it. The payout settles asynchronously; poll GET /payouts for the entry whose payout_request_id matches this payout's id.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "account_id": {
                    "description": "The account to pay out from (a biz_ identifier).",
                    "type": "string"
                  },
                  "amount": {
                    "description": "The amount to pay out in the specified currency.",
                    "format": "float",
                    "type": "number"
                  },
                  "currency": {
                    "description": "The payout currency. Defaults to usd.",
                    "type": "string"
                  },
                  "idempotency_key": {
                    "description": "A client-generated key that makes retries safe. Retrying with the same key returns the original payout instead of creating a second one.",
                    "type": "string"
                  },
                  "payout_method_id": {
                    "description": "The saved payout method to deliver to (a potk_ identifier).",
                    "type": "string"
                  }
                },
                "required": [
                  "account_id",
                  "amount",
                  "payout_method_id"
                ],
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "201": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "amount": {
                      "description": "The payout amount in whole currency units.",
                      "format": "float",
                      "type": "number"
                    },
                    "created_at": {
                      "description": "When the payout was created.",
                      "format": "date-time",
                      "type": "string"
                    },
                    "currency": {
                      "description": "Payout currency.",
                      "type": "string"
                    },
                    "estimated_arrival": {
                      "description": "Estimated time the funds become available in the destination account. Null until the payout settles.",
                      "format": "date-time",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "fee_amount": {
                      "description": "The fee charged for the payout, in the payout currency.",
                      "format": "float",
                      "type": "number"
                    },
                    "id": {
                      "description": "Payout ID.",
                      "type": "string"
                    },
                    "object": {
                      "enum": [
                        "payout"
                      ],
                      "type": "string"
                    },
                    "payer_name": {
                      "description": "Name of the entity processing the payout. Null until the payout settles.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "payout_token": {
                      "description": "The saved payout method used. Requires payout:destination:read; null without it.",
                      "properties": {
                        "nickname": {
                          "description": "Saved payout method nickname.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "payout_destination": {
                          "description": "Payout destination display details.",
                          "properties": {
                            "icon_url": {
                              "description": "Payout destination icon URL.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "payer_name": {
                              "description": "Payout destination display name.",
                              "type": [
                                "string",
                                "null"
                              ]
                            }
                          },
                          "required": [
                            "payer_name",
                            "icon_url"
                          ],
                          "type": [
                            "object",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "nickname",
                        "payout_destination"
                      ],
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "speed": {
                      "description": "Payout delivery speed.",
                      "enum": [
                        "standard",
                        "instant"
                      ],
                      "type": "string"
                    },
                    "status": {
                      "description": "Current payout status, in the same vocabulary as GET /payouts.",
                      "enum": [
                        "requested",
                        "in_transit",
                        "denied",
                        "completed",
                        "failed",
                        "canceled"
                      ],
                      "type": "string"
                    }
                  },
                  "required": [
                    "object",
                    "id",
                    "status",
                    "amount",
                    "currency",
                    "fee_amount",
                    "speed",
                    "created_at",
                    "estimated_arrival",
                    "payer_name",
                    "payout_token"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "payout created"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "amount is not a number"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the withdraw-funds scope"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "payout method not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "payout:withdraw_funds"
            ]
          }
        ],
        "summary": "Create Payout",
        "tags": [
          "Payouts"
        ]
      }
    },
    "/payouts/methods": {
      "get": {
        "description": "Lists the saved payout methods (bank accounts, digital wallets, crypto addresses) that an account or user can withdraw to, most recently added first. Pass exactly one of account_id (a biz_ identifier) or user_id (a user_ identifier). Pass an amount to additionally get a fee and delivery quote per method for withdrawing that amount.",
        "operationId": "methods",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The owning account ID (a biz_ identifier). Provide this or user_id.",
            "in": "query",
            "name": "account_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The owning user ID (a user_ identifier). Provide this or account_id.",
            "in": "query",
            "name": "user_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Optional status filter. `created` means saved but unused, `active` means a payout through it succeeded, `broken` means the last payout failed and the method needs fixing.",
            "in": "query",
            "name": "status",
            "required": false,
            "schema": {
              "enum": [
                "created",
                "active",
                "broken"
              ],
              "type": "string"
            }
          },
          {
            "description": "Optional withdrawal amount in whole currency units, for example `250.00`. When provided, each method includes a quote with the estimated fee, amount received, and delivery date for that amount.",
            "in": "query",
            "name": "amount",
            "required": false,
            "schema": {
              "type": "number"
            }
          },
          {
            "description": "Currency code of the amount, for example `usd`. Only meaningful with amount.",
            "in": "query",
            "name": "currency",
            "required": false,
            "schema": {
              "default": "usd",
              "type": "string"
            }
          },
          {
            "description": "When true, the response also carries available_destinations — payout rails the account could add as a new payout method, with per-currency quotes when an amount is provided.",
            "in": "query",
            "name": "include_available",
            "required": false,
            "schema": {
              "default": false,
              "type": "boolean"
            }
          },
          {
            "description": "Narrows available_destinations to this one destination (a pd_ identifier from a previous listing) and includes its required_fields — the values to collect to add it as a payout method. Implies include_available.",
            "in": "query",
            "name": "destination_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Currency the destination would deliver payouts in. Only meaningful with destination_id; required fields vary by destination currency.",
            "in": "query",
            "name": "destination_currency",
            "required": false,
            "schema": {
              "default": "usd",
              "type": "string"
            }
          },
          {
            "description": "Number of payout methods to return from the start of the window. Capped at 25 when an amount is provided.",
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "default": 20,
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to fetch the page after (from page_info.end_cursor).",
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Number of payout methods to return from the end of the window.",
            "in": "query",
            "name": "last",
            "required": false,
            "schema": {
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to fetch the page before (from page_info.start_cursor).",
            "in": "query",
            "name": "before",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "available_destinations": {
                      "description": "Payout rails the account could add as a new payout method. Only present when include_available is true; empty when the account has no payout setup yet.",
                      "items": {
                        "properties": {
                          "delivery_type": {
                            "description": "How funds are delivered, for example `bank_deposit`.",
                            "type": "string"
                          },
                          "icon_url": {
                            "description": "Payout destination icon URL.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "id": {
                            "description": "Payout destination ID.",
                            "type": "string"
                          },
                          "name": {
                            "description": "Payout destination display name.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "object": {
                            "enum": [
                              "payout_destination"
                            ],
                            "type": "string"
                          },
                          "quotes": {
                            "description": "Fee and delivery estimates for withdrawing the requested amount through this destination, one per destination currency. Null unless an amount was provided.",
                            "items": {
                              "properties": {
                                "amount": {
                                  "description": "The withdrawal amount the quote is for.",
                                  "format": "float",
                                  "type": "number"
                                },
                                "currency": {
                                  "description": "Currency of the quoted amount.",
                                  "type": "string"
                                },
                                "destination_currency": {
                                  "description": "Currency the funds are delivered in.",
                                  "type": "string"
                                },
                                "exchange_rate": {
                                  "description": "Exchange rate from the withdrawal currency to the destination currency.",
                                  "format": "float",
                                  "type": "number"
                                },
                                "instant": {
                                  "description": "Instant-delivery estimate. Null if unsupported, unavailable for the account, or the amount does not cover the fee.",
                                  "properties": {
                                    "estimated_arrival": {
                                      "format": "date-time",
                                      "type": "string"
                                    },
                                    "fee": {
                                      "format": "float",
                                      "type": "number"
                                    },
                                    "total_received": {
                                      "format": "float",
                                      "type": "number"
                                    }
                                  },
                                  "required": [
                                    "fee",
                                    "total_received",
                                    "estimated_arrival"
                                  ],
                                  "type": [
                                    "object",
                                    "null"
                                  ]
                                },
                                "max_limit": {
                                  "description": "Maximum withdrawal amount, in the withdrawal currency.",
                                  "format": "float",
                                  "type": [
                                    "number",
                                    "null"
                                  ]
                                },
                                "min_limit": {
                                  "description": "Minimum withdrawal amount, in the withdrawal currency.",
                                  "format": "float",
                                  "type": "number"
                                },
                                "standard": {
                                  "description": "Standard-delivery estimate. Null if unsupported or the amount does not cover the fee.",
                                  "properties": {
                                    "estimated_arrival": {
                                      "format": "date-time",
                                      "type": "string"
                                    },
                                    "fee": {
                                      "format": "float",
                                      "type": "number"
                                    },
                                    "total_received": {
                                      "format": "float",
                                      "type": "number"
                                    }
                                  },
                                  "required": [
                                    "fee",
                                    "total_received",
                                    "estimated_arrival"
                                  ],
                                  "type": [
                                    "object",
                                    "null"
                                  ]
                                }
                              },
                              "required": [
                                "destination_currency",
                                "amount",
                                "currency",
                                "exchange_rate",
                                "min_limit",
                                "max_limit",
                                "standard",
                                "instant"
                              ],
                              "type": "object"
                            },
                            "type": [
                              "array",
                              "null"
                            ]
                          },
                          "required_fields": {
                            "description": "The fields to collect to add this destination as a payout method. Present only when the request narrows to one destination with destination_id; null otherwise.",
                            "items": {
                              "properties": {
                                "id": {
                                  "description": "Field ID, echoed back as the field key when creating the payout method.",
                                  "type": "string"
                                },
                                "input_type": {
                                  "description": "How to collect the value: `text`, `options`, or `date`.",
                                  "type": "string"
                                },
                                "label": {
                                  "description": "Human-readable field name.",
                                  "type": "string"
                                },
                                "object": {
                                  "enum": [
                                    "required_field"
                                  ],
                                  "type": "string"
                                },
                                "options": {
                                  "description": "Allowed values, for options fields.",
                                  "items": {
                                    "type": "string"
                                  },
                                  "type": [
                                    "array",
                                    "null"
                                  ]
                                },
                                "placeholder": {
                                  "description": "Example value.",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "required": {
                                  "description": "Whether the field must be provided.",
                                  "type": "boolean"
                                },
                                "sensitive": {
                                  "description": "Whether the value is vaulted in transit and never stored raw, such as a bank account number.",
                                  "type": "boolean"
                                },
                                "type": {
                                  "description": "Semantic field type, for example `bank_account_number` or `swift`.",
                                  "type": "string"
                                },
                                "validation": {
                                  "description": "Regex the value must match. Null for options fields.",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                }
                              },
                              "required": [
                                "object",
                                "id",
                                "label",
                                "type",
                                "input_type",
                                "required",
                                "validation",
                                "options",
                                "placeholder",
                                "sensitive"
                              ],
                              "type": "object"
                            },
                            "type": [
                              "array",
                              "null"
                            ]
                          },
                          "supports_instant_delivery": {
                            "type": "boolean"
                          },
                          "supports_standard_delivery": {
                            "type": "boolean"
                          }
                        },
                        "required": [
                          "object",
                          "id",
                          "name",
                          "icon_url",
                          "delivery_type",
                          "supports_standard_delivery",
                          "supports_instant_delivery",
                          "quotes",
                          "required_fields"
                        ],
                        "type": "object"
                      },
                      "type": "array"
                    },
                    "data": {
                      "items": {
                        "properties": {
                          "account_reference": {
                            "description": "Masked identifier for the destination, such as the last four digits of a bank account.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "created_at": {
                            "description": "When the payout method was added.",
                            "format": "date-time",
                            "type": "string"
                          },
                          "destination_currency": {
                            "description": "Currency payouts are delivered in for this method.",
                            "type": "string"
                          },
                          "id": {
                            "description": "Payout method ID.",
                            "type": "string"
                          },
                          "institution_name": {
                            "description": "Name of the bank or institution receiving payouts.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "is_default": {
                            "description": "Whether this is the default payout method for the account.",
                            "type": "boolean"
                          },
                          "nickname": {
                            "description": "User-defined label for the payout method.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "object": {
                            "enum": [
                              "payout_method"
                            ],
                            "type": "string"
                          },
                          "payer_name": {
                            "description": "Display name of the payout rail, such as `ACH Bank Deposit`.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "payout_destination": {
                            "description": "The payout rail this method delivers through.",
                            "properties": {
                              "delivery_type": {
                                "description": "How funds are delivered, for example `bank_deposit`.",
                                "type": "string"
                              },
                              "icon_url": {
                                "description": "Payout destination icon URL.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "name": {
                                "description": "Payout destination display name.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "supports_instant_delivery": {
                                "type": "boolean"
                              },
                              "supports_standard_delivery": {
                                "type": "boolean"
                              }
                            },
                            "required": [
                              "name",
                              "icon_url",
                              "delivery_type",
                              "supports_standard_delivery",
                              "supports_instant_delivery"
                            ],
                            "type": [
                              "object",
                              "null"
                            ]
                          },
                          "quote": {
                            "description": "Fee and delivery estimate for withdrawing the requested amount through this method. Null unless an amount was provided, or when the estimate is unavailable.",
                            "properties": {
                              "amount": {
                                "description": "The withdrawal amount the quote is for.",
                                "format": "float",
                                "type": "number"
                              },
                              "currency": {
                                "description": "Currency of the quoted amount.",
                                "type": "string"
                              },
                              "exchange_rate": {
                                "description": "Exchange rate from the withdrawal currency to the destination currency.",
                                "format": "float",
                                "type": "number"
                              },
                              "instant": {
                                "description": "Instant-delivery estimate. Null if the method does not support instant delivery, instant delivery is unavailable for the account, or the amount does not cover the fee.",
                                "properties": {
                                  "estimated_arrival": {
                                    "description": "Estimated time the funds become available.",
                                    "format": "date-time",
                                    "type": "string"
                                  },
                                  "fee": {
                                    "description": "Total fee charged, in the withdrawal currency.",
                                    "format": "float",
                                    "type": "number"
                                  },
                                  "total_received": {
                                    "description": "Amount delivered after fees, in the withdrawal currency.",
                                    "format": "float",
                                    "type": "number"
                                  }
                                },
                                "required": [
                                  "fee",
                                  "total_received",
                                  "estimated_arrival"
                                ],
                                "type": [
                                  "object",
                                  "null"
                                ]
                              },
                              "max_limit": {
                                "description": "Maximum withdrawal amount for this method, in the withdrawal currency.",
                                "format": "float",
                                "type": [
                                  "number",
                                  "null"
                                ]
                              },
                              "min_limit": {
                                "description": "Minimum withdrawal amount for this method, in the withdrawal currency.",
                                "format": "float",
                                "type": "number"
                              },
                              "standard": {
                                "description": "Standard-delivery estimate. Null if the method does not support standard delivery, or the amount does not cover the fee.",
                                "properties": {
                                  "estimated_arrival": {
                                    "description": "Estimated time the funds become available.",
                                    "format": "date-time",
                                    "type": "string"
                                  },
                                  "fee": {
                                    "description": "Total fee charged, in the withdrawal currency.",
                                    "format": "float",
                                    "type": "number"
                                  },
                                  "total_received": {
                                    "description": "Amount delivered after fees, in the withdrawal currency.",
                                    "format": "float",
                                    "type": "number"
                                  }
                                },
                                "required": [
                                  "fee",
                                  "total_received",
                                  "estimated_arrival"
                                ],
                                "type": [
                                  "object",
                                  "null"
                                ]
                              }
                            },
                            "required": [
                              "amount",
                              "currency",
                              "exchange_rate",
                              "min_limit",
                              "max_limit",
                              "standard",
                              "instant"
                            ],
                            "type": [
                              "object",
                              "null"
                            ]
                          },
                          "status": {
                            "description": "Lifecycle status: `created` means saved but unused, `active` means a payout succeeded through it, `broken` means the last payout failed.",
                            "enum": [
                              "created",
                              "active",
                              "broken"
                            ],
                            "type": "string"
                          }
                        },
                        "required": [
                          "object",
                          "id",
                          "status",
                          "is_default",
                          "nickname",
                          "account_reference",
                          "institution_name",
                          "payer_name",
                          "destination_currency",
                          "created_at",
                          "payout_destination",
                          "quote"
                        ],
                        "type": "object"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "has_next_page",
                        "end_cursor",
                        "has_previous_page",
                        "start_cursor"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "internal stablecoin destinations omitted"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "neither account_id nor user_id provided"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the destination-read scope"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "no ledger for that owner is visible to the credential"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "payout:destination:read"
            ]
          }
        ],
        "summary": "List Payout Methods",
        "tags": [
          "Payouts"
        ]
      },
      "post": {
        "description": "Saves a new payout method for an account or user by submitting the destination's required fields, keyed by field id (list them with GET /payouts/methods?destination_id=...). Sensitive values are vaulted in transit and never stored raw; a Basis Theory token id may be passed in place of a raw value. The created method is immediately usable as payout_method_id on POST /payouts. A field validation failure returns the destination's full required_fields schema alongside the error.",
        "operationId": "create-method",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "account_id": {
                    "description": "The account to add the payout method for (a biz_ identifier). Provide this or user_id.",
                    "type": "string"
                  },
                  "destination_currency": {
                    "default": "usd",
                    "description": "Currency the destination delivers payouts in.",
                    "type": "string"
                  },
                  "destination_id": {
                    "description": "The payout destination to add (a pd_ identifier from a previous listing).",
                    "type": "string"
                  },
                  "fields": {
                    "additionalProperties": {
                      "type": "string"
                    },
                    "description": "The destination's required field values, keyed by field id.",
                    "type": "object"
                  },
                  "is_default": {
                    "default": false,
                    "description": "Whether to make this the account's default payout method.",
                    "type": "boolean"
                  },
                  "nickname": {
                    "description": "A label for the payout method, unique per destination.",
                    "type": "string"
                  },
                  "user_id": {
                    "description": "The user to add the payout method for (a user_ identifier). Provide this or account_id.",
                    "type": "string"
                  }
                },
                "required": [
                  "destination_id",
                  "nickname",
                  "fields"
                ],
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "201": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "account_reference": {
                      "description": "Masked identifier for the destination.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "created_at": {
                      "format": "date-time",
                      "type": "string"
                    },
                    "destination_currency": {
                      "type": "string"
                    },
                    "id": {
                      "description": "Payout method ID, usable as payout_method_id on POST /payouts.",
                      "type": "string"
                    },
                    "institution_name": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "is_default": {
                      "type": "boolean"
                    },
                    "nickname": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "object": {
                      "enum": [
                        "payout_method"
                      ],
                      "type": "string"
                    },
                    "payer_name": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "payout_destination": {
                      "properties": {
                        "delivery_type": {
                          "type": "string"
                        },
                        "icon_url": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "name": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "supports_instant_delivery": {
                          "type": "boolean"
                        },
                        "supports_standard_delivery": {
                          "type": "boolean"
                        }
                      },
                      "required": [
                        "name",
                        "icon_url",
                        "delivery_type",
                        "supports_standard_delivery",
                        "supports_instant_delivery"
                      ],
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "quote": {
                      "description": "Always null on create.",
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "status": {
                      "enum": [
                        "created",
                        "active",
                        "broken"
                      ],
                      "type": "string"
                    }
                  },
                  "required": [
                    "object",
                    "id",
                    "status",
                    "is_default",
                    "nickname",
                    "account_reference",
                    "institution_name",
                    "payer_name",
                    "destination_currency",
                    "created_at",
                    "payout_destination",
                    "quote"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "a pre-tokenized Basis Theory value passes through unvaulted"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "over-long nickname is rejected before any value is vaulted"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the create-destination scope"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "payout destination not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "payout:create_destination"
            ]
          }
        ],
        "summary": "Create Payout Method",
        "tags": [
          "Payouts"
        ]
      }
    },
    "/people": {
      "get": {
        "description": "Lists the people (visitors and customers) of an account, aggregated from pixel events. The account is inferred from an account API key; other credentials must pass account_id.",
        "operationId": "list",
        "parameters": [
          {
            "description": "The ID of the account, which will look like biz_*************. Optional for account API keys; required for credentials that can access multiple accounts.",
            "in": "query",
            "name": "account_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Start of the time range as an ISO 8601 timestamp. Defaults to 366 days before `to`.",
            "in": "query",
            "name": "from",
            "required": false,
            "schema": {
              "format": "date-time",
              "type": "string"
            }
          },
          {
            "description": "End of the time range as an ISO 8601 timestamp. Defaults to now.",
            "in": "query",
            "name": "to",
            "required": false,
            "schema": {
              "format": "date-time",
              "type": "string"
            }
          },
          {
            "description": "The number of people to return (default 100, max 100).",
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "type": "integer"
            }
          },
          {
            "description": "A cursor for fetching people after a previous page.",
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "A cursor for fetching people before a later page.",
            "in": "query",
            "name": "before",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Column to sort by (e.g. last_seen_at, ltv, purchase_count). Defaults to last_seen_at.",
            "in": "query",
            "name": "sort",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Sort direction. Defaults to desc.",
            "in": "query",
            "name": "direction",
            "required": false,
            "schema": {
              "enum": [
                "asc",
                "desc"
              ],
              "type": "string"
            }
          },
          {
            "description": "A JSON-encoded array of filters, each with field, operator, and value keys.",
            "in": "query",
            "name": "filters",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "properties": {
                          "account_id": {
                            "type": "string"
                          },
                          "ad_sets": {
                            "items": {
                              "properties": {
                                "id": {
                                  "type": "string"
                                },
                                "name": {
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "thumbnail_url": {
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                }
                              },
                              "required": [
                                "id"
                              ],
                              "type": "object"
                            },
                            "type": "array"
                          },
                          "ads": {
                            "items": {
                              "properties": {
                                "id": {
                                  "type": "string"
                                },
                                "name": {
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "thumbnail_url": {
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                }
                              },
                              "required": [
                                "id"
                              ],
                              "type": "object"
                            },
                            "type": "array"
                          },
                          "aov": {
                            "type": "number"
                          },
                          "campaigns": {
                            "items": {
                              "properties": {
                                "id": {
                                  "type": "string"
                                },
                                "name": {
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "thumbnail_url": {
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                }
                              },
                              "required": [
                                "id"
                              ],
                              "type": "object"
                            },
                            "type": "array"
                          },
                          "email": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "first_seen_at": {
                            "format": "date-time",
                            "type": "string"
                          },
                          "has_failed_payment": {
                            "type": "boolean"
                          },
                          "id": {
                            "type": "string"
                          },
                          "last_seen_at": {
                            "format": "date-time",
                            "type": "string"
                          },
                          "ltv": {
                            "type": "number"
                          },
                          "name": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "person_id": {
                            "type": "string"
                          },
                          "phone": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "purchase_count": {
                            "type": "integer"
                          }
                        },
                        "required": [
                          "id",
                          "account_id",
                          "person_id",
                          "first_seen_at",
                          "last_seen_at",
                          "purchase_count"
                        ],
                        "type": "object"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "has_next_page",
                        "has_previous_page"
                      ],
                      "type": "object"
                    },
                    "total_count": {
                      "type": "integer"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "people listed"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "company:basic:read",
              "member:basic:read",
              "member:journey:read"
            ]
          }
        ],
        "summary": "List People",
        "tags": [
          "People"
        ]
      }
    },
    "/people/{person_id}": {
      "get": {
        "description": "Retrieves one person for an account, aggregated from pixel events.",
        "operationId": "get",
        "parameters": [
          {
            "description": "The ID of the person.",
            "in": "path",
            "name": "person_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The ID of the account, which will look like biz_*************. Optional for account API keys; required for credentials that can access multiple accounts.",
            "in": "query",
            "name": "account_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Start of the time range as an ISO 8601 timestamp.",
            "in": "query",
            "name": "from",
            "required": false,
            "schema": {
              "format": "date-time",
              "type": "string"
            }
          },
          {
            "description": "End of the time range as an ISO 8601 timestamp. Defaults to now.",
            "in": "query",
            "name": "to",
            "required": false,
            "schema": {
              "format": "date-time",
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "properties": {
                        "account_id": {
                          "type": "string"
                        },
                        "ad_sets": {
                          "items": {
                            "properties": {
                              "id": {
                                "type": "string"
                              },
                              "name": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "thumbnail_url": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              }
                            },
                            "required": [
                              "id"
                            ],
                            "type": "object"
                          },
                          "type": "array"
                        },
                        "ads": {
                          "items": {
                            "properties": {
                              "id": {
                                "type": "string"
                              },
                              "name": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "thumbnail_url": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              }
                            },
                            "required": [
                              "id"
                            ],
                            "type": "object"
                          },
                          "type": "array"
                        },
                        "aov": {
                          "type": "number"
                        },
                        "campaigns": {
                          "items": {
                            "properties": {
                              "id": {
                                "type": "string"
                              },
                              "name": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "thumbnail_url": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              }
                            },
                            "required": [
                              "id"
                            ],
                            "type": "object"
                          },
                          "type": "array"
                        },
                        "email": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "first_seen_at": {
                          "format": "date-time",
                          "type": "string"
                        },
                        "has_failed_payment": {
                          "type": "boolean"
                        },
                        "id": {
                          "type": "string"
                        },
                        "last_seen_at": {
                          "format": "date-time",
                          "type": "string"
                        },
                        "ltv": {
                          "type": "number"
                        },
                        "name": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "person_id": {
                          "type": "string"
                        },
                        "phone": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "purchase_count": {
                          "type": "integer"
                        }
                      },
                      "required": [
                        "id",
                        "account_id",
                        "person_id",
                        "first_seen_at",
                        "last_seen_at",
                        "purchase_count"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "person retrieved"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "company:basic:read",
              "member:basic:read",
              "member:journey:read"
            ]
          }
        ],
        "summary": "Retrieve Person",
        "tags": [
          "People"
        ]
      }
    },
    "/plans": {
      "get": {
        "description": "Returns a paginated list of plans belonging to an account, with optional filtering by visibility, type, release method, and product.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The unique identifier of the account to list plans for.",
            "in": "query",
            "name": "account_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The sort direction for results. Defaults to descending.",
            "in": "query",
            "name": "direction",
            "required": false,
            "schema": {
              "enum": [
                "asc",
                "desc"
              ],
              "type": "string"
            }
          },
          {
            "description": "The field to sort results by. Defaults to created_at.",
            "in": "query",
            "name": "order",
            "required": false,
            "schema": {
              "enum": [
                "id",
                "active_members_count",
                "created_at",
                "internal_notes",
                "expiration_days"
              ],
              "type": "string"
            }
          },
          {
            "description": "Filter to only plans matching these release methods.",
            "in": "query",
            "name": "release_methods",
            "required": false,
            "schema": {
              "items": {
                "type": "string"
              },
              "type": "array"
            }
          },
          {
            "description": "Filter to only plans matching these visibility states.",
            "in": "query",
            "name": "visibilities",
            "required": false,
            "schema": {
              "items": {
                "type": "string"
              },
              "type": "array"
            }
          },
          {
            "description": "Filter to only plans matching these billing types.",
            "in": "query",
            "name": "plan_types",
            "required": false,
            "schema": {
              "items": {
                "type": "string"
              },
              "type": "array"
            }
          },
          {
            "description": "Filter to only plans belonging to these product identifiers.",
            "in": "query",
            "name": "product_ids",
            "required": false,
            "schema": {
              "items": {
                "type": "string"
              },
              "type": "array"
            }
          },
          {
            "description": "Only return plans created before this timestamp.",
            "in": "query",
            "name": "created_before",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Only return plans created after this timestamp.",
            "in": "query",
            "name": "created_after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The number of plans to return (default and max 100).",
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "type": "integer"
            }
          },
          {
            "description": "A cursor; returns plans after this position.",
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The number of plans to return from the end of the range.",
            "in": "query",
            "name": "last",
            "required": false,
            "schema": {
              "type": "integer"
            }
          },
          {
            "description": "A cursor; returns plans before this position.",
            "in": "query",
            "name": "before",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "$ref": "#/components/schemas/PlanListItem"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "end_cursor",
                        "start_cursor",
                        "has_next_page",
                        "has_previous_page"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "plans listed"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "missing account_id"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "plan:basic:read"
            ]
          }
        ],
        "summary": "List Plans",
        "tags": [
          "Plans"
        ]
      },
      "post": {
        "description": "Create a new pricing plan for a product. The plan defines the billing interval, price, and availability for customers.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "account_id": {
                    "description": "The unique identifier of the account to create this plan for. Defaults to the caller's account.",
                    "type": "string"
                  },
                  "adaptive_pricing_enabled": {
                    "description": "Whether this plan accepts local currency payments via adaptive pricing.",
                    "type": [
                      "boolean",
                      "null"
                    ]
                  },
                  "billing_period": {
                    "description": "Recurring billing interval in days, such as 30 for monthly or 365 for annual.",
                    "type": [
                      "integer",
                      "null"
                    ]
                  },
                  "checkout_styling": {
                    "description": "Checkout styling overrides for this plan.",
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "currency": {
                    "description": "The three-letter ISO currency code for the plan's pricing. Defaults to USD.",
                    "type": "string"
                  },
                  "custom_fields": {
                    "description": "An array of custom field definitions to collect from customers at checkout. Omitting this field clears existing custom fields.",
                    "items": {
                      "properties": {
                        "field_type": {
                          "description": "The type of the custom field.",
                          "enum": [
                            "text"
                          ],
                          "type": "string"
                        },
                        "id": {
                          "description": "The ID of the custom field (if being updated).",
                          "type": "string"
                        },
                        "name": {
                          "description": "The name of the custom field.",
                          "type": "string"
                        },
                        "order": {
                          "description": "The order of the field.",
                          "type": "integer"
                        },
                        "placeholder": {
                          "description": "An example response displayed in the input field.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "required": {
                          "description": "Whether or not the field is required.",
                          "type": "boolean"
                        }
                      },
                      "type": "object"
                    },
                    "type": [
                      "array",
                      "null"
                    ]
                  },
                  "description": {
                    "description": "A text description of the plan displayed to customers on the product page.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "expiration_days": {
                    "description": "Access duration in days before the membership expires.",
                    "type": [
                      "integer",
                      "null"
                    ]
                  },
                  "image": {
                    "description": "An image displayed on the product page to represent this plan.",
                    "properties": {
                      "direct_upload_id": {
                        "type": "string"
                      },
                      "id": {
                        "type": "string"
                      }
                    },
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "initial_price": {
                    "description": "Initial amount charged in the plan's currency, e.g. 10.43 for $10.43.",
                    "type": [
                      "number",
                      "null"
                    ]
                  },
                  "internal_notes": {
                    "description": "Private notes visible only to the account owner. Not shown to customers.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "legacy_payment_method_controls": {
                    "description": "Whether this plan uses legacy payment method controls.",
                    "type": [
                      "boolean",
                      "null"
                    ]
                  },
                  "metadata": {
                    "description": "Custom key-value pairs to store on the plan. Included in webhook payloads for payment and membership events. Max 50 keys, 100 chars per key, 500 chars per string value.",
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "override_tax_type": {
                    "description": "Override the default tax classification for this specific plan.",
                    "type": "string"
                  },
                  "payment_method_configuration": {
                    "description": "Explicit payment method configuration for the plan. When not provided, the account's defaults apply.",
                    "properties": {
                      "disabled": {
                        "items": {
                          "type": "string"
                        },
                        "type": "array"
                      },
                      "enabled": {
                        "items": {
                          "type": "string"
                        },
                        "type": "array"
                      },
                      "include_platform_defaults": {
                        "type": "boolean"
                      }
                    },
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "plan_type": {
                    "description": "Plan billing type, such as `one_time` or `renewal`.",
                    "type": "string"
                  },
                  "product_id": {
                    "description": "The unique identifier of the product to attach this plan to.",
                    "type": "string"
                  },
                  "release_method": {
                    "description": "Sales method for this plan, such as `buy_now` or `waitlist`.",
                    "type": "string"
                  },
                  "renewal_price": {
                    "description": "The amount charged each billing period for recurring plans, in the plan's currency.",
                    "type": [
                      "number",
                      "null"
                    ]
                  },
                  "split_pay_required_payments": {
                    "description": "Installment payments required before the subscription pauses.",
                    "type": [
                      "integer",
                      "null"
                    ]
                  },
                  "stock": {
                    "description": "The maximum number of units available for purchase. Ignored when unlimited_stock is true.",
                    "type": [
                      "integer",
                      "null"
                    ]
                  },
                  "three_ds_level": {
                    "description": "3D Secure behavior for this plan. Send `null` to inherit the account default.",
                    "enum": [
                      "mandate_challenge",
                      "frictionless"
                    ],
                    "type": "string"
                  },
                  "title": {
                    "description": "The display name of the plan shown to customers on the product page.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "trial_period_days": {
                    "description": "Free trial duration before the first recurring charge.",
                    "type": [
                      "integer",
                      "null"
                    ]
                  },
                  "unlimited_stock": {
                    "description": "Whether the plan has unlimited stock. When true, the stock field is ignored.",
                    "type": [
                      "boolean",
                      "null"
                    ]
                  },
                  "visibility": {
                    "description": "Whether the plan is visible to customers or hidden from public view.",
                    "type": "string"
                  }
                },
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Plan"
                }
              }
            },
            "description": "plan created"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "invalid parameters"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "plan:create"
            ]
          }
        ],
        "summary": "Create Plan",
        "tags": [
          "Plans"
        ]
      }
    },
    "/plans/{id}": {
      "delete": {
        "description": "Permanently delete a plan from a product. Existing memberships on this plan will not be affected.",
        "operationId": "delete",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "description": "Always true on success.",
                  "type": "boolean"
                }
              }
            },
            "description": "plan deleted"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "no plan with that id"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "plan:delete"
            ]
          }
        ],
        "summary": "Delete Plan",
        "tags": [
          "Plans"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "Plan ID, prefixed `plan_`.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ]
      },
      "get": {
        "description": "Retrieves the details of an existing plan.",
        "operationId": "get",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Plan"
                }
              }
            },
            "description": "plan retrieved"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "no plan with that id"
          }
        },
        "security": [
          {
            "bearerAuth": []
          }
        ],
        "summary": "Retrieve Plan",
        "tags": [
          "Plans"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "Plan ID, prefixed `plan_`.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ]
      },
      "patch": {
        "description": "Update a plan's pricing, billing interval, visibility, stock, and other settings.",
        "operationId": "update",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "Plan ID, prefixed `plan_`.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "adaptive_pricing_enabled": {
                    "description": "Whether this plan accepts local currency payments via adaptive pricing.",
                    "type": [
                      "boolean",
                      "null"
                    ]
                  },
                  "billing_period": {
                    "description": "Recurring billing interval in days, such as 30 for monthly or 365 for annual.",
                    "type": [
                      "integer",
                      "null"
                    ]
                  },
                  "checkout_styling": {
                    "description": "Checkout styling overrides for this plan.",
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "currency": {
                    "description": "The three-letter ISO currency code for the plan's pricing. Defaults to USD.",
                    "type": "string"
                  },
                  "custom_fields": {
                    "description": "An array of custom field definitions to collect from customers at checkout. Omitting this field clears existing custom fields.",
                    "items": {
                      "properties": {
                        "field_type": {
                          "description": "The type of the custom field.",
                          "enum": [
                            "text"
                          ],
                          "type": "string"
                        },
                        "id": {
                          "description": "The ID of the custom field (if being updated).",
                          "type": "string"
                        },
                        "name": {
                          "description": "The name of the custom field.",
                          "type": "string"
                        },
                        "order": {
                          "description": "The order of the field.",
                          "type": "integer"
                        },
                        "placeholder": {
                          "description": "An example response displayed in the input field.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "required": {
                          "description": "Whether or not the field is required.",
                          "type": "boolean"
                        }
                      },
                      "type": "object"
                    },
                    "type": [
                      "array",
                      "null"
                    ]
                  },
                  "description": {
                    "description": "A text description of the plan displayed to customers on the product page.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "expiration_days": {
                    "description": "Access duration in days before the membership expires.",
                    "type": [
                      "integer",
                      "null"
                    ]
                  },
                  "image": {
                    "description": "An image displayed on the product page to represent this plan.",
                    "properties": {
                      "direct_upload_id": {
                        "type": "string"
                      },
                      "id": {
                        "type": "string"
                      }
                    },
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "initial_price": {
                    "description": "Initial amount charged in the plan's currency, e.g. 10.43 for $10.43.",
                    "type": [
                      "number",
                      "null"
                    ]
                  },
                  "internal_notes": {
                    "description": "Private notes visible only to the account owner. Not shown to customers.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "legacy_payment_method_controls": {
                    "description": "Whether this plan uses legacy payment method controls.",
                    "type": [
                      "boolean",
                      "null"
                    ]
                  },
                  "metadata": {
                    "description": "Custom key-value pairs to store on the plan. Included in webhook payloads for payment and membership events. Max 50 keys, 100 chars per key, 500 chars per string value.",
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "offer_cancel_discount": {
                    "description": "Whether to offer a retention discount when a customer attempts to cancel.",
                    "type": [
                      "boolean",
                      "null"
                    ]
                  },
                  "override_tax_type": {
                    "description": "Override the default tax classification for this specific plan.",
                    "type": "string"
                  },
                  "payment_method_configuration": {
                    "description": "Explicit payment method configuration for the plan. When not provided, the account's defaults apply.",
                    "properties": {
                      "disabled": {
                        "items": {
                          "type": "string"
                        },
                        "type": "array"
                      },
                      "enabled": {
                        "items": {
                          "type": "string"
                        },
                        "type": "array"
                      },
                      "include_platform_defaults": {
                        "type": "boolean"
                      }
                    },
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "renewal_price": {
                    "description": "The amount charged each billing period for recurring plans, in the plan's currency.",
                    "type": [
                      "number",
                      "null"
                    ]
                  },
                  "stock": {
                    "description": "The maximum number of units available for purchase. Ignored when unlimited_stock is true.",
                    "type": [
                      "integer",
                      "null"
                    ]
                  },
                  "strike_through_initial_price": {
                    "description": "A comparison price displayed with a strikethrough for the initial price.",
                    "type": [
                      "number",
                      "null"
                    ]
                  },
                  "strike_through_renewal_price": {
                    "description": "A comparison price displayed with a strikethrough for the renewal price.",
                    "type": [
                      "number",
                      "null"
                    ]
                  },
                  "three_ds_level": {
                    "description": "3D Secure behavior for this plan. Send `null` to inherit the account default.",
                    "enum": [
                      "mandate_challenge",
                      "frictionless"
                    ],
                    "type": "string"
                  },
                  "title": {
                    "description": "The display name of the plan shown to customers on the product page.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "trial_period_days": {
                    "description": "Free trial duration before the first recurring charge.",
                    "type": [
                      "integer",
                      "null"
                    ]
                  },
                  "unlimited_stock": {
                    "description": "Whether the plan has unlimited stock. When true, the stock field is ignored.",
                    "type": [
                      "boolean",
                      "null"
                    ]
                  },
                  "visibility": {
                    "description": "Whether the plan is visible to customers or hidden from public view.",
                    "type": "string"
                  }
                },
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Plan"
                }
              }
            },
            "description": "plan updated"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "plan:update"
            ]
          }
        ],
        "summary": "Update Plan",
        "tags": [
          "Plans"
        ]
      }
    },
    "/plans/{id}/calculate_tax": {
      "post": {
        "description": "Previews tax for a plan before checkout, based on the buyer's location.",
        "operationId": "calculate_tax",
        "parameters": [
          {
            "description": "Plan ID, prefixed `plan_`.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "example": {
                  "address": {
                    "country": "DE",
                    "postal_code": "10115"
                  }
                },
                "properties": {
                  "address": {
                    "description": "Buyer billing address used for tax calculation. Provide either `address.country` or `ip_address`; include state and postal code when available for more accurate results.",
                    "properties": {
                      "city": {
                        "description": "City name.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "country": {
                        "description": "ISO 3166-1 alpha-2 country code, such as `US`, `DE`, or `GB`.",
                        "type": "string"
                      },
                      "line1": {
                        "description": "First line of the street address.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "line2": {
                        "description": "Second line of the street address.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "postal_code": {
                        "description": "Postal or ZIP code.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "state": {
                        "description": "State, province, or region code, such as `CA`.",
                        "type": [
                          "string",
                          "null"
                        ]
                      }
                    },
                    "required": [
                      "country"
                    ],
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "ip_address": {
                    "description": "Buyer IP address used to infer location when no billing address is provided.",
                    "type": "string"
                  },
                  "tax_ids": {
                    "description": "Optional buyer tax ID for B2B exemptions. At most one entry is supported.",
                    "items": {
                      "properties": {
                        "type": {
                          "description": "Tax ID type, such as `eu_vat` for an EU VAT number.",
                          "enum": [
                            "ad_nrt",
                            "ao_tin",
                            "ar_cuit",
                            "al_tin",
                            "am_tin",
                            "aw_tin",
                            "au_abn",
                            "au_arn",
                            "eu_vat",
                            "az_tin",
                            "bs_tin",
                            "bh_vat",
                            "bd_bin",
                            "bb_tin",
                            "by_tin",
                            "bj_ifu",
                            "bo_tin",
                            "ba_tin",
                            "br_cnpj",
                            "br_cpf",
                            "bg_uic",
                            "bf_ifu",
                            "kh_tin",
                            "cm_niu",
                            "ca_bn",
                            "ca_gst_hst",
                            "ca_pst_bc",
                            "ca_pst_mb",
                            "ca_pst_sk",
                            "ca_qst",
                            "cv_nif",
                            "cl_tin",
                            "cn_tin",
                            "co_nit",
                            "cd_nif",
                            "cr_tin",
                            "hr_oib",
                            "do_rcn",
                            "ec_ruc",
                            "eg_tin",
                            "sv_nit",
                            "et_tin",
                            "eu_oss_vat",
                            "ge_vat",
                            "gh_tin",
                            "de_stn",
                            "gb_vat",
                            "gn_nif",
                            "hk_br",
                            "hu_tin",
                            "is_vat",
                            "in_gst",
                            "id_npwp",
                            "il_vat",
                            "jp_cn",
                            "jp_rn",
                            "jp_trn",
                            "kz_bin",
                            "ke_pin",
                            "kg_tin",
                            "la_tin",
                            "li_uid",
                            "li_vat",
                            "my_frp",
                            "my_itn",
                            "my_sst",
                            "mr_nif",
                            "mx_rfc",
                            "md_vat",
                            "me_pib",
                            "ma_vat",
                            "np_pan",
                            "nz_gst",
                            "ng_tin",
                            "mk_vat",
                            "no_vat",
                            "no_voec",
                            "om_vat",
                            "pe_ruc",
                            "ph_tin",
                            "ro_tin",
                            "ru_inn",
                            "ru_kpp",
                            "sa_vat",
                            "sn_ninea",
                            "rs_pib",
                            "sg_gst",
                            "sg_uen",
                            "si_tin",
                            "za_vat",
                            "kr_brn",
                            "es_cif",
                            "ch_uid",
                            "ch_vat",
                            "tw_vat",
                            "tj_tin",
                            "tz_vat",
                            "th_vat",
                            "tr_tin",
                            "ug_tin",
                            "ua_vat",
                            "ae_trn",
                            "us_ein",
                            "uy_ruc",
                            "uz_tin",
                            "uz_vat",
                            "ve_rif",
                            "vn_tin",
                            "zm_tin",
                            "zw_tin",
                            "sr_fin"
                          ],
                          "type": "string"
                        },
                        "value": {
                          "description": "Tax ID value, for example `DE123456789`.",
                          "type": "string"
                        }
                      },
                      "type": "object"
                    },
                    "maxItems": 1,
                    "type": [
                      "array",
                      "null"
                    ]
                  }
                },
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "example": {
                    "currency": "usd",
                    "status": "calculated",
                    "subtotal": 1000,
                    "tax_amount": 70,
                    "tax_behavior": "exclusive",
                    "total": 1070
                  },
                  "properties": {
                    "currency": {
                      "description": "Three-letter ISO 4217 currency code for the returned amounts.",
                      "type": "string"
                    },
                    "status": {
                      "description": "Whether Whop calculated tax for this preview. `not_calculated` means no tax could be determined, so `tax_amount` is 0 and `total` equals `subtotal`.",
                      "enum": [
                        "calculated",
                        "not_calculated"
                      ],
                      "type": "string"
                    },
                    "subtotal": {
                      "description": "Plan price in the currency's smallest unit, for example cents. For exclusive tax, this is the pre-tax amount; for inclusive tax, it already includes tax and equals the total.",
                      "type": "integer"
                    },
                    "tax_amount": {
                      "description": "Calculated tax amount in the currency's smallest unit. For exclusive tax, this is added on top of the subtotal; for inclusive tax, it is the portion of the subtotal that is tax.",
                      "type": "integer"
                    },
                    "tax_behavior": {
                      "description": "Whether tax is added on top of the plan price or already included in it for this buyer's location.",
                      "enum": [
                        "exclusive",
                        "inclusive"
                      ],
                      "type": "string"
                    },
                    "total": {
                      "description": "Amount the buyer would pay in the currency's smallest unit.",
                      "type": "integer"
                    }
                  },
                  "required": [
                    "currency",
                    "tax_behavior",
                    "subtotal",
                    "tax_amount",
                    "total",
                    "status"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "tax calculated"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "request is invalid"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the plan read scope"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "plan not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "plan:basic:read"
            ]
          }
        ],
        "summary": "Calculate Tax",
        "tags": [
          "Plans"
        ]
      }
    },
    "/products": {
      "get": {
        "description": "Returns a paginated list of products belonging to a company.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The unique identifier of the company to list products for.",
            "in": "query",
            "name": "company_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter to only products matching these visibility states.",
            "in": "query",
            "name": "visibilities",
            "required": false,
            "schema": {
              "items": {
                "type": "string"
              },
              "type": "array"
            }
          },
          {
            "description": "Filter to only products matching these types.",
            "in": "query",
            "name": "access_pass_types",
            "required": false,
            "schema": {
              "items": {
                "type": "string"
              },
              "type": "array"
            }
          },
          {
            "description": "The sort direction for results. Defaults to descending.",
            "in": "query",
            "name": "direction",
            "required": false,
            "schema": {
              "enum": [
                "asc",
                "desc"
              ],
              "type": "string"
            }
          },
          {
            "description": "The field to sort results by. Defaults to created_at.",
            "in": "query",
            "name": "order",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The number of products to return (default and max 100).",
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "type": "integer"
            }
          },
          {
            "description": "A cursor; returns products after this position.",
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The number of products to return from the end of the range.",
            "in": "query",
            "name": "last",
            "required": false,
            "schema": {
              "type": "integer"
            }
          },
          {
            "description": "A cursor; returns products before this position.",
            "in": "query",
            "name": "before",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "$ref": "#/components/schemas/ProductListItem"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "end_cursor",
                        "start_cursor",
                        "has_next_page",
                        "has_previous_page"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "products listed"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "missing company_id"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "product:basic:read"
            ]
          }
        ],
        "summary": "List Products",
        "tags": [
          "Products"
        ]
      },
      "post": {
        "description": "Creates a new product for a company.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "collect_shipping_address": {
                    "description": "Whether to collect a shipping address at checkout.",
                    "type": [
                      "boolean",
                      "null"
                    ]
                  },
                  "company_id": {
                    "description": "The unique identifier of the company to create this product for.",
                    "type": "string"
                  },
                  "custom_cta": {
                    "description": "The call-to-action button label.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "custom_cta_url": {
                    "description": "A URL the call-to-action button links to.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "custom_statement_descriptor": {
                    "description": "Custom bank statement descriptor. Must start with WHOP*.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "description": {
                    "description": "A written description displayed on the product page.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "global_affiliate_percentage": {
                    "description": "The commission rate affiliates earn.",
                    "type": [
                      "number",
                      "null"
                    ]
                  },
                  "global_affiliate_status": {
                    "description": "The enrollment status in the global affiliate program.",
                    "type": "string"
                  },
                  "headline": {
                    "description": "A short marketing headline for the product page.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "member_affiliate_percentage": {
                    "description": "The commission rate members earn.",
                    "type": [
                      "number",
                      "null"
                    ]
                  },
                  "member_affiliate_status": {
                    "description": "The enrollment status in the member affiliate program.",
                    "type": "string"
                  },
                  "metadata": {
                    "description": "Custom key-value pairs to store on the product.",
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "product_tax_code_id": {
                    "description": "The unique identifier of the tax classification code. See the available [product categories](https://docs.numeral.com/essentials/product-categories).",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "redirect_purchase_url": {
                    "description": "A URL to redirect the customer to after purchase.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "route": {
                    "description": "The URL slug for the product's public link.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "send_welcome_message": {
                    "description": "Whether to send an automated welcome message via support chat when a user joins this product. Defaults to true.",
                    "type": [
                      "boolean",
                      "null"
                    ]
                  },
                  "title": {
                    "description": "The display name of the product. Maximum 80 characters.",
                    "type": "string"
                  },
                  "visibility": {
                    "description": "Whether the product is visible to customers.",
                    "type": "string"
                  }
                },
                "required": [
                  "title"
                ],
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Product"
                }
              }
            },
            "description": "product created"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "product:create"
            ]
          }
        ],
        "summary": "Create Product",
        "tags": [
          "Products"
        ]
      }
    },
    "/products/{id}": {
      "delete": {
        "description": "Deletes a product. Only products with no memberships, entries, reviews, or invoices can be deleted.",
        "operationId": "delete",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "type": "boolean"
                }
              }
            },
            "description": "product deleted"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "product not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "product:delete"
            ]
          }
        ],
        "summary": "Delete Product",
        "tags": [
          "Products"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The unique identifier of the product.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ]
      },
      "get": {
        "description": "Retrieves the details of an existing product. This endpoint is publicly accessible.",
        "operationId": "get",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Product"
                }
              }
            },
            "description": "product retrieved without authentication"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "product not found"
          }
        },
        "security": [
          {
            "bearerAuth": []
          }
        ],
        "summary": "Retrieve Product",
        "tags": [
          "Products"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The unique identifier of the product.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ]
      },
      "patch": {
        "description": "Updates an existing product.",
        "operationId": "update",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The unique identifier of the product.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "description": {
                    "description": "A written description displayed on the product page.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "headline": {
                    "description": "A short marketing headline for the product page.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "metadata": {
                    "description": "Custom key-value pairs to store on the product.",
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "product_tax_code_id": {
                    "description": "The unique identifier of the tax classification code. See the available [product categories](https://docs.numeral.com/essentials/product-categories).",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "send_welcome_message": {
                    "description": "Whether to send an automated welcome message via support chat when a user joins this product.",
                    "type": [
                      "boolean",
                      "null"
                    ]
                  },
                  "title": {
                    "description": "The display name of the product.",
                    "type": "string"
                  },
                  "visibility": {
                    "description": "Whether the product is visible to customers.",
                    "type": "string"
                  }
                },
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/Product"
                }
              }
            },
            "description": "product tax code updated"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "product not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "product:update"
            ]
          }
        ],
        "summary": "Update Product",
        "tags": [
          "Products"
        ]
      }
    },
    "/social_accounts": {
      "get": {
        "description": "Lists the social accounts linked to an account or user.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The Account that the social accounts are connected to. Provide either this or user_id.",
            "in": "query",
            "name": "account_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The User that the social accounts are connected to. Provide either this or account_id.",
            "in": "query",
            "name": "user_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Only return social accounts for the platform that is specified.",
            "in": "query",
            "name": "platform",
            "required": false,
            "schema": {
              "enum": [
                "x",
                "instagram",
                "youtube",
                "tiktok",
                "facebook"
              ],
              "type": "string"
            }
          },
          {
            "description": "Only return social accounts that are verified on the platform.",
            "in": "query",
            "name": "verified",
            "required": false,
            "schema": {
              "type": "boolean"
            }
          },
          {
            "description": "Only return social accounts that have these scopes.",
            "in": "query",
            "name": "scopes",
            "required": false,
            "schema": {
              "items": {
                "enum": [
                  "advertise"
                ],
                "type": "string"
              },
              "type": "array"
            }
          },
          {
            "description": "The number of social accounts to return.",
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "default": 20,
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to fetch the page after (from page_info.end_cursor).",
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The number of social accounts to return from the end of the range.",
            "in": "query",
            "name": "last",
            "required": false,
            "schema": {
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to fetch the page before (from page_info.start_cursor).",
            "in": "query",
            "name": "before",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The field to sort social accounts by.",
            "in": "query",
            "name": "order",
            "required": false,
            "schema": {
              "default": "display_order",
              "enum": [
                "display_order",
                "created_at"
              ],
              "type": "string"
            }
          },
          {
            "description": "Sort direction.",
            "in": "query",
            "name": "direction",
            "required": false,
            "schema": {
              "default": "asc",
              "enum": [
                "asc",
                "desc"
              ],
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "$ref": "#/components/schemas/SocialAccount"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "nullable": true,
                          "type": "string"
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "nullable": true,
                          "type": "string"
                        }
                      },
                      "required": [
                        "has_next_page",
                        "end_cursor",
                        "has_previous_page",
                        "start_cursor"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "filtered to accounts holding the requested scope"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "neither account_id nor user_id resolvable"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "social_account:read"
            ]
          }
        ],
        "summary": "List Social Accounts",
        "tags": [
          "Social Accounts"
        ]
      },
      "post": {
        "description": "Creates or returns a Whop-managed Facebook page for an account.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "account_id": {
                    "description": "The Account (biz_ identifier) to create the social account for. An account-scoped API key may omit this to default to its own account.",
                    "type": "string"
                  },
                  "platform": {
                    "description": "The platform to create the social account on.",
                    "enum": [
                      "facebook"
                    ],
                    "type": "string"
                  }
                },
                "required": [
                  "platform"
                ],
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/SocialAccount"
                }
              }
            },
            "description": "social account created"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "unsupported platform"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "social_account:create"
            ]
          }
        ],
        "summary": "Create a Social Account",
        "tags": [
          "Social Accounts"
        ]
      }
    },
    "/social_accounts/connect": {
      "post": {
        "description": "Starts an OAuth connection flow and returns an authorize_url where the user can connect a social account.",
        "operationId": "connect",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "account_id": {
                    "description": "The Account (biz_ identifier) to connect the social account for. An account-scoped API key may omit this to default to its own account.",
                    "type": "string"
                  },
                  "platform": {
                    "description": "The platform to connect the social account on. Supported options are `meta_business` and `tiktok`.",
                    "enum": [
                      "meta_business",
                      "tiktok"
                    ],
                    "type": "string"
                  },
                  "redirect_url": {
                    "description": "The Whop URL to redirect the user to after they finish connecting.",
                    "type": "string"
                  },
                  "scopes": {
                    "description": "Capabilities to grant for the connected social account. Use `advertise` when connecting a Meta Business or TikTok account for ads.",
                    "items": {
                      "enum": [
                        "advertise"
                      ],
                      "type": "string"
                    },
                    "type": "array"
                  }
                },
                "required": [
                  "platform"
                ],
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "authorize_url": {
                      "description": "The OAuth authorization URL to redirect the user to.",
                      "type": "string"
                    }
                  },
                  "required": [
                    "authorize_url"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "authorize url returned"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "unsupported platform"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "social_account:create"
            ]
          }
        ],
        "summary": "Connect a Social Account",
        "tags": [
          "Social Accounts"
        ]
      }
    },
    "/social_accounts/{id}": {
      "delete": {
        "description": "Disconnects a social account from an account or user without deleting the underlying platform account.",
        "operationId": "delete",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The ID of the social account to disconnect.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The Account that the social account is connected to. Provide either this or user_id.",
            "in": "query",
            "name": "account_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The User that the social account is connected to. Provide either this or account_id.",
            "in": "query",
            "name": "user_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "description": "Always true on success.",
                  "type": "boolean"
                }
              }
            },
            "description": "user social account disconnected"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "account_id and user_id both provided"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "caller lacks delete permission"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "link already disconnected"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "social_account:delete"
            ]
          }
        ],
        "summary": "Delete a Social Account",
        "tags": [
          "Social Accounts"
        ]
      }
    },
    "/social_accounts/{id}/posts": {
      "get": {
        "description": "Lists the existing posts of a connected Facebook page or Instagram account.",
        "operationId": "posts",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The social account (a sacc_ identifier) whose posts to list.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The Account (a biz_ identifier) the social account is connected to.",
            "in": "query",
            "name": "account_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Return only the single post with this platform id, instead of the full list.",
            "in": "query",
            "name": "post_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The number of posts to return.",
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "default": 25,
              "maximum": 100,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to fetch the page after (from page_info.end_cursor).",
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "$ref": "#/components/schemas/SocialAccountPost"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "nullable": true,
                          "type": "string"
                        },
                        "has_next_page": {
                          "type": "boolean"
                        }
                      },
                      "required": [
                        "has_next_page",
                        "end_cursor"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "single post returned when post_id is given"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "account_id is missing"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "social account is not linked to the account"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "social_account:read"
            ]
          }
        ],
        "summary": "List Social Account Posts",
        "tags": [
          "Social Accounts"
        ]
      }
    },
    "/stats": {
      "get": {
        "description": "Lists every metric you can query, with its unit and the properties you can filter or break it down by.",
        "operationId": "list",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "description": "The available metrics.",
                      "items": {
                        "properties": {
                          "description": {
                            "description": "A short description of what the metric measures.",
                            "type": "string"
                          },
                          "key": {
                            "description": "The metric's key. Pass it to GET /stats/{metric} to query its values.",
                            "type": "string"
                          },
                          "name": {
                            "description": "Human-readable display name for the metric.",
                            "type": "string"
                          },
                          "properties": {
                            "description": "The properties you can use with this metric — pass one as a filter (property=value) to narrow the series, or as breakdown_by=property to split it.",
                            "items": {
                              "type": "string"
                            },
                            "type": "array"
                          },
                          "unit": {
                            "description": "How to read the metric's values: count is an integer, currency is a decimal amount, and percent is a number where 1.6 means 1.6%.",
                            "enum": [
                              "count",
                              "currency",
                              "percent"
                            ],
                            "type": "string"
                          },
                          "windows": {
                            "description": "Snapshot metrics only: the trailing windows you can pass as snapshot_window, for example 30d. Absent on live metrics, which use from/to instead.",
                            "items": {
                              "type": "string"
                            },
                            "type": "array"
                          }
                        },
                        "required": [
                          "key",
                          "name",
                          "unit",
                          "properties",
                          "description"
                        ],
                        "type": "object"
                      },
                      "type": "array"
                    }
                  },
                  "required": [
                    "data"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "metric catalog"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          }
        },
        "security": [
          {
            "bearerAuth": []
          }
        ],
        "summary": "List Metrics",
        "tags": [
          "Stats"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ]
      }
    },
    "/stats/{metric}": {
      "get": {
        "description": "Retrieves a metric as a time series of points for an account over a time range.",
        "operationId": "get",
        "parameters": [
          {
            "description": "The metric to retrieve, for example net_revenue. Use GET /stats to see every metric key. The metric sets the unit and the properties you can filter or break down by.",
            "in": "path",
            "name": "metric",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The account this query concerns, for example biz_AbC123.",
            "in": "query",
            "name": "account_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Start of the range — a date (YYYY-MM-DD), expanded to the start of that day, or an ISO 8601 timestamp (for example 2026-07-16T16:37:00Z), used exactly.",
            "in": "query",
            "name": "from",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "End of the range — a date (YYYY-MM-DD), expanded to the end of that day, or an ISO 8601 timestamp (for example 2026-07-17T16:37:00Z), used exactly.",
            "in": "query",
            "name": "to",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "How wide each point is. Defaults to day. Snapshot metrics are day-only.",
            "in": "query",
            "name": "interval",
            "required": false,
            "schema": {
              "enum": [
                "five_minutes",
                "thirty_minutes",
                "hour",
                "day",
                "week",
                "month",
                "year"
              ],
              "type": "string"
            }
          },
          {
            "description": "Split the metric out by one of its properties — each point gets a breakdown array. For example breakdown_by=currency returns an entry for usd, an entry for eur, and so on.",
            "in": "query",
            "name": "breakdown_by",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Display currency for money metrics — every amount is converted into this ISO currency using the exchange rate on each period's date. Defaults to usd. For the ads metrics (ad_spend, ad_report), pass the account's ads reporting currency to match the ad entity endpoints. Ignored when you filter or break down by currency (those report the original transaction currency, unconverted).",
            "in": "query",
            "name": "convert_to",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter to transactions made in this original ISO currency, for example eur — reported in that currency, not converted. Pair with breakdown_by=currency to split a metric by currency. Available on metrics that list currency.",
            "in": "query",
            "name": "currency",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "IANA time zone to bucket the series in, for example America/New_York. Defaults to UTC. Not accepted by snapshot metrics, which are UTC only.",
            "in": "query",
            "name": "time_zone",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter to a single payment method, for example card or crypto. Available on metrics that list payment_method.",
            "in": "query",
            "name": "payment_method",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter to a single card brand, for example visa. A refinement of payment_method=card. Available on metrics that list card_network.",
            "in": "query",
            "name": "card_network",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter disputes to a normalized reason, for example product_not_received. Pair with breakdown_by=dispute_reason to split dispute counts by reason.",
            "in": "query",
            "name": "dispute_reason",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter to a single GMV source, for example payments. Pair with breakdown_by=source to split by source. Available on metrics that list source.",
            "in": "query",
            "name": "source",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter to a single wallet-balance segment, for example available. Pair with breakdown_by=segment to split the balance. Available on metrics that list segment.",
            "in": "query",
            "name": "segment",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter to a single balance-activity category, for example payments. Pair with breakdown_by=category to split the activity. Available on metrics that list category.",
            "in": "query",
            "name": "category",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter to a single fee type. Pair with breakdown_by=fee_type to split fees by type. Available on metrics that list fee_type.",
            "in": "query",
            "name": "fee_type",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter to a single product (access pass id), for example prod_AbC123. Pair with breakdown_by=product. Available on metrics that list product.",
            "in": "query",
            "name": "product",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter to a single membership status. Pair with breakdown_by=status. Available on metrics that list status.",
            "in": "query",
            "name": "status",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter to a single access level. Pair with breakdown_by=access_level. Available on metrics that list access_level.",
            "in": "query",
            "name": "access_level",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter to a single most-recent member action. Pair with breakdown_by=most_recent_action. Available on metrics that list most_recent_action.",
            "in": "query",
            "name": "most_recent_action",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter a referral metric to the businesses attributed to one person you referred, for example user_AbC123. Available on metrics that list referred_user_id.",
            "in": "query",
            "name": "referred_user_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Ad campaign ids (adcamp_...) to scope the report to; stats are summed across them. Available on metrics that list ad_campaign_ids.",
            "in": "query",
            "name": "ad_campaign_ids",
            "required": false,
            "schema": {
              "items": {
                "type": "string"
              },
              "type": "array"
            }
          },
          {
            "description": "Ad group ids (adgrp_...) to scope the report to; stats are summed across them. Available on metrics that list ad_group_ids.",
            "in": "query",
            "name": "ad_group_ids",
            "required": false,
            "schema": {
              "items": {
                "type": "string"
              },
              "type": "array"
            }
          },
          {
            "description": "Ad ids (ad_...) to scope the report to; stats are summed across them. Available on metrics that list ad_ids.",
            "in": "query",
            "name": "ad_ids",
            "required": false,
            "schema": {
              "items": {
                "type": "string"
              },
              "type": "array"
            }
          },
          {
            "description": "Window used by a snapshot metric. Ordinary snapshots accept 30d as their trailing activity window. Cohorted dispute metrics accept 7d or 28d as the sales-transaction pool; their attribution window is fixed in the metric name. Each metric lists its accepted values in the catalog.",
            "in": "query",
            "name": "snapshot_window",
            "required": false,
            "schema": {
              "enum": [
                "7d",
                "28d",
                "30d"
              ],
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "properties": {
                        "currency": {
                          "description": "ISO currency the values are denominated in. Present for currency-unit metrics: the convert_to currency, or usd.",
                          "nullable": true,
                          "type": "string"
                        },
                        "points": {
                          "description": "One entry per period, oldest first.",
                          "items": {
                            "properties": {
                              "breakdown": {
                                "description": "Present only when broken down: one entry per property value in this period.",
                                "items": {
                                  "properties": {
                                    "name": {
                                      "description": "The property value, for example usd or visa.",
                                      "type": "string"
                                    },
                                    "value": {
                                      "description": "The metric's value for this entry.",
                                      "nullable": true,
                                      "type": "number"
                                    }
                                  },
                                  "required": [
                                    "name",
                                    "value"
                                  ],
                                  "type": "object"
                                },
                                "type": "array"
                              },
                              "timestamp": {
                                "description": "Unix timestamp (seconds) of the period start.",
                                "type": "integer"
                              },
                              "value": {
                                "description": "The metric's value for this period, in the metric's unit.",
                                "nullable": true,
                                "type": "number"
                              }
                            },
                            "required": [
                              "timestamp",
                              "value"
                            ],
                            "type": "object"
                          },
                          "type": "array"
                        }
                      },
                      "required": [
                        "points"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "a user-scoped metric via a scoped OAuth token"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "subject tag type mismatch"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "a user-scoped metric with a credential that cannot act as a user"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "unknown account"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "stats:read"
            ]
          }
        ],
        "summary": "Retrieve Metric",
        "tags": [
          "Stats"
        ]
      }
    },
    "/swaps": {
      "get": {
        "description": "Lists the account's swaps. Currently returns the in-flight or most recent swap, so zero or one rows.",
        "operationId": "status",
        "parameters": [
          {
            "description": "Business or user account ID (biz_* / user_*).",
            "in": "query",
            "name": "account_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "description": "Swaps returned for this account.",
                      "items": {
                        "properties": {
                          "account_id": {
                            "description": "Account ID that owns the wallet used for the swap.",
                            "type": "string"
                          },
                          "error": {
                            "description": "Latest error returned for a failed swap.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "id": {
                            "description": "Swap ID.",
                            "type": "string"
                          },
                          "object": {
                            "enum": [
                              "swap"
                            ],
                            "type": "string"
                          },
                          "status": {
                            "description": "Current swap status.",
                            "type": "string"
                          },
                          "tx_hashes": {
                            "description": "On-chain transaction hashes produced by the swap.",
                            "items": {
                              "type": "string"
                            },
                            "type": "array"
                          }
                        },
                        "required": [
                          "object",
                          "id",
                          "account_id",
                          "status",
                          "tx_hashes"
                        ],
                        "type": "object"
                      },
                      "type": "array"
                    }
                  },
                  "required": [
                    "data"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "swaps listed"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the crypto-wallet scope"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "crypto_wallet:swap"
            ]
          }
        ],
        "summary": "List Swaps",
        "tags": [
          "Swaps"
        ]
      },
      "post": {
        "description": "Executes a swap from the account's wallet. Runs asynchronously; poll GET /swaps/{id} for status.",
        "operationId": "create",
        "parameters": [],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "account_id": {
                    "description": "Business or user account ID (biz_* / user_*).",
                    "type": "string"
                  },
                  "amount": {
                    "description": "Source token amount.",
                    "type": "string"
                  },
                  "from_chain": {
                    "description": "Source chain name or chain ID. Defaults to the source token's chain when omitted.",
                    "oneOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "integer"
                      },
                      {
                        "type": "null"
                      }
                    ]
                  },
                  "from_token": {
                    "description": "Source token contract address or ticker symbol, such as \"USDT\".",
                    "type": "string"
                  },
                  "slippage_bps": {
                    "description": "Maximum slippage tolerance in basis points.",
                    "type": [
                      "integer",
                      "null"
                    ]
                  },
                  "to_chain": {
                    "description": "Destination chain name or chain ID. Defaults to the destination token's chain when omitted.",
                    "oneOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "integer"
                      },
                      {
                        "type": "null"
                      }
                    ]
                  },
                  "to_token": {
                    "description": "Destination token contract address or ticker symbol, such as \"XAUT\".",
                    "type": "string"
                  }
                },
                "required": [
                  "account_id",
                  "amount",
                  "from_token",
                  "to_token"
                ],
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "201": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "account_id": {
                      "description": "Account ID that owns the wallet used for the swap.",
                      "type": "string"
                    },
                    "amount_out_expected": {
                      "description": "Expected destination token amount.",
                      "type": "string"
                    },
                    "amount_out_min": {
                      "description": "Minimum destination amount after slippage.",
                      "type": "string"
                    },
                    "id": {
                      "description": "Swap ID. Poll `GET /swaps/:id` for status.",
                      "type": "string"
                    },
                    "object": {
                      "enum": [
                        "swap"
                      ],
                      "type": "string"
                    },
                    "rate": {
                      "description": "Quoted exchange rate used to create the swap.",
                      "type": "string"
                    },
                    "status": {
                      "description": "Initial swap status.",
                      "type": "string"
                    },
                    "to_chain": {
                      "description": "Destination chain for the swap.",
                      "type": "string"
                    }
                  },
                  "required": [
                    "object",
                    "id",
                    "account_id",
                    "status"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "swap created"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "request is invalid"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the crypto-wallet scope"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "crypto_wallet:swap"
            ]
          }
        ],
        "summary": "Create Swap",
        "tags": [
          "Swaps"
        ]
      }
    },
    "/swaps/quote": {
      "post": {
        "description": "Returns a stateless swap price preview. No funds move and nothing is persisted.",
        "operationId": "quote",
        "parameters": [],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "amount": {
                    "description": "Source token amount.",
                    "type": "string"
                  },
                  "from_address": {
                    "description": "Source wallet address used for the quote.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "from_chain": {
                    "description": "Source chain name or chain ID. Defaults to the source token's chain when omitted.",
                    "oneOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "integer"
                      },
                      {
                        "type": "null"
                      }
                    ]
                  },
                  "from_token": {
                    "description": "Source token contract address or ticker symbol, such as \"USDT\".",
                    "type": "string"
                  },
                  "metadata": {
                    "additionalProperties": true,
                    "description": "Metadata to include with the quote response.",
                    "type": "object"
                  },
                  "slippage_bps": {
                    "description": "Maximum slippage tolerance in basis points.",
                    "type": [
                      "integer",
                      "null"
                    ]
                  },
                  "to_address": {
                    "description": "Destination wallet address used for the quote.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "to_chain": {
                    "description": "Destination chain name or chain ID. Defaults to the destination token's chain when omitted.",
                    "oneOf": [
                      {
                        "type": "string"
                      },
                      {
                        "type": "integer"
                      },
                      {
                        "type": "null"
                      }
                    ]
                  },
                  "to_token": {
                    "description": "Destination token contract address or ticker symbol, such as \"XAUT\".",
                    "type": "string"
                  }
                },
                "required": [
                  "amount",
                  "from_token",
                  "to_token"
                ],
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "amount_in": {
                      "description": "Source token amount used for the quote.",
                      "type": "string"
                    },
                    "amount_out": {
                      "description": "Estimated destination token amount.",
                      "type": "string"
                    },
                    "amount_out_min": {
                      "description": "Minimum destination amount after slippage.",
                      "type": "string"
                    },
                    "bridge_fee": {
                      "description": "Estimated bridge fee for cross-chain swaps.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "estimated_duration_seconds": {
                      "description": "Estimated time for the swap to complete.",
                      "type": [
                        "integer",
                        "null"
                      ]
                    },
                    "fee_bps": {
                      "description": "Whop fee in basis points.",
                      "type": "integer"
                    },
                    "from_address": {
                      "description": "Source wallet address used for the quote.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "from_token": {
                      "additionalProperties": true,
                      "description": "Resolved source token details.",
                      "type": "object"
                    },
                    "metadata": {
                      "additionalProperties": true,
                      "description": "Metadata from the request.",
                      "type": "object"
                    },
                    "object": {
                      "enum": [
                        "swap_quote"
                      ],
                      "type": "string"
                    },
                    "rate": {
                      "description": "Quoted exchange rate.",
                      "type": "string"
                    },
                    "requires_token_approval": {
                      "description": "Whether the source token needs approval before swapping.",
                      "type": [
                        "boolean",
                        "null"
                      ]
                    },
                    "to_address": {
                      "description": "Destination wallet address used for the quote.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "to_token": {
                      "additionalProperties": true,
                      "description": "Resolved destination token details.",
                      "type": "object"
                    }
                  },
                  "required": [
                    "object",
                    "amount_in",
                    "amount_out",
                    "rate",
                    "fee_bps",
                    "from_token",
                    "to_token",
                    "metadata"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "swap quote created"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "request is invalid"
          }
        },
        "security": [],
        "summary": "Create Swap Quote",
        "tags": [
          "Swaps"
        ]
      }
    },
    "/swaps/{id}": {
      "get": {
        "description": "Returns the status of a specific swap, by the id returned from POST /swaps.",
        "operationId": "get",
        "parameters": [
          {
            "description": "Swap ID returned from POST /swaps.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "account_id": {
                      "description": "Account ID that owns the wallet used for the swap.",
                      "type": "string"
                    },
                    "error": {
                      "description": "Latest error returned for a failed swap.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "id": {
                      "description": "Swap ID.",
                      "type": "string"
                    },
                    "object": {
                      "enum": [
                        "swap"
                      ],
                      "type": "string"
                    },
                    "status": {
                      "description": "Current swap status.",
                      "type": "string"
                    },
                    "tx_hashes": {
                      "description": "On-chain transaction hashes produced by the swap.",
                      "items": {
                        "type": "string"
                      },
                      "type": "array"
                    }
                  },
                  "required": [
                    "object",
                    "id",
                    "account_id",
                    "status",
                    "tx_hashes"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "swap status retrieved"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "swap id is invalid"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the crypto-wallet scope"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "swap does not exist"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "crypto_wallet:swap"
            ]
          }
        ],
        "summary": "Retrieve Swap",
        "tags": [
          "Swaps"
        ]
      }
    },
    "/transfers": {
      "get": {
        "description": "Lists ledger transfers for an account. You must specify an origin_id or a destination_id.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "Filter to transfers sent from this account.",
            "in": "query",
            "name": "origin_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Filter to transfers received by this account.",
            "in": "query",
            "name": "destination_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Sort column. Defaults to created_at.",
            "in": "query",
            "name": "order",
            "required": false,
            "schema": {
              "enum": [
                "created_at",
                "amount"
              ],
              "type": "string"
            }
          },
          {
            "description": "Sort direction. Defaults to desc.",
            "in": "query",
            "name": "direction",
            "required": false,
            "schema": {
              "enum": [
                "asc",
                "desc"
              ],
              "type": "string"
            }
          },
          {
            "description": "Only transfers created strictly before this ISO 8601 timestamp.",
            "in": "query",
            "name": "created_before",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Only transfers created strictly after this ISO 8601 timestamp.",
            "in": "query",
            "name": "created_after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Number of transfers to return from the start of the window.",
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "default": 50,
              "maximum": 50,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to fetch the page after (from page_info.end_cursor).",
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Number of transfers to return from the end of the window.",
            "in": "query",
            "name": "last",
            "required": false,
            "schema": {
              "maximum": 50,
              "type": "integer"
            }
          },
          {
            "description": "Cursor to fetch the page before (from page_info.start_cursor).",
            "in": "query",
            "name": "before",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "description": "A transfer of credit between two ledger accounts.",
                        "properties": {
                          "amount": {
                            "description": "Transfer amount.",
                            "type": "number"
                          },
                          "created_at": {
                            "description": "When the transfer was created.",
                            "format": "date-time",
                            "type": "string"
                          },
                          "currency": {
                            "description": "Transfer currency.",
                            "type": "string"
                          },
                          "destination_ledger_account_id": {
                            "description": "Destination ledger account ID.",
                            "type": "string"
                          },
                          "fee_amount": {
                            "description": "Fee charged for the transfer.",
                            "type": [
                              "number",
                              "null"
                            ]
                          },
                          "id": {
                            "description": "Transfer ID.",
                            "type": "string"
                          },
                          "metadata": {
                            "additionalProperties": true,
                            "description": "Custom metadata attached to the transfer.",
                            "type": [
                              "object",
                              "null"
                            ]
                          },
                          "notes": {
                            "description": "Transfer note.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "origin_ledger_account_id": {
                            "description": "Source ledger account ID.",
                            "type": "string"
                          }
                        },
                        "required": [
                          "id",
                          "amount",
                          "currency",
                          "created_at",
                          "origin_ledger_account_id",
                          "destination_ledger_account_id"
                        ],
                        "title": "TransferListItem",
                        "type": "object"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "has_next_page",
                        "has_previous_page",
                        "start_cursor",
                        "end_cursor"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "transfers listed"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "neither origin_id nor destination_id given"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "payout:transfer:read"
            ]
          }
        ],
        "summary": "List Transfers",
        "tags": [
          "Transfers"
        ]
      },
      "post": {
        "description": "Moves funds out of an account. `type` selects the kind of movement (default `ledger`): `ledger` transfers credit between two ledger accounts and returns a Transfer; `wallet_send` sends USDT from the origin account's Ethereum wallet to a recipient; `claim_link` funds a shareable claim link anyone with the URL can redeem.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "amount": {
                    "description": "The amount to move, in the transfer currency. For example 25.00.",
                    "type": "number"
                  },
                  "currency": {
                    "description": "Currency, such as `usd`. Required for ledger transfers.",
                    "example": "usd",
                    "type": "string"
                  },
                  "destination_id": {
                    "description": "The recipient. Required for ledger and wallet_send (a user_/biz_/ldgr_ ID, or — for sends — an email). Omit for claim_link.",
                    "type": "string"
                  },
                  "expires_at": {
                    "description": "claim_link only. Link expiry as an ISO 8601 timestamp. Defaults to 24 hours from creation.",
                    "format": "date-time",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "idempotence_key": {
                    "description": "Ledger transfers only. A unique key to prevent duplicate transfers.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "metadata": {
                    "additionalProperties": true,
                    "description": "Ledger transfers only. Custom key-value pairs attached to the transfer. Max 50 keys, 100 chars per key, 500 chars per string value.",
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "notes": {
                    "description": "Ledger transfers only. A short note describing the transfer.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "origin_id": {
                    "description": "The account sending the funds. A user ID (user_xxx), account ID (biz_xxx), or ledger account ID (ldgr_xxx).",
                    "type": "string"
                  },
                  "redeemable_count": {
                    "description": "claim_link only. How many different users can claim the link. Defaults to 1.",
                    "type": "integer"
                  },
                  "type": {
                    "description": "The kind of money movement. Defaults to ledger.",
                    "enum": [
                      "ledger",
                      "wallet_send",
                      "claim_link"
                    ],
                    "type": "string"
                  }
                },
                "required": [
                  "origin_id",
                  "amount"
                ],
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "201": {
            "content": {
              "application/json": {
                "schema": {
                  "oneOf": [
                    {
                      "description": "A transfer of credit between two ledger accounts.",
                      "properties": {
                        "amount": {
                          "description": "Transfer amount.",
                          "type": "number"
                        },
                        "created_at": {
                          "description": "When the transfer was created.",
                          "format": "date-time",
                          "type": "string"
                        },
                        "currency": {
                          "description": "Transfer currency.",
                          "type": "string"
                        },
                        "destination": {
                          "description": "Account or user receiving funds.",
                          "discriminator": {
                            "propertyName": "typename"
                          },
                          "oneOf": [
                            {
                              "properties": {
                                "id": {
                                  "description": "Account ID.",
                                  "type": "string"
                                },
                                "route": {
                                  "description": "Account route.",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "title": {
                                  "description": "Account display name.",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "typename": {
                                  "enum": [
                                    "Company"
                                  ],
                                  "type": "string"
                                }
                              },
                              "required": [
                                "typename",
                                "id"
                              ],
                              "title": "Company",
                              "type": "object"
                            },
                            {
                              "properties": {
                                "id": {
                                  "description": "User ID.",
                                  "type": "string"
                                },
                                "name": {
                                  "description": "User display name.",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "typename": {
                                  "enum": [
                                    "User"
                                  ],
                                  "type": "string"
                                },
                                "username": {
                                  "description": "User's username.",
                                  "type": "string"
                                }
                              },
                              "required": [
                                "typename",
                                "id"
                              ],
                              "title": "User",
                              "type": "object"
                            }
                          ]
                        },
                        "destination_ledger_account_id": {
                          "description": "Destination ledger account ID.",
                          "type": "string"
                        },
                        "fee_amount": {
                          "description": "Fee charged for the transfer.",
                          "type": [
                            "number",
                            "null"
                          ]
                        },
                        "id": {
                          "description": "Transfer ID.",
                          "type": "string"
                        },
                        "metadata": {
                          "additionalProperties": true,
                          "description": "Custom metadata attached to the transfer.",
                          "type": [
                            "object",
                            "null"
                          ]
                        },
                        "notes": {
                          "description": "Transfer note.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "origin": {
                          "description": "Account or user sending funds.",
                          "discriminator": {
                            "propertyName": "typename"
                          },
                          "oneOf": [
                            {
                              "properties": {
                                "id": {
                                  "description": "Account ID.",
                                  "type": "string"
                                },
                                "route": {
                                  "description": "Account route.",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "title": {
                                  "description": "Account display name.",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "typename": {
                                  "enum": [
                                    "Company"
                                  ],
                                  "type": "string"
                                }
                              },
                              "required": [
                                "typename",
                                "id"
                              ],
                              "title": "Company",
                              "type": "object"
                            },
                            {
                              "properties": {
                                "id": {
                                  "description": "User ID.",
                                  "type": "string"
                                },
                                "name": {
                                  "description": "User display name.",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "typename": {
                                  "enum": [
                                    "User"
                                  ],
                                  "type": "string"
                                },
                                "username": {
                                  "description": "User's username.",
                                  "type": "string"
                                }
                              },
                              "required": [
                                "typename",
                                "id"
                              ],
                              "title": "User",
                              "type": "object"
                            }
                          ]
                        },
                        "origin_ledger_account_id": {
                          "description": "Source ledger account ID.",
                          "type": "string"
                        }
                      },
                      "required": [
                        "id",
                        "amount",
                        "currency",
                        "created_at",
                        "origin_ledger_account_id",
                        "destination_ledger_account_id",
                        "origin",
                        "destination"
                      ],
                      "title": "Transfer",
                      "type": "object"
                    },
                    {
                      "description": "Returned for a wallet_send: an onchain USDT send to a recipient.",
                      "properties": {
                        "amount": {
                          "type": "string"
                        },
                        "currency": {
                          "type": "string"
                        },
                        "destination": {
                          "properties": {
                            "account_id": {
                              "type": "string"
                            },
                            "address": {
                              "type": "string"
                            }
                          },
                          "required": [
                            "account_id",
                            "address"
                          ],
                          "type": "object"
                        },
                        "object": {
                          "enum": [
                            "send"
                          ],
                          "type": "string"
                        },
                        "source": {
                          "properties": {
                            "account_id": {
                              "type": "string"
                            },
                            "address": {
                              "type": "string"
                            }
                          },
                          "required": [
                            "account_id",
                            "address"
                          ],
                          "type": "object"
                        },
                        "tx_hash": {
                          "type": "string"
                        }
                      },
                      "required": [
                        "object",
                        "tx_hash",
                        "amount",
                        "currency",
                        "source",
                        "destination"
                      ],
                      "title": "Send",
                      "type": "object"
                    },
                    {
                      "description": "Returned for a claim_link: a shareable URL anyone can open to claim the funds.",
                      "properties": {
                        "amount": {
                          "type": "string"
                        },
                        "claim_url": {
                          "type": "string"
                        },
                        "currency": {
                          "type": "string"
                        },
                        "expires_at": {
                          "format": "date-time",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "id": {
                          "type": "string"
                        },
                        "object": {
                          "enum": [
                            "claim_link"
                          ],
                          "type": "string"
                        },
                        "redeemable_count": {
                          "type": "integer"
                        },
                        "source": {
                          "properties": {
                            "account_id": {
                              "type": "string"
                            }
                          },
                          "required": [
                            "account_id"
                          ],
                          "type": "object"
                        },
                        "status": {
                          "type": "string"
                        }
                      },
                      "required": [
                        "object",
                        "id",
                        "claim_url",
                        "status",
                        "amount",
                        "currency",
                        "redeemable_count",
                        "expires_at",
                        "source"
                      ],
                      "title": "ClaimLink",
                      "type": "object"
                    }
                  ]
                }
              }
            },
            "description": "money movement created"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "request is invalid"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the required scope"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "payout:transfer_funds"
            ]
          },
          {
            "bearerAuth": [
              "payout:withdraw_funds"
            ]
          },
          {
            "bearerAuth": [
              "airdrop_link:manage"
            ]
          }
        ],
        "summary": "Create Transfer",
        "tags": [
          "Transfers"
        ]
      }
    },
    "/transfers/{id}": {
      "get": {
        "description": "Retrieves a ledger transfer by ID.",
        "operationId": "get",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "description": "A transfer of credit between two ledger accounts.",
                  "properties": {
                    "amount": {
                      "description": "Transfer amount.",
                      "type": "number"
                    },
                    "created_at": {
                      "description": "When the transfer was created.",
                      "format": "date-time",
                      "type": "string"
                    },
                    "currency": {
                      "description": "Transfer currency.",
                      "type": "string"
                    },
                    "destination": {
                      "description": "Account or user receiving funds.",
                      "discriminator": {
                        "propertyName": "typename"
                      },
                      "oneOf": [
                        {
                          "properties": {
                            "id": {
                              "description": "Account ID.",
                              "type": "string"
                            },
                            "route": {
                              "description": "Account route.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "title": {
                              "description": "Account display name.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "typename": {
                              "enum": [
                                "Company"
                              ],
                              "type": "string"
                            }
                          },
                          "required": [
                            "typename",
                            "id"
                          ],
                          "title": "Company",
                          "type": "object"
                        },
                        {
                          "properties": {
                            "id": {
                              "description": "User ID.",
                              "type": "string"
                            },
                            "name": {
                              "description": "User display name.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "typename": {
                              "enum": [
                                "User"
                              ],
                              "type": "string"
                            },
                            "username": {
                              "description": "User's username.",
                              "type": "string"
                            }
                          },
                          "required": [
                            "typename",
                            "id"
                          ],
                          "title": "User",
                          "type": "object"
                        }
                      ]
                    },
                    "destination_ledger_account_id": {
                      "description": "Destination ledger account ID.",
                      "type": "string"
                    },
                    "fee_amount": {
                      "description": "Fee charged for the transfer.",
                      "type": [
                        "number",
                        "null"
                      ]
                    },
                    "id": {
                      "description": "Transfer ID.",
                      "type": "string"
                    },
                    "metadata": {
                      "additionalProperties": true,
                      "description": "Custom metadata attached to the transfer.",
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "notes": {
                      "description": "Transfer note.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "origin": {
                      "description": "Account or user sending funds.",
                      "discriminator": {
                        "propertyName": "typename"
                      },
                      "oneOf": [
                        {
                          "properties": {
                            "id": {
                              "description": "Account ID.",
                              "type": "string"
                            },
                            "route": {
                              "description": "Account route.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "title": {
                              "description": "Account display name.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "typename": {
                              "enum": [
                                "Company"
                              ],
                              "type": "string"
                            }
                          },
                          "required": [
                            "typename",
                            "id"
                          ],
                          "title": "Company",
                          "type": "object"
                        },
                        {
                          "properties": {
                            "id": {
                              "description": "User ID.",
                              "type": "string"
                            },
                            "name": {
                              "description": "User display name.",
                              "type": [
                                "string",
                                "null"
                              ]
                            },
                            "typename": {
                              "enum": [
                                "User"
                              ],
                              "type": "string"
                            },
                            "username": {
                              "description": "User's username.",
                              "type": "string"
                            }
                          },
                          "required": [
                            "typename",
                            "id"
                          ],
                          "title": "User",
                          "type": "object"
                        }
                      ]
                    },
                    "origin_ledger_account_id": {
                      "description": "Source ledger account ID.",
                      "type": "string"
                    }
                  },
                  "required": [
                    "id",
                    "amount",
                    "currency",
                    "created_at",
                    "origin_ledger_account_id",
                    "destination_ledger_account_id",
                    "origin",
                    "destination"
                  ],
                  "title": "Transfer",
                  "type": "object"
                }
              }
            },
            "description": "transfer retrieved"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "transfer not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "payout:transfer:read"
            ]
          }
        ],
        "summary": "Retrieve Transfer",
        "tags": [
          "Transfers"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The transfer ID.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ]
      }
    },
    "/users": {
      "get": {
        "description": "Search for users by name or username, ranked by social proximity to the authenticated user. Returns the user's most recently followed users when no query is given.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "A search term to filter users by name or username.",
            "in": "query",
            "name": "query",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The number of users to return (max 50).",
            "in": "query",
            "name": "first",
            "required": false,
            "schema": {
              "type": "integer"
            }
          },
          {
            "description": "A cursor; returns users after this position.",
            "in": "query",
            "name": "after",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The number of users to return from the end of the range.",
            "in": "query",
            "name": "last",
            "required": false,
            "schema": {
              "type": "integer"
            }
          },
          {
            "description": "A cursor; returns users before this position.",
            "in": "query",
            "name": "before",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "$ref": "#/components/schemas/User"
                      },
                      "type": "array"
                    },
                    "page_info": {
                      "properties": {
                        "end_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "has_next_page": {
                          "type": "boolean"
                        },
                        "has_previous_page": {
                          "type": "boolean"
                        },
                        "start_cursor": {
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "required": [
                        "end_cursor",
                        "start_cursor",
                        "has_next_page",
                        "has_previous_page"
                      ],
                      "type": "object"
                    }
                  },
                  "required": [
                    "data",
                    "page_info"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "users listed"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "dms:read"
            ]
          }
        ],
        "summary": "List Users",
        "tags": [
          "Users"
        ]
      }
    },
    "/users/me": {
      "patch": {
        "description": "Updates the authenticated user's global profile, or their profile override for an account when account_id is given. Not available to API keys.",
        "operationId": "me",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "When set, updates the authenticated user's profile override for this account instead of their global profile.",
            "in": "query",
            "name": "account_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "bio": {
                    "type": "string"
                  },
                  "name": {
                    "type": "string"
                  },
                  "profile_picture": {
                    "properties": {
                      "direct_upload_id": {
                        "type": "string"
                      },
                      "id": {
                        "type": "string"
                      }
                    },
                    "type": "object"
                  },
                  "username": {
                    "type": "string"
                  }
                },
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/User"
                }
              }
            },
            "description": "current user updated"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "API keys cannot update a global profile"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "user:profile:update"
            ]
          }
        ],
        "summary": "Update Current User",
        "tags": [
          "Users"
        ]
      }
    },
    "/users/{id}": {
      "get": {
        "description": "Retrieves a user's public profile by user_ tag, username, or 'me'.",
        "operationId": "get",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "User ID (prefixed `user_`), username, or `me`.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "When set, returns the user's account-specific profile overrides for this account.",
            "in": "query",
            "name": "account_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "On `GET /users/me`, also compute the caller's balance history (opt-in; runs a heavier query). Ignored for other users and for callers without balance-read scope.",
            "in": "query",
            "name": "include_balance_history",
            "required": false,
            "schema": {
              "type": "boolean"
            }
          },
          {
            "description": "Balance-history window start, ISO 8601 date or datetime. Defaults to 30 days ago. Only used with `include_balance_history`.",
            "in": "query",
            "name": "from",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Balance-history window end, ISO 8601 date or datetime. Defaults to now. Only used with `include_balance_history`.",
            "in": "query",
            "name": "to",
            "required": false,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Balance-history point granularity. Defaults to `day`. Only used with `include_balance_history`.",
            "in": "query",
            "name": "interval",
            "required": false,
            "schema": {
              "enum": [
                "hour",
                "day",
                "week",
                "month"
              ],
              "type": "string"
            }
          },
          {
            "description": "IANA time zone the balance-history points are bucketed in. Defaults to `UTC`. Only used with `include_balance_history`.",
            "in": "query",
            "name": "time_zone",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/User"
                }
              }
            },
            "description": "user retrieved"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "no user with that id"
          }
        },
        "security": [
          {
            "bearerAuth": []
          }
        ],
        "summary": "Retrieve User",
        "tags": [
          "Users"
        ]
      },
      "patch": {
        "description": "Updates a user. A user token updates their own global profile; an API key updates the user's account-specific profile override (account_id required).",
        "operationId": "update",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "User ID (prefixed `user_`), username, or `me`.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "The account whose profile override to update. Required for API key callers.",
            "in": "query",
            "name": "account_id",
            "required": false,
            "schema": {
              "type": "string"
            }
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "properties": {
                  "bio": {
                    "type": "string"
                  },
                  "name": {
                    "type": "string"
                  },
                  "profile_picture": {
                    "properties": {
                      "direct_upload_id": {
                        "type": "string"
                      },
                      "id": {
                        "type": "string"
                      }
                    },
                    "type": "object"
                  },
                  "username": {
                    "type": "string"
                  }
                },
                "type": "object"
              }
            }
          },
          "required": true
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "$ref": "#/components/schemas/User"
                }
              }
            },
            "description": "user updated"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "user:profile:update"
            ]
          }
        ],
        "summary": "Update User",
        "tags": [
          "Users"
        ]
      }
    },
    "/users/{id}/access/{resource_id}": {
      "get": {
        "description": "Checks whether a user has access to an account, product, or experience the caller can reach.",
        "operationId": "access",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "access_level": {
                      "enum": [
                        "no_access",
                        "admin",
                        "customer"
                      ],
                      "type": "string"
                    },
                    "has_access": {
                      "type": "boolean"
                    }
                  },
                  "required": [
                    "has_access",
                    "access_level"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "access checked"
          }
        },
        "security": [
          {
            "bearerAuth": []
          }
        ],
        "summary": "Check User Access",
        "tags": [
          "Users"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "The user_ tag or username to check access for.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "An account (biz_), product (prod_), or experience (exp_) ID.",
            "in": "path",
            "name": "resource_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ]
      }
    },
    "/users/{id}/recommend_actions": {
      "get": {
        "description": "Lists the recommended actions computed for the user: personal suggestions (e.g. start a business or become an affiliate) pooled with the highest-impact actions across the accounts the user owns. Business actions are tagged with their `account_id`/`account_name`; personal actions leave those `null`. Self-only: `id` must be `me` or the authenticated user's own tag/username.",
        "operationId": "recommend_actions",
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "$ref": "#/components/schemas/UserRecommendedAction"
                      },
                      "type": "array"
                    }
                  },
                  "required": [
                    "data"
                  ],
                  "type": "object"
                }
              }
            },
            "description": "recommended actions listed"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "reading another user's recommended actions"
          }
        },
        "security": [
          {
            "bearerAuth": []
          }
        ],
        "summary": "List Recommended Actions",
        "tags": [
          "Users"
        ],
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "`me`, or the authenticated user's own `user_` tag or username.",
            "in": "path",
            "name": "id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ]
      }
    },
    "/verifications": {
      "get": {
        "description": "Returns verifications for an account, including their status and any required actions.",
        "operationId": "list",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "Account or user ID whose verifications you want to list. Use a `biz_` account ID, or the caller's `user_` ID for personal verifications.",
            "in": "query",
            "name": "account_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          },
          {
            "description": "Field used to sort returned verifications.",
            "in": "query",
            "name": "order",
            "required": false,
            "schema": {
              "default": "updated_at",
              "enum": [
                "updated_at",
                "created_at"
              ],
              "type": "string"
            }
          },
          {
            "description": "Sort direction for returned verifications.",
            "in": "query",
            "name": "direction",
            "required": false,
            "schema": {
              "default": "desc",
              "enum": [
                "asc",
                "desc"
              ],
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "data": {
                      "items": {
                        "properties": {
                          "address": {
                            "description": "Address on the verification profile. `null` when no address is set.",
                            "properties": {
                              "city": {
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "country": {
                                "description": "Two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "line1": {
                                "description": "First line of the street address.",
                                "type": "string"
                              },
                              "line2": {
                                "description": "Second line of the street address.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "postal_code": {
                                "description": "Postal or ZIP code.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              },
                              "state": {
                                "description": "State, province, or region code, for example `CA`.",
                                "type": [
                                  "string",
                                  "null"
                                ]
                              }
                            },
                            "type": [
                              "object",
                              "null"
                            ]
                          },
                          "business_name": {
                            "description": "Legal business name.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "business_structure": {
                            "description": "Legal entity structure of the business, such as `private_corporation` or `sole_proprietorship`. Supported values vary by country of incorporation — see [Business structures](/developer/verification/business-structures).",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "country": {
                            "description": "Two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "created_at": {
                            "description": "When the verification profile was created, as an ISO 8601 timestamp.",
                            "type": "string"
                          },
                          "date_of_birth": {
                            "description": "Formatted as `YYYY-MM-DD`.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "first_name": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "id": {
                            "description": "Verification profile ID, prefixed `idpf_`.",
                            "type": "string"
                          },
                          "kind": {
                            "enum": [
                              "individual",
                              "business"
                            ],
                            "type": "string"
                          },
                          "last_name": {
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "requested_information": {
                            "description": "Fields or documents Whop still needs before review can continue. Submit answers with the Update Verification endpoint.",
                            "items": {
                              "properties": {
                                "description": {
                                  "description": "Additional instructions for this requested item, or `null`.",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "error_message": {
                                  "description": "Reason a previously submitted value was rejected. `null` if no submitted value has been rejected.",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "field": {
                                  "description": "Stable field key, such as `ssn` or `business_description`.",
                                  "type": "string"
                                },
                                "id": {
                                  "description": "Requested information item ID, prefixed `inrqi_`. Include this ID when submitting an answer.",
                                  "type": "string"
                                },
                                "label": {
                                  "description": "Human-readable label for the field, such as `Social Security Number`.",
                                  "type": "string"
                                },
                                "options": {
                                  "description": "Allowed values for a `select` field (e.g. account_type, business_structure) — the submitted value must be one of these; empty for other types.",
                                  "items": {
                                    "type": "string"
                                  },
                                  "type": "array"
                                },
                                "requested_files": {
                                  "description": "Document upload slots for this item. Present when `type` is `files`; upload one file for each required slot and include the slot's `category` when submitting the answer.",
                                  "items": {
                                    "properties": {
                                      "category": {
                                        "description": "File category to include with the uploaded file so Whop can route the document correctly. `null` for a generic upload.",
                                        "type": [
                                          "string",
                                          "null"
                                        ]
                                      },
                                      "is_optional": {
                                        "description": "Whether this slot can be left empty.",
                                        "type": "boolean"
                                      },
                                      "kind": {
                                        "description": "Specific document type requested, such as `Bank Statement`. `null` for standard identity and business document uploads.",
                                        "type": [
                                          "string",
                                          "null"
                                        ]
                                      },
                                      "label": {
                                        "description": "Label for this upload slot, such as `Front of ID Document`.",
                                        "type": "string"
                                      },
                                      "multiple": {
                                        "description": "Whether this slot accepts more than one file.",
                                        "type": "boolean"
                                      }
                                    },
                                    "type": "object"
                                  },
                                  "type": "array"
                                },
                                "type": {
                                  "description": "Input type expected for this item: `text`, `date`, `phone`, `address`, `files`, or `select`.",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                }
                              },
                              "type": "object"
                            },
                            "type": "array"
                          },
                          "required_documents": {
                            "description": "Documents for a document-upload verification and their progress. Present only on verifications created by sending `documents`. `pending_upload` documents were not accepted yet — send the full set again with another Create Verification call.",
                            "items": {
                              "properties": {
                                "document": {
                                  "description": "Document slot key, such as `id_card_front`, `id_card_back`, or `selfie`.",
                                  "type": "string"
                                },
                                "rejection_reason": {
                                  "description": "Why the previous submission was rejected, when the provider requested new documents or declined the verification.",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "status": {
                                  "description": "`pending_upload` until the document has been relayed for review; `submitted` afterwards.",
                                  "enum": [
                                    "pending_upload",
                                    "submitted"
                                  ],
                                  "type": "string"
                                }
                              },
                              "type": "object"
                            },
                            "type": "array"
                          },
                          "session_url": {
                            "description": "Hosted verification session URL for the user to complete identity checks. Expires 7 days after creation.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "status": {
                            "description": "Current verification state. `not_started` before any session has been created; `pending` while a session is in progress and needs the user's input; `processing` while the provider reviews submitted documents — nothing to do but wait; `action_required` when items in `requested_information` need answers before review can continue; `approved` once verification succeeds; `rejected` if it fails. Call the Create Verification endpoint again to start a new session.",
                            "enum": [
                              "not_started",
                              "pending",
                              "processing",
                              "approved",
                              "rejected",
                              "action_required"
                            ],
                            "type": "string"
                          },
                          "updated_at": {
                            "description": "When the verification profile was last updated, as an ISO 8601 timestamp.",
                            "type": "string"
                          }
                        },
                        "type": "object"
                      },
                      "type": "array"
                    }
                  },
                  "type": "object"
                }
              }
            },
            "description": "OK"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "missing account_id"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the required identity-read scope"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "identity:read"
            ]
          }
        ],
        "summary": "List Verifications",
        "tags": [
          "Verifications"
        ]
      },
      "post": {
        "description": "Starts a hosted verification session for an account or user, or returns the active session when one already exists. Any fields you include in the request body are used to prefill the session. Send `documents` (with `document_type`) to instead verify the person from identity documents included in this request — no hosted session involved. If the account already has an `approved` verification the request is rejected; unlink it first to start a new one.",
        "operationId": "create",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "Account or user ID whose identity you want to verify. Use a `biz_` account ID for account verifications, or the caller's `user_` ID for personal verification.",
            "in": "query",
            "name": "account_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "discriminator": {
                  "propertyName": "kind"
                },
                "oneOf": [
                  {
                    "description": "Request body for an individual (KYC) verification. Omit `kind` or set it to `individual`. KYC is required to pay out funds and is a prerequisite for Whop Card access. Accepting payments does not require verification until a business reaches $5000 in payments.\n\nAdd `business_name` and `business_structure` if the individual operates under a business entity — this enables payouts to be received by a business bank account. `country` is always the individual's own country, and the supported `business_structure` values vary by it — see [Business structures](/developer/verification/business-structures).",
                    "properties": {
                      "address": {
                        "properties": {
                          "city": {
                            "type": "string"
                          },
                          "country": {
                            "description": "Two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`.",
                            "type": "string"
                          },
                          "line1": {
                            "description": "First line of the street address.",
                            "type": "string"
                          },
                          "line2": {
                            "description": "Second line of the street address.",
                            "type": "string"
                          },
                          "postal_code": {
                            "description": "Postal or ZIP code.",
                            "type": "string"
                          },
                          "state": {
                            "description": "State, province, or region code, for example `CA`.",
                            "type": "string"
                          }
                        },
                        "type": "object"
                      },
                      "business_name": {
                        "description": "Legal business name for a sole proprietor or single-member LLC.",
                        "type": "string"
                      },
                      "business_structure": {
                        "description": "Entity type for sole proprietors, such as `single_member_llc`. Supported values vary by country of incorporation — see [Business structures](/developer/verification/business-structures).",
                        "type": "string"
                      },
                      "business_tax_identification_number": {
                        "description": "The business ID number of the company, as appropriate for the company's country. Examples are an Employer Identification Number (EIN) in the US, a Business Number in Canada, or a Company Number in the UK.",
                        "type": "string"
                      },
                      "business_website": {
                        "description": "Business website URL. Whop store pages are not accepted.",
                        "type": "string"
                      },
                      "country": {
                        "description": "Two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`.",
                        "type": "string"
                      },
                      "date_of_birth": {
                        "description": "Formatted as `YYYY-MM-DD`.",
                        "type": "string"
                      },
                      "document_type": {
                        "description": "Identity document being sent, when verifying with `documents`. Decides exactly which file slots to send: `ID_CARD` → `id_card_front` + `id_card_back` + `selfie`; `DRIVERS` → `drivers_front` + `drivers_back` + `selfie`; `RESIDENCE_PERMIT` → `residence_permit_front` + `residence_permit_back` + `selfie`; `PASSPORT` → `passport_front` + `selfie`. See [Identity documents](/developer/verification/identity-documents).",
                        "enum": [
                          "ID_CARD",
                          "DRIVERS",
                          "RESIDENCE_PERMIT",
                          "PASSPORT"
                        ],
                        "type": "string"
                      },
                      "documents": {
                        "description": "Identity document files, each value the file's raw bytes base64-encoded (JPEG, PNG, or PDF, up to 5MB per file before encoding). Sending this object verifies the person from the files in this request instead of a hosted session — individual verifications only, and the request must also carry `document_type`, `first_name`, `last_name`, `date_of_birth`, `country`, `phone`, `tax_identification_number`, and an `address` with `line1`, `city`, `state`, and `postal_code`. Send every slot for your `document_type` — a missing or rejected file fails the whole request and nothing is submitted; review starts automatically once every document is accepted. See [Identity documents](/developer/verification/identity-documents) for a full walkthrough.",
                        "properties": {
                          "drivers_back": {
                            "description": "Back of the driver's license, base64-encoded. Required when `document_type` is `DRIVERS`.",
                            "type": "string"
                          },
                          "drivers_front": {
                            "description": "Front of the driver's license, base64-encoded. Required when `document_type` is `DRIVERS`.",
                            "type": "string"
                          },
                          "id_card_back": {
                            "description": "Back of the ID card, base64-encoded. Required when `document_type` is `ID_CARD`.",
                            "type": "string"
                          },
                          "id_card_front": {
                            "description": "Front of the ID card, base64-encoded. Required when `document_type` is `ID_CARD`.",
                            "type": "string"
                          },
                          "passport_front": {
                            "description": "Photo page of the passport, base64-encoded. Required when `document_type` is `PASSPORT`.",
                            "type": "string"
                          },
                          "residence_permit_back": {
                            "description": "Back of the residence permit, base64-encoded. Required when `document_type` is `RESIDENCE_PERMIT`.",
                            "type": "string"
                          },
                          "residence_permit_front": {
                            "description": "Front of the residence permit, base64-encoded. Required when `document_type` is `RESIDENCE_PERMIT`.",
                            "type": "string"
                          },
                          "selfie": {
                            "description": "Photo of the person's face, base64-encoded. Always required, with every document type. Must be JPEG or PNG.",
                            "type": "string"
                          }
                        },
                        "type": "object"
                      },
                      "first_name": {
                        "type": "string"
                      },
                      "kind": {
                        "description": "Verification type. Defaults to `individual`.",
                        "enum": [
                          "individual"
                        ],
                        "type": "string"
                      },
                      "last_name": {
                        "type": "string"
                      },
                      "phone": {
                        "type": "string"
                      },
                      "tax_identification_number": {
                        "description": "The government-issued ID number of the person being verified — the individual for a KYC verification, or the business representative for a KYB verification — as appropriate for their country. Examples are a Social Security Number (SSN) in the US, or a Social Insurance Number in Canada.",
                        "type": "string"
                      }
                    },
                    "title": "CreateIndividualVerification",
                    "type": "object"
                  },
                  {
                    "description": "Request body for a business entity (KYB) verification. Set `kind` to `business`. KYB includes everything KYC provides and additionally unlocks financing options for payments and business Whop Cards.",
                    "properties": {
                      "address": {
                        "properties": {
                          "city": {
                            "type": "string"
                          },
                          "country": {
                            "description": "Two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`.",
                            "type": "string"
                          },
                          "line1": {
                            "description": "First line of the street address.",
                            "type": "string"
                          },
                          "line2": {
                            "description": "Second line of the street address.",
                            "type": "string"
                          },
                          "postal_code": {
                            "description": "Postal or ZIP code.",
                            "type": "string"
                          },
                          "state": {
                            "description": "State, province, or region code, for example `CA`.",
                            "type": "string"
                          }
                        },
                        "type": "object"
                      },
                      "business_name": {
                        "description": "Legal business name.",
                        "type": "string"
                      },
                      "business_structure": {
                        "description": "Legal entity structure of the business, such as `private_corporation` or `sole_proprietorship`. Supported values vary by country of incorporation — see [Business structures](/developer/verification/business-structures).",
                        "type": "string"
                      },
                      "business_tax_identification_number": {
                        "description": "The business ID number of the company, as appropriate for the company's country. Examples are an Employer Identification Number (EIN) in the US, a Business Number in Canada, or a Company Number in the UK.",
                        "type": "string"
                      },
                      "business_website": {
                        "description": "Business website URL. Whop store pages are not accepted.",
                        "type": "string"
                      },
                      "country": {
                        "description": "Country of incorporation as a two-letter ISO 3166-1 country code.",
                        "type": "string"
                      },
                      "kind": {
                        "description": "Must be `business` to start a KYB verification.",
                        "enum": [
                          "business"
                        ],
                        "type": "string"
                      },
                      "place_of_incorporation": {
                        "description": "State or region where the business is incorporated.",
                        "type": "string"
                      },
                      "tax_identification_number": {
                        "description": "The government-issued ID number of the person being verified — the individual for a KYC verification, or the business representative for a KYB verification — as appropriate for their country. Examples are a Social Security Number (SSN) in the US, or a Social Insurance Number in Canada.",
                        "type": "string"
                      }
                    },
                    "title": "CreateBusinessVerification",
                    "type": "object"
                  }
                ]
              }
            }
          }
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "address": {
                      "description": "Address on the verification profile. `null` when no address is set.",
                      "properties": {
                        "city": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "country": {
                          "description": "Two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "line1": {
                          "description": "First line of the street address.",
                          "type": "string"
                        },
                        "line2": {
                          "description": "Second line of the street address.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "postal_code": {
                          "description": "Postal or ZIP code.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "state": {
                          "description": "State, province, or region code, for example `CA`.",
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "business_name": {
                      "description": "Legal business name.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "business_structure": {
                      "description": "Legal entity structure of the business, such as `private_corporation` or `sole_proprietorship`. Supported values vary by country of incorporation — see [Business structures](/developer/verification/business-structures).",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "country": {
                      "description": "Two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "created_at": {
                      "description": "When the verification profile was created, as an ISO 8601 timestamp.",
                      "type": "string"
                    },
                    "date_of_birth": {
                      "description": "Formatted as `YYYY-MM-DD`.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "first_name": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "id": {
                      "description": "Verification profile ID, prefixed `idpf_`.",
                      "type": "string"
                    },
                    "kind": {
                      "enum": [
                        "individual",
                        "business"
                      ],
                      "type": "string"
                    },
                    "last_name": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "requested_information": {
                      "description": "Fields or documents Whop still needs before review can continue. Submit answers with the Update Verification endpoint.",
                      "items": {
                        "properties": {
                          "description": {
                            "description": "Additional instructions for this requested item, or `null`.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "error_message": {
                            "description": "Reason a previously submitted value was rejected. `null` if no submitted value has been rejected.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "field": {
                            "description": "Stable field key, such as `ssn` or `business_description`.",
                            "type": "string"
                          },
                          "id": {
                            "description": "Requested information item ID, prefixed `inrqi_`. Include this ID when submitting an answer.",
                            "type": "string"
                          },
                          "label": {
                            "description": "Human-readable label for the field, such as `Social Security Number`.",
                            "type": "string"
                          },
                          "options": {
                            "description": "Allowed values for a `select` field (e.g. account_type, business_structure) — the submitted value must be one of these; empty for other types.",
                            "items": {
                              "type": "string"
                            },
                            "type": "array"
                          },
                          "requested_files": {
                            "description": "Document upload slots for this item. Present when `type` is `files`; upload one file for each required slot and include the slot's `category` when submitting the answer.",
                            "items": {
                              "properties": {
                                "category": {
                                  "description": "File category to include with the uploaded file so Whop can route the document correctly. `null` for a generic upload.",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "is_optional": {
                                  "description": "Whether this slot can be left empty.",
                                  "type": "boolean"
                                },
                                "kind": {
                                  "description": "Specific document type requested, such as `Bank Statement`. `null` for standard identity and business document uploads.",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "label": {
                                  "description": "Label for this upload slot, such as `Front of ID Document`.",
                                  "type": "string"
                                },
                                "multiple": {
                                  "description": "Whether this slot accepts more than one file.",
                                  "type": "boolean"
                                }
                              },
                              "type": "object"
                            },
                            "type": "array"
                          },
                          "type": {
                            "description": "Input type expected for this item: `text`, `date`, `phone`, `address`, `files`, or `select`.",
                            "type": [
                              "string",
                              "null"
                            ]
                          }
                        },
                        "type": "object"
                      },
                      "type": "array"
                    },
                    "required_documents": {
                      "description": "Documents for a document-upload verification and their progress. Present only on verifications created by sending `documents`. `pending_upload` documents were not accepted yet — send the full set again with another Create Verification call.",
                      "items": {
                        "properties": {
                          "document": {
                            "description": "Document slot key, such as `id_card_front`, `id_card_back`, or `selfie`.",
                            "type": "string"
                          },
                          "rejection_reason": {
                            "description": "Why the previous submission was rejected, when the provider requested new documents or declined the verification.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "status": {
                            "description": "`pending_upload` until the document has been relayed for review; `submitted` afterwards.",
                            "enum": [
                              "pending_upload",
                              "submitted"
                            ],
                            "type": "string"
                          }
                        },
                        "type": "object"
                      },
                      "type": "array"
                    },
                    "session_url": {
                      "description": "Hosted verification session URL for the user to complete identity checks. Expires 7 days after creation.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "status": {
                      "description": "Current verification state. `not_started` before any session has been created; `pending` while a session is in progress and needs the user's input; `processing` while the provider reviews submitted documents — nothing to do but wait; `action_required` when items in `requested_information` need answers before review can continue; `approved` once verification succeeds; `rejected` if it fails. Call the Create Verification endpoint again to start a new session.",
                      "enum": [
                        "not_started",
                        "pending",
                        "processing",
                        "approved",
                        "rejected",
                        "action_required"
                      ],
                      "type": "string"
                    },
                    "updated_at": {
                      "description": "When the verification profile was last updated, as an ISO 8601 timestamp.",
                      "type": "string"
                    }
                  },
                  "type": "object"
                }
              }
            },
            "description": "OK"
          },
          "201": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "address": {
                      "description": "Address on the verification profile. `null` when no address is set.",
                      "properties": {
                        "city": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "country": {
                          "description": "Two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "line1": {
                          "description": "First line of the street address.",
                          "type": "string"
                        },
                        "line2": {
                          "description": "Second line of the street address.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "postal_code": {
                          "description": "Postal or ZIP code.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "state": {
                          "description": "State, province, or region code, for example `CA`.",
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "business_name": {
                      "description": "Legal business name.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "business_structure": {
                      "description": "Legal entity structure of the business, such as `private_corporation` or `sole_proprietorship`. Supported values vary by country of incorporation — see [Business structures](/developer/verification/business-structures).",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "country": {
                      "description": "Two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "created_at": {
                      "description": "When the verification profile was created, as an ISO 8601 timestamp.",
                      "type": "string"
                    },
                    "date_of_birth": {
                      "description": "Formatted as `YYYY-MM-DD`.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "first_name": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "id": {
                      "description": "Verification profile ID, prefixed `idpf_`.",
                      "type": "string"
                    },
                    "kind": {
                      "enum": [
                        "individual",
                        "business"
                      ],
                      "type": "string"
                    },
                    "last_name": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "requested_information": {
                      "description": "Fields or documents Whop still needs before review can continue. Submit answers with the Update Verification endpoint.",
                      "items": {
                        "properties": {
                          "description": {
                            "description": "Additional instructions for this requested item, or `null`.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "error_message": {
                            "description": "Reason a previously submitted value was rejected. `null` if no submitted value has been rejected.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "field": {
                            "description": "Stable field key, such as `ssn` or `business_description`.",
                            "type": "string"
                          },
                          "id": {
                            "description": "Requested information item ID, prefixed `inrqi_`. Include this ID when submitting an answer.",
                            "type": "string"
                          },
                          "label": {
                            "description": "Human-readable label for the field, such as `Social Security Number`.",
                            "type": "string"
                          },
                          "options": {
                            "description": "Allowed values for a `select` field (e.g. account_type, business_structure) — the submitted value must be one of these; empty for other types.",
                            "items": {
                              "type": "string"
                            },
                            "type": "array"
                          },
                          "requested_files": {
                            "description": "Document upload slots for this item. Present when `type` is `files`; upload one file for each required slot and include the slot's `category` when submitting the answer.",
                            "items": {
                              "properties": {
                                "category": {
                                  "description": "File category to include with the uploaded file so Whop can route the document correctly. `null` for a generic upload.",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "is_optional": {
                                  "description": "Whether this slot can be left empty.",
                                  "type": "boolean"
                                },
                                "kind": {
                                  "description": "Specific document type requested, such as `Bank Statement`. `null` for standard identity and business document uploads.",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "label": {
                                  "description": "Label for this upload slot, such as `Front of ID Document`.",
                                  "type": "string"
                                },
                                "multiple": {
                                  "description": "Whether this slot accepts more than one file.",
                                  "type": "boolean"
                                }
                              },
                              "type": "object"
                            },
                            "type": "array"
                          },
                          "type": {
                            "description": "Input type expected for this item: `text`, `date`, `phone`, `address`, `files`, or `select`.",
                            "type": [
                              "string",
                              "null"
                            ]
                          }
                        },
                        "type": "object"
                      },
                      "type": "array"
                    },
                    "required_documents": {
                      "description": "Documents for a document-upload verification and their progress. Present only on verifications created by sending `documents`. `pending_upload` documents were not accepted yet — send the full set again with another Create Verification call.",
                      "items": {
                        "properties": {
                          "document": {
                            "description": "Document slot key, such as `id_card_front`, `id_card_back`, or `selfie`.",
                            "type": "string"
                          },
                          "rejection_reason": {
                            "description": "Why the previous submission was rejected, when the provider requested new documents or declined the verification.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "status": {
                            "description": "`pending_upload` until the document has been relayed for review; `submitted` afterwards.",
                            "enum": [
                              "pending_upload",
                              "submitted"
                            ],
                            "type": "string"
                          }
                        },
                        "type": "object"
                      },
                      "type": "array"
                    },
                    "session_url": {
                      "description": "Hosted verification session URL for the user to complete identity checks. Expires 7 days after creation.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "status": {
                      "description": "Current verification state. `not_started` before any session has been created; `pending` while a session is in progress and needs the user's input; `processing` while the provider reviews submitted documents — nothing to do but wait; `action_required` when items in `requested_information` need answers before review can continue; `approved` once verification succeeds; `rejected` if it fails. Call the Create Verification endpoint again to start a new session.",
                      "enum": [
                        "not_started",
                        "pending",
                        "processing",
                        "approved",
                        "rejected",
                        "action_required"
                      ],
                      "type": "string"
                    },
                    "updated_at": {
                      "description": "When the verification profile was last updated, as an ISO 8601 timestamp.",
                      "type": "string"
                    }
                  },
                  "type": "object"
                }
              }
            },
            "description": "verification created"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "missing account_id"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the required identity-write scope"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "account not found"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "identity:write"
            ]
          }
        ],
        "summary": "Create Verification",
        "tags": [
          "Verifications"
        ]
      }
    },
    "/verifications/{verification_id}": {
      "get": {
        "description": "Returns verifications for an account, including their status and any required actions.",
        "operationId": "get",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "Verification profile ID, prefixed `idpf_`.",
            "in": "path",
            "name": "verification_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ],
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "address": {
                      "description": "Address on the verification profile. `null` when no address is set.",
                      "properties": {
                        "city": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "country": {
                          "description": "Two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "line1": {
                          "description": "First line of the street address.",
                          "type": "string"
                        },
                        "line2": {
                          "description": "Second line of the street address.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "postal_code": {
                          "description": "Postal or ZIP code.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "state": {
                          "description": "State, province, or region code, for example `CA`.",
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "business_name": {
                      "description": "Legal business name.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "business_structure": {
                      "description": "Legal entity structure of the business, such as `private_corporation` or `sole_proprietorship`. Supported values vary by country of incorporation — see [Business structures](/developer/verification/business-structures).",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "country": {
                      "description": "Two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "created_at": {
                      "description": "When the verification profile was created, as an ISO 8601 timestamp.",
                      "type": "string"
                    },
                    "date_of_birth": {
                      "description": "Formatted as `YYYY-MM-DD`.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "first_name": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "id": {
                      "description": "Verification profile ID, prefixed `idpf_`.",
                      "type": "string"
                    },
                    "kind": {
                      "enum": [
                        "individual",
                        "business"
                      ],
                      "type": "string"
                    },
                    "last_name": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "requested_information": {
                      "description": "Fields or documents Whop still needs before review can continue. Submit answers with the Update Verification endpoint.",
                      "items": {
                        "properties": {
                          "description": {
                            "description": "Additional instructions for this requested item, or `null`.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "error_message": {
                            "description": "Reason a previously submitted value was rejected. `null` if no submitted value has been rejected.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "field": {
                            "description": "Stable field key, such as `ssn` or `business_description`.",
                            "type": "string"
                          },
                          "id": {
                            "description": "Requested information item ID, prefixed `inrqi_`. Include this ID when submitting an answer.",
                            "type": "string"
                          },
                          "label": {
                            "description": "Human-readable label for the field, such as `Social Security Number`.",
                            "type": "string"
                          },
                          "options": {
                            "description": "Allowed values for a `select` field (e.g. account_type, business_structure) — the submitted value must be one of these; empty for other types.",
                            "items": {
                              "type": "string"
                            },
                            "type": "array"
                          },
                          "requested_files": {
                            "description": "Document upload slots for this item. Present when `type` is `files`; upload one file for each required slot and include the slot's `category` when submitting the answer.",
                            "items": {
                              "properties": {
                                "category": {
                                  "description": "File category to include with the uploaded file so Whop can route the document correctly. `null` for a generic upload.",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "is_optional": {
                                  "description": "Whether this slot can be left empty.",
                                  "type": "boolean"
                                },
                                "kind": {
                                  "description": "Specific document type requested, such as `Bank Statement`. `null` for standard identity and business document uploads.",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "label": {
                                  "description": "Label for this upload slot, such as `Front of ID Document`.",
                                  "type": "string"
                                },
                                "multiple": {
                                  "description": "Whether this slot accepts more than one file.",
                                  "type": "boolean"
                                }
                              },
                              "type": "object"
                            },
                            "type": "array"
                          },
                          "type": {
                            "description": "Input type expected for this item: `text`, `date`, `phone`, `address`, `files`, or `select`.",
                            "type": [
                              "string",
                              "null"
                            ]
                          }
                        },
                        "type": "object"
                      },
                      "type": "array"
                    },
                    "required_documents": {
                      "description": "Documents for a document-upload verification and their progress. Present only on verifications created by sending `documents`. `pending_upload` documents were not accepted yet — send the full set again with another Create Verification call.",
                      "items": {
                        "properties": {
                          "document": {
                            "description": "Document slot key, such as `id_card_front`, `id_card_back`, or `selfie`.",
                            "type": "string"
                          },
                          "rejection_reason": {
                            "description": "Why the previous submission was rejected, when the provider requested new documents or declined the verification.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "status": {
                            "description": "`pending_upload` until the document has been relayed for review; `submitted` afterwards.",
                            "enum": [
                              "pending_upload",
                              "submitted"
                            ],
                            "type": "string"
                          }
                        },
                        "type": "object"
                      },
                      "type": "array"
                    },
                    "session_url": {
                      "description": "Hosted verification session URL for the user to complete identity checks. Expires 7 days after creation.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "status": {
                      "description": "Current verification state. `not_started` before any session has been created; `pending` while a session is in progress and needs the user's input; `processing` while the provider reviews submitted documents — nothing to do but wait; `action_required` when items in `requested_information` need answers before review can continue; `approved` once verification succeeds; `rejected` if it fails. Call the Create Verification endpoint again to start a new session.",
                      "enum": [
                        "not_started",
                        "pending",
                        "processing",
                        "approved",
                        "rejected",
                        "action_required"
                      ],
                      "type": "string"
                    },
                    "updated_at": {
                      "description": "When the verification profile was last updated, as an ISO 8601 timestamp.",
                      "type": "string"
                    }
                  },
                  "type": "object"
                }
              }
            },
            "description": "verification retrieved"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "no verification with that ID is visible to the credential"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "identity:read"
            ]
          }
        ],
        "summary": "Retrieve Verification",
        "tags": [
          "Verifications"
        ]
      },
      "patch": {
        "description": "Updates editable profile details or submits answers for items returned in `requested_information`. Once a verification is `approved` its profile details are locked and can no longer be edited.",
        "operationId": "update",
        "parameters": [
          {
            "$ref": "#/components/parameters/ApiVersionDate"
          },
          {
            "description": "Verification profile ID, prefixed `idpf_`.",
            "in": "path",
            "name": "verification_id",
            "required": true,
            "schema": {
              "type": "string"
            }
          }
        ],
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "example": {
                  "requested_information": [
                    {
                      "id": "inrqi_textxxxxxx",
                      "value": "https://example.com",
                      "value_type": "raw"
                    },
                    {
                      "address": {
                        "city": "New York",
                        "country": "US",
                        "line1": "123 Spring Street",
                        "postal_code": "10012",
                        "state": "NY"
                      },
                      "id": "inrqi_addrxxxxxx"
                    },
                    {
                      "files": [
                        {
                          "category": "identity_document_front",
                          "direct_upload_id": "upload_xxxxxxxxxxxx",
                          "kind": "identity_document"
                        }
                      ],
                      "id": "inrqi_filexxxxxx"
                    }
                  ]
                },
                "oneOf": [
                  {
                    "description": "Fields that can be updated on an individual (KYC) verification. At least one field is required.",
                    "properties": {
                      "business_tax_identification_number": {
                        "description": "The business ID number of the company, as appropriate for the company's country. Examples are an Employer Identification Number (EIN) in the US, a Business Number in Canada, or a Company Number in the UK.",
                        "type": "string"
                      },
                      "country": {
                        "description": "Two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`.",
                        "type": "string"
                      },
                      "date_of_birth": {
                        "description": "Formatted as `YYYY-MM-DD`.",
                        "type": "string"
                      },
                      "first_name": {
                        "type": "string"
                      },
                      "last_name": {
                        "type": "string"
                      },
                      "personal_address": {
                        "description": "Personal address for the individual.",
                        "properties": {
                          "city": {
                            "type": "string"
                          },
                          "country": {
                            "description": "Two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`.",
                            "type": "string"
                          },
                          "line1": {
                            "description": "First line of the street address.",
                            "type": "string"
                          },
                          "line2": {
                            "description": "Second line of the street address.",
                            "type": "string"
                          },
                          "postal_code": {
                            "description": "Postal or ZIP code.",
                            "type": "string"
                          },
                          "state": {
                            "description": "State, province, or region code, for example `CA`.",
                            "type": "string"
                          }
                        },
                        "type": "object"
                      },
                      "requested_information": {
                        "description": "Answers to items returned in `requested_information`. Each entry must include the requested item `id` and exactly one answer payload matching the item's `type`: `value` for `text`, `date`, or `phone`; `address` for `address`; `files` for `files`.",
                        "items": {
                          "properties": {
                            "address": {
                              "description": "Address payload for `address` items.",
                              "properties": {
                                "city": {
                                  "type": "string"
                                },
                                "country": {
                                  "description": "Two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`.",
                                  "type": "string"
                                },
                                "line1": {
                                  "description": "First line of the street address.",
                                  "type": "string"
                                },
                                "line2": {
                                  "description": "Second line of the street address.",
                                  "type": "string"
                                },
                                "postal_code": {
                                  "description": "Postal or ZIP code.",
                                  "type": "string"
                                },
                                "state": {
                                  "description": "State, province, or region code, for example `CA`.",
                                  "type": "string"
                                }
                              },
                              "type": "object"
                            },
                            "files": {
                              "description": "Uploaded file payloads for `files` items. Each file should include a `direct_upload_id` from the upload flow, plus the requested file `category` and `kind` when provided.",
                              "items": {
                                "properties": {
                                  "attachment_id": {
                                    "description": "Existing attachment ID, when reusing an already attached document.",
                                    "type": "string"
                                  },
                                  "category": {
                                    "description": "Requested file category copied from `requested_files.category`.",
                                    "type": "string"
                                  },
                                  "direct_upload_id": {
                                    "description": "Direct upload ID for the uploaded document.",
                                    "type": "string"
                                  },
                                  "kind": {
                                    "description": "Requested document kind copied from `requested_files.kind`.",
                                    "type": "string"
                                  }
                                },
                                "type": "object"
                              },
                              "type": "array"
                            },
                            "id": {
                              "description": "Requested information item ID, prefixed `inrqi_`.",
                              "type": "string"
                            },
                            "value": {
                              "description": "Answer value for `text`, `date`, or `phone` items.",
                              "type": "string"
                            },
                            "value_type": {
                              "description": "Whether `value` is raw input or a vault token.",
                              "enum": [
                                "raw",
                                "vault_token"
                              ],
                              "type": "string"
                            }
                          },
                          "required": [
                            "id"
                          ],
                          "type": "object"
                        },
                        "type": "array"
                      },
                      "tax_identification_number": {
                        "description": "The government-issued ID number of the person being verified — the individual for a KYC verification, or the business representative for a KYB verification — as appropriate for their country. Examples are a Social Security Number (SSN) in the US, or a Social Insurance Number in Canada.",
                        "type": "string"
                      }
                    },
                    "title": "UpdateIndividualVerification",
                    "type": "object"
                  },
                  {
                    "description": "Fields that can be updated on a business entity (KYB) verification. At least one field is required.",
                    "properties": {
                      "business_address": {
                        "description": "Business address.",
                        "properties": {
                          "city": {
                            "type": "string"
                          },
                          "country": {
                            "description": "Two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`.",
                            "type": "string"
                          },
                          "line1": {
                            "description": "First line of the street address.",
                            "type": "string"
                          },
                          "line2": {
                            "description": "Second line of the street address.",
                            "type": "string"
                          },
                          "postal_code": {
                            "description": "Postal or ZIP code.",
                            "type": "string"
                          },
                          "state": {
                            "description": "State, province, or region code, for example `CA`.",
                            "type": "string"
                          }
                        },
                        "type": "object"
                      },
                      "business_name": {
                        "description": "Legal business name.",
                        "type": "string"
                      },
                      "business_structure": {
                        "description": "Legal entity structure of the business, such as `private_corporation` or `sole_proprietorship`. Supported values vary by country of incorporation — see [Business structures](/developer/verification/business-structures).",
                        "type": "string"
                      },
                      "business_tax_identification_number": {
                        "description": "The business ID number of the company, as appropriate for the company's country. Examples are an Employer Identification Number (EIN) in the US, a Business Number in Canada, or a Company Number in the UK.",
                        "type": "string"
                      },
                      "country": {
                        "description": "Two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`.",
                        "type": "string"
                      },
                      "requested_information": {
                        "description": "Answers to items returned in `requested_information`. Each entry must include the requested item `id` and exactly one answer payload matching the item's `type`: `value` for `text`, `date`, or `phone`; `address` for `address`; `files` for `files`.",
                        "items": {
                          "properties": {
                            "address": {
                              "description": "Address payload for `address` items.",
                              "properties": {
                                "city": {
                                  "type": "string"
                                },
                                "country": {
                                  "description": "Two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`.",
                                  "type": "string"
                                },
                                "line1": {
                                  "description": "First line of the street address.",
                                  "type": "string"
                                },
                                "line2": {
                                  "description": "Second line of the street address.",
                                  "type": "string"
                                },
                                "postal_code": {
                                  "description": "Postal or ZIP code.",
                                  "type": "string"
                                },
                                "state": {
                                  "description": "State, province, or region code, for example `CA`.",
                                  "type": "string"
                                }
                              },
                              "type": "object"
                            },
                            "files": {
                              "description": "Uploaded file payloads for `files` items. Each file should include a `direct_upload_id` from the upload flow, plus the requested file `category` and `kind` when provided.",
                              "items": {
                                "properties": {
                                  "attachment_id": {
                                    "description": "Existing attachment ID, when reusing an already attached document.",
                                    "type": "string"
                                  },
                                  "category": {
                                    "description": "Requested file category copied from `requested_files.category`.",
                                    "type": "string"
                                  },
                                  "direct_upload_id": {
                                    "description": "Direct upload ID for the uploaded document.",
                                    "type": "string"
                                  },
                                  "kind": {
                                    "description": "Requested document kind copied from `requested_files.kind`.",
                                    "type": "string"
                                  }
                                },
                                "type": "object"
                              },
                              "type": "array"
                            },
                            "id": {
                              "description": "Requested information item ID, prefixed `inrqi_`.",
                              "type": "string"
                            },
                            "value": {
                              "description": "Answer value for `text`, `date`, or `phone` items.",
                              "type": "string"
                            },
                            "value_type": {
                              "description": "Whether `value` is raw input or a vault token.",
                              "enum": [
                                "raw",
                                "vault_token"
                              ],
                              "type": "string"
                            }
                          },
                          "required": [
                            "id"
                          ],
                          "type": "object"
                        },
                        "type": "array"
                      },
                      "tax_identification_number": {
                        "description": "The government-issued ID number of the person being verified — the individual for a KYC verification, or the business representative for a KYB verification — as appropriate for their country. Examples are a Social Security Number (SSN) in the US, or a Social Insurance Number in Canada.",
                        "type": "string"
                      }
                    },
                    "title": "UpdateBusinessVerification",
                    "type": "object"
                  }
                ]
              }
            }
          },
          "required": true
        },
        "responses": {
          "200": {
            "content": {
              "application/json": {
                "schema": {
                  "properties": {
                    "address": {
                      "description": "Address on the verification profile. `null` when no address is set.",
                      "properties": {
                        "city": {
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "country": {
                          "description": "Two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "line1": {
                          "description": "First line of the street address.",
                          "type": "string"
                        },
                        "line2": {
                          "description": "Second line of the street address.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "postal_code": {
                          "description": "Postal or ZIP code.",
                          "type": [
                            "string",
                            "null"
                          ]
                        },
                        "state": {
                          "description": "State, province, or region code, for example `CA`.",
                          "type": [
                            "string",
                            "null"
                          ]
                        }
                      },
                      "type": [
                        "object",
                        "null"
                      ]
                    },
                    "business_name": {
                      "description": "Legal business name.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "business_structure": {
                      "description": "Legal entity structure of the business, such as `private_corporation` or `sole_proprietorship`. Supported values vary by country of incorporation — see [Business structures](/developer/verification/business-structures).",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "country": {
                      "description": "Two-letter ISO 3166-1 country code, for example `US`, `DE`, or `GB`.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "created_at": {
                      "description": "When the verification profile was created, as an ISO 8601 timestamp.",
                      "type": "string"
                    },
                    "date_of_birth": {
                      "description": "Formatted as `YYYY-MM-DD`.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "first_name": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "id": {
                      "description": "Verification profile ID, prefixed `idpf_`.",
                      "type": "string"
                    },
                    "kind": {
                      "enum": [
                        "individual",
                        "business"
                      ],
                      "type": "string"
                    },
                    "last_name": {
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "requested_information": {
                      "description": "Fields or documents Whop still needs before review can continue. Submit answers with the Update Verification endpoint.",
                      "items": {
                        "properties": {
                          "description": {
                            "description": "Additional instructions for this requested item, or `null`.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "error_message": {
                            "description": "Reason a previously submitted value was rejected. `null` if no submitted value has been rejected.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "field": {
                            "description": "Stable field key, such as `ssn` or `business_description`.",
                            "type": "string"
                          },
                          "id": {
                            "description": "Requested information item ID, prefixed `inrqi_`. Include this ID when submitting an answer.",
                            "type": "string"
                          },
                          "label": {
                            "description": "Human-readable label for the field, such as `Social Security Number`.",
                            "type": "string"
                          },
                          "options": {
                            "description": "Allowed values for a `select` field (e.g. account_type, business_structure) — the submitted value must be one of these; empty for other types.",
                            "items": {
                              "type": "string"
                            },
                            "type": "array"
                          },
                          "requested_files": {
                            "description": "Document upload slots for this item. Present when `type` is `files`; upload one file for each required slot and include the slot's `category` when submitting the answer.",
                            "items": {
                              "properties": {
                                "category": {
                                  "description": "File category to include with the uploaded file so Whop can route the document correctly. `null` for a generic upload.",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "is_optional": {
                                  "description": "Whether this slot can be left empty.",
                                  "type": "boolean"
                                },
                                "kind": {
                                  "description": "Specific document type requested, such as `Bank Statement`. `null` for standard identity and business document uploads.",
                                  "type": [
                                    "string",
                                    "null"
                                  ]
                                },
                                "label": {
                                  "description": "Label for this upload slot, such as `Front of ID Document`.",
                                  "type": "string"
                                },
                                "multiple": {
                                  "description": "Whether this slot accepts more than one file.",
                                  "type": "boolean"
                                }
                              },
                              "type": "object"
                            },
                            "type": "array"
                          },
                          "type": {
                            "description": "Input type expected for this item: `text`, `date`, `phone`, `address`, `files`, or `select`.",
                            "type": [
                              "string",
                              "null"
                            ]
                          }
                        },
                        "type": "object"
                      },
                      "type": "array"
                    },
                    "required_documents": {
                      "description": "Documents for a document-upload verification and their progress. Present only on verifications created by sending `documents`. `pending_upload` documents were not accepted yet — send the full set again with another Create Verification call.",
                      "items": {
                        "properties": {
                          "document": {
                            "description": "Document slot key, such as `id_card_front`, `id_card_back`, or `selfie`.",
                            "type": "string"
                          },
                          "rejection_reason": {
                            "description": "Why the previous submission was rejected, when the provider requested new documents or declined the verification.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "status": {
                            "description": "`pending_upload` until the document has been relayed for review; `submitted` afterwards.",
                            "enum": [
                              "pending_upload",
                              "submitted"
                            ],
                            "type": "string"
                          }
                        },
                        "type": "object"
                      },
                      "type": "array"
                    },
                    "session_url": {
                      "description": "Hosted verification session URL for the user to complete identity checks. Expires 7 days after creation.",
                      "type": [
                        "string",
                        "null"
                      ]
                    },
                    "status": {
                      "description": "Current verification state. `not_started` before any session has been created; `pending` while a session is in progress and needs the user's input; `processing` while the provider reviews submitted documents — nothing to do but wait; `action_required` when items in `requested_information` need answers before review can continue; `approved` once verification succeeds; `rejected` if it fails. Call the Create Verification endpoint again to start a new session.",
                      "enum": [
                        "not_started",
                        "pending",
                        "processing",
                        "approved",
                        "rejected",
                        "action_required"
                      ],
                      "type": "string"
                    },
                    "updated_at": {
                      "description": "When the verification profile was last updated, as an ISO 8601 timestamp.",
                      "type": "string"
                    }
                  },
                  "type": "object"
                }
              }
            },
            "description": "verification updated"
          },
          "400": {
            "$ref": "#/components/responses/InvalidParameters",
            "description": "the request body has unsupported fields"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized",
            "description": "missing or invalid authentication"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden",
            "description": "credential lacks the required identity-write scope"
          },
          "404": {
            "$ref": "#/components/responses/NotFound",
            "description": "no verification with that ID is visible to the credential"
          }
        },
        "security": [
          {
            "bearerAuth": [
              "identity:write"
            ]
          }
        ],
        "summary": "Update Verification",
        "tags": [
          "Verifications"
        ]
      }
    }
  },
  "components": {
    "parameters": {
      "ApiVersionDate": {
        "description": "Pins the request to a dated API version.",
        "in": "header",
        "name": "Api-Version-Date",
        "required": false,
        "schema": {
          "example": "2026-07-08-1",
          "type": "string"
        }
      }
    },
    "responses": {
      "Forbidden": {
        "content": {
          "application/json": {
            "schema": {
              "$ref": "#/components/schemas/V1ErrorResponse"
            }
          }
        },
        "description": "Forbidden"
      },
      "InvalidParameters": {
        "content": {
          "application/json": {
            "schema": {
              "$ref": "#/components/schemas/V1ErrorResponse"
            }
          }
        },
        "description": "Invalid Parameters"
      },
      "NotFound": {
        "content": {
          "application/json": {
            "schema": {
              "$ref": "#/components/schemas/V1ErrorResponse"
            }
          }
        },
        "description": "Resource not found"
      },
      "Unauthorized": {
        "content": {
          "application/json": {
            "schema": {
              "$ref": "#/components/schemas/V1ErrorResponse"
            }
          }
        },
        "description": "Unauthorized"
      }
    },
    "schemas": {
      "Account": {
        "properties": {
          "balances": {
            "items": {
              "$ref": "#/components/schemas/AccountBalanceToken",
              "description": "Account holdings, each with USD value. Empty when `total_usd` is `null`."
            },
            "type": "array"
          },
          "banner_image_url": {
            "description": "Account banner image URL.",
            "type": [
              "string",
              "null"
            ]
          },
          "business_address": {
            "description": "Account business address used to calculate tax, with `line1`, `line2`, `city`, `state`, `postal_code`, and `country`. `null` when no address is set.",
            "type": [
              "object",
              "null"
            ]
          },
          "business_type": {
            "description": "High-level business category for the account.",
            "type": [
              "string",
              "null"
            ]
          },
          "capabilities": {
            "description": "Payment rails enabled for this account, each `active`, `inactive`, or `pending` (onboarding or review in progress). Computed only on `retrieve` and `me` for callers with `company:balance:read` scope; `null` otherwise.",
            "oneOf": [
              {
                "$ref": "#/components/schemas/AccountCapabilities"
              },
              {
                "type": "null"
              }
            ]
          },
          "country": {
            "description": "Country where the account is located.",
            "type": [
              "string",
              "null"
            ]
          },
          "created_at": {
            "description": "When the account was created, as an ISO 8601 timestamp.",
            "type": "string"
          },
          "description": {
            "description": "Account promotional description.",
            "type": [
              "string",
              "null"
            ]
          },
          "email": {
            "description": "Account owner email address.",
            "type": [
              "string",
              "null"
            ]
          },
          "home_preferences": {
            "items": {
              "description": "Public account home page preferences.",
              "type": "string"
            },
            "type": "array"
          },
          "id": {
            "description": "Account ID, prefixed `biz_`.",
            "type": "string"
          },
          "industry_group": {
            "description": "Account industry group.",
            "type": [
              "string",
              "null"
            ]
          },
          "industry_type": {
            "description": "Specific industry vertical for the account.",
            "type": [
              "string",
              "null"
            ]
          },
          "invoice_prefix": {
            "description": "Prefix used for account invoices.",
            "type": [
              "string",
              "null"
            ]
          },
          "llc_formation": {
            "description": "LLC formation state for the account: a draft state with `payment_pending`, or the live filing state with registration progress, documents, and pending signatures.",
            "type": "object"
          },
          "logo_url": {
            "description": "Account logo image URL.",
            "type": [
              "string",
              "null"
            ]
          },
          "metadata": {
            "description": "Arbitrary key/value metadata supplied at account creation.",
            "type": "object"
          },
          "onboarding_type": {
            "description": "Type of onboarding the account has completed.",
            "type": [
              "string",
              "null"
            ]
          },
          "opengraph_image_url": {
            "description": "Account Open Graph image URL.",
            "type": [
              "string",
              "null"
            ]
          },
          "opengraph_image_variant": {
            "description": "Account Open Graph image variant.",
            "type": [
              "string",
              "null"
            ]
          },
          "other_business_description": {
            "description": "Business type details when business_type is `other`.",
            "type": [
              "string",
              "null"
            ]
          },
          "other_industry_description": {
            "description": "Industry details when industry_type is `other`.",
            "type": [
              "string",
              "null"
            ]
          },
          "parent_account_id": {
            "description": "Parent account ID for connected accounts.",
            "type": [
              "string",
              "null"
            ]
          },
          "payment_controls": {
            "description": "Payment health controls currently applied to the account. Computed only on `retrieve` and `me` for callers with `company:balance:read` scope; `null` otherwise.",
            "oneOf": [
              {
                "$ref": "#/components/schemas/AccountPaymentControls"
              },
              {
                "type": "null"
              }
            ]
          },
          "product_tax_code": {
            "description": "Tax classification code applied by default to the account's products, with `id`, `name`, and `product_type`. `null` when no default is set.",
            "type": [
              "object",
              "null"
            ]
          },
          "recommended_actions": {
            "deprecated": true,
            "description": "DEPRECATED: Use the `GET /accounts/{account_id}/recommend_actions` endpoint instead.",
            "items": {
              "$ref": "#/components/schemas/AccountRecommendedAction",
              "description": "Deprecated: use the `GET /accounts/{account_id}/recommend_actions` endpoint instead. Optional actions that unlock capabilities or grow the account, same shape as `required_actions`. Computed only on `retrieve` and `me`; `null` otherwise."
            },
            "type": [
              "array",
              "null"
            ]
          },
          "require_2fa": {
            "description": "Whether authorized users must enable two-factor authentication.",
            "type": "boolean"
          },
          "required_actions": {
            "items": {
              "$ref": "#/components/schemas/AccountRequiredAction",
              "description": "Actions the account owner must take to unblock capabilities like payouts and card spend, ordered by display priority. Computed only on `retrieve` and `me` for callers with `company:balance:read` scope; `null` otherwise."
            },
            "type": [
              "array",
              "null"
            ]
          },
          "route": {
            "description": "Account public route identifier.",
            "type": "string"
          },
          "send_customer_emails": {
            "description": "Whether Whop sends transactional emails to customers on behalf of this account.",
            "type": "boolean"
          },
          "show_joined_whops": {
            "description": "Whether the account appears in joined whops on other accounts.",
            "type": "boolean"
          },
          "show_reviews_dtc": {
            "description": "Whether reviews are displayed on direct-to-consumer product pages.",
            "type": "boolean"
          },
          "show_user_directory": {
            "description": "Whether the account shows users in the user directory.",
            "type": "boolean"
          },
          "social_links": {
            "items": {
              "$ref": "#/components/schemas/AccountSocialLink",
              "description": "Account social links."
            },
            "type": "array"
          },
          "status": {
            "description": "Whether the account can operate on Whop: `active` or `suspended`. Computed only on `retrieve` and `me`; `null` otherwise.",
            "type": [
              "string",
              "null"
            ]
          },
          "store_page_config": {
            "description": "Account store page display configuration.",
            "type": "object"
          },
          "target_audience": {
            "description": "Target audience for this account.",
            "type": [
              "string",
              "null"
            ]
          },
          "tax_collection_enabled_states": {
            "items": {
              "description": "US state codes (of the 50 states plus `DC`) where the account collects tax: the full set when Whop remits (`tax_remitted_by` `whop`), the configured subset when the account self-remits (`self`), and empty when neither. On update, send the complete list to replace it (only allowed when `self`).",
              "type": "string"
            },
            "type": "array"
          },
          "tax_identifiers": {
            "description": "Account tax/VAT registrations, each with `id`, `tax_id_type`, and `tax_id_value`. Empty when none are set.",
            "type": "array"
          },
          "tax_remitted_by": {
            "description": "Who calculates and remits tax for the account: `whop` (Whop calculates and remits), `self` (Whop calculates; the account collects and remits), or `none` (neither; the account is responsible). `null` until the account enrolls in the Whop tax service.",
            "type": [
              "string",
              "null"
            ]
          },
          "title": {
            "description": "Account display name.",
            "type": "string"
          },
          "total_earned_usd": {
            "description": "Account lifetime sales, normalized to USD. Computed only on `retrieve` and `me` for callers with `stats:read` scope; `null` otherwise.",
            "type": [
              "number",
              "null"
            ]
          },
          "total_usd": {
            "description": "Total USD value across balances with known exchange rates. Computed only on single-account reads (`retrieve` and `me`); `null` on list responses, writes, missing balance-read permission, or unavailable balance source.",
            "type": [
              "string",
              "null"
            ]
          },
          "use_logo_as_opengraph_image_fallback": {
            "description": "Whether the account uses its logo as the fallback Open Graph image.",
            "type": "boolean"
          },
          "verification": {
            "description": "Account identity verification status for the `individual` (KYC) and `business` (KYB) profiles. Each is `null` until created, otherwise a `status` of `not_started`, `pending`, `approved`, or `rejected`.",
            "type": "object"
          },
          "wallet": {
            "description": "Account primary crypto wallet, or `null` if none has been provisioned.",
            "oneOf": [
              {
                "$ref": "#/components/schemas/AccountWallet"
              },
              {
                "type": "null"
              }
            ]
          }
        },
        "required": [
          "id",
          "email",
          "title",
          "description",
          "route",
          "logo_url",
          "banner_image_url",
          "business_type",
          "country",
          "home_preferences",
          "industry_group",
          "industry_type",
          "invoice_prefix",
          "onboarding_type",
          "opengraph_image_url",
          "opengraph_image_variant",
          "other_business_description",
          "other_industry_description",
          "require_2fa",
          "target_audience",
          "send_customer_emails",
          "show_joined_whops",
          "show_reviews_dtc",
          "show_user_directory",
          "store_page_config",
          "parent_account_id",
          "metadata",
          "created_at",
          "use_logo_as_opengraph_image_fallback",
          "total_earned_usd",
          "status",
          "capabilities",
          "payment_controls",
          "required_actions",
          "recommended_actions",
          "tax_remitted_by",
          "product_tax_code",
          "business_address",
          "tax_identifiers",
          "tax_collection_enabled_states",
          "total_usd",
          "balances",
          "verification",
          "llc_formation",
          "wallet",
          "social_links"
        ],
        "type": "object"
      },
      "AccountBalanceToken": {
        "properties": {
          "balance": {
            "description": "Total amount held in native units, as a decimal string.",
            "type": "string"
          },
          "breakdown": {
            "description": "Balance split into available, pending, and reserve amounts, as native-unit decimal strings. On-chain crypto is entirely available; good_funds and fiat cash can have pending or reserve portions.",
            "type": "object"
          },
          "icon_url": {
            "description": "Holding icon URL.",
            "type": [
              "string",
              "null"
            ]
          },
          "name": {
            "description": "The holding's display name",
            "type": "string"
          },
          "price_usd": {
            "description": "USD price per unit, or `null` when no exchange rate is available.",
            "type": [
              "number",
              "null"
            ]
          },
          "symbol": {
            "description": "Holding display symbol, such as `USDT`, `cbBTC`, or `EUR`.",
            "type": "string"
          },
          "value_usd": {
            "description": "Holding USD value, or `null` when no exchange rate is available.",
            "type": [
              "string",
              "null"
            ]
          }
        },
        "required": [
          "symbol",
          "name",
          "balance",
          "value_usd",
          "price_usd",
          "icon_url",
          "breakdown"
        ],
        "type": "object"
      },
      "AccountCapabilities": {
        "properties": {
          "accept_bank_payments": {
            "description": "Bank payins: debits, transfers, and local bank rails",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active",
            "type": "string"
          },
          "accept_bnpl_payments": {
            "description": "Buy-now-pay-later payins; requires approval",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active",
            "type": "string"
          },
          "accept_card_payments": {
            "description": "Card payins, including Apple Pay and Google Pay",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active",
            "type": "string"
          },
          "bank_deposit": {
            "description": "Deposits by bank wire or ACH to the account's virtual bank account",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active",
            "type": "string"
          },
          "card_deposit": {
            "description": "Balance top-ups by charging a stored payment method",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active",
            "type": "string"
          },
          "card_issuing": {
            "description": "Issuing Whop cards; requires card application approval",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active",
            "type": "string"
          },
          "crypto_deposit": {
            "description": "On-chain deposits to the account's crypto wallet",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active",
            "type": "string"
          },
          "crypto_payout": {
            "description": "On-chain payouts to a crypto wallet",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active",
            "type": "string"
          },
          "instant_payout": {
            "description": "Instant payouts to an eligible payout destination",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active",
            "type": "string"
          },
          "standard_payout": {
            "description": "Standard payouts to an external payout destination",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active",
            "type": "string"
          },
          "transfer": {
            "description": "Transfers to other accounts",
            "enum": [
              "active",
              "inactive",
              "pending"
            ],
            "example": "active",
            "type": "string"
          }
        },
        "required": [
          "accept_card_payments",
          "accept_bank_payments",
          "accept_bnpl_payments",
          "standard_payout",
          "instant_payout",
          "crypto_payout",
          "transfer",
          "bank_deposit",
          "crypto_deposit",
          "card_deposit",
          "card_issuing"
        ],
        "type": "object"
      },
      "AccountDisputeAlertAutoRefundControl": {
        "properties": {
          "locked": {
            "description": "Whether the account owner is prevented from changing this threshold.",
            "type": "boolean"
          },
          "threshold_usd": {
            "description": "Maximum dispute alert amount automatically refunded in USD. `null` when automatic refunds are disabled.",
            "type": [
              "number",
              "null"
            ]
          }
        },
        "required": [
          "threshold_usd",
          "locked"
        ],
        "type": "object"
      },
      "AccountPaymentControls": {
        "properties": {
          "dispute_alert_auto_refund": {
            "$ref": "#/components/schemas/AccountDisputeAlertAutoRefundControl",
            "description": "Automatic refund settings for pre-chargeback dispute alerts."
          },
          "dispute_alert_fee_usd": {
            "description": "Fee charged for each dispute alert in USD. `null` when unavailable.",
            "type": [
              "number",
              "null"
            ]
          },
          "financing_disabled": {
            "description": "Whether payment health controls explicitly disable financing. This is independent of financing approval in `capabilities.accept_bnpl_payments`.",
            "type": "boolean"
          },
          "high_risk_processing_fee_percentage": {
            "description": "Additional processing fee percentage for high-risk processing. Currently `0` for all accounts.",
            "type": "number"
          },
          "pending_balance_delay_days": {
            "description": "Additional days payments remain pending before becoming available.",
            "type": "integer"
          },
          "reserve": {
            "$ref": "#/components/schemas/AccountReserveControl",
            "description": "Reserve currently applied to incoming payment volume."
          },
          "resolution_center_auto_refund": {
            "$ref": "#/components/schemas/AccountResolutionCenterAutoRefundControl",
            "description": "Automatic refund settings for resolution center cases."
          }
        },
        "required": [
          "dispute_alert_auto_refund",
          "resolution_center_auto_refund",
          "dispute_alert_fee_usd",
          "high_risk_processing_fee_percentage",
          "pending_balance_delay_days",
          "financing_disabled",
          "reserve"
        ],
        "type": "object"
      },
      "AccountRecommendedAction": {
        "properties": {
          "action": {
            "description": "The recommendation; new values may be added, so handle unknown actions gracefully",
            "enum": [
              "theme_business",
              "create_product",
              "create_plan",
              "verify_identity",
              "connect_affiliate_program",
              "create_promotion",
              "setup_tracking_pixel",
              "migrate_from_stripe",
              "accept_first_payment",
              "launch_first_ad",
              "launch_draft_campaign",
              "increase_ad_budget",
              "refresh_ad_creatives",
              "fix_ad_billing",
              "exclude_customers_from_ads",
              "retarget_abandoned_checkouts",
              "invite_team_member",
              "enable_tax_collection",
              "create_card",
              "join_whop_university",
              "apply_for_financing"
            ],
            "example": "theme_business",
            "type": "string"
          },
          "blocked_capabilities": {
            "items": {
              "description": "Capabilities this would unlock, or empty",
              "type": "string"
            },
            "type": "array"
          },
          "cta": {
            "description": "The URL the call-to-action links to",
            "type": "string"
          },
          "cta_label": {
            "description": "Button label",
            "type": "string"
          },
          "description": {
            "description": "Supporting copy, or empty",
            "type": "string"
          },
          "icon_url": {
            "description": "Illustration icon URL, or `null`",
            "type": [
              "string",
              "null"
            ]
          },
          "impact_score": {
            "description": "Estimated impact from 0-100, or `null` when not ranked",
            "type": [
              "integer",
              "null"
            ]
          },
          "reasoning": {
            "description": "Why this action was recommended, or `null`",
            "type": [
              "string",
              "null"
            ]
          },
          "status": {
            "description": "Always optional — never blocking",
            "enum": [
              "optional"
            ],
            "example": "optional",
            "type": "string"
          },
          "title": {
            "description": "Headline for the recommendation",
            "type": "string"
          }
        },
        "required": [
          "action",
          "status",
          "title",
          "description",
          "cta_label",
          "cta",
          "icon_url",
          "blocked_capabilities",
          "reasoning",
          "impact_score"
        ],
        "type": "object"
      },
      "AccountRequiredAction": {
        "properties": {
          "action": {
            "description": "What the holder must do; new values may be added, so handle unknown actions gracefully",
            "enum": [
              "deposit_funds",
              "submit_information_request",
              "verify_identity",
              "connect_fulfillment_tracker"
            ],
            "example": "deposit_funds",
            "type": "string"
          },
          "blocked_capabilities": {
            "items": {
              "description": "Capabilities gated until this is resolved",
              "type": "string"
            },
            "type": "array"
          },
          "cta": {
            "description": "The URL the call-to-action links to, or null when there is no button",
            "type": [
              "string",
              "null"
            ]
          },
          "cta_label": {
            "description": "Button label, or empty when there is no button",
            "type": "string"
          },
          "description": {
            "description": "Supporting copy, or empty",
            "type": "string"
          },
          "icon_url": {
            "description": "The URL of the action's illustration icon, or null if it has none",
            "type": [
              "string",
              "null"
            ]
          },
          "status": {
            "description": "required (act now) or pending (under review)",
            "enum": [
              "required",
              "pending"
            ],
            "example": "required",
            "type": "string"
          },
          "title": {
            "description": "Headline for the action",
            "type": "string"
          }
        },
        "required": [
          "action",
          "status",
          "title",
          "description",
          "cta_label",
          "cta",
          "icon_url",
          "blocked_capabilities"
        ],
        "type": "object"
      },
      "AccountReserveControl": {
        "properties": {
          "hold_period_days": {
            "description": "Number of days reserved funds are held before release.",
            "type": "integer"
          },
          "percentage": {
            "description": "Percentage of incoming payment volume held in reserve. `null` when no reserve is applied.",
            "type": [
              "number",
              "null"
            ]
          }
        },
        "required": [
          "percentage",
          "hold_period_days"
        ],
        "type": "object"
      },
      "AccountResolutionCenterAutoRefundControl": {
        "properties": {
          "card_threshold_usd": {
            "description": "Maximum card-funded resolution center case amount automatically refunded in USD. `null` when automatic refunds are disabled for cards.",
            "type": [
              "number",
              "null"
            ]
          },
          "financing_threshold_usd": {
            "description": "Maximum financing-funded resolution center case amount automatically refunded in USD. `null` when automatic refunds are disabled for financing.",
            "type": [
              "number",
              "null"
            ]
          },
          "locked": {
            "description": "Whether the account owner is prevented from changing these thresholds.",
            "type": "boolean"
          },
          "paypal_threshold_usd": {
            "description": "Maximum PayPal-funded resolution center case amount automatically refunded in USD. `null` when automatic refunds are disabled for PayPal.",
            "type": [
              "number",
              "null"
            ]
          }
        },
        "required": [
          "card_threshold_usd",
          "financing_threshold_usd",
          "paypal_threshold_usd",
          "locked"
        ],
        "type": "object"
      },
      "AccountSocialLink": {
        "properties": {
          "id": {
            "description": "The ID of the social link",
            "type": "string"
          },
          "title": {
            "description": "The optional display title for the social link",
            "type": [
              "string",
              "null"
            ]
          },
          "url": {
            "description": "The social link URL",
            "type": "string"
          },
          "website": {
            "description": "The social platform for this link",
            "enum": [
              "x",
              "instagram",
              "facebook",
              "tiktok",
              "youtube",
              "linkedin",
              "twitch",
              "website",
              "custom"
            ],
            "example": "x",
            "type": "string"
          }
        },
        "required": [
          "id",
          "website",
          "url",
          "title"
        ],
        "type": "object"
      },
      "AccountSummary": {
        "properties": {
          "id": {
            "description": "Account ID, prefixed `biz_`.",
            "type": "string"
          },
          "title": {
            "description": "Account display name.",
            "type": "string"
          }
        },
        "required": [
          "id",
          "title"
        ],
        "type": "object"
      },
      "AccountWallet": {
        "properties": {
          "address": {
            "description": "The on-chain address of the wallet",
            "type": "string"
          },
          "id": {
            "description": "Wallet ID, prefixed `wallet_`.",
            "type": "string"
          },
          "network": {
            "description": "The blockchain network the wallet lives on",
            "enum": [
              "solana",
              "ethereum",
              "bitcoin"
            ],
            "example": "solana",
            "type": "string"
          }
        },
        "required": [
          "id",
          "address",
          "network"
        ],
        "type": "object"
      },
      "Ad": {
        "properties": {
          "ad_campaign": {
            "$ref": "#/components/schemas/AdEntityReference",
            "description": "The ad campaign this ad belongs to."
          },
          "ad_group": {
            "$ref": "#/components/schemas/AdEntityReference",
            "description": "The ad group this ad belongs to."
          },
          "added_to_cart_value": {
            "description": "USD value attributed to add-to-cart events. Sums the value sent with each event, normalized to USD; events without a value contribute 0.",
            "type": "number"
          },
          "added_to_carts": {
            "description": "Whop pixel-attributed add-to-cart events, last-click.",
            "type": "number"
          },
          "call_to_action": {
            "description": "The call-to-action button shown on the ad.",
            "enum": [
              "learn_more",
              "shop_now",
              "sign_up",
              "subscribe",
              "get_started",
              "book_now",
              "apply_now",
              "contact_us",
              "download",
              "order_now",
              "buy_now",
              "get_quote",
              "message_page",
              "whatsapp_message",
              "instagram_message",
              "call_now",
              "get_directions",
              "send_updates",
              "get_offer",
              "watch_more",
              "listen_now",
              "play_game",
              "open_link",
              "no_button",
              "get_offer_view",
              "get_event_tickets",
              "see_menu",
              "request_time",
              "event_rsvp",
              "see_details",
              "view_instagram_profile",
              null
            ],
            "example": "learn_more",
            "type": [
              "string",
              "null"
            ]
          },
          "click_through_rate": {
            "description": "Clicks divided by impressions, between 0 and 1.",
            "type": "number"
          },
          "clicks": {
            "description": "The number of clicks.",
            "type": "number"
          },
          "completed_registration_value": {
            "description": "USD value attributed to complete-registration events. Sums the value sent with each event, normalized to USD; events without a value contribute 0.",
            "type": "number"
          },
          "completed_registrations": {
            "description": "Whop pixel-attributed complete-registration events, last-click.",
            "type": "number"
          },
          "contact_value": {
            "description": "USD value attributed to contact events. Sums the value sent with each event, normalized to USD; events without a value contribute 0.",
            "type": "number"
          },
          "contacts": {
            "description": "Whop pixel-attributed contact events, last-click.",
            "type": "number"
          },
          "cost_per_added_to_cart": {
            "description": "Spend divided by attributed add-to-cart events; null when they are not the goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_click": {
            "description": "Spend divided by clicks; 0 when there are no clicks.",
            "type": "number"
          },
          "cost_per_completed_registration": {
            "description": "Spend divided by attributed complete-registration events; null when they are not the goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_contact": {
            "description": "Spend divided by attributed contact events; null when contacts are not the goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_lead": {
            "description": "Spend divided by attributed leads; null when leads are not a goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_mille": {
            "description": "Spend per 1,000 impressions; 0 when there are no impressions.",
            "type": "number"
          },
          "cost_per_purchase": {
            "description": "Spend divided by attributed purchases; null when purchases are not a goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_result": {
            "description": "Spend divided by Whop pixel-attributed results; null when nothing Whop-attributable is being optimized for.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_schedule": {
            "description": "Spend divided by attributed schedule events; null when schedules are not the goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_submitted_application": {
            "description": "Spend divided by attributed submit-application events; null when they are not the goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_viewed_content": {
            "description": "Spend divided by attributed view-content events; null when they are not the goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "created_at": {
            "description": "When the ad was created, as an ISO 8601 timestamp.",
            "type": "string"
          },
          "creatives": {
            "items": {
              "$ref": "#/components/schemas/AdCreative",
              "description": "The creative assets used by this ad. The original asset has a null format; square, vertical, and horizontal entries are placement-specific variants."
            },
            "type": "array"
          },
          "custom_conversions": {
            "description": "Whop pixel-attributed custom (merchant-defined) conversion events, last-click, across all custom event names.",
            "type": "number"
          },
          "custom_event_counts": {
            "description": "Whop pixel-attributed custom conversions, keyed by your event name with its last-click count as the value. Empty when no named custom events are attributed. Custom events fired without a name are counted in custom_conversions but omitted here, so these values sum to at most custom_conversions.",
            "type": "object"
          },
          "custom_event_values": {
            "description": "Conversion value attributed to each custom event, keyed by event name like custom_event_counts. Sums the value passed to whop.track, normalized to USD; events fired without a value contribute 0.",
            "type": "object"
          },
          "delivery_status": {
            "description": "Whether the ad is delivering right now, and if not, why. When several states apply at once, the highest-precedence one is returned.",
            "enum": [
              "rejected",
              "in_review",
              "draft",
              "campaign_paused",
              "ad_group_paused",
              "paused",
              "processing",
              "issues",
              "learning_limited",
              "learning",
              "active"
            ],
            "example": "rejected",
            "type": "string"
          },
          "descriptions": {
            "items": {
              "description": "The description variants shown on the ad.",
              "type": "string"
            },
            "type": "array"
          },
          "frequency": {
            "description": "Platform-reported impressions divided by reach.",
            "type": [
              "number",
              "null"
            ]
          },
          "headlines": {
            "items": {
              "description": "The headline variants shown on the ad.",
              "type": "string"
            },
            "type": "array"
          },
          "id": {
            "description": "Unique identifier for the ad, prefixed `ad_`.",
            "type": "string"
          },
          "impressions": {
            "description": "The number of impressions.",
            "type": "number"
          },
          "issues": {
            "items": {
              "$ref": "#/components/schemas/AdPlatformIssue",
              "description": "Open issues affecting this ad. Empty when there are none."
            },
            "type": "array"
          },
          "lead_form": {
            "description": "The instant lead form shown when someone taps this ad. `null` when the ad group's conversion_location is not an instant-form destination.",
            "oneOf": [
              {
                "$ref": "#/components/schemas/AdLeadForm"
              },
              {
                "type": "null"
              }
            ]
          },
          "lead_form_id": {
            "description": "The ad platform's ID for the instant form the ad uses. Set when the ad references an existing form via `lead_form_id`, or once a form built from `lead_form` has been created on the platform.",
            "type": [
              "string",
              "null"
            ]
          },
          "lead_value": {
            "description": "USD value attributed to lead events. Sums the value sent with each event, normalized to USD; events without a value contribute 0.",
            "type": "number"
          },
          "leads": {
            "description": "Whop pixel-attributed leads, last-click.",
            "type": "number"
          },
          "messaging_config": {
            "description": "Welcome message for click-to-message ads, shown when the conversation opens. `null` when the ad has none.",
            "oneOf": [
              {
                "$ref": "#/components/schemas/AdMessagingConfig"
              },
              {
                "type": "null"
              }
            ]
          },
          "multi_advertiser_ads": {
            "description": "Whether the ad can appear alongside other advertisers' ads in the same unit. Defaults to true.",
            "type": "boolean"
          },
          "post_id": {
            "description": "The existing post this ad promotes — a Facebook post or Instagram media ID. `null` when the ad uses uploaded creatives.",
            "type": [
              "string",
              "null"
            ]
          },
          "post_source": {
            "description": "Which network `post_id` refers to: `facebook` (a page post) or `instagram` (a media ID). `null` when the ad uses uploaded creatives.",
            "enum": [
              "facebook",
              "instagram",
              null
            ],
            "example": "facebook",
            "type": [
              "string",
              "null"
            ]
          },
          "post_thumbnail_url": {
            "description": "Preview image of the existing post this ad promotes. `null` for ads that use uploaded creatives, or until the post's media has been fetched from the network.",
            "type": [
              "string",
              "null"
            ]
          },
          "primary_texts": {
            "items": {
              "description": "The primary text variants shown in the ad body.",
              "type": "string"
            },
            "type": "array"
          },
          "purchase_value": {
            "description": "USD value of pixel-attributed purchases.",
            "type": "number"
          },
          "purchases": {
            "description": "Whop pixel-attributed purchases, last-click.",
            "type": "number"
          },
          "reach": {
            "description": "The number of unique people who saw this.",
            "type": "number"
          },
          "result_event": {
            "description": "The Whop pixel conversion event whose attributed count represents results — the optimization goal, or the highest-volume attributed event for campaigns that budget per ad group. Null when the goal isn't a Whop-attributed event.",
            "enum": [
              "purchase",
              "lead",
              "schedule",
              "submit_application",
              "contact",
              "complete_registration",
              "view_content",
              "add_to_cart",
              "custom",
              null
            ],
            "example": "purchase",
            "type": [
              "string",
              "null"
            ]
          },
          "result_event_name": {
            "description": "The merchant-defined event name when result_event is custom; null for the standard events.",
            "type": [
              "string",
              "null"
            ]
          },
          "results": {
            "description": "The Whop pixel-attributed count behind result_event. When a campaign's ad groups optimize different goals there is no single result_event (it is null), and this is instead the sum of each ad group's own attributed results. Null when nothing Whop-attributable is being optimized for.",
            "type": [
              "number",
              "null"
            ]
          },
          "return_on_ad_spend": {
            "description": "Purchase value divided by spend, both in USD (a currency-neutral ratio); 0 when there is no spend.",
            "type": "number"
          },
          "schedule_value": {
            "description": "USD value attributed to schedule events. Sums the value sent with each event, normalized to USD; events without a value contribute 0.",
            "type": "number"
          },
          "schedules": {
            "description": "Whop pixel-attributed schedule events, last-click.",
            "type": "number"
          },
          "social_accounts": {
            "items": {
              "$ref": "#/components/schemas/AdEntityReference",
              "description": "The social accounts the ad runs under — its Facebook page and Instagram profile — each referenced by ID, prefixed `sacc_`."
            },
            "type": "array"
          },
          "spend": {
            "description": "The amount charged, in spend_currency.",
            "type": "number"
          },
          "spend_currency": {
            "description": "The ISO 4217 currency code of all monetary metrics.",
            "type": [
              "string",
              "null"
            ]
          },
          "status": {
            "description": "Whether the ad is enabled. `active` and `paused` are set by you; `in_review` and `rejected` come from ad review.",
            "enum": [
              "active",
              "paused",
              "in_review",
              "rejected"
            ],
            "example": "active",
            "type": "string"
          },
          "submitted_application_value": {
            "description": "USD value attributed to submit-application events. Sums the value sent with each event, normalized to USD; events without a value contribute 0.",
            "type": "number"
          },
          "submitted_applications": {
            "description": "Whop pixel-attributed submit-application events, last-click.",
            "type": "number"
          },
          "title": {
            "description": "Display title of the ad.",
            "type": [
              "string",
              "null"
            ]
          },
          "unique_click_through_rate": {
            "description": "Unique clicks divided by impressions, between 0 and 1.",
            "type": [
              "number",
              "null"
            ]
          },
          "unique_clicks": {
            "description": "The number of unique clicks.",
            "type": "number"
          },
          "updated_at": {
            "description": "When the ad was last updated, as an ISO 8601 timestamp.",
            "type": "string"
          },
          "url": {
            "description": "The URL the ad links to.",
            "type": [
              "string",
              "null"
            ]
          },
          "url_parameters": {
            "description": "Query parameters appended to the URL, keyed by parameter name.",
            "type": "object"
          },
          "viewed_content_value": {
            "description": "USD value attributed to view-content events. Sums the value sent with each event, normalized to USD; events without a value contribute 0.",
            "type": "number"
          },
          "viewed_contents": {
            "description": "Whop pixel-attributed view-content events, last-click.",
            "type": "number"
          }
        },
        "required": [
          "click_through_rate",
          "clicks",
          "cost_per_click",
          "cost_per_lead",
          "cost_per_mille",
          "cost_per_purchase",
          "cost_per_schedule",
          "cost_per_submitted_application",
          "cost_per_contact",
          "cost_per_completed_registration",
          "cost_per_viewed_content",
          "cost_per_added_to_cart",
          "cost_per_result",
          "frequency",
          "impressions",
          "leads",
          "purchase_value",
          "purchases",
          "reach",
          "result_event",
          "result_event_name",
          "results",
          "return_on_ad_spend",
          "spend",
          "spend_currency",
          "unique_click_through_rate",
          "unique_clicks",
          "schedules",
          "submitted_applications",
          "contacts",
          "completed_registrations",
          "viewed_contents",
          "added_to_carts",
          "custom_conversions",
          "custom_event_counts",
          "custom_event_values",
          "lead_value",
          "schedule_value",
          "submitted_application_value",
          "contact_value",
          "completed_registration_value",
          "viewed_content_value",
          "added_to_cart_value",
          "id",
          "title",
          "status",
          "delivery_status",
          "primary_texts",
          "headlines",
          "descriptions",
          "call_to_action",
          "url",
          "url_parameters",
          "post_id",
          "post_source",
          "post_thumbnail_url",
          "messaging_config",
          "multi_advertiser_ads",
          "ad_group",
          "ad_campaign",
          "creatives",
          "lead_form",
          "lead_form_id",
          "social_accounts",
          "created_at",
          "updated_at",
          "issues"
        ],
        "type": "object"
      },
      "AdCampaign": {
        "properties": {
          "added_to_cart_value": {
            "description": "USD value attributed to add-to-cart events. Sums the value sent with each event, normalized to USD; events without a value contribute 0.",
            "type": "number"
          },
          "added_to_carts": {
            "description": "Whop pixel-attributed add-to-cart events, last-click.",
            "type": "number"
          },
          "bid_type": {
            "description": "How delivery bids in the ad auction: `minimum_cost` gets the most results for the budget, `average_target` holds an average cost per result, and `maximum_target` never bids above a cap.",
            "enum": [
              "minimum_cost",
              "average_target",
              "maximum_target",
              null
            ],
            "example": "minimum_cost",
            "type": [
              "string",
              "null"
            ]
          },
          "budget_amount": {
            "description": "The campaign's budget, in the ad account's currency. `null` when each ad group sets its own budget instead.",
            "type": [
              "number",
              "null"
            ]
          },
          "budget_optimization": {
            "description": "Which level owns the budget: the whole campaign (`ad_campaign`) or each ad group individually (`ad_group`).",
            "enum": [
              "ad_campaign",
              "ad_group",
              null
            ],
            "example": "ad_campaign",
            "type": [
              "string",
              "null"
            ]
          },
          "budget_type": {
            "description": "Whether `budget_amount` is spent per day (`daily`) or over the campaign's full run (`lifetime`).",
            "enum": [
              "daily",
              "lifetime",
              null
            ],
            "example": "daily",
            "type": [
              "string",
              "null"
            ]
          },
          "click_through_rate": {
            "description": "Clicks divided by impressions, between 0 and 1.",
            "type": "number"
          },
          "clicks": {
            "description": "The number of clicks.",
            "type": "number"
          },
          "completed_registration_value": {
            "description": "USD value attributed to complete-registration events. Sums the value sent with each event, normalized to USD; events without a value contribute 0.",
            "type": "number"
          },
          "completed_registrations": {
            "description": "Whop pixel-attributed complete-registration events, last-click.",
            "type": "number"
          },
          "contact_value": {
            "description": "USD value attributed to contact events. Sums the value sent with each event, normalized to USD; events without a value contribute 0.",
            "type": "number"
          },
          "contacts": {
            "description": "Whop pixel-attributed contact events, last-click.",
            "type": "number"
          },
          "cost_per_added_to_cart": {
            "description": "Spend divided by attributed add-to-cart events; null when they are not the goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_click": {
            "description": "Spend divided by clicks; 0 when there are no clicks.",
            "type": "number"
          },
          "cost_per_completed_registration": {
            "description": "Spend divided by attributed complete-registration events; null when they are not the goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_contact": {
            "description": "Spend divided by attributed contact events; null when contacts are not the goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_lead": {
            "description": "Spend divided by attributed leads; null when leads are not a goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_mille": {
            "description": "Spend per 1,000 impressions; 0 when there are no impressions.",
            "type": "number"
          },
          "cost_per_purchase": {
            "description": "Spend divided by attributed purchases; null when purchases are not a goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_result": {
            "description": "Spend divided by Whop pixel-attributed results; null when nothing Whop-attributable is being optimized for.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_schedule": {
            "description": "Spend divided by attributed schedule events; null when schedules are not the goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_submitted_application": {
            "description": "Spend divided by attributed submit-application events; null when they are not the goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_viewed_content": {
            "description": "Spend divided by attributed view-content events; null when they are not the goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "created_at": {
            "description": "When the campaign was created, as an ISO 8601 timestamp.",
            "type": "string"
          },
          "custom_conversions": {
            "description": "Whop pixel-attributed custom (merchant-defined) conversion events, last-click, across all custom event names.",
            "type": "number"
          },
          "custom_event_counts": {
            "description": "Whop pixel-attributed custom conversions, keyed by your event name with its last-click count as the value. Empty when no named custom events are attributed. Custom events fired without a name are counted in custom_conversions but omitted here, so these values sum to at most custom_conversions.",
            "type": "object"
          },
          "custom_event_values": {
            "description": "Conversion value attributed to each custom event, keyed by event name like custom_event_counts. Sums the value passed to whop.track, normalized to USD; events fired without a value contribute 0.",
            "type": "object"
          },
          "delivery_status": {
            "description": "Whether the campaign's ads are delivering right now, and if not, why. When several states apply at once, the highest-precedence one is returned.",
            "enum": [
              "payment_failed",
              "all_ads_rejected",
              "draft",
              "no_ad_groups",
              "no_ads",
              "paused",
              "processing",
              "issues",
              "scheduled",
              "completed",
              "ad_groups_off",
              "active"
            ],
            "example": "payment_failed",
            "type": "string"
          },
          "frequency": {
            "description": "Platform-reported impressions divided by reach.",
            "type": [
              "number",
              "null"
            ]
          },
          "id": {
            "description": "Unique identifier for the ad campaign, prefixed `adcamp_`.",
            "type": "string"
          },
          "impressions": {
            "description": "The number of impressions.",
            "type": "number"
          },
          "issues": {
            "items": {
              "$ref": "#/components/schemas/AdPlatformIssue",
              "description": "Open issues affecting the campaign and its descendant ad groups and ads."
            },
            "type": "array"
          },
          "lead_value": {
            "description": "USD value attributed to lead events. Sums the value sent with each event, normalized to USD; events without a value contribute 0.",
            "type": "number"
          },
          "leads": {
            "description": "Whop pixel-attributed leads, last-click.",
            "type": "number"
          },
          "objective": {
            "description": "The goal the campaign optimizes toward.",
            "enum": [
              "awareness",
              "traffic",
              "engagement",
              "leads",
              "sales",
              null
            ],
            "example": "awareness",
            "type": [
              "string",
              "null"
            ]
          },
          "optimization_goal": {
            "description": "The event the campaign optimizes for when a single goal is set campaign-wide. `null` when each ad group sets its own optimization_goal.",
            "type": [
              "string",
              "null"
            ]
          },
          "platform": {
            "description": "The ad network the campaign runs on.",
            "enum": [
              "meta"
            ],
            "example": "meta",
            "type": "string"
          },
          "purchase_value": {
            "description": "USD value of pixel-attributed purchases.",
            "type": "number"
          },
          "purchases": {
            "description": "Whop pixel-attributed purchases, last-click.",
            "type": "number"
          },
          "reach": {
            "description": "The number of unique people who saw this.",
            "type": "number"
          },
          "result_event": {
            "description": "The Whop pixel conversion event whose attributed count represents results — the optimization goal, or the highest-volume attributed event for campaigns that budget per ad group. Null when the goal isn't a Whop-attributed event.",
            "enum": [
              "purchase",
              "lead",
              "schedule",
              "submit_application",
              "contact",
              "complete_registration",
              "view_content",
              "add_to_cart",
              "custom",
              null
            ],
            "example": "purchase",
            "type": [
              "string",
              "null"
            ]
          },
          "result_event_name": {
            "description": "The merchant-defined event name when result_event is custom; null for the standard events.",
            "type": [
              "string",
              "null"
            ]
          },
          "results": {
            "description": "The Whop pixel-attributed count behind result_event. When a campaign's ad groups optimize different goals there is no single result_event (it is null), and this is instead the sum of each ad group's own attributed results. Null when nothing Whop-attributable is being optimized for.",
            "type": [
              "number",
              "null"
            ]
          },
          "return_on_ad_spend": {
            "description": "Purchase value divided by spend, both in USD (a currency-neutral ratio); 0 when there is no spend.",
            "type": "number"
          },
          "schedule_value": {
            "description": "USD value attributed to schedule events. Sums the value sent with each event, normalized to USD; events without a value contribute 0.",
            "type": "number"
          },
          "schedules": {
            "description": "Whop pixel-attributed schedule events, last-click.",
            "type": "number"
          },
          "special_ad_categories": {
            "items": {
              "description": "Regulated categories the campaign is declared under. Ads in these categories are subject to extra targeting restrictions. Empty when none apply.",
              "enum": [
                "housing",
                "employment",
                "financial_products",
                "politics"
              ],
              "example": "housing",
              "type": "string"
            },
            "type": "array"
          },
          "spend": {
            "description": "The amount charged, in spend_currency.",
            "type": "number"
          },
          "spend_currency": {
            "description": "The ISO 4217 currency code of all monetary metrics.",
            "type": [
              "string",
              "null"
            ]
          },
          "status": {
            "description": "The lifecycle status of the ad campaign.",
            "enum": [
              "active",
              "paused",
              "inactive",
              "stale",
              "pending_refund",
              "payment_failed",
              "draft",
              "in_review",
              "flagged",
              "importing",
              "imported"
            ],
            "example": "active",
            "type": "string"
          },
          "submitted_application_value": {
            "description": "USD value attributed to submit-application events. Sums the value sent with each event, normalized to USD; events without a value contribute 0.",
            "type": "number"
          },
          "submitted_applications": {
            "description": "Whop pixel-attributed submit-application events, last-click.",
            "type": "number"
          },
          "title": {
            "description": "Display name of the ad campaign.",
            "type": "string"
          },
          "unique_click_through_rate": {
            "description": "Unique clicks divided by impressions, between 0 and 1.",
            "type": [
              "number",
              "null"
            ]
          },
          "unique_clicks": {
            "description": "The number of unique clicks.",
            "type": "number"
          },
          "updated_at": {
            "description": "When the campaign was last updated, as an ISO 8601 timestamp.",
            "type": "string"
          },
          "viewed_content_value": {
            "description": "USD value attributed to view-content events. Sums the value sent with each event, normalized to USD; events without a value contribute 0.",
            "type": "number"
          },
          "viewed_contents": {
            "description": "Whop pixel-attributed view-content events, last-click.",
            "type": "number"
          }
        },
        "required": [
          "click_through_rate",
          "clicks",
          "cost_per_click",
          "cost_per_lead",
          "cost_per_mille",
          "cost_per_purchase",
          "cost_per_schedule",
          "cost_per_submitted_application",
          "cost_per_contact",
          "cost_per_completed_registration",
          "cost_per_viewed_content",
          "cost_per_added_to_cart",
          "cost_per_result",
          "frequency",
          "impressions",
          "leads",
          "purchase_value",
          "purchases",
          "reach",
          "result_event",
          "result_event_name",
          "results",
          "return_on_ad_spend",
          "spend",
          "spend_currency",
          "unique_click_through_rate",
          "unique_clicks",
          "schedules",
          "submitted_applications",
          "contacts",
          "completed_registrations",
          "viewed_contents",
          "added_to_carts",
          "custom_conversions",
          "custom_event_counts",
          "custom_event_values",
          "lead_value",
          "schedule_value",
          "submitted_application_value",
          "contact_value",
          "completed_registration_value",
          "viewed_content_value",
          "added_to_cart_value",
          "id",
          "title",
          "status",
          "delivery_status",
          "platform",
          "objective",
          "special_ad_categories",
          "optimization_goal",
          "budget_amount",
          "budget_type",
          "budget_optimization",
          "bid_type",
          "created_at",
          "updated_at",
          "issues"
        ],
        "type": "object"
      },
      "AdCreative": {
        "properties": {
          "crop": {
            "description": "The saved crop window for this creative, in source image pixels. Null for the original asset or a format that has not been cropped.",
            "oneOf": [
              {
                "$ref": "#/components/schemas/AdCreativeCrop"
              },
              {
                "type": "null"
              }
            ]
          },
          "format": {
            "description": "The placement variant this asset covers, or null for the original asset.",
            "enum": [
              "square",
              "vertical",
              "horizontal",
              null
            ],
            "example": "square",
            "type": [
              "string",
              "null"
            ]
          },
          "id": {
            "description": "The creative attachment's file id.",
            "type": "string"
          },
          "media_type": {
            "description": "The kind of asset, image or video.",
            "type": [
              "string",
              "null"
            ]
          },
          "url": {
            "description": "CDN url of the asset.",
            "type": [
              "string",
              "null"
            ]
          }
        },
        "required": [
          "id",
          "format",
          "url",
          "media_type",
          "crop"
        ],
        "type": "object"
      },
      "AdCreativeCrop": {
        "properties": {
          "height": {
            "description": "Height of the crop window in source pixels.",
            "type": "number"
          },
          "width": {
            "description": "Width of the crop window in source pixels.",
            "type": "number"
          },
          "x": {
            "description": "Left edge of the crop window in source pixels.",
            "type": "number"
          },
          "y": {
            "description": "Top edge of the crop window in source pixels.",
            "type": "number"
          }
        },
        "required": [
          "x",
          "y",
          "width",
          "height"
        ],
        "type": "object"
      },
      "AdEntityReference": {
        "properties": {
          "id": {
            "description": "The referenced entity's id.",
            "type": "string"
          }
        },
        "required": [
          "id"
        ],
        "type": "object"
      },
      "AdGroup": {
        "properties": {
          "ad_campaign": {
            "$ref": "#/components/schemas/AdEntityReference",
            "description": "The ad campaign this ad group belongs to."
          },
          "added_to_cart_value": {
            "description": "USD value attributed to add-to-cart events. Sums the value sent with each event, normalized to USD; events without a value contribute 0.",
            "type": "number"
          },
          "added_to_carts": {
            "description": "Whop pixel-attributed add-to-cart events, last-click.",
            "type": "number"
          },
          "audiences": {
            "$ref": "#/components/schemas/AdGroupAudiences",
            "description": "Saved audiences this ad group delivers to or excludes."
          },
          "bid_type": {
            "description": "How delivery bids in the ad auction: `minimum_cost` gets the most results for the budget, `average_target` keeps the average cost per result near `desired_cost_per_result`, and `maximum_target` never bids above it.",
            "enum": [
              "minimum_cost",
              "average_target",
              "maximum_target",
              null
            ],
            "example": "minimum_cost",
            "type": [
              "string",
              "null"
            ]
          },
          "budget_amount": {
            "description": "This ad group's budget, in the ad account's currency. `null` when the budget is set on the campaign instead.",
            "type": [
              "number",
              "null"
            ]
          },
          "budget_type": {
            "description": "Whether `budget_amount` is spent per day (`daily`) or over the ad group's full run (`lifetime`).",
            "enum": [
              "daily",
              "lifetime",
              null
            ],
            "example": "daily",
            "type": [
              "string",
              "null"
            ]
          },
          "click_through_rate": {
            "description": "Clicks divided by impressions, between 0 and 1.",
            "type": "number"
          },
          "clicks": {
            "description": "The number of clicks.",
            "type": "number"
          },
          "completed_registration_value": {
            "description": "USD value attributed to complete-registration events. Sums the value sent with each event, normalized to USD; events without a value contribute 0.",
            "type": "number"
          },
          "completed_registrations": {
            "description": "Whop pixel-attributed complete-registration events, last-click.",
            "type": "number"
          },
          "contact_value": {
            "description": "USD value attributed to contact events. Sums the value sent with each event, normalized to USD; events without a value contribute 0.",
            "type": "number"
          },
          "contacts": {
            "description": "Whop pixel-attributed contact events, last-click.",
            "type": "number"
          },
          "conversion_event": {
            "$ref": "#/components/schemas/ConversionEvent"
          },
          "conversion_location": {
            "description": "Where the result you're optimizing for happens: `website` (your site), `profile` (your social media profile), `messaging` (a direct-message conversation), `on_ad` (engagement with the ad itself), or a lead form (`instant_forms`, `instant_forms_and_messenger`, `website_and_instant_forms`).",
            "enum": [
              "website",
              "profile",
              "messaging",
              "on_ad",
              "instant_forms",
              "instant_forms_and_messenger",
              "website_and_instant_forms",
              null
            ],
            "example": "website",
            "type": [
              "string",
              "null"
            ]
          },
          "cost_per_added_to_cart": {
            "description": "Spend divided by attributed add-to-cart events; null when they are not the goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_click": {
            "description": "Spend divided by clicks; 0 when there are no clicks.",
            "type": "number"
          },
          "cost_per_completed_registration": {
            "description": "Spend divided by attributed complete-registration events; null when they are not the goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_contact": {
            "description": "Spend divided by attributed contact events; null when contacts are not the goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_lead": {
            "description": "Spend divided by attributed leads; null when leads are not a goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_mille": {
            "description": "Spend per 1,000 impressions; 0 when there are no impressions.",
            "type": "number"
          },
          "cost_per_purchase": {
            "description": "Spend divided by attributed purchases; null when purchases are not a goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_result": {
            "description": "Spend divided by Whop pixel-attributed results; null when nothing Whop-attributable is being optimized for.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_schedule": {
            "description": "Spend divided by attributed schedule events; null when schedules are not the goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_submitted_application": {
            "description": "Spend divided by attributed submit-application events; null when they are not the goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "cost_per_viewed_content": {
            "description": "Spend divided by attributed view-content events; null when they are not the goal and none are attributed.",
            "type": [
              "number",
              "null"
            ]
          },
          "created_at": {
            "description": "When the ad group was created, as an ISO 8601 timestamp.",
            "type": "string"
          },
          "custom_conversions": {
            "description": "Whop pixel-attributed custom (merchant-defined) conversion events, last-click, across all custom event names.",
            "type": "number"
          },
          "custom_event_counts": {
            "description": "Whop pixel-attributed custom conversions, keyed by your event name with its last-click count as the value. Empty when no named custom events are attributed. Custom events fired without a name are counted in custom_conversions but omitted here, so these values sum to at most custom_conversions.",
            "type": "object"
          },
          "custom_event_values": {
            "description": "Conversion value attributed to each custom event, keyed by event name like custom_event_counts. Sums the value passed to whop.track, normalized to USD; events fired without a value contribute 0.",
            "type": "object"
          },
          "delivery_status": {
            "description": "Whether ads in this ad group are delivering right now, and if not, why. When several states apply at once, the highest-precedence one is returned.",
            "enum": [
              "all_ads_rejected",
              "rejected",
              "draft",
              "no_ads",
              "campaign_paused",
              "paused",
              "processing",
              "issues",
              "scheduled",
              "completed",
              "ads_off",
              "learning_limited",
              "learning",
              "active"
            ],
            "example": "all_ads_rejected",
            "type": "string"
          },
          "demographics": {
            "$ref": "#/components/schemas/AdGroupDemographics",
            "description": "Age, gender, and automatic-audience targeting."
          },
          "desired_cost_per_result": {
            "description": "Cost per result to aim for (`average_target`) or never exceed (`maximum_target`). `null` for `minimum_cost` bidding.",
            "type": [
              "number",
              "null"
            ]
          },
          "detailed_targeting": {
            "$ref": "#/components/schemas/AdGroupDetailedTargeting",
            "description": "Interest, behavior, and demographic targeting, using categories from the ad platform's targeting taxonomy. Can't be combined with automatic audience targeting, and unavailable to campaigns with special_ad_categories."
          },
          "devices": {
            "$ref": "#/components/schemas/AdGroupDevices",
            "description": "Device platforms and operating systems targeted."
          },
          "dynamic_creative": {
            "description": "Whether the ad platform automatically mixes and matches this ad group's creatives and copy to find the best-performing combinations.",
            "type": "boolean"
          },
          "ends_at": {
            "description": "When the ad group stops delivering, as an ISO 8601 timestamp. `null` when it runs until paused.",
            "type": [
              "string",
              "null"
            ]
          },
          "frequency": {
            "description": "Platform-reported impressions divided by reach.",
            "type": [
              "number",
              "null"
            ]
          },
          "frequency_cap": {
            "description": "Cap on how often one person sees ads from this ad group. Only available with `reach` optimization; `null` when uncapped.",
            "oneOf": [
              {
                "$ref": "#/components/schemas/AdGroupFrequencyCap"
              },
              {
                "type": "null"
              }
            ]
          },
          "id": {
            "description": "Unique identifier for the ad group, prefixed `adgrp_`.",
            "type": "string"
          },
          "impressions": {
            "description": "The number of impressions.",
            "type": "number"
          },
          "issues": {
            "items": {
              "$ref": "#/components/schemas/AdPlatformIssue",
              "description": "Open issues affecting this ad group and its ads. Empty when there are none."
            },
            "type": "array"
          },
          "languages": {
            "items": {
              "description": "Languages targeted, as ISO 639 codes such as `en` or `es`. A region-specific locale with no ISO code appears as its numeric platform locale key. Empty targets all languages.",
              "type": "string"
            },
            "type": "array"
          },
          "lead_value": {
            "description": "USD value attributed to lead events. Sums the value sent with each event, normalized to USD; events without a value contribute 0.",
            "type": "number"
          },
          "leads": {
            "description": "Whop pixel-attributed leads, last-click.",
            "type": "number"
          },
          "message_apps": {
            "items": {
              "description": "Apps the conversation opens in when `conversion_location` is `messaging`. Empty for other conversion locations.",
              "enum": [
                "messenger",
                "instagram",
                "whatsapp"
              ],
              "example": "messenger",
              "type": "string"
            },
            "type": "array"
          },
          "minimum_daily_spend": {
            "description": "Minimum the ad group tries to spend each day. `null` when no floor is set.",
            "type": [
              "number",
              "null"
            ]
          },
          "optimization_goal": {
            "description": "The result the ad group's delivery is optimized to get the most of.",
            "enum": [
              "conversions",
              "link_clicks",
              "landing_page_views",
              "reach",
              "impressions",
              "engagement",
              "conversations",
              "video_views",
              "two_second_views",
              "page_likes",
              "social_profile",
              "ad_recall_lift",
              "event_responses",
              "reminders_set",
              "lead_generation",
              "quality_lead",
              "value",
              "profile_and_page_engagement",
              null
            ],
            "example": "conversions",
            "type": [
              "string",
              "null"
            ]
          },
          "placements": {
            "items": {
              "$ref": "#/components/schemas/AdGroupPlacement",
              "description": "Where ads can appear, per platform. Empty when placements are chosen automatically."
            },
            "type": "array"
          },
          "purchase_value": {
            "description": "USD value of pixel-attributed purchases.",
            "type": "number"
          },
          "purchases": {
            "description": "Whop pixel-attributed purchases, last-click.",
            "type": "number"
          },
          "reach": {
            "description": "The number of unique people who saw this.",
            "type": "number"
          },
          "regions": {
            "$ref": "#/components/schemas/AdGroupRegions",
            "description": "Locations targeted and excluded."
          },
          "result_event": {
            "description": "The Whop pixel conversion event whose attributed count represents results — the optimization goal, or the highest-volume attributed event for campaigns that budget per ad group. Null when the goal isn't a Whop-attributed event.",
            "enum": [
              "purchase",
              "lead",
              "schedule",
              "submit_application",
              "contact",
              "complete_registration",
              "view_content",
              "add_to_cart",
              "custom",
              null
            ],
            "example": "purchase",
            "type": [
              "string",
              "null"
            ]
          },
          "result_event_name": {
            "description": "The merchant-defined event name when result_event is custom; null for the standard events.",
            "type": [
              "string",
              "null"
            ]
          },
          "results": {
            "description": "The Whop pixel-attributed count behind result_event. When a campaign's ad groups optimize different goals there is no single result_event (it is null), and this is instead the sum of each ad group's own attributed results. Null when nothing Whop-attributable is being optimized for.",
            "type": [
              "number",
              "null"
            ]
          },
          "return_on_ad_spend": {
            "description": "Purchase value divided by spend, both in USD (a currency-neutral ratio); 0 when there is no spend.",
            "type": "number"
          },
          "schedule_value": {
            "description": "USD value attributed to schedule events. Sums the value sent with each event, normalized to USD; events without a value contribute 0.",
            "type": "number"
          },
          "schedules": {
            "description": "Whop pixel-attributed schedule events, last-click.",
            "type": "number"
          },
          "spend": {
            "description": "The amount charged, in spend_currency.",
            "type": "number"
          },
          "spend_currency": {
            "description": "The ISO 4217 currency code of all monetary metrics.",
            "type": [
              "string",
              "null"
            ]
          },
          "starts_at": {
            "description": "When the ad group starts delivering, as an ISO 8601 timestamp. `null` when it starts as soon as it's active.",
            "type": [
              "string",
              "null"
            ]
          },
          "status": {
            "description": "Whether the ad group is enabled. `active` and `paused` are set by you; `rejected` means it failed ad review.",
            "enum": [
              "active",
              "paused",
              "rejected"
            ],
            "example": "active",
            "type": "string"
          },
          "submitted_application_value": {
            "description": "USD value attributed to submit-application events. Sums the value sent with each event, normalized to USD; events without a value contribute 0.",
            "type": "number"
          },
          "submitted_applications": {
            "description": "Whop pixel-attributed submit-application events, last-click.",
            "type": "number"
          },
          "title": {
            "description": "Display name of the ad group.",
            "type": [
              "string",
              "null"
            ]
          },
          "unique_click_through_rate": {
            "description": "Unique clicks divided by impressions, between 0 and 1.",
            "type": [
              "number",
              "null"
            ]
          },
          "unique_clicks": {
            "description": "The number of unique clicks.",
            "type": "number"
          },
          "updated_at": {
            "description": "When the ad group was last updated, as an ISO 8601 timestamp.",
            "type": "string"
          },
          "viewed_content_value": {
            "description": "USD value attributed to view-content events. Sums the value sent with each event, normalized to USD; events without a value contribute 0.",
            "type": "number"
          },
          "viewed_contents": {
            "description": "Whop pixel-attributed view-content events, last-click.",
            "type": "number"
          }
        },
        "required": [
          "click_through_rate",
          "clicks",
          "cost_per_click",
          "cost_per_lead",
          "cost_per_mille",
          "cost_per_purchase",
          "cost_per_schedule",
          "cost_per_submitted_application",
          "cost_per_contact",
          "cost_per_completed_registration",
          "cost_per_viewed_content",
          "cost_per_added_to_cart",
          "cost_per_result",
          "frequency",
          "impressions",
          "leads",
          "purchase_value",
          "purchases",
          "reach",
          "result_event",
          "result_event_name",
          "results",
          "return_on_ad_spend",
          "spend",
          "spend_currency",
          "unique_click_through_rate",
          "unique_clicks",
          "schedules",
          "submitted_applications",
          "contacts",
          "completed_registrations",
          "viewed_contents",
          "added_to_carts",
          "custom_conversions",
          "custom_event_counts",
          "custom_event_values",
          "lead_value",
          "schedule_value",
          "submitted_application_value",
          "contact_value",
          "completed_registration_value",
          "viewed_content_value",
          "added_to_cart_value",
          "id",
          "title",
          "status",
          "delivery_status",
          "optimization_goal",
          "conversion_location",
          "message_apps",
          "conversion_event",
          "budget_amount",
          "budget_type",
          "minimum_daily_spend",
          "bid_type",
          "desired_cost_per_result",
          "starts_at",
          "ends_at",
          "regions",
          "demographics",
          "detailed_targeting",
          "audiences",
          "languages",
          "dynamic_creative",
          "devices",
          "frequency_cap",
          "placements",
          "ad_campaign",
          "created_at",
          "updated_at",
          "issues"
        ],
        "type": "object"
      },
      "AdGroupAudiences": {
        "properties": {
          "exclude": {
            "items": {
              "description": "IDs of saved audiences excluded from delivery, prefixed `adaud_`.",
              "type": "string"
            },
            "type": "array"
          },
          "include": {
            "items": {
              "description": "IDs of saved audiences the ad group delivers to, prefixed `adaud_`.",
              "type": "string"
            },
            "type": "array"
          }
        },
        "required": [
          "include",
          "exclude"
        ],
        "type": "object"
      },
      "AdGroupCity": {
        "properties": {
          "key": {
            "description": "The ad platform's key for the city in its location taxonomy.",
            "type": "string"
          },
          "name": {
            "description": "City name, such as `Austin`. Absent when the platform doesn't return one.",
            "type": "string"
          }
        },
        "required": [
          "key"
        ],
        "type": "object"
      },
      "AdGroupCustomLocation": {
        "properties": {
          "distance_unit": {
            "description": "Unit for `radius`.",
            "enum": [
              "mile",
              "kilometer"
            ],
            "example": "mile",
            "type": "string"
          },
          "latitude": {
            "description": "Latitude of the center point.",
            "type": "number"
          },
          "longitude": {
            "description": "Longitude of the center point.",
            "type": "number"
          },
          "name": {
            "description": "Label for the location, such as a city or address. Absent when the location has no label.",
            "type": "string"
          },
          "radius": {
            "description": "Radius around the center point, in `distance_unit`.",
            "type": "number"
          }
        },
        "required": [
          "latitude",
          "longitude",
          "radius",
          "distance_unit"
        ],
        "type": "object"
      },
      "AdGroupDemographicCategory": {
        "properties": {
          "id": {
            "description": "The ad platform's ID for the category in its targeting taxonomy.",
            "type": "string"
          },
          "name": {
            "description": "Category name, such as `Recently moved`.",
            "type": "string"
          },
          "type": {
            "description": "Kind of demographic the category belongs to.",
            "enum": [
              "life_events",
              "industries",
              "income",
              "family_statuses"
            ],
            "example": "life_events",
            "type": "string"
          }
        },
        "required": [
          "id",
          "type"
        ],
        "type": "object"
      },
      "AdGroupDemographics": {
        "properties": {
          "automatic": {
            "description": "Whether automatic audience targeting is on (Advantage+ on Meta). When `true`, the platform can deliver beyond the ages, genders, and detailed targeting you set, treating them as suggestions.",
            "type": "boolean"
          },
          "gender": {
            "description": "Gender targeted.",
            "enum": [
              "all",
              "male",
              "female"
            ],
            "example": "all",
            "type": "string"
          },
          "maximum_age": {
            "description": "Oldest age targeted. `null` when no maximum is set.",
            "type": [
              "number",
              "null"
            ]
          },
          "minimum_age": {
            "description": "Youngest age targeted. `null` when no minimum is set.",
            "type": [
              "number",
              "null"
            ]
          }
        },
        "required": [
          "automatic",
          "minimum_age",
          "maximum_age",
          "gender"
        ],
        "type": "object"
      },
      "AdGroupDetailedTargeting": {
        "properties": {
          "behaviors": {
            "items": {
              "$ref": "#/components/schemas/AdGroupTargetingCategory",
              "description": "Behavior categories targeted, such as frequent travelers."
            },
            "type": "array"
          },
          "demographics": {
            "items": {
              "$ref": "#/components/schemas/AdGroupDemographicCategory",
              "description": "Demographic categories targeted, such as life events or industries."
            },
            "type": "array"
          },
          "interests": {
            "items": {
              "$ref": "#/components/schemas/AdGroupTargetingCategory",
              "description": "Interest categories targeted, such as an interest in movies."
            },
            "type": "array"
          }
        },
        "required": [
          "interests",
          "behaviors",
          "demographics"
        ],
        "type": "object"
      },
      "AdGroupDevices": {
        "properties": {
          "operating_systems": {
            "items": {
              "$ref": "#/components/schemas/AdGroupOperatingSystem",
              "description": "Operating systems targeted. Empty targets all operating systems."
            },
            "type": "array"
          },
          "platforms": {
            "items": {
              "description": "Device types targeted. Empty targets all devices.",
              "enum": [
                "mobile",
                "desktop"
              ],
              "example": "mobile",
              "type": "string"
            },
            "type": "array"
          }
        },
        "required": [
          "platforms",
          "operating_systems"
        ],
        "type": "object"
      },
      "AdGroupFrequencyCap": {
        "properties": {
          "maximum_impressions": {
            "description": "Most times one person can be shown ads from this ad group within the window.",
            "type": "number"
          },
          "per_days": {
            "description": "Length of the rolling window, in days.",
            "type": [
              "number",
              "null"
            ]
          }
        },
        "required": [
          "maximum_impressions",
          "per_days"
        ],
        "type": "object"
      },
      "AdGroupGeoLocations": {
        "properties": {
          "cities": {
            "items": {
              "$ref": "#/components/schemas/AdGroupCity",
              "description": "Cities, keyed by the ad platform's location taxonomy."
            },
            "type": "array"
          },
          "countries": {
            "items": {
              "description": "Countries, as ISO 3166-1 alpha-2 codes such as `US`.",
              "type": "string"
            },
            "type": "array"
          },
          "country_groups": {
            "items": {
              "description": "Multi-country groups such as `worldwide` or `europe`.",
              "type": "string"
            },
            "type": "array"
          },
          "custom_locations": {
            "items": {
              "$ref": "#/components/schemas/AdGroupCustomLocation",
              "description": "Circular areas, each a coordinate plus a radius."
            },
            "type": "array"
          },
          "regions": {
            "items": {
              "description": "States and provinces, as ISO 3166-2 codes such as `US-CA`.",
              "type": "string"
            },
            "type": "array"
          },
          "zips": {
            "items": {
              "description": "ZIP and postal codes.",
              "type": "string"
            },
            "type": "array"
          }
        },
        "required": [
          "countries",
          "country_groups",
          "regions",
          "cities",
          "zips",
          "custom_locations"
        ],
        "type": "object"
      },
      "AdGroupOperatingSystem": {
        "properties": {
          "minimum_version": {
            "description": "Lowest OS version targeted, such as `18.0`. Absent when any version qualifies.",
            "type": "string"
          },
          "os": {
            "description": "Operating system targeted.",
            "enum": [
              "ios",
              "android"
            ],
            "example": "ios",
            "type": "string"
          }
        },
        "required": [
          "os"
        ],
        "type": "object"
      },
      "AdGroupPlacement": {
        "properties": {
          "platform": {
            "description": "Platform the ads run on: `facebook`, `instagram`, `messenger`, `audience_network`, `threads`, or `whatsapp`.",
            "type": "string"
          },
          "positions": {
            "items": {
              "description": "Positions targeted within the platform, such as `feed` or `story`. Empty targets all of the platform's positions.",
              "type": "string"
            },
            "type": "array"
          }
        },
        "required": [
          "platform",
          "positions"
        ],
        "type": "object"
      },
      "AdGroupRegions": {
        "properties": {
          "exclude": {
            "$ref": "#/components/schemas/AdGroupGeoLocations",
            "description": "Locations excluded from targeting. Country groups can't be excluded."
          },
          "include": {
            "$ref": "#/components/schemas/AdGroupGeoLocations",
            "description": "Locations the ad group targets."
          }
        },
        "required": [
          "include",
          "exclude"
        ],
        "type": "object"
      },
      "AdGroupTargetingCategory": {
        "properties": {
          "id": {
            "description": "The ad platform's ID for the category in its targeting taxonomy.",
            "type": "string"
          },
          "name": {
            "description": "Category name, such as `Movies`.",
            "type": "string"
          }
        },
        "required": [
          "id"
        ],
        "type": "object"
      },
      "AdLeadForm": {
        "properties": {
          "completion": {
            "description": "Screen shown after the form is submitted. `null` when the form uses the default.",
            "oneOf": [
              {
                "$ref": "#/components/schemas/AdLeadFormCompletion"
              },
              {
                "type": "null"
              }
            ]
          },
          "disclaimer": {
            "description": "Custom consent disclaimer shown before submission. `null` when the form has none.",
            "oneOf": [
              {
                "$ref": "#/components/schemas/AdLeadFormDisclaimer"
              },
              {
                "type": "null"
              }
            ]
          },
          "form_type": {
            "description": "`more_volume` is quickest to submit; `higher_intent` adds a confirmation step before submission.",
            "enum": [
              "more_volume",
              "higher_intent"
            ],
            "example": "more_volume",
            "type": "string"
          },
          "intro": {
            "description": "Intro screen shown before the questions. `null` when the form has none.",
            "oneOf": [
              {
                "$ref": "#/components/schemas/AdLeadFormIntro"
              },
              {
                "type": "null"
              }
            ]
          },
          "name": {
            "description": "Internal name of the form.",
            "type": [
              "string",
              "null"
            ]
          },
          "phone_verification": {
            "description": "Whether the phone number must be verified by SMS before submitting.",
            "type": "boolean"
          },
          "privacy_policy": {
            "description": "Your privacy policy, linked from the form. `null` when unset.",
            "oneOf": [
              {
                "$ref": "#/components/schemas/AdLeadFormPrivacyPolicy"
              },
              {
                "type": "null"
              }
            ]
          },
          "questions": {
            "items": {
              "$ref": "#/components/schemas/AdLeadFormQuestion",
              "description": "Questions on the form, in order."
            },
            "type": "array"
          }
        },
        "required": [
          "name",
          "form_type",
          "intro",
          "questions",
          "privacy_policy",
          "completion",
          "disclaimer",
          "phone_verification"
        ],
        "type": "object"
      },
      "AdLeadFormCompletion": {
        "properties": {
          "button_text": {
            "description": "Text of the follow-up button.",
            "type": [
              "string",
              "null"
            ]
          },
          "description": {
            "description": "Body text under the headline.",
            "type": [
              "string",
              "null"
            ]
          },
          "headline": {
            "description": "Headline of the completion screen.",
            "type": [
              "string",
              "null"
            ]
          },
          "url": {
            "description": "Website the follow-up button opens. `null` when the screen has no button.",
            "type": [
              "string",
              "null"
            ]
          }
        },
        "required": [
          "headline",
          "description",
          "button_text",
          "url"
        ],
        "type": "object"
      },
      "AdLeadFormDisclaimer": {
        "properties": {
          "body": {
            "description": "Disclaimer text.",
            "type": [
              "string",
              "null"
            ]
          },
          "checkboxes": {
            "items": {
              "$ref": "#/components/schemas/AdLeadFormDisclaimerCheckbox",
              "description": "Consent checkboxes the person can tick. Empty when the disclaimer is text-only."
            },
            "type": "array"
          },
          "title": {
            "description": "Disclaimer title.",
            "type": [
              "string",
              "null"
            ]
          }
        },
        "required": [
          "title",
          "body",
          "checkboxes"
        ],
        "type": "object"
      },
      "AdLeadFormDisclaimerCheckbox": {
        "properties": {
          "checked_by_default": {
            "description": "Whether the checkbox starts ticked.",
            "type": [
              "boolean",
              "null"
            ]
          },
          "key": {
            "description": "Stable identifier consent responses are stored under.",
            "type": [
              "string",
              "null"
            ]
          },
          "required": {
            "description": "Whether the checkbox must be ticked to submit the form.",
            "type": [
              "boolean",
              "null"
            ]
          },
          "text": {
            "description": "Consent text next to the checkbox.",
            "type": "string"
          }
        },
        "required": [
          "text",
          "key",
          "required",
          "checked_by_default"
        ],
        "type": "object"
      },
      "AdLeadFormIntro": {
        "properties": {
          "description": {
            "description": "Body text under the headline.",
            "type": [
              "string",
              "null"
            ]
          },
          "headline": {
            "description": "Headline of the intro screen.",
            "type": [
              "string",
              "null"
            ]
          }
        },
        "required": [
          "headline",
          "description"
        ],
        "type": "object"
      },
      "AdLeadFormOptionLogic": {
        "properties": {
          "action": {
            "description": "What happens when the choice is selected.",
            "enum": [
              "go_to_question",
              "submit_form",
              "close_form"
            ],
            "example": "go_to_question",
            "type": "string"
          },
          "target_end_page_index": {
            "description": "Zero-based index of the ending screen to jump to.",
            "type": "number"
          },
          "target_question_index": {
            "description": "Zero-based index of the question to jump to, for `go_to_question`.",
            "type": "number"
          }
        },
        "required": [
          "action"
        ],
        "type": "object"
      },
      "AdLeadFormPrivacyPolicy": {
        "properties": {
          "link_text": {
            "description": "Link text shown for the policy. `null` uses the platform default.",
            "type": [
              "string",
              "null"
            ]
          },
          "url": {
            "description": "URL of your privacy policy.",
            "type": "string"
          }
        },
        "required": [
          "url",
          "link_text"
        ],
        "type": "object"
      },
      "AdLeadFormQuestion": {
        "properties": {
          "format": {
            "description": "Answer format for `custom` questions: `short_answer`, `multiple_choice`, or `appointment`. Absent otherwise.",
            "type": "string"
          },
          "label": {
            "description": "Question text for `custom` questions. Absent for standard prefill questions.",
            "type": "string"
          },
          "options": {
            "items": {
              "$ref": "#/components/schemas/AdLeadFormQuestionOption",
              "description": "Choices for `multiple_choice` questions. Absent for other formats."
            },
            "type": "array"
          },
          "type": {
            "description": "Question type: a standard prefill type such as `email`, `phone`, or `full_name`, or `custom` for your own question.",
            "type": "string"
          }
        },
        "required": [
          "type"
        ],
        "type": "object"
      },
      "AdLeadFormQuestionOption": {
        "properties": {
          "key": {
            "description": "Stable identifier the choice's answers are stored under. Absent for simple choices.",
            "type": [
              "string",
              "null"
            ]
          },
          "logic": {
            "$ref": "#/components/schemas/AdLeadFormOptionLogic",
            "description": "Where the form goes when this choice is selected. Absent when the form just continues to the next question."
          },
          "value": {
            "description": "Choice text shown to the person.",
            "type": "string"
          }
        },
        "required": [
          "value"
        ],
        "type": "object"
      },
      "AdMessagingConfig": {
        "properties": {
          "keyword": {
            "description": "Suggested reply the person can tap to start the conversation.",
            "type": [
              "string",
              "null"
            ]
          },
          "message": {
            "description": "Greeting shown when the conversation opens.",
            "type": [
              "string",
              "null"
            ]
          }
        },
        "required": [
          "message",
          "keyword"
        ],
        "type": "object"
      },
      "AdPlatformIssue": {
        "properties": {
          "id": {
            "description": "Unique identifier for the issue.",
            "type": "string"
          },
          "message": {
            "description": "A description of what the issue is and how it can be resolved.",
            "type": "string"
          },
          "resource_id": {
            "description": "The ID of the campaign, ad group, or ad the issue is attached to.",
            "type": [
              "string",
              "null"
            ]
          },
          "resource_type": {
            "description": "The type of resource the issue is attached to.",
            "enum": [
              "ad_campaign",
              "ad_group",
              "ad"
            ],
            "example": "ad_campaign",
            "type": "string"
          }
        },
        "required": [
          "id",
          "message",
          "resource_id",
          "resource_type"
        ],
        "type": "object"
      },
      "Audience": {
        "properties": {
          "audience_type": {
            "description": "`custom` = uploaded customer list; `lookalike` = Meta lookalike built from a custom audience.",
            "enum": [
              "custom",
              "lookalike"
            ],
            "example": "custom",
            "type": "string"
          },
          "created_at": {
            "description": "When the audience was created, as an ISO 8601 timestamp.",
            "type": "string"
          },
          "error_message": {
            "description": "Processing error message. `null` unless processing is partial or failed.",
            "type": [
              "string",
              "null"
            ]
          },
          "id": {
            "description": "Audience ID, prefixed `adaud_`.",
            "type": "string"
          },
          "lookalike_ratio": {
            "description": "For lookalikes: the upper bound of the similarity band as a fraction (0.02 = top 2%). `null` for custom audiences.",
            "type": [
              "number",
              "null"
            ]
          },
          "lookalike_starting_ratio": {
            "description": "For lookalikes: the lower bound of the similarity band as a fraction. `null` for custom audiences and first-tier lookalikes.",
            "type": [
              "number",
              "null"
            ]
          },
          "match_rates": {
            "items": {
              "$ref": "#/components/schemas/AudienceMatchRate",
              "description": "Estimated match rates by ad platform. Empty when the audience was not sent to a supported platform."
            },
            "type": "array"
          },
          "matched_rows": {
            "description": "Rows successfully uploaded to connected ad accounts. Always 0 for lookalikes.",
            "type": "number"
          },
          "name": {
            "description": "Audience display name.",
            "type": "string"
          },
          "platform_audience_ids": {
            "items": {
              "description": "External audience IDs created on connected ad platforms, such as Meta.",
              "type": "string"
            },
            "type": "array"
          },
          "processed_rows": {
            "description": "Rows processed from the uploaded CSV. Always 0 for lookalikes.",
            "type": "number"
          },
          "progress_percent": {
            "description": "Processing progress from 0 to 100.",
            "type": "number"
          },
          "source_audience_id": {
            "description": "For lookalikes: the audience this lookalike was built from. `null` for custom audiences.",
            "type": [
              "string",
              "null"
            ]
          },
          "status": {
            "description": "Current state of the audience import. `syncing` means Whop is sending matched rows to connected ad accounts. When status is `partial` or `failed`, `error_message` explains what went wrong.",
            "enum": [
              "pending",
              "processing",
              "syncing",
              "ready",
              "partial",
              "failed"
            ],
            "type": "string"
          },
          "total_rows": {
            "description": "Total rows detected in the uploaded CSV. Always 0 for lookalikes.",
            "type": "number"
          },
          "updated_at": {
            "description": "When the audience was last updated, as an ISO 8601 timestamp.",
            "type": "string"
          }
        },
        "required": [
          "id",
          "name",
          "audience_type",
          "status",
          "total_rows",
          "processed_rows",
          "matched_rows",
          "progress_percent",
          "error_message",
          "platform_audience_ids",
          "source_audience_id",
          "lookalike_ratio",
          "lookalike_starting_ratio",
          "created_at",
          "updated_at",
          "match_rates"
        ],
        "type": "object"
      },
      "AudienceMatchRate": {
        "properties": {
          "lower_bound": {
            "description": "Lower bound of the estimated match rate percentage. `null` until available.",
            "type": [
              "number",
              "null"
            ]
          },
          "platform": {
            "description": "The ad platform that provided the match-rate estimate.",
            "enum": [
              "meta"
            ],
            "example": "meta",
            "type": "string"
          },
          "status": {
            "description": "Availability of the estimated match rate.",
            "enum": [
              "calculating",
              "available",
              "unavailable",
              null
            ],
            "example": "calculating",
            "type": [
              "string",
              "null"
            ]
          },
          "upper_bound": {
            "description": "Upper bound of the estimated match rate percentage. `null` until available.",
            "type": [
              "number",
              "null"
            ]
          }
        },
        "required": [
          "platform",
          "status",
          "lower_bound",
          "upper_bound"
        ],
        "type": "object"
      },
      "Bounty": {
        "properties": {
          "accepted_deliverable_types": {
            "items": {
              "description": "Deliverable types this bounty accepts when submitting work through the API: `content_url` (posted links) or `media` (uploaded files). Empty when the bounty does not yet accept API submissions.",
              "enum": [
                "content_url",
                "media"
              ],
              "example": "content_url",
              "type": "string"
            },
            "type": "array"
          },
          "accepted_submissions_count": {
            "description": "Submissions accepted so far.",
            "type": "integer"
          },
          "accepted_submissions_limit": {
            "description": "Number of submissions that can be accepted (winner slots).",
            "type": "integer"
          },
          "allowed_country_codes": {
            "items": {
              "description": "Countries whose residents can work the bounty, as ISO 3166 alpha-2 codes. Empty means worldwide.",
              "type": "string"
            },
            "type": "array"
          },
          "budget_amount": {
            "description": "Total gross budget committed to the bounty: `gross_reward_amount` times `accepted_submissions_limit`.",
            "type": "number"
          },
          "business_goal_type": {
            "description": "What the poster wants the work to achieve. Determines which deliverable types the bounty accepts through the submissions API. `null` for bounties created before the taxonomy rolled out.",
            "enum": [
              "clipping",
              "post_engagement",
              "owned_account_growth",
              "ugc_content",
              "local_activation",
              "data_capture",
              "other",
              null
            ],
            "example": "clipping",
            "type": [
              "string",
              "null"
            ]
          },
          "created_at": {
            "description": "When the bounty was created, as an ISO 8601 timestamp.",
            "type": "string"
          },
          "currency": {
            "description": "Currency for all amounts on the bounty, as a lowercase ISO 4217 code.",
            "enum": [
              "usd",
              "sgd",
              "inr",
              "aud",
              "brl",
              "cad",
              "dkk",
              "eur",
              "nok",
              "gbp",
              "sek",
              "chf",
              "hkd",
              "huf",
              "jpy",
              "mxn",
              "myr",
              "pln",
              "czk",
              "nzd",
              "aed",
              "cop",
              "ron",
              "thb",
              "bgn",
              "idr",
              "dop",
              "php",
              "try",
              "krw",
              "twd",
              "vnd",
              "pkr",
              "clp",
              "uyu",
              "ars",
              "zar",
              "dzd",
              "tnd",
              "mad",
              "kes",
              "kwd",
              "jod",
              "all",
              "xcd",
              "amd",
              "bsd",
              "bhd",
              "bob",
              "bam",
              "khr",
              "crc",
              "xof",
              "egp",
              "etb",
              "gmd",
              "ghs",
              "gtq",
              "gyd",
              "ils",
              "jmd",
              "mop",
              "mga",
              "mur",
              "mdl",
              "mnt",
              "nad",
              "ngn",
              "mkd",
              "omr",
              "pyg",
              "pen",
              "qar",
              "rwf",
              "sar",
              "rsd",
              "lkr",
              "tzs",
              "ttd",
              "uzs",
              "rub",
              "cny",
              "kzt",
              "awg"
            ],
            "example": "usd",
            "type": "string"
          },
          "description": {
            "description": "Full task instructions shown to workers.",
            "type": "string"
          },
          "experience_id": {
            "description": "Experience the bounty is hosted in, prefixed `exp_`. `null` for platform-wide bounties; may belong to a different account than the funder.",
            "type": [
              "string",
              "null"
            ]
          },
          "funding_account": {
            "description": "Account whose balance funds the bounty pool, or `null` when a user funds it personally. May differ from the account hosting `experience_id`.",
            "oneOf": [
              {
                "$ref": "#/components/schemas/AccountSummary"
              },
              {
                "type": "null"
              }
            ]
          },
          "gross_paid_out_amount": {
            "description": "Gross amount paid out from the bounty pool across accepted submissions — worker payouts, platform fees, and affiliate shares together. Tips and reviewer rewards are excluded.",
            "type": "number"
          },
          "gross_reward_amount": {
            "description": "Gross bounty-pool amount allocated per accepted submission, in whole currency units.",
            "type": "number"
          },
          "id": {
            "description": "Bounty ID, prefixed `bnty_`.",
            "type": "string"
          },
          "poster": {
            "$ref": "#/components/schemas/UserSummary",
            "description": "User who posted the bounty — the account owner when created with an account API key."
          },
          "scheduled_frequency": {
            "description": "How often the schedule creates a new bounty. Each occurrence is a separate bounty; the original is not republished.",
            "enum": [
              "once",
              "hourly",
              "daily",
              "weekly",
              "monthly",
              null
            ],
            "example": "once",
            "type": [
              "string",
              "null"
            ]
          },
          "scheduled_publish_at": {
            "description": "When a scheduled bounty will publish, as an ISO 8601 timestamp. `null` once published, for bounties that were never scheduled, and for terminally failed drafts parked for manual rescheduling.",
            "type": [
              "string",
              "null"
            ]
          },
          "spots_remaining": {
            "description": "Unfilled winner capacity: `accepted_submissions_limit` minus `accepted_submissions_count`, clamped to zero. Not a signal that the bounty currently accepts new claims.",
            "type": "integer"
          },
          "status": {
            "description": "Lifecycle state. `scheduled` bounties are unpublished drafts, visible to their poster and the account's authorized managers; `open` bounties accept new submissions; `closed` bounties are live but no longer accept new submissions; `completed` bounties paid out every winner slot; `canceled` bounties ended before filling their slots.",
            "enum": [
              "scheduled",
              "open",
              "closed",
              "completed",
              "canceled"
            ],
            "example": "scheduled",
            "type": "string"
          },
          "submissions_closed_at": {
            "description": "When new submissions were explicitly stopped, as an ISO 8601 timestamp. `null` when submissions were never explicitly stopped — including closed or completed bounties that simply filled every winner slot.",
            "type": [
              "string",
              "null"
            ]
          },
          "title": {
            "description": "Short name of the task shown to workers.",
            "type": "string"
          },
          "unresolved_submissions_count": {
            "description": "Submissions still awaiting an outcome: in progress or pending review.",
            "type": "integer"
          },
          "updated_at": {
            "description": "When the bounty was last updated, as an ISO 8601 timestamp.",
            "type": "string"
          }
        },
        "required": [
          "id",
          "title",
          "status",
          "currency",
          "gross_reward_amount",
          "budget_amount",
          "gross_paid_out_amount",
          "accepted_submissions_limit",
          "accepted_submissions_count",
          "unresolved_submissions_count",
          "spots_remaining",
          "business_goal_type",
          "accepted_deliverable_types",
          "allowed_country_codes",
          "submissions_closed_at",
          "scheduled_publish_at",
          "scheduled_frequency",
          "experience_id",
          "created_at",
          "updated_at",
          "description",
          "poster",
          "funding_account"
        ],
        "type": "object"
      },
      "BountyListItem": {
        "properties": {
          "accepted_deliverable_types": {
            "items": {
              "description": "Deliverable types this bounty accepts when submitting work through the API: `content_url` (posted links) or `media` (uploaded files). Empty when the bounty does not yet accept API submissions.",
              "enum": [
                "content_url",
                "media"
              ],
              "example": "content_url",
              "type": "string"
            },
            "type": "array"
          },
          "accepted_submissions_count": {
            "description": "Submissions accepted so far.",
            "type": "integer"
          },
          "accepted_submissions_limit": {
            "description": "Number of submissions that can be accepted (winner slots).",
            "type": "integer"
          },
          "allowed_country_codes": {
            "items": {
              "description": "Countries whose residents can work the bounty, as ISO 3166 alpha-2 codes. Empty means worldwide.",
              "type": "string"
            },
            "type": "array"
          },
          "budget_amount": {
            "description": "Total gross budget committed to the bounty: `gross_reward_amount` times `accepted_submissions_limit`.",
            "type": "number"
          },
          "business_goal_type": {
            "description": "What the poster wants the work to achieve. Determines which deliverable types the bounty accepts through the submissions API. `null` for bounties created before the taxonomy rolled out.",
            "enum": [
              "clipping",
              "post_engagement",
              "owned_account_growth",
              "ugc_content",
              "local_activation",
              "data_capture",
              "other",
              null
            ],
            "example": "clipping",
            "type": [
              "string",
              "null"
            ]
          },
          "created_at": {
            "description": "When the bounty was created, as an ISO 8601 timestamp.",
            "type": "string"
          },
          "currency": {
            "description": "Currency for all amounts on the bounty, as a lowercase ISO 4217 code.",
            "type": "string"
          },
          "experience_id": {
            "description": "Experience the bounty is hosted in, prefixed `exp_`. `null` for platform-wide bounties; may belong to a different account than the funder.",
            "type": [
              "string",
              "null"
            ]
          },
          "funding_account": {
            "description": "Account whose balance funds the bounty pool, or `null` when a user funds it personally. May differ from the account hosting `experience_id`.",
            "oneOf": [
              {
                "$ref": "#/components/schemas/AccountSummary"
              },
              {
                "type": "null"
              }
            ]
          },
          "gross_paid_out_amount": {
            "description": "Gross amount paid out from the bounty pool across accepted submissions — worker payouts, platform fees, and affiliate shares together. Tips and reviewer rewards are excluded.",
            "type": "number"
          },
          "gross_reward_amount": {
            "description": "Gross bounty-pool amount allocated per accepted submission, in whole currency units.",
            "type": "number"
          },
          "id": {
            "description": "Bounty ID, prefixed `bnty_`.",
            "type": "string"
          },
          "poster": {
            "$ref": "#/components/schemas/UserSummary",
            "description": "User who posted the bounty — the account owner when created with an account API key."
          },
          "scheduled_frequency": {
            "description": "How often the schedule creates a new bounty. Each occurrence is a separate bounty; the original is not republished.",
            "enum": [
              "once",
              "hourly",
              "daily",
              "weekly",
              "monthly",
              null
            ],
            "example": "once",
            "type": [
              "string",
              "null"
            ]
          },
          "scheduled_publish_at": {
            "description": "When a scheduled bounty will publish, as an ISO 8601 timestamp. `null` once published, for bounties that were never scheduled, and for terminally failed drafts parked for manual rescheduling.",
            "type": [
              "string",
              "null"
            ]
          },
          "spots_remaining": {
            "description": "Unfilled winner capacity: `accepted_submissions_limit` minus `accepted_submissions_count`, clamped to zero. Not a signal that the bounty currently accepts new claims.",
            "type": "integer"
          },
          "status": {
            "description": "Lifecycle state. `scheduled` bounties are unpublished drafts, visible to their poster and the account's authorized managers; `open` bounties accept new submissions; `closed` bounties are live but no longer accept new submissions; `completed` bounties paid out every winner slot; `canceled` bounties ended before filling their slots.",
            "enum": [
              "scheduled",
              "open",
              "closed",
              "completed",
              "canceled"
            ],
            "example": "scheduled",
            "type": "string"
          },
          "submissions_closed_at": {
            "description": "When new submissions were explicitly stopped, as an ISO 8601 timestamp. `null` when submissions were never explicitly stopped — including closed or completed bounties that simply filled every winner slot.",
            "type": [
              "string",
              "null"
            ]
          },
          "title": {
            "description": "Short name of the task shown to workers.",
            "type": "string"
          },
          "unresolved_submissions_count": {
            "description": "Submissions still awaiting an outcome: in progress or pending review.",
            "type": "integer"
          },
          "updated_at": {
            "description": "When the bounty was last updated, as an ISO 8601 timestamp.",
            "type": "string"
          }
        },
        "required": [
          "id",
          "title",
          "status",
          "currency",
          "gross_reward_amount",
          "budget_amount",
          "gross_paid_out_amount",
          "accepted_submissions_limit",
          "accepted_submissions_count",
          "unresolved_submissions_count",
          "spots_remaining",
          "business_goal_type",
          "accepted_deliverable_types",
          "allowed_country_codes",
          "submissions_closed_at",
          "scheduled_publish_at",
          "scheduled_frequency",
          "experience_id",
          "created_at",
          "updated_at",
          "poster",
          "funding_account"
        ],
        "type": "object"
      },
      "BountySubmission": {
        "properties": {
          "bounty_id": {
            "description": "The bounty the work was submitted to, prefixed `bnty_`.",
            "type": "string"
          },
          "content": {
            "description": "Written proof the worker submitted with their work.",
            "type": [
              "string",
              "null"
            ]
          },
          "created_at": {
            "description": "When the submission was created, as an ISO 8601 timestamp.",
            "type": "string"
          },
          "deliverable_type": {
            "description": "Deliverable shape the worker submitted. `content_url` is links to posted content; `media` is uploaded files. `null` on submissions authored before deliverable types existed.",
            "enum": [
              "content_url",
              "media",
              null
            ],
            "example": "content_url",
            "type": [
              "string",
              "null"
            ]
          },
          "deliverable_urls": {
            "items": {
              "description": "The submitted content links. Only set when `deliverable_type` is `content_url`.",
              "type": "string"
            },
            "type": [
              "array",
              "null"
            ]
          },
          "denial_reason": {
            "description": "Why the submission was denied, when a presentable reason exists. Always `null` unless `status` is `denied`.",
            "type": [
              "string",
              "null"
            ]
          },
          "id": {
            "description": "Submission ID, prefixed `btys_`.",
            "type": "string"
          },
          "resolved_at": {
            "description": "When the submission was approved or denied, as an ISO 8601 timestamp. `null` until then.",
            "type": [
              "string",
              "null"
            ]
          },
          "status": {
            "description": "Lifecycle state. `in_progress` submissions are active attempts that have not submitted proof yet; `submitted` submissions await review; `approved` submissions were accepted and paid; `denied` submissions were rejected.",
            "enum": [
              "in_progress",
              "submitted",
              "approved",
              "denied"
            ],
            "example": "in_progress",
            "type": "string"
          },
          "submitted_at": {
            "description": "When proof was submitted for review, as an ISO 8601 timestamp. `null` while the attempt is in progress.",
            "type": [
              "string",
              "null"
            ]
          },
          "updated_at": {
            "description": "When the submission was last updated, as an ISO 8601 timestamp.",
            "type": "string"
          },
          "worker": {
            "$ref": "#/components/schemas/UserSummary",
            "description": "User who submitted the work."
          }
        },
        "required": [
          "id",
          "bounty_id",
          "status",
          "deliverable_type",
          "deliverable_urls",
          "content",
          "denial_reason",
          "submitted_at",
          "resolved_at",
          "created_at",
          "updated_at",
          "worker"
        ],
        "type": "object"
      },
      "ConversionEvent": {
        "anyOf": [
          {
            "enum": [
              "purchase",
              "add_to_cart",
              "initiated_checkout",
              "add_payment_info",
              "complete_registration",
              "lead",
              "content_view",
              "search",
              "contact",
              "customize_product",
              "donate",
              "find_location",
              "schedule",
              "start_trial",
              "submit_application",
              "subscribe"
            ],
            "type": "string"
          },
          {
            "type": "string"
          },
          {
            "type": "null"
          }
        ],
        "description": "The pixel event optimized for. A standard event, or any custom pixel event name."
      },
      "CustomCtas": {
        "description": "The different types of custom CTAs that can be selected.",
        "enum": [
          "get_access",
          "join",
          "order_now",
          "shop_now",
          "call_now",
          "donate_now",
          "contact_us",
          "sign_up",
          "subscribe",
          "purchase",
          "get_offer",
          "apply_now",
          "complete_order"
        ],
        "type": "string"
      },
      "GlobalAffiliateStatuses": {
        "description": "The different statuses of the global affiliate program for a product.",
        "enum": [
          "enabled",
          "disabled"
        ],
        "type": "string"
      },
      "LedgerActivity": {
        "properties": {
          "account": {
            "description": "The viewer account that owns this row's ledger. Present only when the response aggregates owned accounts (include_owned_accounts=true); omitted otherwise.",
            "oneOf": [
              {
                "properties": {
                  "id": {
                    "description": "Account ID.",
                    "type": "string"
                  },
                  "logo_url": {
                    "description": "Account logo URL.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "object": {
                    "enum": [
                      "account"
                    ],
                    "type": "string"
                  },
                  "route": {
                    "description": "Account route.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "title": {
                    "description": "Account display name.",
                    "type": [
                      "string",
                      "null"
                    ]
                  }
                },
                "required": [
                  "object",
                  "id",
                  "title",
                  "route",
                  "logo_url"
                ],
                "type": "object"
              },
              {
                "properties": {
                  "id": {
                    "description": "User ID.",
                    "type": "string"
                  },
                  "name": {
                    "description": "User display name.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "object": {
                    "enum": [
                      "user"
                    ],
                    "type": "string"
                  },
                  "profile_picture_url": {
                    "description": "User profile image URL.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "username": {
                    "description": "User's username.",
                    "type": [
                      "string",
                      "null"
                    ]
                  }
                },
                "required": [
                  "object",
                  "id",
                  "name",
                  "username",
                  "profile_picture_url"
                ],
                "type": "object"
              }
            ]
          },
          "amount": {
            "description": "Signed amount in the currency's smallest precision units.",
            "type": "string"
          },
          "available_at": {
            "description": "ISO 8601 timestamp these funds became (or are scheduled to become) withdrawable: the posted time for already-settled funds, or 00:00:00 UTC on the scheduled release date for pending funds. Present only on inflows entering the balance (payments, top-ups, incoming transfers/affiliate); null on withdrawals, refunds, disputes and on-chain rows. The available_after/before filters window on its UTC settlement date.",
            "format": "date-time",
            "type": [
              "string",
              "null"
            ]
          },
          "created_at": {
            "description": "When the activity record was created.",
            "format": "date-time",
            "type": [
              "string",
              "null"
            ]
          },
          "currency": {
            "description": "Currency for this ledger activity.",
            "properties": {
              "code": {
                "description": "Currency code.",
                "type": "string"
              },
              "precision": {
                "description": "Precision factor for the currency, for example `100000000` for USD.",
                "type": "string"
              }
            },
            "required": [
              "code",
              "precision"
            ],
            "type": "object"
          },
          "id": {
            "description": "Ledger activity ID.",
            "type": "string"
          },
          "ledger_account_id": {
            "description": "The ledger account (a ldgr_ identifier) this row belongs to. Present only when the response aggregates owned accounts (include_owned_accounts=true); omitted otherwise. Pair it with `account` to scope drawers and dashboard links to the owning business.",
            "type": [
              "string",
              "null"
            ]
          },
          "line_type": {
            "description": "Type of ledger activity.",
            "type": "string"
          },
          "object": {
            "enum": [
              "ledger_activity"
            ],
            "type": "string"
          },
          "posted_at": {
            "description": "When the activity posted to the ledger.",
            "format": "date-time",
            "type": "string"
          },
          "resource": {
            "description": "Resource associated with this ledger activity.",
            "oneOf": [
              {
                "properties": {
                  "id": {
                    "description": "Account ID.",
                    "type": "string"
                  },
                  "logo_url": {
                    "description": "Account logo URL.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "object": {
                    "enum": [
                      "account"
                    ],
                    "type": "string"
                  },
                  "route": {
                    "description": "Account route.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "title": {
                    "description": "Account display name.",
                    "type": [
                      "string",
                      "null"
                    ]
                  }
                },
                "required": [
                  "object",
                  "id",
                  "title",
                  "route",
                  "logo_url"
                ],
                "type": "object"
              },
              {
                "properties": {
                  "id": {
                    "description": "User ID.",
                    "type": "string"
                  },
                  "name": {
                    "description": "User display name.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "object": {
                    "enum": [
                      "user"
                    ],
                    "type": "string"
                  },
                  "profile_picture_url": {
                    "description": "User profile image URL.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "username": {
                    "description": "User's username.",
                    "type": [
                      "string",
                      "null"
                    ]
                  }
                },
                "required": [
                  "object",
                  "id",
                  "name",
                  "username",
                  "profile_picture_url"
                ],
                "type": "object"
              },
              {
                "properties": {
                  "id": {
                    "description": "Bounty ID.",
                    "type": "string"
                  },
                  "object": {
                    "enum": [
                      "bounty"
                    ],
                    "type": "string"
                  },
                  "status": {
                    "description": "Bounty lifecycle status.",
                    "type": "string"
                  },
                  "title": {
                    "description": "Bounty title.",
                    "type": "string"
                  }
                },
                "required": [
                  "object",
                  "id",
                  "title",
                  "status"
                ],
                "type": "object"
              },
              {
                "properties": {
                  "id": {
                    "description": "Ledger account ID.",
                    "type": "string"
                  },
                  "object": {
                    "enum": [
                      "ledger_account"
                    ],
                    "type": "string"
                  },
                  "owner": {
                    "oneOf": [
                      {
                        "properties": {
                          "id": {
                            "description": "Account ID.",
                            "type": "string"
                          },
                          "logo_url": {
                            "description": "Account logo URL.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "object": {
                            "enum": [
                              "account"
                            ],
                            "type": "string"
                          },
                          "route": {
                            "description": "Account route.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "title": {
                            "description": "Account display name.",
                            "type": [
                              "string",
                              "null"
                            ]
                          }
                        },
                        "required": [
                          "object",
                          "id",
                          "title",
                          "route",
                          "logo_url"
                        ],
                        "type": "object"
                      },
                      {
                        "properties": {
                          "id": {
                            "description": "User ID.",
                            "type": "string"
                          },
                          "name": {
                            "description": "User display name.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "object": {
                            "enum": [
                              "user"
                            ],
                            "type": "string"
                          },
                          "profile_picture_url": {
                            "description": "User profile image URL.",
                            "type": [
                              "string",
                              "null"
                            ]
                          },
                          "username": {
                            "description": "User's username.",
                            "type": [
                              "string",
                              "null"
                            ]
                          }
                        },
                        "required": [
                          "object",
                          "id",
                          "name",
                          "username",
                          "profile_picture_url"
                        ],
                        "type": "object"
                      },
                      {
                        "type": "null"
                      }
                    ]
                  }
                },
                "required": [
                  "object",
                  "id",
                  "owner"
                ],
                "type": "object"
              },
              {
                "properties": {
                  "bank": {
                    "properties": {
                      "account_name": {
                        "description": "Bank account holder name.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "account_type": {
                        "description": "Bank account type.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "bank_name": {
                        "description": "Bank name.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "last4": {
                        "description": "Last four digits of the bank account.",
                        "type": [
                          "string",
                          "null"
                        ]
                      }
                    },
                    "required": [
                      "bank_name",
                      "account_name",
                      "last4",
                      "account_type"
                    ],
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "card": {
                    "properties": {
                      "brand": {
                        "description": "Card brand.",
                        "type": [
                          "string",
                          "null"
                        ]
                      },
                      "exp_month": {
                        "description": "Card expiration month.",
                        "type": [
                          "integer",
                          "null"
                        ]
                      },
                      "exp_year": {
                        "description": "Card expiration year.",
                        "type": [
                          "integer",
                          "null"
                        ]
                      },
                      "last4": {
                        "description": "Last four digits of the card.",
                        "type": [
                          "string",
                          "null"
                        ]
                      }
                    },
                    "required": [
                      "brand",
                      "last4",
                      "exp_month",
                      "exp_year"
                    ],
                    "type": [
                      "object",
                      "null"
                    ]
                  },
                  "email_identifier": {
                    "description": "Email identifier for email-based payment methods.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "gateway_type": {
                    "description": "Payment gateway type.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "id": {
                    "description": "Payment method ID.",
                    "type": "string"
                  },
                  "object": {
                    "enum": [
                      "payment_method"
                    ],
                    "type": "string"
                  },
                  "payment_method_type": {
                    "description": "Payment method type.",
                    "type": [
                      "string",
                      "null"
                    ]
                  }
                },
                "required": [
                  "object",
                  "id",
                  "payment_method_type",
                  "gateway_type",
                  "card",
                  "bank",
                  "email_identifier"
                ],
                "type": "object"
              },
              {
                "properties": {
                  "account_reference": {
                    "description": "Masked account reference.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "destination_currency_code": {
                    "description": "Destination currency code.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "id": {
                    "description": "Payout method ID.",
                    "type": "string"
                  },
                  "institution_name": {
                    "description": "Payout institution name.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "nickname": {
                    "description": "Payout method nickname.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "object": {
                    "enum": [
                      "payout_method"
                    ],
                    "type": "string"
                  },
                  "provider": {
                    "description": "Payout provider.",
                    "type": [
                      "string",
                      "null"
                    ]
                  }
                },
                "required": [
                  "object",
                  "id",
                  "nickname",
                  "institution_name",
                  "account_reference",
                  "provider",
                  "destination_currency_code"
                ],
                "type": "object"
              },
              {
                "properties": {
                  "authorized_at": {
                    "description": "ISO 8601 timestamp the transaction was authorized.",
                    "format": "date-time",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "card_id": {
                    "description": "Identifier of the card that the transaction was charged to.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "cashback_usd": {
                    "description": "Cashback earned on this transaction as a USD decimal string. Zero for declined or ineligible transactions; null when cashback has not been computed yet.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "declined_reason": {
                    "description": "Reason the transaction was declined (when status is declined).",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "id": {
                    "description": "Card transaction ID.",
                    "type": "string"
                  },
                  "local_amount": {
                    "description": "Amount the merchant charged in their local currency, as a decimal string. Pair with local_currency.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "local_currency": {
                    "description": "ISO 4217 currency code of the merchant-charged amount in local_amount.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "merchant_category": {
                    "description": "Merchant category.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "merchant_icon_url": {
                    "description": "Merchant icon URL.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "merchant_name": {
                    "description": "Merchant display name.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "object": {
                    "enum": [
                      "card_transaction"
                    ],
                    "type": "string"
                  },
                  "posted_at": {
                    "description": "ISO 8601 timestamp the transaction was settled by the card network.",
                    "format": "date-time",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "status": {
                    "description": "Current card transaction status.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "usd_amount": {
                    "description": "The processor-settled USD amount as a decimal string. The ledger's USDT leg is posted 1:1 from this value.",
                    "type": [
                      "string",
                      "null"
                    ]
                  }
                },
                "required": [
                  "object",
                  "id",
                  "card_id",
                  "merchant_name",
                  "merchant_icon_url",
                  "merchant_category",
                  "status",
                  "usd_amount",
                  "local_amount",
                  "local_currency",
                  "cashback_usd",
                  "authorized_at",
                  "posted_at",
                  "declined_reason"
                ],
                "type": "object"
              },
              {
                "type": "null"
              }
            ]
          },
          "source": {
            "additionalProperties": true,
            "description": "Source of this ledger activity.",
            "properties": {
              "amount_float": {
                "description": "Withdrawal amount as a decimal number in the destination currency (withdrawal sources only; requires payout:withdrawal:read).",
                "type": [
                  "number",
                  "null"
                ]
              },
              "chain": {
                "description": "Chain the deposit landed on, for example plasma (onchain_transaction sources only).",
                "type": [
                  "string",
                  "null"
                ]
              },
              "claim_url": {
                "description": "Public claim URL for the airdrop link (airdrop_link sources only).",
                "type": [
                  "string",
                  "null"
                ]
              },
              "created_at": {
                "description": "Withdrawal creation time as an ISO 8601 timestamp (withdrawal sources only; requires payout:withdrawal:read).",
                "format": "date-time",
                "type": [
                  "string",
                  "null"
                ]
              },
              "estimated_arrival": {
                "description": "Estimated arrival as an ISO 8601 timestamp (withdrawal sources only; requires payout:withdrawal:read).",
                "format": "date-time",
                "type": [
                  "string",
                  "null"
                ]
              },
              "from_amount": {
                "description": "Amount converted out of from_currency as a decimal string (swap sources only).",
                "type": [
                  "string",
                  "null"
                ]
              },
              "from_currency": {
                "description": "Lowercase currency code converted from (swap sources only).",
                "type": [
                  "string",
                  "null"
                ]
              },
              "id": {
                "type": "string"
              },
              "object": {
                "type": "string"
              },
              "payer_name": {
                "description": "Name of the entity processing the payout (withdrawal sources only; requires payout:withdrawal:read).",
                "type": [
                  "string",
                  "null"
                ]
              },
              "payout_destination": {
                "description": "Payout destination display info (withdrawal sources only).",
                "properties": {
                  "icon_url": {
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "payer_name": {
                    "type": [
                      "string",
                      "null"
                    ]
                  }
                },
                "type": [
                  "object",
                  "null"
                ]
              },
              "payout_token_nickname": {
                "description": "Saved payout destination nickname (withdrawal sources only).",
                "type": [
                  "string",
                  "null"
                ]
              },
              "reason": {
                "description": "Transfer reason on transfer sources, for example pool_top_up or bounty_return.",
                "type": [
                  "string",
                  "null"
                ]
              },
              "sender_address": {
                "description": "Sender wallet address or onramp provider identifier (onchain_transaction sources only).",
                "type": [
                  "string",
                  "null"
                ]
              },
              "status": {
                "description": "Lifecycle status. On withdrawal sources this is the withdrawal status (requires payout:withdrawal:read); on airdrop_link sources it is the claim-link status (ungated); on payment and top-up sources it is the friendly payment status such as succeeded/pending/failed (ungated).",
                "type": [
                  "string",
                  "null"
                ]
              },
              "to_amount": {
                "description": "Amount received in to_currency as a decimal string (swap sources only).",
                "type": [
                  "string",
                  "null"
                ]
              },
              "to_currency": {
                "description": "Lowercase currency code converted to (swap sources only).",
                "type": [
                  "string",
                  "null"
                ]
              },
              "tx_hash": {
                "description": "On-chain transaction hash (onchain_transaction and swap sources only).",
                "type": [
                  "string",
                  "null"
                ]
              }
            },
            "required": [
              "object",
              "id"
            ],
            "type": [
              "object",
              "null"
            ]
          }
        },
        "required": [
          "object",
          "id",
          "line_type",
          "amount",
          "currency",
          "posted_at",
          "available_at",
          "created_at",
          "resource",
          "source"
        ],
        "type": "object"
      },
      "MediaAsset": {
        "properties": {
          "amount_charged": {
            "description": "USD amount charged to the account's balance for this generation. `null` if the generation wasn't billed.",
            "type": [
              "number",
              "null"
            ]
          },
          "completed_at": {
            "description": "ISO 8601 timestamp when the asset reached a terminal state. `null` while `processing`.",
            "type": [
              "string",
              "null"
            ]
          },
          "created_at": {
            "description": "ISO 8601 timestamp when the generation was requested.",
            "type": "string"
          },
          "currency": {
            "description": "Currency of `amount_charged`. Always `usd`.",
            "type": "string"
          },
          "error_message": {
            "description": "Why generation failed. `null` unless status is `failed`.",
            "type": [
              "string",
              "null"
            ]
          },
          "file": {
            "description": "The produced file, usable anywhere attachments are accepted. `null` until the asset is `ready`.",
            "oneOf": [
              {
                "$ref": "#/components/schemas/MediaAssetFile"
              },
              {
                "type": "null"
              }
            ]
          },
          "generation": {
            "$ref": "#/components/schemas/MediaAssetGeneration",
            "description": "The inputs the asset was generated from."
          },
          "id": {
            "description": "Media asset ID, prefixed `media_`.",
            "type": "string"
          },
          "media_type": {
            "description": "The kind of media this asset holds.",
            "enum": [
              "video",
              "image"
            ],
            "example": "video",
            "type": "string"
          },
          "source": {
            "description": "How the asset was created. Always `generated`.",
            "enum": [
              "generated"
            ],
            "example": "generated",
            "type": "string"
          },
          "status": {
            "description": "Lifecycle state: `processing` while generation runs, `ready` when the file is available, `failed` when generation failed and the charge was refunded.",
            "enum": [
              "processing",
              "ready",
              "failed"
            ],
            "example": "processing",
            "type": "string"
          }
        },
        "required": [
          "id",
          "media_type",
          "source",
          "status",
          "amount_charged",
          "currency",
          "error_message",
          "created_at",
          "completed_at",
          "generation",
          "file"
        ],
        "type": "object"
      },
      "MediaAssetFile": {
        "properties": {
          "id": {
            "description": "File ID, prefixed `file_`.",
            "type": "string"
          },
          "url": {
            "description": "CDN URL for downloading the file.",
            "type": "string"
          }
        },
        "required": [
          "id",
          "url"
        ],
        "type": "object"
      },
      "MediaAssetGeneration": {
        "properties": {
          "duration_seconds": {
            "description": "Requested video length in seconds. `null` for images.",
            "type": [
              "number",
              "null"
            ]
          },
          "prompt": {
            "description": "What the asset was generated from.",
            "type": "string"
          },
          "reference_media": {
            "items": {
              "description": "Reference image file IDs (`file_` prefixed) supplied at generation time.",
              "type": "string"
            },
            "type": "array"
          },
          "resolution": {
            "description": "Requested video resolution. `null` for images. `1080p` is not supported by Seedance 2.0 Fast or Mini; `4k` is only supported by Seedance 2.0.",
            "enum": [
              "480p",
              "720p",
              "1080p",
              "4k",
              null
            ],
            "example": "480p",
            "type": [
              "string",
              "null"
            ]
          }
        },
        "required": [
          "prompt",
          "reference_media",
          "duration_seconds",
          "resolution"
        ],
        "type": "object"
      },
      "Plan": {
        "properties": {
          "account": {
            "description": "Account that sells this plan; `null` for standalone invoice plans.",
            "type": [
              "object",
              "null"
            ]
          },
          "adaptive_pricing_enabled": {
            "description": "Whether this plan accepts local currency payments via adaptive pricing.",
            "type": "boolean"
          },
          "billing_period": {
            "description": "Recurring billing interval in days, such as 30 for monthly or 365 for annual. `null` for one-time plans.",
            "type": [
              "number",
              "null"
            ]
          },
          "collect_tax": {
            "description": "Whether tax is collected on purchases of this plan.",
            "type": "boolean"
          },
          "created_at": {
            "description": "When the plan was created, as an ISO 8601 timestamp.",
            "type": "string"
          },
          "currency": {
            "description": "Three-letter ISO currency code for this plan's prices.",
            "enum": [
              "usd",
              "sgd",
              "inr",
              "aud",
              "brl",
              "cad",
              "dkk",
              "eur",
              "nok",
              "gbp",
              "sek",
              "chf",
              "hkd",
              "huf",
              "jpy",
              "mxn",
              "myr",
              "pln",
              "czk",
              "nzd",
              "aed",
              "eth",
              "ape",
              "cop",
              "ron",
              "thb",
              "bgn",
              "idr",
              "dop",
              "php",
              "try",
              "krw",
              "twd",
              "vnd",
              "pkr",
              "clp",
              "uyu",
              "ars",
              "zar",
              "dzd",
              "tnd",
              "mad",
              "kes",
              "kwd",
              "jod",
              "all",
              "xcd",
              "amd",
              "bsd",
              "bhd",
              "bob",
              "bam",
              "khr",
              "crc",
              "xof",
              "egp",
              "etb",
              "gmd",
              "ghs",
              "gtq",
              "gyd",
              "ils",
              "jmd",
              "mop",
              "mga",
              "mur",
              "mdl",
              "mnt",
              "nad",
              "ngn",
              "mkd",
              "omr",
              "pyg",
              "pen",
              "qar",
              "rwf",
              "sar",
              "rsd",
              "lkr",
              "tzs",
              "ttd",
              "uzs",
              "rub",
              "btc",
              "cny",
              "usdt",
              "kzt",
              "awg",
              "whop_usd",
              "xau"
            ],
            "example": "usd",
            "type": "string"
          },
          "custom_fields": {
            "items": {
              "$ref": "#/components/schemas/PlanCustomField",
              "description": "Custom input fields collected on the checkout form."
            },
            "type": "array"
          },
          "description": {
            "description": "Customer-visible plan description.",
            "type": [
              "string",
              "null"
            ]
          },
          "expiration_days": {
            "description": "Access duration in days for expiration-based plans.",
            "type": [
              "number",
              "null"
            ]
          },
          "id": {
            "description": "Plan ID, prefixed `plan_`.",
            "type": "string"
          },
          "initial_price": {
            "description": "Initial purchase price in plan currency.",
            "type": "number"
          },
          "internal_notes": {
            "description": "Private notes visible only to authorized team members.",
            "type": [
              "string",
              "null"
            ]
          },
          "invoice": {
            "description": "Invoice this plan was generated for; `null` unless created for an invoice.",
            "type": [
              "object",
              "null"
            ]
          },
          "member_count": {
            "description": "Active memberships through this plan, when visible to the requester.",
            "type": [
              "number",
              "null"
            ]
          },
          "metadata": {
            "description": "Custom key-value pairs stored on the plan.",
            "type": [
              "object",
              "null"
            ]
          },
          "payment_method_configuration": {
            "description": "Payment method configuration (`enabled`, `disabled`, `include_platform_defaults`); `null` when plan uses default settings.",
            "type": [
              "object",
              "null"
            ]
          },
          "plan_type": {
            "description": "Billing model for this plan: `renewal` (recurring) or `one_time` (single payment).",
            "enum": [
              "renewal",
              "one_time"
            ],
            "example": "renewal",
            "type": "string"
          },
          "product": {
            "description": "Product this plan belongs to; `null` for standalone plans.",
            "type": [
              "object",
              "null"
            ]
          },
          "purchase_url": {
            "description": "URL where customers can purchase this plan directly.",
            "type": "string"
          },
          "release_method": {
            "description": "Sales method for this plan, such as `buy_now` or `waitlist`.",
            "enum": [
              "buy_now",
              "waitlist"
            ],
            "example": "buy_now",
            "type": "string"
          },
          "renewal_price": {
            "description": "Recurring price charged every billing period.",
            "type": "number"
          },
          "split_pay_required_payments": {
            "description": "Installment payments required before the subscription pauses.",
            "type": [
              "number",
              "null"
            ]
          },
          "stock": {
            "description": "Units available for purchase, when visible to the requester.",
            "type": [
              "number",
              "null"
            ]
          },
          "tax_type": {
            "description": "How tax is handled for this plan.",
            "enum": [
              "inclusive",
              "exclusive",
              "unspecified"
            ],
            "example": "inclusive",
            "type": "string"
          },
          "three_ds_level": {
            "description": "3D Secure behavior for this plan; `null` inherits account default.",
            "enum": [
              "mandate_challenge",
              "frictionless",
              null
            ],
            "example": "mandate_challenge",
            "type": [
              "string",
              "null"
            ]
          },
          "title": {
            "description": "Plan display name shown to customers.",
            "type": [
              "string",
              "null"
            ]
          },
          "trial_period_days": {
            "description": "Free trial days before the first renewal charge. `null` if no trial is configured or the user has already used a trial for this plan.",
            "type": [
              "number",
              "null"
            ]
          },
          "unlimited_stock": {
            "description": "Whether the plan has unlimited stock.",
            "type": "boolean"
          },
          "updated_at": {
            "description": "When the plan was last updated, as an ISO 8601 timestamp.",
            "type": "string"
          },
          "visibility": {
            "description": "Whether the plan is visible to customers or hidden from public view.",
            "enum": [
              "visible",
              "hidden",
              "archived",
              "quick_link"
            ],
            "example": "visible",
            "type": "string"
          }
        },
        "required": [
          "id",
          "created_at",
          "updated_at",
          "visibility",
          "plan_type",
          "release_method",
          "currency",
          "account",
          "product",
          "invoice",
          "billing_period",
          "title",
          "description",
          "purchase_url",
          "expiration_days",
          "initial_price",
          "renewal_price",
          "trial_period_days",
          "three_ds_level",
          "member_count",
          "internal_notes",
          "metadata",
          "stock",
          "unlimited_stock",
          "split_pay_required_payments",
          "adaptive_pricing_enabled",
          "payment_method_configuration",
          "tax_type",
          "collect_tax",
          "custom_fields"
        ],
        "type": "object"
      },
      "PlanCustomField": {
        "properties": {
          "field_type": {
            "description": "Custom field input type.",
            "enum": [
              "text"
            ],
            "example": "text",
            "type": "string"
          },
          "id": {
            "description": "Custom field ID.",
            "type": "string"
          },
          "name": {
            "description": "Field label shown to customer at checkout.",
            "type": "string"
          },
          "order": {
            "description": "Field position on checkout form.",
            "type": "number"
          },
          "placeholder": {
            "description": "Placeholder text shown in empty field.",
            "type": [
              "string",
              "null"
            ]
          },
          "required": {
            "description": "Whether the customer must complete this field to check out.",
            "type": "boolean"
          }
        },
        "required": [
          "id",
          "field_type",
          "name",
          "order",
          "placeholder",
          "required"
        ],
        "type": "object"
      },
      "PlanListItem": {
        "properties": {
          "account": {
            "description": "Account that sells this plan; `null` for standalone invoice plans.",
            "type": [
              "object",
              "null"
            ]
          },
          "adaptive_pricing_enabled": {
            "description": "Whether this plan accepts local currency payments via adaptive pricing.",
            "type": "boolean"
          },
          "billing_period": {
            "description": "Recurring billing interval in days, such as 30 for monthly or 365 for annual. `null` for one-time plans.",
            "type": [
              "number",
              "null"
            ]
          },
          "created_at": {
            "description": "When the plan was created, as an ISO 8601 timestamp.",
            "type": "string"
          },
          "currency": {
            "description": "Three-letter ISO currency code for this plan's prices.",
            "type": "string"
          },
          "description": {
            "description": "Customer-visible plan description.",
            "type": [
              "string",
              "null"
            ]
          },
          "expiration_days": {
            "description": "Access duration in days for expiration-based plans.",
            "type": [
              "number",
              "null"
            ]
          },
          "id": {
            "description": "Plan ID, prefixed `plan_`.",
            "type": "string"
          },
          "initial_price": {
            "description": "Initial purchase price in plan currency.",
            "type": "number"
          },
          "internal_notes": {
            "description": "Private notes visible only to authorized team members.",
            "type": [
              "string",
              "null"
            ]
          },
          "invoice": {
            "description": "Invoice this plan was generated for; `null` unless created for an invoice.",
            "type": [
              "object",
              "null"
            ]
          },
          "member_count": {
            "description": "Active memberships through this plan, when visible to the requester.",
            "type": [
              "number",
              "null"
            ]
          },
          "metadata": {
            "description": "Custom key-value pairs stored on the plan.",
            "type": [
              "object",
              "null"
            ]
          },
          "payment_method_configuration": {
            "description": "Payment method configuration (`enabled`, `disabled`, `include_platform_defaults`); `null` when plan uses default settings.",
            "type": [
              "object",
              "null"
            ]
          },
          "plan_type": {
            "description": "Billing model for this plan: `renewal` (recurring) or `one_time` (single payment).",
            "type": "string"
          },
          "product": {
            "description": "Product this plan belongs to; `null` for standalone plans.",
            "type": [
              "object",
              "null"
            ]
          },
          "purchase_url": {
            "description": "URL where customers can purchase this plan directly.",
            "type": "string"
          },
          "release_method": {
            "description": "Sales method for this plan, such as `buy_now` or `waitlist`.",
            "type": "string"
          },
          "renewal_price": {
            "description": "Recurring price charged every billing period.",
            "type": "number"
          },
          "split_pay_required_payments": {
            "description": "Installment payments required before the subscription pauses.",
            "type": [
              "number",
              "null"
            ]
          },
          "stock": {
            "description": "Units available for purchase, when visible to the requester.",
            "type": [
              "number",
              "null"
            ]
          },
          "three_ds_level": {
            "description": "3D Secure behavior for this plan; `null` inherits account default.",
            "type": [
              "string",
              "null"
            ]
          },
          "title": {
            "description": "Plan display name shown to customers.",
            "type": [
              "string",
              "null"
            ]
          },
          "trial_period_days": {
            "description": "Free trial days before the first renewal charge. `null` if no trial is configured or the user has already used a trial for this plan.",
            "type": [
              "number",
              "null"
            ]
          },
          "unlimited_stock": {
            "description": "Whether the plan has unlimited stock.",
            "type": "boolean"
          },
          "updated_at": {
            "description": "When the plan was last updated, as an ISO 8601 timestamp.",
            "type": "string"
          },
          "visibility": {
            "description": "Whether the plan is visible to customers or hidden from public view.",
            "type": "string"
          }
        },
        "required": [
          "id",
          "created_at",
          "updated_at",
          "visibility",
          "plan_type",
          "release_method",
          "currency",
          "account",
          "product",
          "invoice",
          "billing_period",
          "title",
          "description",
          "purchase_url",
          "expiration_days",
          "initial_price",
          "renewal_price",
          "trial_period_days",
          "three_ds_level",
          "member_count",
          "internal_notes",
          "metadata",
          "stock",
          "unlimited_stock",
          "split_pay_required_payments",
          "adaptive_pricing_enabled",
          "payment_method_configuration"
        ],
        "type": "object"
      },
      "Product": {
        "description": "A product is a digital good or service sold on Whop. Products contain plans for pricing and experiences for content delivery.",
        "properties": {
          "company": {
            "description": "The company this product belongs to.",
            "properties": {
              "id": {
                "description": "The unique identifier for the company.",
                "example": "biz_xxxxxxxxxxxxxx",
                "type": "string"
              },
              "route": {
                "description": "URL slug for the account's store page, e.g. `pickaxe` in whop.com/pickaxe.",
                "example": "pickaxe",
                "type": "string"
              },
              "title": {
                "description": "The display name of the company shown to customers.",
                "example": "Pickaxe",
                "type": "string"
              }
            },
            "required": [
              "id",
              "route",
              "title"
            ],
            "type": "object"
          },
          "created_at": {
            "description": "The datetime the product was created.",
            "example": "2023-12-01T05:00:00.401Z",
            "format": "date-time",
            "type": "string"
          },
          "custom_cta": {
            "$ref": "#/components/schemas/CustomCtas",
            "description": "Call-to-action button label shown on the product purchase page."
          },
          "custom_cta_url": {
            "description": "An optional URL that the call-to-action button links to instead of the default checkout flow. Null if no custom URL is set.",
            "example": "https://example.com/signup",
            "type": [
              "string",
              "null"
            ]
          },
          "custom_statement_descriptor": {
            "description": "Custom bank statement descriptor for product purchases. Maximum 22 characters, including required `WHOP*` prefix.",
            "example": "PICKAXE",
            "type": [
              "string",
              "null"
            ]
          },
          "description": {
            "description": "A brief summary of what the product offers, displayed on product pages and search results.",
            "example": "Track your revenue, members, and growth in real time.",
            "type": [
              "string",
              "null"
            ]
          },
          "external_identifier": {
            "description": "External identifier for the product. Providing it on a product creation endpoint updates the existing product with this identifier instead of creating a new one.",
            "example": "ext_prod_12345",
            "type": [
              "string",
              "null"
            ]
          },
          "gallery_images": {
            "description": "The gallery images for this product, ordered by position.",
            "items": {
              "description": "Represents an image attachment",
              "properties": {
                "content_type": {
                  "description": "Uploaded file MIME type, such as image/jpeg, video/mp4, or audio/mpeg.",
                  "example": "image/jpeg",
                  "type": [
                    "string",
                    "null"
                  ]
                },
                "id": {
                  "description": "Represents a unique identifier that is Base64 obfuscated. It is often used to refetch an object or as key for a cache. The ID type appears in a JSON response as a String; however, it is not intended to be human-readable. When expected as an input type, any string (such as `\"VXNlci0xMA==\"`) or integer (such as `4`) input value will be accepted as an ID.",
                  "type": "string"
                },
                "url": {
                  "description": "A pre-optimized URL for rendering this attachment on the client. This should be used for displaying attachments in apps.",
                  "example": "https://media.whop.com/abc123/optimized.jpg",
                  "type": [
                    "string",
                    "null"
                  ]
                }
              },
              "required": [
                "id",
                "url",
                "content_type"
              ],
              "type": "object"
            },
            "type": "array"
          },
          "global_affiliate_percentage": {
            "description": "Marketplace affiliate commission percentage for this product, or `null` if program is inactive.",
            "example": 6.9,
            "type": [
              "number",
              "null"
            ]
          },
          "global_affiliate_status": {
            "$ref": "#/components/schemas/GlobalAffiliateStatuses",
            "description": "The enrollment status of this product in the Whop marketplace global affiliate program."
          },
          "headline": {
            "description": "A short marketing headline displayed prominently on the product's product page.",
            "example": "Real-time data analytics for creators",
            "type": [
              "string",
              "null"
            ]
          },
          "id": {
            "description": "The unique identifier for the product.",
            "example": "prod_xxxxxxxxxxxxx",
            "type": "string"
          },
          "member_affiliate_percentage": {
            "description": "Member referral commission percentage for this product, or `null` if program is inactive.",
            "example": 6.9,
            "type": [
              "number",
              "null"
            ]
          },
          "member_affiliate_status": {
            "$ref": "#/components/schemas/GlobalAffiliateStatuses",
            "description": "The enrollment status of this product in the member affiliate program."
          },
          "member_count": {
            "description": "Active memberships for this product. Returns `0` if the account has disabled public member counts.",
            "example": 42,
            "type": "integer"
          },
          "metadata": {
            "additionalProperties": true,
            "description": "Custom key-value pairs stored on the product and included in payment and membership webhook payloads. Max 50 keys, 100 characters per key, 500 characters per string value.",
            "type": [
              "object",
              "null"
            ]
          },
          "owner_user": {
            "description": "The user who owns the company that sells this product.",
            "properties": {
              "id": {
                "description": "The unique identifier for the user.",
                "example": "user_xxxxxxxxxxxxx",
                "type": "string"
              },
              "name": {
                "description": "The user's display name shown on their public profile.",
                "example": "John Doe",
                "type": [
                  "string",
                  "null"
                ]
              },
              "username": {
                "description": "The user's unique username shown on their public profile.",
                "example": "johndoe42",
                "type": "string"
              }
            },
            "required": [
              "id",
              "name",
              "username"
            ],
            "type": "object"
          },
          "product_tax_code": {
            "description": "The tax classification code applied to purchases of this product for sales tax calculation. Null if no tax code is assigned.",
            "properties": {
              "id": {
                "description": "The unique identifier for the product tax code.",
                "example": "ptc_xxxxxxxxxxxxxx",
                "type": "string"
              },
              "name": {
                "description": "Human-readable name of this tax classification, such as 'Digital - SaaS'.",
                "example": "Digital - SaaS",
                "type": "string"
              },
              "product_type": {
                "$ref": "#/components/schemas/ProductTaxCodeProductTypes",
                "description": "Broad product category this tax code covers, such as physical goods or digital services."
              }
            },
            "required": [
              "id",
              "name",
              "product_type"
            ],
            "type": [
              "object",
              "null"
            ]
          },
          "published_reviews_count": {
            "description": "The total number of published customer reviews for this product's company.",
            "example": 42,
            "type": "integer"
          },
          "route": {
            "description": "URL slug in the product's public link, e.g. `pickaxe-analytics` in whop.com/company/pickaxe-analytics.",
            "example": "pickaxe-analytics",
            "type": "string"
          },
          "title": {
            "description": "The display name of the product shown to customers on the product page and in search results.",
            "example": "Pickaxe Analytics",
            "type": "string"
          },
          "updated_at": {
            "description": "The datetime the product was last updated.",
            "example": "2023-12-01T05:00:00.401Z",
            "format": "date-time",
            "type": "string"
          },
          "verified": {
            "description": "Whether this company has been verified by Whop's trust and safety team.",
            "type": "boolean"
          },
          "visibility": {
            "$ref": "#/components/schemas/Visibility",
            "description": "Controls whether the product is visible to customers. When set to 'hidden', the product is only accessible via direct link."
          }
        },
        "required": [
          "id",
          "title",
          "visibility",
          "headline",
          "verified",
          "created_at",
          "updated_at",
          "member_count",
          "route",
          "published_reviews_count",
          "external_identifier",
          "metadata",
          "gallery_images",
          "description",
          "custom_cta",
          "custom_cta_url",
          "custom_statement_descriptor",
          "global_affiliate_percentage",
          "global_affiliate_status",
          "member_affiliate_percentage",
          "member_affiliate_status",
          "product_tax_code",
          "owner_user",
          "company"
        ],
        "type": "object"
      },
      "ProductListItem": {
        "description": "A product is a digital good or service sold on Whop. Products contain plans for pricing and experiences for content delivery.",
        "properties": {
          "created_at": {
            "description": "The datetime the product was created.",
            "example": "2023-12-01T05:00:00.401Z",
            "format": "date-time",
            "type": "string"
          },
          "external_identifier": {
            "description": "External identifier for the product. Providing it on a product creation endpoint updates the existing product with this identifier instead of creating a new one.",
            "example": "ext_prod_12345",
            "type": [
              "string",
              "null"
            ]
          },
          "gallery_images": {
            "description": "The gallery images for this product, ordered by position.",
            "items": {
              "description": "Represents an image attachment",
              "properties": {
                "content_type": {
                  "description": "Uploaded file MIME type, such as image/jpeg, video/mp4, or audio/mpeg.",
                  "example": "image/jpeg",
                  "type": [
                    "string",
                    "null"
                  ]
                },
                "id": {
                  "description": "Represents a unique identifier that is Base64 obfuscated. It is often used to refetch an object or as key for a cache. The ID type appears in a JSON response as a String; however, it is not intended to be human-readable. When expected as an input type, any string (such as `\"VXNlci0xMA==\"`) or integer (such as `4`) input value will be accepted as an ID.",
                  "type": "string"
                },
                "url": {
                  "description": "A pre-optimized URL for rendering this attachment on the client. This should be used for displaying attachments in apps.",
                  "example": "https://media.whop.com/abc123/optimized.jpg",
                  "type": [
                    "string",
                    "null"
                  ]
                }
              },
              "required": [
                "id",
                "url",
                "content_type"
              ],
              "type": "object"
            },
            "type": "array"
          },
          "headline": {
            "description": "A short marketing headline displayed prominently on the product's product page.",
            "example": "Real-time data analytics for creators",
            "type": [
              "string",
              "null"
            ]
          },
          "id": {
            "description": "The unique identifier for the product.",
            "example": "prod_xxxxxxxxxxxxx",
            "type": "string"
          },
          "member_count": {
            "description": "Active memberships for this product. Returns `0` if the account has disabled public member counts.",
            "example": 42,
            "type": "integer"
          },
          "metadata": {
            "additionalProperties": true,
            "description": "Custom key-value pairs stored on the product and included in payment and membership webhook payloads. Max 50 keys, 100 characters per key, 500 characters per string value.",
            "type": [
              "object",
              "null"
            ]
          },
          "published_reviews_count": {
            "description": "The total number of published customer reviews for this product's company.",
            "example": 42,
            "type": "integer"
          },
          "route": {
            "description": "URL slug in the product's public link, e.g. `pickaxe-analytics` in whop.com/company/pickaxe-analytics.",
            "example": "pickaxe-analytics",
            "type": "string"
          },
          "title": {
            "description": "The display name of the product shown to customers on the product page and in search results.",
            "example": "Pickaxe Analytics",
            "type": "string"
          },
          "updated_at": {
            "description": "The datetime the product was last updated.",
            "example": "2023-12-01T05:00:00.401Z",
            "format": "date-time",
            "type": "string"
          },
          "verified": {
            "description": "Whether this company has been verified by Whop's trust and safety team.",
            "type": "boolean"
          },
          "visibility": {
            "$ref": "#/components/schemas/Visibility",
            "description": "Controls whether the product is visible to customers. When set to 'hidden', the product is only accessible via direct link."
          }
        },
        "required": [
          "id",
          "title",
          "visibility",
          "headline",
          "verified",
          "created_at",
          "updated_at",
          "member_count",
          "route",
          "published_reviews_count",
          "external_identifier",
          "metadata",
          "gallery_images"
        ],
        "type": "object"
      },
      "ProductTaxCodeProductTypes": {
        "description": "The product_type of the ProductTaxCode",
        "enum": [
          "physical",
          "digital",
          "services"
        ],
        "type": "string"
      },
      "SocialAccount": {
        "properties": {
          "error": {
            "description": "Why this social account currently can't be used for advertising — a failed share or a Meta-side restriction. Null when the account is healthy.",
            "type": [
              "string",
              "null"
            ]
          },
          "external_id": {
            "description": "The platform-specific ID for this social account.",
            "type": [
              "string",
              "null"
            ]
          },
          "id": {
            "description": "Unique identifier for the social account.",
            "type": "string"
          },
          "name": {
            "description": "The display name of the social account on the platform.",
            "type": [
              "string",
              "null"
            ]
          },
          "platform": {
            "description": "The platform the social account exists on.",
            "enum": [
              "x",
              "instagram",
              "youtube",
              "tiktok",
              "facebook"
            ],
            "example": "x",
            "type": "string"
          },
          "profile_picture_url": {
            "description": "The URL where the profile picture of the social account can be accessed.",
            "type": [
              "string",
              "null"
            ]
          },
          "scopes": {
            "items": {
              "description": "Capabilities Whop retains specific to this social account. For example, Whop may request the ability to run advertisements that use this social account's identity, reflected by the presence of `advertise` in this value.",
              "type": "string"
            },
            "type": "array"
          },
          "url": {
            "description": "The URL where the social account can be accessed on the platform. Null while a Whop-owned page is still being provisioned.",
            "type": [
              "string",
              "null"
            ]
          },
          "username": {
            "description": "The username of the social account on the platform. Null while a Whop-owned page is still being provisioned.",
            "type": [
              "string",
              "null"
            ]
          },
          "verified": {
            "description": "Whether the social account is verified on the platform.",
            "type": "boolean"
          }
        },
        "required": [
          "id",
          "platform",
          "username",
          "name",
          "url",
          "profile_picture_url",
          "verified",
          "external_id",
          "scopes",
          "error"
        ],
        "type": "object"
      },
      "SocialAccountPost": {
        "properties": {
          "call_to_action": {
            "description": "The post's call-to-action button, for example shop_now (Facebook only; null for Instagram).",
            "enum": [
              "learn_more",
              "shop_now",
              "sign_up",
              "subscribe",
              "get_started",
              "book_now",
              "apply_now",
              "contact_us",
              "download",
              "order_now",
              "buy_now",
              "get_quote",
              "message_page",
              "whatsapp_message",
              "instagram_message",
              "call_now",
              "get_directions",
              "send_updates",
              "get_offer",
              "watch_more",
              "listen_now",
              "play_game",
              "open_link",
              "no_button",
              "get_offer_view",
              "get_event_tickets",
              "see_menu",
              "request_time",
              "event_rsvp",
              "see_details",
              "view_instagram_profile",
              null
            ],
            "example": "learn_more",
            "type": [
              "string",
              "null"
            ]
          },
          "destination_url": {
            "description": "The URL the post's call-to-action drives to (Facebook only; null for Instagram).",
            "type": [
              "string",
              "null"
            ]
          },
          "id": {
            "description": "The platform's own identifier for the post or media. Use it to reference the post on an ad.",
            "type": "string"
          },
          "media_url": {
            "description": "The URL of the post's media — the image for image posts, the playable video file for video posts. Meta signs these and they expire after roughly 24 hours, so don't store them.",
            "type": [
              "string",
              "null"
            ]
          },
          "thumbnail_url": {
            "description": "Poster image for video posts; null for image posts, where media_url is already the image. Signed and short-lived like media_url.",
            "type": [
              "string",
              "null"
            ]
          }
        },
        "required": [
          "id",
          "media_url",
          "thumbnail_url",
          "call_to_action",
          "destination_url"
        ],
        "type": "object"
      },
      "User": {
        "properties": {
          "balance": {
            "description": "The user's balance: personal cash + crypto + in-flight treasury deposits, plus account balances for accounts they own. Computed only on `GET /users/me` self-view for callers with balance-read scope; `null` otherwise.",
            "oneOf": [
              {
                "$ref": "#/components/schemas/UserBalance"
              },
              {
                "type": "null"
              }
            ]
          },
          "balance_history": {
            "description": "The user's cumulative wallet balance over time (USD `{ t, v }` points plus last/min/max), for the balance chart. Opt in with `include_balance_history=true` on `GET /users/me`; populated only for callers with balance-read scope and `null` otherwise. A user with no wallet activity returns an empty series.",
            "oneOf": [
              {
                "$ref": "#/components/schemas/UserBalanceHistory"
              },
              {
                "type": "null"
              }
            ]
          },
          "bio": {
            "description": "The user's biography",
            "type": [
              "string",
              "null"
            ]
          },
          "created_at": {
            "description": "When the user was created, as an ISO 8601 timestamp",
            "type": "string"
          },
          "earnings_usd": {
            "description": "The user's gross USD income over time. Populated only on single-user self reads for callers with balance-read scope; `null` otherwise.",
            "oneOf": [
              {
                "$ref": "#/components/schemas/UserEarnings"
              },
              {
                "type": "null"
              }
            ]
          },
          "id": {
            "description": "User ID, prefixed `user_`.",
            "type": "string"
          },
          "name": {
            "description": "The user's display name",
            "type": [
              "string",
              "null"
            ]
          },
          "profile_picture": {
            "description": "The user's profile picture, an object with a url",
            "type": [
              "object",
              "null"
            ]
          },
          "username": {
            "description": "The user's unique username",
            "type": "string"
          },
          "verification": {
            "description": "Identity verification status for the user's `individual` (KYC) and `business` (KYB) profiles. Each is `null` until created, otherwise a `status` of `not_started`, `pending`, `approved`, or `rejected`.",
            "type": "object"
          },
          "whop_partner_enabled_at": {
            "description": "When the user became an enrolled Whop Partner, as an ISO 8601 timestamp. `null` if never enrolled.",
            "type": [
              "string",
              "null"
            ]
          }
        },
        "required": [
          "id",
          "username",
          "name",
          "bio",
          "created_at",
          "profile_picture",
          "balance",
          "earnings_usd",
          "balance_history",
          "verification",
          "whop_partner_enabled_at"
        ],
        "type": "object"
      },
      "UserBalance": {
        "properties": {
          "businesses": {
            "items": {
              "$ref": "#/components/schemas/UserBalanceBusiness",
              "description": "Account balances for accounts the user owns, highest balance first. Excludes accounts with no balance."
            },
            "type": "array"
          },
          "businesses_total_usd": {
            "description": "Combined USD balance across every account the user owns.",
            "type": "string"
          },
          "cash": {
            "items": {
              "$ref": "#/components/schemas/UserBalanceCash",
              "description": "Per-currency fiat cash balances."
            },
            "type": "array"
          },
          "cash_usd": {
            "description": "Fiat cash in USD, including pending and reserve.",
            "type": "string"
          },
          "crypto": {
            "items": {
              "$ref": "#/components/schemas/UserBalanceToken",
              "description": "Per-token crypto holdings in the ledger's own wallet."
            },
            "type": "array"
          },
          "crypto_usd": {
            "description": "Crypto holdings in USD.",
            "type": "string"
          },
          "pending_usd": {
            "description": "Pending funds in USD: fiat pending + in-flight treasury deposits.",
            "type": "string"
          },
          "total_usd": {
            "description": "The user's personal balance in USD: cash (available + pending + reserve) + crypto + in-flight treasury deposits. Excludes account balances (see businesses_total_usd).",
            "type": "string"
          },
          "treasury_pending_usd": {
            "description": "Balance-to-wallet USDT0 withdrawals still in flight, in USD.",
            "type": "string"
          }
        },
        "required": [
          "total_usd",
          "cash_usd",
          "crypto_usd",
          "treasury_pending_usd",
          "pending_usd",
          "cash",
          "crypto",
          "businesses_total_usd",
          "businesses"
        ],
        "type": "object"
      },
      "UserBalanceBusiness": {
        "properties": {
          "balance_usd": {
            "description": "The account's total balance in USD.",
            "type": "string"
          },
          "id": {
            "description": "The account ID, which looks like biz_*************.",
            "type": "string"
          },
          "logo_url": {
            "description": "The account's logo URL.",
            "type": [
              "string",
              "null"
            ]
          },
          "name": {
            "description": "The account's display name.",
            "type": [
              "string",
              "null"
            ]
          }
        },
        "required": [
          "id",
          "name",
          "logo_url",
          "balance_usd"
        ],
        "type": "object"
      },
      "UserBalanceCash": {
        "properties": {
          "balance": {
            "description": "Available balance in the native currency.",
            "type": "number"
          },
          "balance_usd": {
            "description": "Available balance converted to USD.",
            "type": "number"
          },
          "currency": {
            "description": "Lowercase ISO currency code, such as `usd` or `eur`.",
            "type": "string"
          },
          "pending_balance_usd": {
            "description": "Pending balance converted to USD.",
            "type": "number"
          },
          "reserve_balance_usd": {
            "description": "Reserved balance converted to USD.",
            "type": "number"
          },
          "total_withdrawable_balance": {
            "description": "Withdrawable amount in the native currency.",
            "type": "number"
          }
        },
        "required": [
          "currency",
          "balance",
          "balance_usd",
          "pending_balance_usd",
          "reserve_balance_usd",
          "total_withdrawable_balance"
        ],
        "type": "object"
      },
      "UserBalanceHistory": {
        "properties": {
          "data": {
            "items": {
              "$ref": "#/components/schemas/UserBalanceHistoryPoint",
              "description": "Cumulative balance points over the requested window, oldest first."
            },
            "type": "array"
          },
          "last": {
            "description": "Value of the most recent point, in USD.",
            "type": "number"
          },
          "max": {
            "description": "Maximum value across the window, in USD.",
            "type": "number"
          },
          "min": {
            "description": "Minimum value across the window, in USD.",
            "type": "number"
          }
        },
        "required": [
          "data",
          "last",
          "min",
          "max"
        ],
        "type": "object"
      },
      "UserBalanceHistoryPoint": {
        "properties": {
          "t": {
            "description": "Point timestamp, in Unix seconds.",
            "type": "integer"
          },
          "v": {
            "description": "Cumulative wallet balance at this point, in USD.",
            "type": "number"
          }
        },
        "required": [
          "t",
          "v"
        ],
        "type": "object"
      },
      "UserBalanceToken": {
        "properties": {
          "balance": {
            "description": "Amount held in native token units, as a decimal string.",
            "type": "string"
          },
          "icon_url": {
            "description": "Token icon URL.",
            "type": [
              "string",
              "null"
            ]
          },
          "name": {
            "description": "The token's display name.",
            "type": [
              "string",
              "null"
            ]
          },
          "price_usd": {
            "description": "USD price per token, or `null` when unknown.",
            "type": [
              "number",
              "null"
            ]
          },
          "symbol": {
            "description": "Token display symbol, such as `USDT`, `XAUT`, or `cbBTC`.",
            "type": "string"
          },
          "value_usd": {
            "description": "Holding USD value.",
            "type": "number"
          }
        },
        "required": [
          "symbol",
          "name",
          "balance",
          "value_usd",
          "price_usd",
          "icon_url"
        ],
        "type": "object"
      },
      "UserEarnings": {
        "properties": {
          "first_earned_at": {
            "description": "The first time the user earned gross income, as an ISO 8601 timestamp.",
            "type": [
              "string",
              "null"
            ]
          },
          "owned_accounts": {
            "$ref": "#/components/schemas/UserEarningsAmount",
            "description": "Gross income from accounts the user owns or is owner-authorized on."
          },
          "personal": {
            "$ref": "#/components/schemas/UserEarningsAmount",
            "description": "Gross income from the user's personal wallet."
          },
          "total": {
            "$ref": "#/components/schemas/UserEarningsAmount",
            "description": "Gross income from the user's personal wallet plus accounts they own or are owner-authorized on."
          }
        },
        "required": [
          "total",
          "personal",
          "owned_accounts",
          "first_earned_at"
        ],
        "type": "object"
      },
      "UserEarningsAmount": {
        "properties": {
          "last_24_hours": {
            "description": "Gross income in USD over the last 24 hours.",
            "type": "string"
          },
          "last_30_days": {
            "description": "Gross income in USD over the last 30 days.",
            "type": "string"
          },
          "last_7_days": {
            "description": "Gross income in USD over the last 7 days.",
            "type": "string"
          },
          "lifetime": {
            "description": "All-time gross income in USD.",
            "type": "string"
          }
        },
        "required": [
          "lifetime",
          "last_30_days",
          "last_7_days",
          "last_24_hours"
        ],
        "type": "object"
      },
      "UserProfilePicture": {
        "properties": {
          "url": {
            "description": "Avatar image URL. Always present — a generated placeholder when the user set no picture.",
            "type": "string"
          }
        },
        "required": [
          "url"
        ],
        "type": "object"
      },
      "UserRecommendedAction": {
        "properties": {
          "account_id": {
            "description": "The account (`biz_`) a business recommendation is for, or `null` for personal recommendations",
            "type": [
              "string",
              "null"
            ]
          },
          "account_name": {
            "description": "The account's display name, or `null`",
            "type": [
              "string",
              "null"
            ]
          },
          "action": {
            "description": "The recommendation; new values may be added, so handle unknown actions gracefully",
            "enum": [
              "create_business",
              "become_affiliate",
              "become_whop_partner",
              "theme_business",
              "create_product",
              "create_plan",
              "verify_identity",
              "connect_affiliate_program",
              "create_promotion",
              "setup_tracking_pixel",
              "migrate_from_stripe",
              "accept_first_payment",
              "launch_first_ad",
              "launch_draft_campaign",
              "increase_ad_budget",
              "refresh_ad_creatives",
              "fix_ad_billing",
              "exclude_customers_from_ads",
              "retarget_abandoned_checkouts",
              "invite_team_member",
              "enable_tax_collection",
              "create_card",
              "join_whop_university",
              "apply_for_financing"
            ],
            "example": "create_business",
            "type": "string"
          },
          "blocked_capabilities": {
            "items": {
              "description": "Capabilities this would unlock, or empty",
              "type": "string"
            },
            "type": "array"
          },
          "cta": {
            "description": "The URL the call-to-action links to",
            "type": "string"
          },
          "cta_label": {
            "description": "Button label",
            "type": "string"
          },
          "description": {
            "description": "Supporting copy, or empty",
            "type": "string"
          },
          "icon_url": {
            "description": "Illustration icon URL, or `null`",
            "type": [
              "string",
              "null"
            ]
          },
          "impact_score": {
            "description": "Estimated impact from 0-100, or `null` when not ranked",
            "type": [
              "integer",
              "null"
            ]
          },
          "reasoning": {
            "description": "Why this action was recommended, or `null`",
            "type": [
              "string",
              "null"
            ]
          },
          "status": {
            "description": "Always optional — never blocking",
            "enum": [
              "optional"
            ],
            "example": "optional",
            "type": "string"
          },
          "title": {
            "description": "Headline for the recommendation",
            "type": "string"
          }
        },
        "required": [
          "action",
          "status",
          "title",
          "description",
          "cta_label",
          "cta",
          "icon_url",
          "blocked_capabilities",
          "reasoning",
          "impact_score",
          "account_id",
          "account_name"
        ],
        "type": "object"
      },
      "UserSummary": {
        "properties": {
          "id": {
            "description": "User ID, prefixed `user_`.",
            "type": "string"
          },
          "name": {
            "description": "Display name.",
            "type": [
              "string",
              "null"
            ]
          },
          "profile_picture": {
            "$ref": "#/components/schemas/UserProfilePicture",
            "description": "Avatar wrapper; its `url` is always present, using a generated placeholder when the user set no picture."
          },
          "username": {
            "description": "Public username.",
            "type": "string"
          }
        },
        "required": [
          "id",
          "username",
          "name",
          "profile_picture"
        ],
        "type": "object"
      },
      "V1ErrorResponse": {
        "properties": {
          "error": {
            "properties": {
              "message": {
                "description": "Human-readable error message.",
                "type": "string"
              },
              "type": {
                "description": "Machine-readable error code.",
                "type": "string"
              }
            },
            "required": [
              "type",
              "message"
            ],
            "type": "object"
          }
        },
        "required": [
          "error"
        ],
        "type": "object"
      },
      "Visibility": {
        "description": "Visibility of a resource",
        "enum": [
          "visible",
          "hidden",
          "archived",
          "quick_link"
        ],
        "type": "string"
      }
    },
    "securitySchemes": {
      "bearerAuth": {
        "bearerFormat": "auth-scheme",
        "description": "A company API key, company scoped JWT, app API key, or user OAuth token. You must prepend your key/token with the word 'Bearer', which will look like `Bearer ***************************`",
        "scheme": "bearer",
        "type": "http"
      }
    }
  }
}
